// App React para o Gerador de Propostas Comerciais com Versionamento (Modelo de Produção)

const { useState, useEffect, useMemo, useRef, useCallback } = React;

if (typeof Chart !== 'undefined') {
  Chart.Tooltip.positioners.followMouse = function(elements, eventPosition) {
    return { x: eventPosition.x, y: eventPosition.y };
  };
}

const DEAL_VALUE_FIELD_ID = 'ee65221a-029d-4d0a-a981-b71b5a29b4b4';
const RESPONSAVEL_FIELD_ID = ''; // Mapeado via assignees nativos do ClickUp
const API_KEY = '';

// Mesmas opções (id/nome/cor) do custom field "Estágio da Venda" no ClickUp,
// hardcoded aqui pra não precisar mais buscar isso do ClickUp toda vez que o
// Kanban carrega (a SPA passou a ser a fonte de verdade do estágio — ver
// docs/resumo.md, tabela `negocios`). Se as opções mudarem no ClickUp, tem
// que atualizar aqui e em supabase/functions/mcp-brain/clickup.ts também.
const ESTAGIO_OPTIONS = [
  { id: '3c4bcf81-91d3-40e7-97ae-a67b6bccea0c', name: 'Registro', color: '#96c7f2' },
  { id: '1cc9d0c7-cbee-45ff-8bbe-ac4a29ec9f46', name: 'Qualificação', color: '#96c7f2' },
  { id: '5366c82c-2317-4978-8f4d-b41cb953be35', name: 'Proposta', color: '#8dcec3' },
  { id: '97c5f286-e054-4351-b368-25977e8c429d', name: 'Desenvolvimento', color: '#92ceac' },
  { id: '4863ea9f-ccd7-4b49-9aa5-685ee479e091', name: 'Negociação', color: '#12a594' },
  { id: '22e91843-d067-4358-8238-6e619fc66653', name: 'Termo de aceite', color: '#30a46c' },
  { id: 'c59ad408-ae8e-45d7-804f-eb9e6cd2935b', name: 'Ganho', color: '#2ecd6f' },
  { id: '7520c5bc-95a4-47aa-8b12-0711f5bc9bfe', name: 'Perdido', color: '#E65100' },
  { id: 'c231299c-44f8-4f5e-ad8e-58f7b8e01213', name: 'Congelado', color: '#0091ff' },
];

const TIPO_OPORTUNIDADE_CLICKUP = {
  'Projeto': 'fa509e92-7528-4a8b-a9bc-11f2f5da3350',
  'Garantias': '52b4285a-1e92-4ecb-b8b9-7a2348461882',
  'Serviços': '2e351ad7-2af5-4532-be83-fe24423a1994',
  'SSU': '62c6d78c-fa67-44d8-b594-66ed63264df1',
  'Volumes': '62f161bc-b78b-46b7-a73b-1d8faa1a1246',
  'Upgrade': 'e55ef41f-51e6-436e-bb53-79ff688960c7'
};

const chartColors = [
  'rgba(79, 70, 229, 0.8)',   // Indigo (#4f46e5)
  'rgba(16, 185, 129, 0.8)',   // Emerald (#10b981)
  'rgba(245, 158, 11, 0.8)',   // Amber (#f59e0b)
  'rgba(139, 92, 246, 0.8)',   // Violet (#8b5cf6)
  'rgba(6, 182, 212, 0.8)',    // Cyan (#06b6d4)
  'rgba(236, 72, 153, 0.8)',   // Pink
  'rgba(249, 115, 22, 0.8)',    // Orange
];

const chartBorderColors = [
  'rgba(79, 70, 229, 1)',
  'rgba(16, 185, 129, 1)',
  'rgba(245, 158, 11, 1)',
  'rgba(139, 92, 246, 1)',
  'rgba(6, 182, 212, 1)',
  'rgba(236, 72, 153, 1)',
  'rgba(249, 115, 22, 1)',
];

// Cores de "interface" do gráfico de Sazonalidade (eixo, grade, legenda,
// tooltip) — as cores de série (chartColors/chartBorderColors acima) já são
// saturadas o bastante pra funcionar nos dois temas e ficam como estão.
const CHART_UI_COLORS_LIGHT = {
  legendText: '#475569',
  axisTicks: '#64748b',
  axisGrid: '#f1f5f9',
  tooltipBg: '#0f172a',
  tooltipTitle: '#ffffff',
  tooltipBody: '#e2e8f0',
};
const CHART_UI_COLORS_DARK = {
  legendText: '#cbd5e1',
  axisTicks: '#94a3b8',
  axisGrid: '#334155',
  tooltipBg: '#f8fafc',
  tooltipTitle: '#0f172a',
  tooltipBody: '#334155',
};

// Estabiliza a saída de um useMemo por VALOR, não só por referência. O poll
// de 3 em 3 min de Relatórios sempre força um refetch (loadDashboardData com
// forceRefresh padrão true no caminho do polling) e setCommercialData sempre
// recebe um array novo de .filter() — mesmo quando o conteúdo é idêntico ao
// que já estava na tela. Sem isso, distributorTotals/manufacturerTotals/
// topProductsAggregated (que dependem de commercialData) ganham uma
// referência nova a cada poll, o que invalida por referência o useEffect que
// cria os gráficos Chart.js (suas dependências), causando destroy+recreate
// (o "piscar") mesmo sem nenhuma mudança real de valor. Usado com um useRef
// por chamador — guarda a última versão serializada + o objeto retornado, e
// devolve o objeto ANTIGO quando o novo é igual por valor.
function stabilizeByValue(ref, newValue) {
  const key = JSON.stringify(newValue);
  if (ref.current && ref.current.key === key) return ref.current.value;
  ref.current = { key, value: newValue };
  return newValue;
}

const getCleanBusinessName = (raw) => {
  if (!raw) return 'Projeto';
  return String(raw)
    .replace(/^S\/N\s*\|\s*/i, '')
    .replace(/\s*-\s*v+([A-Z]{1,3}|\d+)$/i, '')
    .replace(/\s*-\s*versão\s*[A-Z0-9]+/i, '')
    .trim() || 'Projeto';
};

const calcularValidadeProposta = (prop, dealStatus = null) => {
  if (!prop) return null;
  
  // 1. Propostas ou Negócios já fechados (Ganho / Perdido / Inativo) NUNCA têm tag de vencimento
  const propSit = (prop.situacao || '').trim().toLowerCase();
  const dealSit = (dealStatus || (typeof selectedTask !== 'undefined' && selectedTask ? (selectedTask.estagio || selectedTask.status) : '') || '').trim().toLowerCase();
  
  if (['ganho', 'perdido', 'substituída', 'substituida', 'descartada', 'desconsiderada', 'inativa'].includes(propSit) ||
      ['ganho', 'perdido', 'concluido', 'concluído', 'cancelado'].includes(dealSit)) {
    return null;
  }

  // 2. Propostas zeradas (R$ 0,00) são rascunhos em elaboração -> sem tag
  const valorTotal = parseFloat(prop.total_proposta) || 0;
  if (valorTotal <= 0) return null;

  // 3. A validade comercial obedece à Data de Fechamento definida pelo usuário no formulário
  const dataAlvo = prop.data_fechamento;
  if (!dataAlvo) return null;

  const parts = String(dataAlvo).substring(0, 10).split('-');
  if (parts.length !== 3) return null;
  
  const dAlvo = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  const agora = new Date();
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());

  const diffTime = dAlvo.getTime() - hoje.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  const dataAlvoStr = `${parts[2]}/${parts[1]}`;

  if (diffDays < 0) {
    return {
      status: 'vencida',
      diasVencidos: Math.abs(diffDays),
      dataValidadeStr: dataAlvoStr,
      label: `Expirada em ${dataAlvoStr}`
    };
  } else if (diffDays === 0) {
    return {
      status: 'vence_hoje',
      diasRestantes: 0,
      dataValidadeStr: dataAlvoStr,
      label: `Vence hoje (${dataAlvoStr})`
    };
  } else {
    return {
      status: 'valida',
      diasRestantes: diffDays,
      dataValidadeStr: dataAlvoStr,
      label: `Válida até ${dataAlvoStr} (${diffDays}d)`
    };
  }
};

// ─────────────────────────────────────────────
// TAREFAS COMERCIAIS — helpers de apresentação
// ─────────────────────────────────────────────
// Vivem no escopo de módulo (e não dentro do bloco da aba de Tarefas, como
// antes) porque são puros e agora servem a dois consumidores: os cards da
// lista e o modal de resumo da tarefa.

// Tipo de tarefa é texto livre, editável pelo usuário (Configurações →
// Tipos de Tarefa) — sem essa normalização, uma variação como "Follow Up"
// (sem hífen) não bate com a chave 'Follow-up' e cai sempre no fallback
// genérico (ícone/cor errados), mesmo sendo claramente o mesmo tipo.
// Compara ignorando espaço/hífen e maiúsculas.
const normalizeTypeKey = (s) => (s || '').toLowerCase().replace(/[\s-]+/g, '');

// getTaskTypeConfig é uma FUNÇÃO (e não um objeto pré-computado) de
// propósito: os componentes de ícone vêm de empresas.js, outro <script>.
// Resolver na hora da chamada — sempre em render, muito depois dos dois
// arquivos carregarem — elimina qualquer dependência de ordem de avaliação
// entre eles.
const getTaskTypeConfig = (tipo) => {
  const TASK_TYPE_CONFIG = {
    'Ligação':   { dot: 'bg-indigo-500',  bg: 'bg-indigo-50',   text: 'text-indigo-700',   border: 'border-indigo-200', Icon: (typeof IconPhone !== 'undefined' ? IconPhone : null) },
    'Reunião':   { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700',  border: 'border-emerald-200', Icon: (typeof IconUsers !== 'undefined' ? IconUsers : null) },
    'E-mail':    { dot: 'bg-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-200',   Icon: (typeof IconMail !== 'undefined' ? IconMail : null) },
    'Follow-up': { dot: 'bg-rose-500',    bg: 'bg-rose-50',     text: 'text-rose-700',     border: 'border-rose-200',    Icon: (typeof IconRefresh !== 'undefined' ? IconRefresh : null) },
    'Visita':    { dot: 'bg-violet-500',  bg: 'bg-violet-50',   text: 'text-violet-700',   border: 'border-violet-200',  Icon: (typeof IconMapPin !== 'undefined' ? IconMapPin : null) },
    'Proposta':  { dot: 'bg-sky-500',     bg: 'bg-sky-50',      text: 'text-sky-700',      border: 'border-sky-200',     Icon: (typeof IconDocument !== 'undefined' ? IconDocument : null) },
  };
  const key = normalizeTypeKey(tipo);
  const match = Object.entries(TASK_TYPE_CONFIG).find(([k]) => normalizeTypeKey(k) === key);
  if (match) return match[1];
  return { dot: 'bg-slate-400', bg: 'bg-slate-50 dark:bg-slate-700', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-700', Icon: (typeof IconDocument !== 'undefined' ? IconDocument : null) };
};

const TASK_URGENCY_CONFIG = {
  overdue: { bg: 'bg-rose-100',   text: 'text-rose-700',   ring: 'ring-rose-200' },
  today:   { bg: 'bg-amber-100',  text: 'text-amber-700',  ring: 'ring-amber-200' },
  soon:    { bg: 'bg-sky-50',     text: 'text-sky-700',    ring: 'ring-sky-200' },
  normal:  { bg: 'bg-slate-100 dark:bg-slate-700',  text: 'text-slate-500 dark:text-slate-400',  ring: 'ring-slate-200 dark:ring-slate-700' },
};

// `now` entra por parâmetro (antes era capturado do escopo do bloco da aba):
// a lista passa o mesmo `now` de todos os cards, mantendo os rótulos
// coerentes entre si, e o modal usa o instante da abertura.
const formatTaskDate = (dateStr, now = new Date()) => {
  const d = new Date(dateStr);
  const dStr = d.toDateString();
  const todayStr = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toDateString();
  const diffMs = d - now;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (dStr === todayStr) return { label: 'Hoje ' + d.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'}), urgent: 'today' };
  if (diffDays < 0) return { label: `Venceu há ${Math.abs(diffDays)}d`, urgent: 'overdue' };
  if (diffDays === 1) return { label: 'Amanhã', urgent: 'soon' };
  if (diffDays <= 3) return { label: `Em ${diffDays} dias`, urgent: 'soon' };
  return { label: d.toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'}), urgent: 'normal' };
};

// Utilitário seguro para localStorage blindado contra QuotaExceededError do Safari
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key, val) => {
    try {
      localStorage.setItem(key, val);
    } catch (e) {
      try {
        localStorage.removeItem('crm_cache_kanban_tasks_v2');
        localStorage.removeItem('crm_cache_kanban_tasks_v3');
        localStorage.removeItem('crm_cache_kanban_tasks_v4');
        localStorage.removeItem('crm_cache_vendedores');
        localStorage.setItem(key, val);
      } catch (err) {}
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {}
  }
};

// ─────────────────────────────────────────────
// TEMA (claro/escuro) — escopado por usuário
// ─────────────────────────────────────────────
// A preferência mora só no navegador (nunca no banco), mas a chave é por
// usuário: dois vendedores no mesmo navegador têm temas independentes, e
// trocar de conta não herda o tema de quem estava logado antes.
// O id usado é o clickup_user_id (userProfile.id) — o mesmo identificador
// canônico de autoria do sistema (criado_por_user_id) e o único disponível
// de forma SÍNCRONA no boot (via crm_user_profile no localStorage), o que
// permite ao script anti-flash do index.html resolver o tema certo antes do
// primeiro paint. O id da sessão do Supabase só resolve async e traria de
// volta o flash de tema errado.
const themeKeyFor = (uid) => `crm_theme:${uid || 'anon'}`;

const readProfileIdFromStorage = () => {
  try {
    const raw = safeStorage.getItem('crm_user_profile');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? String(parsed.id) : null;
  } catch (e) {
    return null;
  }
};

// Restauração de sessão (recarregar a página / renovar a sessão): o servidor só CONFIRMA o acesso (`/me/acesso` não devolve nome); o nome vem do login (`/me/identidade`) e fica em
// `crm_user_profile`. Sem isto o gate devolve o texto genérico «Usuário CRM» e a barra mostra «US». O nome guardado só vale para a MESMA pessoa (clickup id); nunca de outra.
const perfilParaExibicao = (confirmado, armazenado) => {
  try {
    if (!confirmado) return confirmado;
    if (typeof confirmado.username === 'string' && confirmado.username && confirmado.username !== 'Usuário CRM') return confirmado;
    const guardado = typeof armazenado === 'string' ? JSON.parse(armazenado) : armazenado;
    if (guardado && String(guardado.id) === String(confirmado.id) && typeof guardado.username === 'string' && guardado.username.trim() && guardado.username.length <= 160
        && guardado.username !== 'Usuário CRM') return { ...confirmado, username: guardado.username };
  } catch (e) { /* cache ilegível: mantém o perfil confirmado pelo servidor */ }
  return confirmado;
};

const readStoredTheme = (uid) => {
  const v = safeStorage.getItem(themeKeyFor(uid));
  return (v === 'dark' || v === 'light') ? v : null;
};

const getSystemTheme = () =>
  (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';

// Filtros da aba Tarefas escopados por usuário (mesmo padrão do tema): cada
// vendedor tem seus próprios filtros preferidos (ex.: "só minhas tarefas"),
// e trocar de conta no mesmo navegador não deve herdar o que a outra pessoa
// deixou marcado.
const tasksFiltersKeyFor = (uid) => `crm_tasks_filters:${uid || 'anon'}`;

const readTasksFilters = (uid) => {
  try {
    const raw = safeStorage.getItem(tasksFiltersKeyFor(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

// Migração one-shot da chave global antiga (crm_theme) para a chave escopada,
// preservando a preferência de quem já usava o CRM. O removeItem é a parte
// essencial: se a chave global sobreviver, o index.html continua caindo nela
// como fallback e o próximo usuário a logar nesse navegador herda o tema do
// anterior — exatamente o bug que essa mudança resolve.
(() => {
  const legacy = safeStorage.getItem('crm_theme');
  if (legacy === 'dark' || legacy === 'light') {
    safeStorage.setItem(themeKeyFor(readProfileIdFromStorage()), legacy);
    safeStorage.removeItem('crm_theme');
  }
})();

// Configuração padrão
const getInitialConfig = () => {
  return {
    url: '',
    anonKey: '',
  };
};

const getSupabaseHeaders = () => {
  const token = safeStorage.getItem('crm_user_clickup_token');
  return token ? { 'Authorization': token } : {};
};

// CRM surfaces use the Supabase session; ClickUp relay continues using its own token.
const getCrmHeaders = () => window.CrmUsuarios ? window.CrmUsuarios.headers(getSupabaseHeaders) : Promise.resolve(getSupabaseHeaders());

// Registra uma atividade no histórico de um negócio. Sem estado de propósito:
// além do formulário do drawer (handleCreateAtividade), o fluxo de "concluir
// tarefa registrando atividade" chama isso de fora do negócio, passando o id
// que veio da própria tarefa. Lança em caso de erro — quem chama decide como
// avisar o usuário.
//
// `texto` é só o corpo digitado pela pessoa (sem prefixo/colchetes) — a
// referência à tarefa de origem vai em campos estruturados (origem/
// tarefaTipo/tarefaTitulo), não mais embutida na string. O comentário
// espelhado no ClickUp continua legível: a Edge Function monta a frase de
// contexto lá, a partir desses mesmos campos.
const createAtividade = async ({ clickupNegocioId, texto, autorNome, autorClickupId, origem, tarefaId, tarefaTipo, tarefaTitulo, anexos }) => {
  const idClean = String(clickupNegocioId || '').replace('#', '').trim();
  if (!idClean) throw new Error('Negócio não identificado para registrar a atividade.');
  const conteudo = String(texto || '').trim();
  if (!conteudo) throw new Error('Escreva o texto da atividade.');

  const res = await fetch('/api/atividades', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
    body: JSON.stringify({
      clickup_negocio_id: idClean,
      texto: conteudo,
      autor_nome: autorNome || undefined,
      autor_clickup_id: autorClickupId || undefined,
      origem: origem || undefined,
      tarefa_id: tarefaId || undefined,
      tarefa_tipo: tarefaTipo || undefined,
      tarefa_titulo: tarefaTitulo || undefined,
      anexos: anexos || undefined
    })
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || 'Erro ao registrar atividade.');
  }
  return res.json().catch(() => ({}));
};

// Sobe um arquivo (imagem, PDF, etc.) como anexo NATIVO da task do ClickUp
// que representa o negócio — o arquivo em si nunca passa pelo nosso banco,
// só o ponteiro (ver createAtividade `anexos`). Vai por /clickup-upload/,
// um proxy nginx dedicado que — diferente de /clickup-api/ — não sobrescreve
// o Content-Type (precisa preservar o boundary do multipart) e aceita corpo
// maior (client_max_body_size 30m nessa location, só nela).
const uploadAtividadeAnexo = async ({ clickupNegocioId, file }) => {
  const idClean = String(clickupNegocioId || '').replace('#', '').trim();
  if (!idClean) throw new Error('Negócio não identificado para anexar o arquivo.');
  if (!file) throw new Error('Nenhum arquivo selecionado.');

  const formData = new FormData();
  formData.append('attachment', file, file.name);

  const res = await fetch(`/clickup-upload/task/${idClean}/attachment`, {
    method: 'POST',
    // Sem Content-Type aqui de propósito: o navegador define
    // "multipart/form-data; boundary=..." sozinho a partir do FormData —
    // declarar um Content-Type manual quebraria o boundary.
    headers: { ...getSupabaseHeaders() },
    body: formData
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Erro ao enviar anexo ao ClickUp (${res.status}).${errText ? ' ' + errText.slice(0, 200) : ''}`);
  }
  const data = await res.json();
  return {
    clickup_attachment_id: data.id || null,
    title: data.title || data.name || file.name,
    url: data.url || data.url_w_query || null,
    mimetype: file.type || 'application/octet-stream',
    size: file.size || null
  };
};

// O mesmo domínio carregado pelo navegador é executado pelos testes de R.O.
const {
  fetchRegistrosOportunidade,
  fetchTodasRosOportunidade,
  obterCicloRo,
  obterDataRelevanteRo,
  prepararCardsRosDaOportunidade,
  fetchResumoRos,
  atualizarFiltrosRos,
  limparFiltrosRos,
  formatarDataCivil,
  gerarIdentificacaoDerivadaRo,
  formatarCategoriaRo,
  estaOportunidadeElegivelParaRo,
  obterLinkOportunidade,
  tratarEstadoResumo,
  obterHojeSaoPaulo,
  calcularVigenciaRo,
  obterRotuloSituacao,
  calcularPaginacao,
  ordenarEventosRo,
  resumirCiclosRo,
  obterAcoesPermitidasRo,
  roSemVinculo,
  empresaDaRo,
  oportunidadesElegiveisParaVinculo,
  MENSAGEM_SEM_OPORTUNIDADE_ELEGIVEL,
  CATEGORIAS_RO,
  opcoesCategoriaEdicaoRo,
  rotuloOportunidadeVinculo,
  etapaOriginalImportacao,
  pendenciasImportacaoRo,
  normalizarFabricantesAtivosRo,
  montarOpcoesFabricantesEdicaoRo,
  validarPayloadCriacaoRo,
  validarPayloadAcaoRo,
  executarAcaoRo,
  hidratarEdicaoRo,
  salvarEdicaoRo,
  fetchRegistroOportunidade,
  fetchPermissaoRos,
  fetchRosExcluidas,
  fetchCadeiaExclusaoRo,
  podeRestaurarRo,
  obterTipoExclusaoRo,
  montarConfirmacaoCadeia,
  cadeiaTemBloqueio,
  rotuloBloqueioCadeia,
  gerarRequestIdRo,
  desambiguarOportunidade,
  criarRegistroOportunidade,
  prepararAberturaNovaRo,
  obterPeriodoMesCivilAtual,
  validarPeriodoEvidencias,
  fetchEvidenciasRos,
  formatarRelatorioAtualizacaoFabricante,
  hidratarFormularioOportunidade,
  montarPayloadPersistenciaOportunidade,
  montarCamposClickUpOportunidade,
  prepararRosOportunidadeParaExibicao,
} = window.RosUiDomain || {};

// ─────────────────────────────────────────────────────────────────────────
// PILHA DE CAMADAS MODAIS (Fase 2 do upgrade de navegação por teclado).
// Antes disso, ESC dependia de uma cadeia if/else escrita à mão que tinha
// que ser mantida manualmente em sincronia com quais modais existiam — o
// modal de Editar Oportunidade nunca entrou nela e por isso ESC não o
// fechava. Agora cada modal aberto se registra aqui (via useModalLayer) com
// sua própria função de fechar, e o listener global de ESC só chama o topo
// da pilha, na ordem real de abertura — sem precisar de uma lista hard-coded.
// Array de módulo (não estado React): o registro precisa ser síncrono com a
// montagem/desmontagem do modal, e nenhum componente precisa re-renderizar
// só porque outro modal abriu/fechou.
// ─────────────────────────────────────────────────────────────────────────
let modalLayerSeq = 0;
const modalLayers = [];

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Hook: registra um modal na pilha enquanto `open` for true. Cuida de foco
// inicial (data-autofocus > primeiro focável > o próprio painel), devolve o
// foco pra quem abriu ao fechar, e devolve um onKeyDown pra grudar no painel
// que faz o focus trap (Tab/Shift+Tab não escapam do modal).
const useModalLayer = ({ open, onClose, panelRef }) => {
  const idRef = React.useRef(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    const id = ++modalLayerSeq;
    idRef.current = id;
    modalLayers.push({ id, close: () => onCloseRef.current && onCloseRef.current() });

    const previouslyFocused = document.activeElement;
    const focusTimer = setTimeout(() => {
      const panel = panelRef && panelRef.current;
      if (!panel) return;
      const target = panel.querySelector('[data-autofocus]') || panel.querySelector(FOCUSABLE_SELECTOR) || panel;
      if (target && typeof target.focus === 'function') target.focus();
    }, 0);

    return () => {
      clearTimeout(focusTimer);
      const idx = modalLayers.findIndex(l => l.id === id);
      if (idx !== -1) modalLayers.splice(idx, 1);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = React.useCallback((e) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef && panelRef.current;
    if (!panel) return;
    // Só faz o trap se esta camada for a do topo da pilha — evita capturar
    // Tab de um modal que ficou por baixo caso dois coexistam por um instante.
    const top = modalLayers[modalLayers.length - 1];
    if (top && top.id !== idRef.current) return;
    const focusables = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(el => el.offsetParent !== null || el === document.activeElement);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first || !panel.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  return { onKeyDown };
};

// Navegação por setas dentro de um grupo marcado com data-arrow-group: os
// filhos com data-arrow-item viram uma "roving tabindex" informal — Home/End
// pulam pras pontas. Usado nas abas do drawer, nas abas de Configurações e
// no rodapé de ações do modal de resumo da tarefa.
const handleArrowNav = (e) => {
  if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
  const group = e.currentTarget;
  // Ignora itens desabilitados — .focus() neles é um no-op no navegador, e
  // sem filtrar a navegação "trava" quando calha de cair num deles.
  const items = Array.from(group.querySelectorAll('[data-arrow-item]')).filter(el => !el.disabled);
  if (items.length === 0) return;
  const currentIndex = items.indexOf(document.activeElement);
  let nextIndex = currentIndex;
  if (e.key === 'Home') nextIndex = 0;
  else if (e.key === 'End') nextIndex = items.length - 1;
  else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
  else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') nextIndex = currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
  if (nextIndex !== currentIndex) {
    e.preventDefault();
    const nextItem = items[nextIndex];
    nextItem.focus();
    // Abas (data-arrow-activate): padrão de "ativação automática" — a seta
    // já troca de aba, sem precisar de Enter/Espaço depois.
    if (nextItem.hasAttribute('data-arrow-activate')) nextItem.click();
  }
};

// Sincroniza o token pessoal e perfil do ClickUp de forma segura (criptografado com AES-GCM)
// com as Edge Functions do Supabase para que as ações em background possam agir em nome do vendedor.
const syncUserClickUpCredentialsToEdge = async (token, userObj, client = null, cfg = null) => {
  if (cfg?.identity_mode === 'canonico_v1') {
    if (!window.CrmIdentidadeLogin || !client) throw new Error('Validação de acesso indisponível.');
    const { data, error } = await client.auth.getSession();
    if (error || !data?.session) throw new Error('Sessão indisponível.');
    const result = await window.CrmIdentidadeLogin.criarGate().confirmar({ session: data.session, token, bootstrap: true });
    if (!result) throw new Error('Validação de acesso cancelada.');
    return result;
  }
  try {
    if (!token || !userObj) return;
    if (client && client.functions) {
      const { data, error } = await client.functions.invoke('save-clickup-credentials', {
        body: { token, user: userObj }
      });
      if (error) {
        console.warn('[Token Sync] Aviso da Edge Function:', error.message);
      } else {
        console.log('[Token Sync] Credenciais ClickUp sincronizadas com Edge Functions com sucesso.');
      }
    } else {
      const supaUrl = cfg?.url || 'https://supabase.llworkflow.com.br';
      const anonKey = cfg?.anonKey || '';
      const url = `${supaUrl.replace(/\/+$/, '')}/functions/v1/save-clickup-credentials`;
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(anonKey ? { 'apikey': anonKey, 'Authorization': `Bearer ${anonKey}` } : {})
        },
        body: JSON.stringify({ token, user: userObj })
      }).catch(e => console.warn('[Token Sync] Aviso ao enviar credenciais:', e));
    }
  } catch (err) {
    console.warn('[Token Sync] Erro ao sincronizar credenciais com Edge Functions (não bloqueante):', err);
  }
};

// Função utilitária global: extrai o nome do estágio de forma segura.
// Evita crash fatal quando stage_name ou status são objetos em vez de strings.
const getSafeStageName = (card) => {
  if (!card) return "";
  let val = "";
  if (card.stage_name) {
    val = typeof card.stage_name === 'object' 
      ? (card.stage_name.name || card.stage_name.status || card.stage_name.value || "") 
      : card.stage_name;
  } else if (card.status) {
    val = typeof card.status === 'object' 
      ? (card.status.status || card.status.name || card.status.value || "") 
      : card.status;
  }
  return String(val || "").toLowerCase().trim();
};

const formatValueCompact = (val) => {
  if (val === undefined || val === null) return 'R$ 0';
  if (val >= 1e6) return `R$ ${(val / 1e6).toFixed(2)}M`;
  if (val >= 1e3) return `R$ ${(val / 1e3).toFixed(0)}K`;
  return `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatMaskedCurrency = (value) => {
  if (value === undefined || value === null) return '0,00';
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// ─────────────────────────────────────────────
// ABA ATIVIDADES REPAGINADA — helpers puros (Fase 3).
// ─────────────────────────────────────────────

// Normaliza acento/caixa pra busca: "genetica" acha "genética" e vice-versa.
const normalizeForSearch = (s) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Marca as ocorrências de `query` num texto PURO (sem marcador de menção) em
// <mark>. Usado tanto pela busca do histórico quanto embutido dentro de
// renderTextoComMencoes — nunca aplicado sobre o `@[Nome](id)` cru, só sobre
// os pedaços de texto normal que ficam entre as menções.
const highlightMatches = (texto, query, keyPrefix = 'hl') => {
  const str = String(texto || '');
  const q = String(query || '').trim();
  if (!q) return [str];
  const normStr = normalizeForSearch(str);
  const normQ = normalizeForSearch(q);
  if (!normQ) return [str];
  const parts = [];
  let cursor = 0;
  let idx;
  let key = 0;
  while ((idx = normStr.indexOf(normQ, cursor)) !== -1) {
    if (idx > cursor) parts.push(str.slice(cursor, idx));
    parts.push(
      <mark key={`${keyPrefix}-${key++}`} className="bg-amber-200 dark:bg-amber-500/40 text-inherit rounded-sm px-0.5">
        {str.slice(idx, idx + q.length)}
      </mark>
    );
    cursor = idx + normQ.length;
  }
  if (cursor < str.length) parts.push(str.slice(cursor));
  return parts.length ? parts : [str];
};

// Resolve a redundância "[Follow Up] Follow": quando um contém o outro (sem
// acento/hífen/caixa), devolve só o mais longo dos dois. Senão, "Tipo · Título".
const tarefaSeloLabel = (tipo, titulo) => {
  const t = String(tipo || '').trim();
  const ti = String(titulo || '').trim();
  if (!t) return ti || '';
  if (!ti) return t;
  const norm = (s) => normalizeForSearch(s).replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
  const nt = norm(t);
  const nti = norm(ti);
  if (nt === nti || nt.includes(nti) || nti.includes(nt)) {
    return nt.length >= nti.length ? t : ti;
  }
  return `${t} · ${ti}`;
};

// "agora" / "há N min" / "há Nh" / "ontem" / "há N dias" / data absoluta
// (7+ dias). `now` por parâmetro pra dar pra testar com um instante fixo.
const formatRelativeTime = (iso, now = new Date()) => {
  if (!iso) return { relative: '—', absolute: '—' };
  const date = new Date(iso);
  if (isNaN(date.getTime())) return { relative: '—', absolute: '—' };
  const absolute = date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  let relative;
  if (diffMin < 1) relative = 'agora';
  else if (diffMin < 60) relative = `há ${diffMin} min`;
  else if (diffHours < 24) relative = `há ${diffHours}h`;
  else if (diffDays === 1) relative = 'ontem';
  else if (diffDays <= 6) relative = `há ${diffDays} dias`;
  else relative = absolute;
  return { relative, absolute };
};

// Lê uma linha de atividade num formato único, cobrindo tanto as colunas
// novas (autor_nome/origem/tarefa_tipo/tarefa_titulo — ver migração
// 20260904) quanto o histórico anterior a elas, cujo `texto` carrega tudo
// misturado num de dois formatos manuscritos.
const parseAtividade = (ativ) => {
  const base = {
    autorNome: ativ?.autor_nome || null,
    autorId: ativ?.autor_clickup_id || null,
    quando: ativ?.data_execucao || ativ?.created_at || null,
    origem: ativ?.origem || 'manual',
    tarefaTipo: ativ?.tarefa_tipo || null,
    tarefaTitulo: ativ?.tarefa_titulo || null,
    corpo: String(ativ?.texto || ''),
    anexos: Array.isArray(ativ?.anexos) ? ativ.anexos : [],
    clickupSynced: !!ativ?.clickup_comment_id
  };

  // Colunas novas presentes → leitura direta, sem tocar no texto. Não basta
  // checar `ativ.origem`: a coluna tem DEFAULT 'manual' e por isso toda
  // linha antiga também ganhou esse valor na migração — o sinal real de
  // "isso já é registro novo" é ter autor ou referência de tarefa.
  if (base.autorNome || base.tarefaTipo || base.tarefaTitulo) {
    return base;
  }

  const texto = String(ativ?.texto || '');

  // Formato criado ontem: "✅ [Tipo] Título — corpo"
  let m = texto.match(/^✅ \[([^\]]+)\]\s*(.+?)\s+—\s+([\s\S]*)$/);
  if (m) {
    return { ...base, origem: 'tarefa', tarefaTipo: m[1].trim(), tarefaTitulo: m[2].trim(), corpo: m[3].trim() };
  }

  // Formato antigo digitado à mão: "[dd/mm/aaaa, hh:mm:ss] Nome: corpo"
  m = texto.match(/^\[(\d{2}\/\d{2}\/\d{4}[^\]]*)\]\s*([^:\n]{1,60}):\s*([\s\S]*)$/);
  if (m) {
    return { ...base, autorNome: m[2].trim(), corpo: m[3].trim() };
  }

  return base;
};

// Busca sem acento/caixa sobre corpo + autor + referência de tarefa.
const matchAtividade = (parsed, query) => {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const haystack = normalizeForSearch(
    [parsed.corpo, parsed.autorNome, parsed.tarefaTipo, parsed.tarefaTitulo].filter(Boolean).join(' ')
  );
  return haystack.includes(q);
};

// Cabeçalho de agrupamento por dia do feed: "Hoje" / "Ontem" / "01 de setembro".
const atividadeDiaLabel = (iso, now = new Date()) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
};

// ─────────────────────────────────────────────
// @MENÇÕES (Registrar Atividade) — marcador "@[Nome](clickupUserId)" no
// texto bruto. Preserva o id numérico do ClickUp pra virar uma menção real
// (clicável, notifica a pessoa) quando o comentário é sincronizado lá —
// ver handle_create_atividade em server.py. `query` opcional (Fase 3): marca
// ocorrências de busca nos trechos de texto puro, sem tocar nas menções.
// ─────────────────────────────────────────────
const renderTextoComMencoes = (texto, query = '') => {
  if (!texto) return null;
  const re = /@\[([^\]]+)\]\((\d+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = re.exec(texto)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...highlightMatches(texto.slice(lastIndex, match.index), query, `hl${key}`));
    }
    parts.push(
      <span key={`mencao-${key++}`} className="inline-block font-bold text-indigo-700 bg-indigo-50 px-1 rounded">@{match[1]}</span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < texto.length) {
    parts.push(...highlightMatches(texto.slice(lastIndex), query, 'hlEnd'));
  }
  return parts;
};

const MentionTextarea = ({ value, onChange, membros = [], placeholder = '', rows = 3 }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const [triggerPos, setTriggerPos] = React.useState(null);
  const textareaRef = React.useRef(null);

  const filtered = React.useMemo(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    return (membros || []).filter(m => !q || (m.nome || '').toLowerCase().includes(q)).slice(0, 6);
  }, [open, query, membros]);

  React.useEffect(() => { setHighlight(0); }, [filtered]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    const cursor = e.target.selectionStart;
    onChange(newVal);

    const beforeCursor = newVal.slice(0, cursor);
    const atIdx = beforeCursor.lastIndexOf('@');
    if (atIdx === -1 || /\s/.test(beforeCursor.slice(atIdx + 1))) {
      setOpen(false);
      return;
    }
    setTriggerPos(atIdx);
    setQuery(beforeCursor.slice(atIdx + 1));
    setOpen(true);
  };

  const handleSelect = (membro) => {
    if (triggerPos === null || !textareaRef.current) return;
    const cursor = textareaRef.current.selectionStart;
    const before = value.slice(0, triggerPos);
    const after = value.slice(cursor);
    const insertion = `@[${membro.nome}](${membro.id}) `;
    const newVal = before + insertion + after;
    onChange(newVal);
    setOpen(false);
    setQuery('');
    setTriggerPos(null);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = (before + insertion).length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(i => (i + 1) % filtered.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(i => (i - 1 + filtered.length) % filtered.length); }
    else if (e.key === 'Enter') { e.preventDefault(); handleSelect(filtered[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        rows={rows}
        className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 shadow-xs focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none transition-all"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden max-h-48 overflow-y-auto">
          {filtered.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(m); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center gap-2 transition-colors ${i === highlight ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
            >
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-black flex items-center justify-center shrink-0">{(m.nome || '?').slice(0, 1).toUpperCase()}</span>
              <span>{m.nome}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const KanbanCard = React.memo(({ task, dealValue, formattedValue, responsavel, handleDragStart, handleCardClick, hasOverdue, stageColor }) => {
  return (
    <div
      data-id={task.id}
      draggable={true}
      onDragStart={(e) => handleDragStart(e, task)}
      onClick={() => handleCardClick(task)}
      className="kanban-card flex flex-col relative"
      style={{ borderLeft: `4px solid ${stageColor || '#6366f1'}` }}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 line-clamp-2 pr-2">{task.name}</h4>
        {hasOverdue && (
          <span
            className="w-2.5 h-2.5 rounded-full bg-red-500 border border-white flex-shrink-0 mt-1 animate-pulse"
            title="Possui tarefa comercial atrasada!"
          />
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mt-auto">
        <span className="flex items-center gap-1.5 min-w-0">
          {responsavel && typeof AvatarInicial !== 'undefined' && (
            <AvatarInicial nome={responsavel} size="xs" />
          )}
          <span className="truncate">{responsavel || 'Sem Responsável'}</span>
        </span>
        <span className="text-emerald-600 font-semibold text-sm shrink-0">{formattedValue}</span>
      </div>
    </div>
  );
});

const STAGE_ORDER = [
  { key: 'registro',       width: '100%' },
  { key: 'qualifica',      width: '88%'  },
  { key: 'proposta',       width: '76%'  },
  { key: 'desenvolvimento',width: '64%'  },
  { key: 'negocia',        width: '52%'  },
  { key: 'termo',          width: '40%'  },
  { key: 'aceite',         width: '40%'  },
];

const getStageSortKey = (name) => {
  const n = name.toLowerCase();
  for (let i = 0; i < STAGE_ORDER.length; i++) {
    if (n.includes(STAGE_ORDER[i].key)) return i;
  }
  return 99;
};

const getStageWidth = (name) => {
  const n = name.toLowerCase();
  for (let i = 0; i < STAGE_ORDER.length; i++) {
    if (n.includes(STAGE_ORDER[i].key)) return STAGE_ORDER[i].width;
  }
  return '100%';
};

// Mesmo predicado usado para filtrar os cards do Kanban pela busca — compartilhado
// aqui também com o ForecastFunnelPanel para que o forecast reflita exatamente
// os mesmos negócios que a busca já mostra nos cards.
const taskMatchesSearchTerm = (task, term) => {
  if (!term) return true;
  const nameMatch = (task.name || '').toLowerCase().includes(term);
  const customFieldsStr = (task.custom_fields || []).map(f => String(f.value || '')).join(' ').toLowerCase();
  return nameMatch || customFieldsStr.includes(term);
};

const ForecastFunnelPanel = ({
  kanbanColumns,
  kanbanTasks,
  filterStage,
  setFilterStage,
  filterFabricante,
  setFilterFabricante,
  kanbanSearchTerm,
  kanbanFilterResponsavelNome,
  getTaskOptionId,
  getOpportunityValue,
  onCardClick,
  theme
}) => {
  const isDark = theme === 'dark';
  // Guards defensivos: garante que arrays nunca sejam undefined
  const safeColumns = Array.isArray(kanbanColumns) ? kanbanColumns : [];
  const allTasks = Array.isArray(kanbanTasks) ? kanbanTasks : [];

  // Lista de fabricantes distintos presentes nos negócios ativos, para o filtro
  const fabricantesDisponiveis = Array.from(
    new Set(allTasks.flatMap(t => Array.isArray(t.fabricantes) ? t.fabricantes : []))
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const searchTermNormalized = (kanbanSearchTerm || '').toLowerCase().trim();

  // Com fabricante selecionado, mostra só a fatia de valor daquele fabricante no
  // negócio (não o valor cheio da proposta) — negócio.valorPorFabricante é montado
  // em fetchKanbanData a partir de itens_proposta (quantidade * preco_unitario).
  const valueForCurrentFilter = (t) => filterFabricante
    ? (t?.valorPorFabricante?.[filterFabricante] || 0)
    : (getOpportunityValue ? (getOpportunityValue(t) || 0) : 0);

  const safeTasks = allTasks
    .filter(t => !filterFabricante || (Array.isArray(t.fabricantes) && t.fabricantes.includes(filterFabricante)))
    .filter(t => !kanbanFilterResponsavelNome || t.responsavel_negocio === kanbanFilterResponsavelNome)
    .filter(t => taskMatchesSearchTerm(t, searchTermNormalized));

  const activeCols = safeColumns.filter(col => {
    if (!col || typeof col.name !== 'string') return false;
    const colName = col.name.toLowerCase();
    if (colName.includes("ganho") || colName.includes("perdido") || colName.includes("congelado")) return false;
    return true;
  });

  const rawStageData = activeCols.map(col => {
    const tasksInCol = safeTasks.filter(t => getTaskOptionId && getTaskOptionId(t, safeColumns) === col.id);
    const total = tasksInCol.reduce((acc, t) => acc + valueForCurrentFilter(t), 0);
    return {
      id: col.id,
      name: col.name,
      color: col.color || '#6366f1',
      total,
      count: tasksInCol.length,
      funnelWidth: getStageWidth(col.name),
    };
  });

  // Sort stages chronologically (top of funnel first)
  const stageData = [...rawStageData].sort((a, b) => getStageSortKey(a.name) - getStageSortKey(b.name));

  const totalFunnelSum = stageData.reduce((acc, s) => acc + s.total, 0);
  const selectedStageObj = filterStage ? stageData.find(s => s.id === filterStage) : null;
  const displayTotal = selectedStageObj ? selectedStageObj.total : totalFunnelSum;
  const displayTitle = selectedStageObj ? selectedStageObj.name : "Total Funil";

  return (
    <div className={`px-6 py-5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col ${filterStage ? 'flex-1 min-h-0 overflow-hidden' : 'flex-shrink-0'}`}>
      <div className="mb-4 flex items-center justify-between flex-shrink-0 flex-wrap gap-2">
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Funil de Vendas &amp; Forecast</span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Fabricante:</span>
            <select
              value={filterFabricante || ''}
              onChange={(e) => setFilterFabricante(e.target.value || null)}
              className="text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-600/70 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <option value="">Todos</option>
              {fabricantesDisponiveis.map(fab => (
                <option key={fab} value={fab}>{fab}</option>
              ))}
            </select>
          </div>
          {(filterStage || filterFabricante) && (
            <button
              onClick={() => { setFilterStage(null); setFilterFabricante(null); }}
              className="text-[10px] text-indigo-400 hover:text-indigo-300 font-bold underline cursor-pointer"
            >
              Limpar Filtro
            </button>
          )}
        </div>
      </div>

      <div className={`flex flex-col lg:flex-row gap-6 items-stretch w-full ${filterStage ? 'flex-1 min-h-0' : ''}`}>
        {/* Left Column: Funil (e Card de Total quando estágio selecionado) */}
        <div className={`flex flex-col items-stretch space-y-3 flex-shrink-0 ${
          filterStage && selectedStageObj ? 'w-full lg:w-[38%]' : 'w-full lg:w-[65%]'
        }`}>
          <div className="flex flex-col space-y-2 py-1">
            {stageData.map((stage) => {
              const isSelected = filterStage === stage.id;
              return (
                <div key={stage.id} className="w-full flex justify-center">
                  <button
                    onClick={() => setFilterStage(filterStage === stage.id ? null : stage.id)}
                    style={{
                      width: stage.funnelWidth,
                      borderLeft: `4px solid ${stage.color}`,
                      // No dark mode as cores pastel dos estágios (pensadas pra fundo
                      // claro) quase somem com uma tintura de 10% (1A) — sobe pra ~28%
                      // (47) só no escuro, e reforça a borda com a própria cor em vez
                      // do cinza padrão, que tinha contraste quase nulo ali.
                      backgroundColor: isSelected ? undefined : `${stage.color}${isDark ? '47' : '1A'}`,
                      ...(isDark && !isSelected ? {
                        borderTopColor: `${stage.color}80`,
                        borderRightColor: `${stage.color}80`,
                        borderBottomColor: `${stage.color}80`,
                      } : {}),
                    }}
                    className={`flex justify-between items-center py-2.5 px-4 rounded-lg transition-all duration-200 border cursor-pointer relative overflow-hidden ${
                      isSelected
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-md shadow-indigo-200/50'
                        : 'border-slate-200/80 dark:border-slate-700/80 hover:brightness-95 text-slate-950 dark:text-slate-100'
                    }`}
                  >
                    <div className="z-10 flex items-center gap-2 pr-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: stage.color,
                          boxShadow: isDark ? `0 0 6px ${stage.color}` : undefined,
                        }}
                      />
                      <span
                        className={`text-[10px] md:text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${isSelected ? 'text-white font-bold' : 'text-slate-950'}`}
                        style={{ color: isDark && !isSelected ? stage.color : undefined }}
                      >
                        {stage.name}
                      </span>
                    </div>
                    
                    <div className="z-10 flex items-center gap-3.5 flex-shrink-0 ml-auto justify-end text-right">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${isSelected ? 'bg-indigo-700/60 text-indigo-100' : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300'}`}>
                        {stage.count}
                      </span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Resumo de valor da etapa — empilhado abaixo do funil quando filtrado */}
          {filterStage && selectedStageObj && (
            <div className="w-full mt-2 bg-gradient-to-br from-emerald-50/70 to-white dark:from-emerald-950/30 dark:to-slate-800 p-5 rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50 dark:shadow-emerald-950/30 flex flex-col justify-center items-center text-center">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 block">
                {`TOTAL EM ${selectedStageObj.name.toUpperCase()}`}
              </span>
              <span className="text-3xl font-black text-emerald-600 tracking-tight leading-none select-all">
                R$ {displayTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="bg-slate-900 text-white font-semibold text-xs px-3 py-1 rounded-full mt-3 shadow-sm">
                {`${selectedStageObj.count} negócios nesta etapa`}
              </span>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 max-w-xs leading-relaxed">
                {`Soma dos negócios na etapa "${selectedStageObj.name}".`}
              </p>
            </div>
          )}
        </div>

        {/* Right Column: Card de Total Geral (quando sem filtro) */}
        {!filterStage && (
          <div className="w-full lg:w-[35%] bg-gradient-to-br from-emerald-50/70 to-white dark:from-emerald-950/30 dark:to-slate-800 p-8 rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50 dark:shadow-emerald-950/30 flex flex-col justify-center items-center text-center h-full">
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3 block">
              TOTAL EM NEGOCIAÇÃO
            </span>
            <span className="text-4xl lg:text-5xl font-black text-emerald-600 tracking-tight leading-none select-all">
              R$ {displayTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="bg-slate-900 text-white font-semibold text-xs px-3 py-1.5 rounded-full mt-4 shadow-sm">
              {`${stageData.reduce((a, s) => a + s.count, 0)} negócios em andamento`}
            </span>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-4 max-w-xs leading-relaxed">
              Soma total de todos os negócios comerciais ativos em andamento no funil.
            </p>
          </div>
        )}

        {/* Right Column: Lista de Oportunidades do Estágio (quando com filtro) */}
        {filterStage && selectedStageObj && (
          <div className="w-full lg:w-[62%] flex flex-col min-h-0">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-900 rounded-t-xl border border-slate-200 dark:border-slate-700 border-b-0 flex-shrink-0">
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedStageObj.color || '#6366f1' }}></span>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">{selectedStageObj.name}</span>
              </div>
              <span className="bg-indigo-100 text-indigo-700 px-2.5 py-0.5 rounded-full text-xs font-bold">
                {selectedStageObj.count} negócios
              </span>
            </div>
            <div
              className="flex-1 min-h-0 overflow-y-auto bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-b-xl p-3 space-y-2.5"
            >
              {safeTasks
                .filter(t => getTaskOptionId(t, kanbanColumns) === filterStage)
                .map(task => {
                  const dealValue = valueForCurrentFilter(task);
                  const formattedValue = dealValue !== null && dealValue !== undefined
                    ? `R$ ${Number(dealValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
                    : 'R$ 0,00';
                  const responsavel = task.responsavel_negocio;
                  return (
                    <div
                      key={task.id}
                      onClick={() => onCardClick && onCardClick(task)}
                      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3.5 hover:shadow-md hover:border-indigo-200 transition-all duration-200 cursor-pointer group"
                      style={{ borderLeft: `4px solid ${selectedStageObj.color || '#6366f1'}` }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight group-hover:text-indigo-700 transition-colors truncate">
                            {task.name}
                          </p>
                          <div className="flex items-center gap-1.5 mt-1 min-w-0">
                            {responsavel && typeof AvatarInicial !== 'undefined' && (
                              <AvatarInicial nome={responsavel} size="xs" />
                            )}
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{responsavel || 'Sem responsável'}</p>
                          </div>
                        </div>
                        <span className={`text-sm font-black flex-shrink-0 ${dealValue > 0 ? 'text-emerald-600' : 'text-slate-400 dark:text-slate-500'}`}>
                          {formattedValue}
                        </span>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Lista de negócios estilo "Negócios" do Agendor: busca + filtros por status,
// etapa, responsável e período de fechamento, com colunas Cliente / Responsável /
// Status / Etapa / Data de Fechamento / Valor.
// Exportação CSV client-side (sem backend) — ponto e vírgula como separador
// (não vírgula), porque o Excel em locale pt-BR usa vírgula como separador
// decimal e interpretaria um CSV com vírgula como campo único quebrado.
function downloadCsv(filename, headers, rows) {
  const escapeCsvField = (val) => {
    const str = val === null || val === undefined ? '' : String(val);
    if (str.includes(';') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const lines = [headers, ...rows].map(row => row.map(escapeCsvField).join(';'));
  // BOM (﻿) pra o Excel reconhecer UTF-8 e não corromper acentos.
  const csvContent = '﻿' + lines.join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const DealsListView = ({
  kanbanTasks,
  kanbanColumns,
  getTaskOptionId,
  getOpportunityValue,
  onCardClick,
  statusFilter,
  setStatusFilter,
  onClose,
  supabaseClient,
  kanbanFilterResponsavelNome,
}) => {
  const [exportingCompleto, setExportingCompleto] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [etapaFilter, setEtapaFilter] = useState('');
  const [responsavelFilter, setResponsavelFilter] = useState('');
  const [periodPreset, setPeriodPreset] = useState('todo_historico');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [sortKey, setSortKey] = useState('data_fechamento');
  const [sortDir, setSortDir] = useState('desc');

  const safeTasks = (Array.isArray(kanbanTasks) ? kanbanTasks : [])
    .filter(t => !kanbanFilterResponsavelNome || t.responsavel_negocio === kanbanFilterResponsavelNome);
  const safeColumns = Array.isArray(kanbanColumns) ? kanbanColumns : [];

  const getStatus = (task) => {
    const optId = getTaskOptionId ? getTaskOptionId(task, safeColumns) : null;
    const col = safeColumns.find(c => c.id === optId);
    const name = (col?.name || '').toLowerCase();
    if (name.includes('ganho')) return 'Ganho';
    if (name.includes('perdido')) return 'Perdido';
    if (name.includes('congelado')) return 'Congelado';
    return 'Em andamento';
  };

  const getEtapaName = (task) => {
    const optId = getTaskOptionId ? getTaskOptionId(task, safeColumns) : null;
    const col = safeColumns.find(c => c.id === optId);
    return col?.name || '—';
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '—';
    const clean = String(dateStr).substring(0, 10);
    const parts = clean.split('-');
    if (parts.length !== 3) return '—';
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const etapasDisponiveis = safeColumns.map(c => c.name).filter(Boolean);
  const responsaveisDisponiveis = Array.from(
    new Set(safeTasks.map(t => t.responsavel_negocio).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const getPeriodRange = () => {
    if (periodPreset === 'todo_historico') return null;
    const now = new Date();
    const year = now.getFullYear();
    const pad = (n) => String(n).padStart(2, '0');
    if (periodPreset === 'ano_atual') {
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    }
    if (periodPreset === 'trimestre_atual') {
      const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const qEndDate = new Date(year, qStartMonth + 3, 0);
      return {
        start: `${year}-${pad(qStartMonth + 1)}-01`,
        end: `${year}-${pad(qEndDate.getMonth() + 1)}-${pad(qEndDate.getDate())}`,
      };
    }
    if (periodPreset === 'personalizado' && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return null;
  };

  const periodRange = getPeriodRange();

  const filtered = safeTasks.filter(task => {
    if (statusFilter !== 'Todos' && getStatus(task) !== statusFilter) return false;
    if (etapaFilter && getEtapaName(task) !== etapaFilter) return false;
    if (responsavelFilter && task.responsavel_negocio !== responsavelFilter) return false;
    if (searchTerm.trim() && !(task.name || '').toLowerCase().includes(searchTerm.toLowerCase().trim())) return false;
    if (periodRange) {
      const df = task.data_fechamento ? String(task.data_fechamento).substring(0, 10) : null;
      if (!df) return false;
      if (df < periodRange.start || df > periodRange.end) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let valA, valB;
    switch (sortKey) {
      case 'cliente':
        valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase();
        break;
      case 'responsavel':
        valA = (a.responsavel_negocio || '').toLowerCase(); valB = (b.responsavel_negocio || '').toLowerCase();
        break;
      case 'status':
        valA = getStatus(a); valB = getStatus(b);
        break;
      case 'etapa':
        valA = getEtapaName(a); valB = getEtapaName(b);
        break;
      case 'valor':
        valA = getOpportunityValue(a) || 0; valB = getOpportunityValue(b) || 0;
        break;
      case 'data_fechamento':
      default:
        valA = a.data_fechamento || '';
        valB = b.data_fechamento || '';
        break;
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalValor = filtered.reduce((acc, t) => acc + (getOpportunityValue(t) || 0), 0);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ label, sortField }) => (
    <th
      onClick={() => handleSort(sortField)}
      className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-indigo-600"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === sortField && <span className="text-indigo-500">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'Ganho': return 'bg-emerald-100 text-emerald-700';
      case 'Perdido': return 'bg-rose-100 text-rose-700';
      case 'Congelado': return 'bg-blue-100 text-blue-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  // Exportação "completa" — uma linha por item de produto (denormalizada,
  // formato padrão de export pra importação em outro sistema), com dados de
  // empresa/CNPJ e produto/fabricante/distribuidor, não só o resumo do
  // negócio. Busca itens_proposta/contas sob demanda (só ao clicar) em vez
  // de sempre, já que é bem mais dado do que a lista normalmente precisa.
  const handleExportCompleto = async () => {
    if (!supabaseClient || sorted.length === 0) return;
    setExportingCompleto(true);
    try {
      const [{ data: itens, error: itensErr }, { data: contas, error: contasErr }] = await Promise.all([
        supabaseClient.from('itens_proposta').select('proposta_id, quantidade, preco_unitario, produtos(nome, fabricante), distribuidores(nome)'),
        supabaseClient.from('contas').select('id, nome, cnpj'),
      ]);
      if (itensErr) throw itensErr;
      if (contasErr) throw contasErr;

      const itensByProposta = new Map();
      (itens || []).forEach(it => {
        if (!it.proposta_id) return;
        if (!itensByProposta.has(it.proposta_id)) itensByProposta.set(it.proposta_id, []);
        itensByProposta.get(it.proposta_id).push(it);
      });
      const contaById = new Map((contas || []).map(c => [c.id, c]));

      const headers = [
        'Cliente (Negócio)', 'Empresa', 'CNPJ', 'Responsável', 'Status', 'Etapa',
        'Situação Proposta', 'Motivo Perda', 'Data de Fechamento', 'Valor Total Negócio (R$)',
        'Produto', 'Fabricante', 'Distribuidor', 'Quantidade', 'Preço Unitário (R$)', 'Total Item (R$)',
      ];
      const rows = [];
      sorted.forEach(t => {
        const conta = t.conta_id ? contaById.get(t.conta_id) : null;
        const itensDoNegocio = t.proposta_id ? (itensByProposta.get(t.proposta_id) || []) : [];
        const baseFields = [
          t.name || '',
          conta?.nome || '',
          conta?.cnpj || '',
          t.responsavel_negocio || '',
          getStatus(t),
          getEtapaName(t),
          t.situacao || '',
          t.motivo_perda || '',
          formatDateDisplay(t.data_fechamento),
          (getOpportunityValue(t) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        ];
        if (itensDoNegocio.length === 0) {
          rows.push([...baseFields, '', '', '', '', '', '']);
        } else {
          itensDoNegocio.forEach(it => {
            const prod = Array.isArray(it.produtos) ? it.produtos[0] : it.produtos;
            const dist = Array.isArray(it.distribuidores) ? it.distribuidores[0] : it.distribuidores;
            const qty = parseFloat(it.quantidade) || 0;
            const preco = parseFloat(it.preco_unitario) || 0;
            rows.push([
              ...baseFields,
              prod?.nome || '',
              prod?.fabricante || '',
              dist?.nome || '',
              qty,
              preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              (qty * preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            ]);
          });
        }
      });
      downloadCsv(`negocios_completo_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
    } catch (err) {
      console.error('Erro ao exportar CSV completo:', err);
      alert('Erro ao gerar exportação completa: ' + (err?.message || err));
    } finally {
      setExportingCompleto(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-slate-800">
      {/* Barra de filtros */}
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider flex items-center gap-2">
            📋 Lista de Negócios
            <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-bold normal-case tracking-normal">
              {filtered.length} {filtered.length === 1 ? 'negócio' : 'negócios'}
            </span>
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const headers = ['Cliente', 'Responsável', 'Status', 'Etapa', 'Data de Fechamento', 'Valor (R$)'];
                const rows = sorted.map(t => [
                  t.name || '',
                  t.responsavel_negocio || '',
                  getStatus(t),
                  getEtapaName(t),
                  formatDateDisplay(t.data_fechamento),
                  (getOpportunityValue(t) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                ]);
                downloadCsv(`negocios_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
              }}
              disabled={sorted.length === 0}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ⬇ Exportar CSV
            </button>
            <button
              onClick={handleExportCompleto}
              disabled={sorted.length === 0 || exportingCompleto || !supabaseClient}
              title="Exporta todos os dados do negócio, incluindo produtos, empresa e CNPJ — uma linha por item, útil pra migração/backup"
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {exportingCompleto ? '⏳ Gerando...' : '⬇ Exportar Completo'}
            </button>
            <button
              onClick={onClose}
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 font-semibold cursor-pointer"
            >
              ✕ Fechar Lista
            </button>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por cliente / negócio..."
            className="flex-1 min-w-[200px] rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer focus:outline-none focus:border-indigo-500"
          >
            <option value="Todos">Status: Todos</option>
            <option value="Em andamento">Em andamento</option>
            <option value="Ganho">Ganho</option>
            <option value="Perdido">Perdido</option>
            <option value="Congelado">Congelado</option>
          </select>

          <select
            value={etapaFilter}
            onChange={(e) => setEtapaFilter(e.target.value)}
            className="rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer focus:outline-none focus:border-indigo-500"
          >
            <option value="">Etapa: Todas</option>
            {etapasDisponiveis.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          <select
            value={responsavelFilter}
            onChange={(e) => setResponsavelFilter(e.target.value)}
            className="rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer focus:outline-none focus:border-indigo-500"
          >
            <option value="">Responsável: Todos</option>
            {responsaveisDisponiveis.map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </select>

          <div className="flex items-center bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg p-0.5 text-xs font-bold">
            {[
              { key: 'ano_atual', label: 'Ano Atual' },
              { key: 'trimestre_atual', label: 'Trimestre Atual' },
              { key: 'todo_historico', label: 'Todo Histórico' },
              { key: 'personalizado', label: 'Personalizar' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setPeriodPreset(opt.key)}
                className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                  periodPreset === opt.key ? 'bg-indigo-600 text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-600/70'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {periodPreset === 'personalizado' && (
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500"
              />
              <span className="text-slate-400 dark:text-slate-500 text-xs">até</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500"
              />
            </div>
          )}

          {(searchTerm || statusFilter !== 'Todos' || etapaFilter || responsavelFilter || periodPreset !== 'todo_historico') && (
            <button
              onClick={() => {
                setSearchTerm(''); setStatusFilter('Todos'); setEtapaFilter('');
                setResponsavelFilter(''); setPeriodPreset('todo_historico');
                setCustomStart(''); setCustomEnd('');
              }}
              className="text-[10px] text-indigo-500 hover:text-indigo-700 font-bold underline cursor-pointer"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 z-10">
            <tr>
              <SortHeader label="Cliente" sortField="cliente" />
              <SortHeader label="Responsável" sortField="responsavel" />
              <SortHeader label="Status" sortField="status" />
              <SortHeader label="Etapa" sortField="etapa" />
              <SortHeader label="Data Fechamento" sortField="data_fechamento" />
              <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-indigo-600" onClick={() => handleSort('valor')}>
                <span className="inline-flex items-center gap-1">
                  Valor
                  {sortKey === 'valor' && <span className="text-indigo-500">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(task => {
              const status = getStatus(task);
              const dealValue = getOpportunityValue(task);
              return (
                <tr
                  key={task.id}
                  onClick={() => onCardClick && onCardClick(task)}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 text-sm font-semibold text-slate-800 dark:text-slate-200 max-w-xs truncate">{task.name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">{task.responsavel_negocio || 'Sem responsável'}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadgeClass(status)}`}>{status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">{getEtapaName(task)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">{formatDateDisplay(task.data_fechamento)}</td>
                  <td className="px-4 py-2.5 text-sm font-bold text-right text-emerald-600">
                    {dealValue ? `R$ ${Number(dealValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-sm text-slate-400 dark:text-slate-500">
                  Nenhum negócio encontrado com os filtros atuais.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Rodapé com total */}
      <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between flex-shrink-0">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filtered.length} {filtered.length === 1 ? 'negócio listado' : 'negócios listados'}
        </span>
        <span className="text-sm font-black text-emerald-600">
          Total: R$ {totalValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
};

const LoginScreen = ({ onLogin, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clickupToken, setClickupToken] = useState(() => localStorage.getItem('crm_user_clickup_token') || '');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showTokenHelp, setShowTokenHelp] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clickupToken.trim()) {
      setLocalError('O Personal API Token do ClickUp é obrigatório.');
      return;
    }
    setLoading(true);
    setLocalError('');
    try {
      const res = await onLogin(email, password, clickupToken);
      if (res && res.error) {
        setLocalError(res.error.message);
      }
    } catch (err) {
      setLocalError(err.message || 'Erro ao realizar login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4">
      <div className="w-full max-w-md p-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl shadow-2xl space-y-6">
        <div className="text-center">
          <div className="inline-flex p-3 bg-indigo-50 text-indigo-600 rounded-2xl mb-3 border border-indigo-100 shadow-sm">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight">Suprimática CRM</h2>
          <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold mt-1 uppercase tracking-wider">Gerador de Propostas Comerciais</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">E-mail Corporativo</label>
            <input 
              type="email" 
              required
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-slate-900 dark:text-slate-100 rounded-xl outline-none transition-all text-sm font-medium"
              placeholder="seu-email@suprimatica.com.br"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">Senha de Acesso</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"} 
                required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-slate-900 dark:text-slate-100 rounded-xl outline-none transition-all text-sm font-medium pr-10"
                placeholder="Sua senha secreta"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                {showPassword ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Personal API Token (ClickUp)</label>
              <button 
                type="button"
                onClick={() => setShowTokenHelp(!showTokenHelp)}
                className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-bold transition-colors cursor-pointer"
              >
                {showTokenHelp ? "Ocultar Dica" : "Como obter?"}
              </button>
            </div>
            <input 
              type="password" 
              required
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-slate-900 dark:text-slate-100 rounded-xl outline-none transition-all text-xs font-mono"
              placeholder="Cole seu token pk_..."
              value={clickupToken}
              onChange={(e) => setClickupToken(e.target.value)}
            />
            
            {showTokenHelp && (
              <div className="mt-2 bg-indigo-50/70 dark:bg-slate-900 border border-indigo-100 dark:border-slate-700 rounded-xl p-3 text-[11px] text-indigo-900 dark:text-slate-200 space-y-1 animate-in fade-in slide-in-from-top-1 duration-150">
                <p className="font-bold">💡 Como obter seu token no ClickUp:</p>
                <ol className="list-decimal list-inside space-y-1 leading-relaxed text-[11px]">
                  <li>Clique no seu <b>perfil / foto</b> no canto superior direito do ClickUp.</li>
                  <li>Clique em <b>Configurações</b>.</li>
                  <li>Na barra lateral esquerda, na seção <i>Integrações e ClickApps</i>, clique em <b>API da ClickUp</b>.</li>
                  <li>Clique em <b>Copiar</b> ao lado do seu <b>Token API</b> (código que começa com <code className="font-bold bg-white dark:bg-slate-800 px-1 py-0.5 rounded border border-indigo-200">pk_...</code>).</li>
                </ol>
              </div>
            )}
          </div>

          {(localError || error) && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-xl flex items-start gap-2">
              <span className="text-sm mt-0.5">⚠️</span>
              <span>{localError || error}</span>
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-xl shadow-lg shadow-indigo-600/10 hover:shadow-indigo-600/20 transition-all cursor-pointer flex items-center justify-center gap-2 text-sm"
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : "Entrar no SPA"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// CONFIGURAÇÕES: Segmentos de Atuação
// ─────────────────────────────────────────────
const SEGMENTOS_DEFAULT_APP = [
  'Saúde / Hospitalar', 'Agronegócio / Usinas', 'Indústria Metalmecânica',
  'Construção Civil', 'Distribuição & Logística', 'Educação',
  'Financeiro & Seguros', 'Varejo & E-commerce', 'Tecnologia',
  'Têxtil & Moda', 'Alimentício & Bebidas', 'Energia & Utilities',
  'Governo & Público', 'Automotivo', 'Mineração', 'Telecomunicações',
];

const SegmentosSettings = ({ client, kanbanModo = 'legacy', canonico = null }) => {
  const [segmentos, setSegmentos] = useState(() => {
    try {
      const s = localStorage.getItem('crm_segmentos');
      return s ? JSON.parse(s) : SEGMENTOS_DEFAULT_APP;
    } catch { return SEGMENTOS_DEFAULT_APP; }
  });
  const [novoNome, setNovoNome] = useState('');
  const [editandoIdx, setEditandoIdx] = useState(null);
  const [editandoNome, setEditandoNome] = useState('');
  const [busca, setBusca] = useState('');
  // nome -> id no Supabase, pra viabilizar update/delete por id (a lista em si
  // continua sendo um array de strings, pra não mexer no resto do componente)
  const idsRef = useRef({});

  // Supabase é a fonte de verdade (antes só localStorage, divergia entre
  // navegadores/máquinas — inclusive o módulo Empresa 360 em empresas.js lia
  // essa mesma chave de cache). localStorage vira só cache pra load instantâneo.
  const carregar = async () => {
    if (!client) return;
    const { data, error } = await client.from('segmentos').select('id, nome').eq('ativo', true).order('nome');
    if (!error && data) {
      idsRef.current = Object.fromEntries(data.map(s => [s.nome, s.id]));
      const nomes = data.map(s => s.nome);
      setSegmentos(nomes);
      try { localStorage.setItem('crm_segmentos', JSON.stringify(nomes)); } catch (e) {}
    }
  };
  useEffect(() => { carregar(); }, [client]);

  const salvar = (lista) => {
    setSegmentos(lista);
    localStorage.setItem('crm_segmentos', JSON.stringify(lista));
  };

  /* B2_I12_HELPER_BEGIN */
  // G-Cadastros: canonical commands; there is no REST fallback behind these branches.
  const comando = (nome, entidade, params) => {
    if (kanbanModo !== 'canonico_v1' || !canonico?.config || !canonico?.userId || !client || !window.CrmComercial) throw Object.assign(new Error('Gateway indisponível.'), { code: 'GATEWAY_INDISPONIVEL' });
    return window.CrmComercial.executarRetido({ client, config: canonico.config, userId: canonico.userId, nome, entidade, params });
  };
  const falha = (err, padrao) => alert(window.CrmComercial?.mensagem(err, padrao) || padrao);
  /* B2_I12_HELPER_END */
  const adicionar = async () => {
    const n = novoNome.trim();
    if (!n || segmentos.includes(n)) return;
    /* B2_I12_BEGIN */
    if (kanbanModo !== 'legacy') {
      setNovoNome('');
      try { await comando('segmento_criar', 'novo', { nome: n }); await carregar(); } catch (err) { falha(err, 'Erro ao adicionar segmento.'); }
      return;
    }
    /* B2_I12_END */
    setNovoNome('');
    if (client) {
      const { error } = await client.from('segmentos').insert({ nome: n });
      if (error) { alert('Erro ao adicionar segmento: ' + error.message); return; }
      await carregar();
    } else {
      salvar([...segmentos, n].sort((a, b) => a.localeCompare(b)));
    }
  };

  const excluir = async (idx) => {
    const nome = segmentos[idx];
    if (!confirm(`Excluir o segmento "${nome}"?`)) return;
    /* B2_I12_BEGIN */
    if (kanbanModo !== 'legacy') {
      try {
        if (!idsRef.current[nome]) throw Object.assign(new Error('Registro indisponível.'), { code: 'REGISTRO_NAO_ENCONTRADO' });
        await comando('segmento_excluir', idsRef.current[nome], { segmento_id: idsRef.current[nome] });
        await carregar();
      } catch (err) { falha(err, 'Erro ao excluir segmento.'); }
      return;
    }
    /* B2_I12_END */
    if (client && idsRef.current[nome]) {
      const { error } = await client.from('segmentos').delete().eq('id', idsRef.current[nome]);
      if (error) { alert('Erro ao excluir segmento: ' + error.message); return; }
      await carregar();
    } else {
      salvar(segmentos.filter((_, i) => i !== idx));
    }
  };

  const salvarEdicao = async (idx) => {
    const n = editandoNome.trim();
    if (!n) return;
    const nomeAntigo = segmentos[idx];
    setEditandoIdx(null);
    setEditandoNome('');
    /* B2_I12_BEGIN */
    if (kanbanModo !== 'legacy') {
      try {
        if (!idsRef.current[nomeAntigo]) throw Object.assign(new Error('Registro indisponível.'), { code: 'REGISTRO_NAO_ENCONTRADO' });
        await comando('segmento_renomear', idsRef.current[nomeAntigo], { segmento_id: idsRef.current[nomeAntigo], nome: n });
        await carregar();
      } catch (err) { falha(err, 'Erro ao renomear segmento.'); }
      return;
    }
    /* B2_I12_END */
    if (client && idsRef.current[nomeAntigo]) {
      const { error } = await client.from('segmentos').update({ nome: n }).eq('id', idsRef.current[nomeAntigo]);
      if (error) { alert('Erro ao renomear segmento: ' + error.message); return; }
      await carregar();
    } else {
      const nova = [...segmentos];
      nova[idx] = n;
      salvar(nova.sort((a, b) => a.localeCompare(b)));
    }
  };

  const resetar = async () => {
    if (!confirm('Restaurar os segmentos padrão? (segmentos personalizados adicionados não são removidos)')) return;
    /* B2_I12_BEGIN */
    if (kanbanModo !== 'legacy') {
      try { await comando('segmento_padrao', 'padrao', {}); await carregar(); } catch (err) { falha(err, 'Erro ao restaurar padrão.'); }
      return;
    }
    /* B2_I12_END */
    if (client) {
      const rows = SEGMENTOS_DEFAULT_APP.map(nome => ({ nome }));
      const { error } = await client.from('segmentos').upsert(rows, { onConflict: 'nome', ignoreDuplicates: true });
      if (error) { alert('Erro ao restaurar padrão: ' + error.message); return; }
      await carregar();
    } else {
      salvar([...SEGMENTOS_DEFAULT_APP]);
    }
  };

  const filtrados = segmentos.filter(s => !busca.trim() || s.toLowerCase().includes(busca.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Segmentos de Atuação</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
            Gerencie os segmentos disponíveis no formulário de empresa. Salvos localmente neste navegador.
          </p>
        </div>
        <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold shrink-0">
          {segmentos.length} segmentos
        </span>
      </div>

      {/* Adicionar novo */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-5 shadow-xs space-y-3">
        <h3 className="text-xs font-bold text-slate-700 dark:text-slate-300">Adicionar Novo Segmento</h3>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Ex: Petroquímica & Refino"
            value={novoNome}
            onChange={e => setNovoNome(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && adicionar()}
            className="flex-1 px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
          />
          <button
            onClick={adicionar}
            disabled={!novoNome.trim()}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-indigo-200 cursor-pointer"
          >
            + Adicionar
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl shadow-xs overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60">
          <input
            type="text"
            placeholder="🔍 Filtrar segmentos..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-400 transition-all"
          />
          <button onClick={resetar} className="text-[11px] font-bold text-slate-400 dark:text-slate-500 hover:text-rose-600 transition-colors cursor-pointer">↺ Restaurar Padrão</button>
        </div>

        <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
          {filtrados.map((s, i) => {
            const realIdx = segmentos.indexOf(s);
            return (
              <div key={s} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-900 group transition-colors">
                {editandoIdx === realIdx ? (
                  <>
                    <input
                      autoFocus
                      value={editandoNome}
                      onChange={e => setEditandoNome(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') salvarEdicao(realIdx); if (e.key === 'Escape') { setEditandoIdx(null); setEditandoNome(''); } }}
                      className="flex-1 px-3 py-1.5 bg-indigo-50 border border-indigo-300 rounded-lg text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all"
                    />
                    <button onClick={() => salvarEdicao(realIdx)} className="px-2.5 py-1 bg-indigo-600 text-white text-xs font-bold rounded-lg cursor-pointer">✓</button>
                    <button onClick={() => { setEditandoIdx(null); setEditandoNome(''); }} className="px-2.5 py-1 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-xs font-bold rounded-lg cursor-pointer">✕</button>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 rounded-full bg-indigo-400 shrink-0"></div>
                    <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-200">{s}</span>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setEditandoIdx(realIdx); setEditandoNome(s); }} className="px-2.5 py-1 text-[11px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer">Editar</button>
                      <button onClick={() => excluir(realIdx)} className="px-2.5 py-1 text-[11px] font-bold text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer">Excluir</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {filtrados.length === 0 && (
            <div className="px-5 py-8 text-center text-xs text-slate-400 dark:text-slate-500 font-medium">Nenhum segmento encontrado.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// MODAL REUTILIZÁVEL DE AÇÕES DE R.O. (Task 6)
// ─────────────────────────────────────────────────────────────────────────
function RoActionModal({
  aberto,
  acao,
  ro,
  cicloPendente = null,
  onClose,
  onSuccess,
  getSupabaseHeaders = null,
  onConflitoRoAtualizada = null,
}) {
  const modalRef = React.useRef(null);
  const modalLayer = useModalLayer({ open: aberto, onClose, panelRef: modalRef });

  const [requestId, setRequestId] = useState(() => (gerarRequestIdRo ? gerarRequestIdRo() : `req-${Date.now()}`));
  const [versaoEsperada, setVersaoEsperada] = useState(ro?.versao || 1);

  // Campos do formulário
  const [dataSolicitacao, setDataSolicitacao] = useState(() => (obterHojeSaoPaulo ? obterHojeSaoPaulo() : ''));
  const [observacao, setObservacao] = useState('');
  const [numeroRo, setNumeroRo] = useState(ro?.numero_ro || '');
  const [dataAprovacao, setDataAprovacao] = useState(() => (obterHojeSaoPaulo ? obterHojeSaoPaulo() : ''));
  const [dataVencimento, setDataVencimento] = useState(ro?.data_vencimento || '');
  const [tipoResposta, setTipoResposta] = useState('aprovar');
  const [dataResposta, setDataResposta] = useState(() => (obterHojeSaoPaulo ? obterHojeSaoPaulo() : ''));
  const [novoVencimento, setNovoVencimento] = useState('');
  const [motivo, setMotivo] = useState('');
  const [situacaoFinal, setSituacaoFinal] = useState('Encerrada');
  const [dataEncerramento, setDataEncerramento] = useState(() => (obterHojeSaoPaulo ? obterHojeSaoPaulo() : ''));

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [avisoConflito, setAvisoConflito] = useState(null);

  useEffect(() => {
    if (aberto) {
      setRequestId(gerarRequestIdRo ? gerarRequestIdRo() : `req-${Date.now()}`);
      setVersaoEsperada(ro?.versao || 1);
      setErro(null);
      setAvisoConflito(null);
      setObservacao('');
      setMotivo('');
      setTipoResposta('aprovar');
      setNovoVencimento('');
      if (acao === 'enviar') {
        setDataSolicitacao(obterHojeSaoPaulo ? obterHojeSaoPaulo() : '');
      } else if (acao === 'aprovar') {
        setNumeroRo(ro?.numero_ro || '');
        setDataAprovacao(obterHojeSaoPaulo ? obterHojeSaoPaulo() : '');
        setDataVencimento(ro?.data_vencimento || '');
      } else if (acao === 'solicitar_renovacao') {
        setDataSolicitacao(obterHojeSaoPaulo ? obterHojeSaoPaulo() : '');
      } else if (acao === 'responder_renovacao') {
        setDataResposta(obterHojeSaoPaulo ? obterHojeSaoPaulo() : '');
      } else if (acao === 'substituir') {
        // Substituição não exige campos adicionais;
        // a R.O. sucessora nasce em Backoffice sem número/vencimento.
      } else if (acao === 'encerrar') {
        setSituacaoFinal('Encerrada');
        setDataEncerramento(obterHojeSaoPaulo ? obterHojeSaoPaulo() : '');
      }
    }
  }, [aberto, acao, ro?.id]);

  if (!aberto || !acao) return null;

  const handleSubmeter = async (e) => {
    if (e) e.preventDefault();
    setErro(null);
    setAvisoConflito(null);

    const dadosAcao = {
      data_solicitacao: dataSolicitacao,
      observacao,
      numero_ro: numeroRo,
      data_aprovacao: dataAprovacao,
      data_vencimento: dataVencimento,
      tipo_resposta: tipoResposta,
      data_resposta: dataResposta,
      novo_vencimento: novoVencimento,
      motivo,
      situacao: situacaoFinal,
      data_encerramento: dataEncerramento,
      ciclo: cicloPendente?.ciclo || 1,
      vencimento_anterior: ro?.data_vencimento,
      versao_esperada: versaoEsperada,
      request_id: requestId,
    };

    const contexto = {
      versao: versaoEsperada,
      data_solicitacao: ro?.data_solicitacao,
      data_vencimento: ro?.data_vencimento,
      ciclo: cicloPendente?.ciclo || 1,
    };

    let validacao;
    try {
      if (!validarPayloadAcaoRo) throw new Error('Módulo de validação indisponível.');
      validacao = validarPayloadAcaoRo(acao, dadosAcao, contexto);
    } catch (err) {
      setErro(err.message || 'Dados inválidos.');
      return;
    }

    setSalvando(true);
    try {
      if (!executarAcaoRo) throw new Error('Cliente de execução indisponível.');
      const res = await executarAcaoRo(
        ro.id,
        validacao.rota,
        validacao.payload,
        { getSupabaseHeaders, requestId }
      );

      let msg = 'Ação executada com sucesso!';
      if (acao === 'enviar') msg = 'Envio ao fabricante registrado com sucesso!';
      else if (acao === 'aprovar') msg = 'R.O. aprovada com sucesso!';
      else if (acao === 'solicitar_renovacao') msg = 'Renovação solicitada com sucesso!';
      else if (acao === 'responder_renovacao') {
        msg = tipoResposta === 'aprovar' ? 'Renovação aprovada com sucesso!' : 'Negativa de renovação registrada.';
      } else if (acao === 'substituir') {
        msg = 'Substituição iniciada! A R.O. sucessora foi criada em Backoffice.';
      } else if (acao === 'encerrar') {
        msg = situacaoFinal === 'Reprovada' ? 'R.O. reprovada com sucesso.' : 'R.O. encerrada com sucesso.';
      }

      if (onSuccess) onSuccess(res, msg);
    } catch (err) {
      if (err.status === 409) {
        // Conflito de versão (409):
        // - Manter todos os campos do formulário preenchidos;
        // - Buscar a R.O. atualizada;
        // - Atualizar o estado do RegistroOportunidadeDrawer via callback explícita;
        // - Manter o ActionModal aberto;
        // - Atualizar apenas a versaoEsperada para a nova confirmação;
        // - Exibir aviso claro para revisão dos dados novos no painel antes de confirmar novamente.
        try {
          if (fetchRegistroOportunidade) {
            const roFresca = await fetchRegistroOportunidade(ro.id, { getSupabaseHeaders });
            if (roFresca && roFresca.versao) {
              setVersaoEsperada(roFresca.versao);
              if (typeof onConflitoRoAtualizada === 'function') {
                onConflitoRoAtualizada(roFresca);
              }
            }
          }
        } catch (reloadErr) {
          console.warn('[RoActionModal] Falha ao recarregar R.O. em 409:', reloadErr);
        }
        setAvisoConflito('Esta R.O. foi alterada por outra pessoa. Revise os dados atualizados antes de confirmar novamente.');
        setErro(null);
      } else {
        setErro(err.message || 'Erro ao processar operação.');
      }
    } finally {
      setSalvando(false);
    }
  };

  let titulo = 'Ação na R.O.';
  let subtitulo = '';
  let labelBotaoConfirmar = 'Confirmar';

  if (acao === 'enviar') {
    titulo = 'Registrar envio ao fabricante';
    subtitulo = 'A R.O. passará para Aguardando aprovação. O número e vencimento continuam vazios até a resposta do fabricante.';
    labelBotaoConfirmar = 'Confirmar envio';
  } else if (acao === 'aprovar') {
    titulo = 'Aprovar R.O.';
    subtitulo = 'Aprovação oficial da R.O. pelo fabricante. A aprovação pode ocorrer diretamente mesmo sem envio prévio registrado.';
    labelBotaoConfirmar = 'Confirmar aprovação';
  } else if (acao === 'solicitar_renovacao') {
    titulo = 'Solicitar renovação de R.O.';
    subtitulo = 'Abre um novo ciclo de renovação em análise com o fabricante. A situação "Aprovada" e o vencimento atual permanecem inalterados.';
    labelBotaoConfirmar = 'Solicitar renovação';
  } else if (acao === 'responder_renovacao') {
    const numCiclo = cicloPendente?.ciclo || 1;
    titulo = `Responder renovação (Ciclo ${numCiclo})`;
    subtitulo = 'Registre o retorno oficial do fabricante quanto à solicitação de renovação desta R.O.';
    labelBotaoConfirmar = 'Confirmar resposta';
  } else if (acao === 'substituir') {
    titulo = 'Iniciar substituição de R.O.';
    subtitulo = 'Cria uma nova R.O. em Backoffice para substituir esta, com mesmo fabricante e oportunidade. A R.O. atual permanece válida até a aprovação da sucessora.';
    labelBotaoConfirmar = 'Confirmar substituição';
  } else if (acao === 'encerrar') {
    titulo = 'Encerrar ou reprovar R.O.';
    subtitulo = 'Finaliza o ciclo comercial ativo desta R.O. Não altera o estágio nem dados da oportunidade no CRM.';
    labelBotaoConfirmar = 'Finalizar R.O.';
  }

  const prazoSugeridoFabricante = ro?.fabricantes_ro?.prazo_inicial_sugerido_dias;

  return (
    <div
      ref={modalRef}
      onKeyDown={modalLayer.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !salvando) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">{titulo}</h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{subtitulo}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={salvando}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            title="Fechar"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmeter} className="p-5 space-y-4 overflow-y-auto flex-1">
          {avisoConflito && (
            <div className="rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 p-3.5 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2.5">
              <span className="text-base">⚠️</span>
              <div>
                <p className="font-bold">Aviso de concorrência</p>
                <p className="mt-0.5 leading-relaxed">{avisoConflito}</p>
              </div>
            </div>
          )}

          {erro && (
            <div className="rounded-xl border border-rose-300 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/40 p-3.5 text-xs text-rose-800 dark:text-rose-200 flex items-start gap-2.5">
              <span className="text-base">⚠️</span>
              <div>
                <p className="font-bold">Não foi possível concluir</p>
                <p className="mt-0.5 leading-relaxed">{erro}</p>
              </div>
            </div>
          )}

          {/* A) ENVIAR AO FABRICANTE */}
          {acao === 'enviar' && (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Data do envio / solicitação <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={dataSolicitacao}
                  onChange={(e) => setDataSolicitacao(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Observação <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  rows={3}
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Ex: Enviado por e-mail para o gerente de contas do fabricante..."
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </>
          )}

          {/* B) APROVAR R.O. */}
          {acao === 'aprovar' && (
            <>
              {prazoSugeridoFabricante && (
                <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/50 p-2.5 text-xs text-indigo-800 dark:text-indigo-300">
                  <span className="font-bold">Sugestão do fabricante:</span> {prazoSugeridoFabricante} dias de vigência a partir da aprovação (informação apenas sugestiva — preenchimento manual obrigatório abaixo).
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Número oficial da R.O. <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: RO-DELL-2026-0987"
                  value={numeroRo}
                  onChange={(e) => setNumeroRo(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Data de aprovação <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={dataAprovacao}
                    onChange={(e) => setDataAprovacao(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Data de vencimento <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={dataVencimento}
                    onChange={(e) => setDataVencimento(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* C) SOLICITAR RENOVAÇÃO */}
          {acao === 'solicitar_renovacao' && (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Data da solicitação <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={dataSolicitacao}
                  onChange={(e) => setDataSolicitacao(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800/60 p-3 text-xs text-slate-600 dark:text-slate-400">
                <p><span className="font-bold">Vencimento atual:</span> {formatarDataCivil ? formatarDataCivil(ro?.data_vencimento) : ro?.data_vencimento || '—'}</p>
                <p className="mt-1">A R.O. continuará como Aprovada com seu vencimento preservado enquanto a renovação estiver em análise.</p>
              </div>
            </>
          )}

          {/* D) RESPONDER RENOVAÇÃO */}
          {acao === 'responder_renovacao' && (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">
                  Resposta do fabricante <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTipoResposta('aprovar')}
                    className={`rounded-lg p-2.5 text-xs font-bold text-center border transition-all cursor-pointer ${
                      tipoResposta === 'aprovar'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 ring-2 ring-emerald-500/20'
                        : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    ✓ Aprovar renovação
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipoResposta('negar')}
                    className={`rounded-lg p-2.5 text-xs font-bold text-center border transition-all cursor-pointer ${
                      tipoResposta === 'negar'
                        ? 'border-rose-500 bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 ring-2 ring-rose-500/20'
                        : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    ✕ Negar renovação
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Data da resposta <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={dataResposta}
                  onChange={(e) => setDataResposta(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {tipoResposta === 'aprovar' ? (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                      Novo vencimento <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={novoVencimento}
                      onChange={(e) => setNovoVencimento(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Vencimento atual: <span className="font-semibold">{formatarDataCivil ? formatarDataCivil(ro?.data_vencimento) : ro?.data_vencimento || '—'}</span>. O novo vencimento deve ser posterior a esta data.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                      Motivo da negativa <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      required
                      placeholder="Descreva o motivo apresentado pelo fabricante para não renovar a R.O..."
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      O vencimento atual da R.O. ({formatarDataCivil ? formatarDataCivil(ro?.data_vencimento) : ro?.data_vencimento || '—'}) será preservado.
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {/* E) ENCERRAR OU REPROVAR */}
          {acao === 'encerrar' && (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Situação final <span className="text-rose-500">*</span>
                </label>
                <select
                  required
                  value={situacaoFinal}
                  onChange={(e) => setSituacaoFinal(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Encerrada">Encerrada</option>
                  <option value="Reprovada">Reprovada</option>
                </select>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {situacaoFinal === 'Encerrada'
                    ? 'Use para projetos concluídos, perdidos para concorrentes ou cancelados pelo cliente.'
                    : 'Use quando o fabricante tiver recusado a oportunidade definitivamente.'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Data do encerramento / reprovação <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={dataEncerramento}
                  onChange={(e) => setDataEncerramento(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Motivo <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Justifique o motivo do encerramento ou reprovação da R.O..."
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </>
          )}

          {acao === 'substituir' && (
            <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 p-4 space-y-2">
              <p className="text-xs font-semibold text-purple-800 dark:text-purple-200">O que vai acontecer:</p>
              <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1 list-disc pl-4">
                <li>Uma nova R.O. será criada em <strong>Backoffice</strong>, vinculada a esta R.O. atual.</li>
                <li>O fabricante (<strong>{ro?.fabricantes_ro?.nome || '—'}</strong>) e a oportunidade serão mantidos automaticamente.</li>
                <li>Esta R.O. permanece <strong>Aprovada</strong> e válida até que a nova seja oficialmente aprovada pelo fabricante.</li>
                <li>Somente após a aprovação da nova R.O. é que esta será marcada como <strong>Substituída</strong>.</li>
              </ul>
            </div>
          )}

          <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
            <button
              type="button"
              disabled={salvando}
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className={`rounded-lg ${acao === 'substituir' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:opacity-50 px-4 py-2 text-xs font-bold text-white transition-colors flex items-center gap-2 cursor-pointer shadow-sm`}
            >
              {salvando && (
                <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {labelBotaoConfirmar}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DRAWER DE DETALHES E AÇÕES DA R.O. (Task 6 e Task 7)
// ─────────────────────────────────────────────────────────────────────────
function ExcluirRoModal({ aberto, ro, onClose, onSuccess, getSupabaseHeaders = null, permissaoRos = {} }) {
  const modalRef = React.useRef(null);
  const modalLayer = useModalLayer({ open: aberto, onClose, panelRef: modalRef });
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [requestId, setRequestId] = useState(null);
  // Modo cadeia (somente administrador): entra quando o banco informa que existe sucessora ativa.
  const [modoCadeia, setModoCadeia] = useState(false);
  const [cadeia, setCadeia] = useState([]);
  const [carregandoCadeia, setCarregandoCadeia] = useState(false);
  const [entendiCadeia, setEntendiCadeia] = useState(false);
  const [requestIdCadeia, setRequestIdCadeia] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setMotivo('');
    setErro(null);
    setSalvando(false);
    setModoCadeia(false);
    setCadeia([]);
    setCarregandoCadeia(false);
    setEntendiCadeia(false);
    setRequestIdCadeia(null);
    setRequestId(gerarRequestIdRo ? gerarRequestIdRo() : `req-${Date.now()}`);
  }, [aberto, ro?.id]);

  if (!aberto || !ro) return null;

  const carregarCadeia = async () => {
    setCarregandoCadeia(true);
    setEntendiCadeia(false);
    try {
      setCadeia(await fetchCadeiaExclusaoRo(ro.id, { getSupabaseHeaders }));
    } catch (err) {
      setCadeia([]);
      setErro(err?.message || 'Não foi possível consultar a cadeia da R.O.');
    } finally {
      setCarregandoCadeia(false);
    }
  };

  const confirmarExclusao = async (evento) => {
    evento.preventDefault();
    const motivoLimpo = motivo.trim();
    if (!motivoLimpo) {
      setErro('Informe o motivo da exclusão.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      if (modoCadeia) {
        // A confirmação reproduz exatamente o que a prévia mostrou (id + versão); o banco revalida tudo sob lock.
        await executarAcaoRo(ro.id, 'excluir-cadeia', {
          motivo: motivoLimpo,
          confirmacao: montarConfirmacaoCadeia(cadeia),
        }, {
          getSupabaseHeaders,
          requestId: requestIdCadeia,
        });
        onSuccess({ cadeia: true, quantidade: cadeia.length });
        return;
      }
      const resultado = await executarAcaoRo(ro.id, 'excluir', {
        motivo: motivoLimpo,
        versao_esperada: ro.versao,
      }, {
        getSupabaseHeaders,
        requestId,
      });
      onSuccess(resultado);
    } catch (err) {
      const codigo = err?.data?.code;
      if (!modoCadeia && codigo === 'RO_COM_SUCESSORA' && permissaoRos.pode_excluir_cadeia) {
        // Sucessora ativa: o administrador pode tratar a cadeia inteira, de forma atômica e com confirmação explícita.
        setModoCadeia(true);
        setRequestIdCadeia(gerarRequestIdRo ? gerarRequestIdRo() : `req-cadeia-${Date.now()}`);
        setErro(null);
        await carregarCadeia();
      } else if (modoCadeia && codigo === 'RO_CADEIA_DIVERGENTE') {
        setErro(err?.message);
        setRequestIdCadeia(gerarRequestIdRo ? gerarRequestIdRo() : `req-cadeia-${Date.now()}`);
        await carregarCadeia();
      } else {
        setErro(err?.message || 'Não foi possível excluir a R.O.');
      }
    } finally {
      setSalvando(false);
    }
  };

  const bloqueada = modoCadeia && (carregandoCadeia || cadeiaTemBloqueio(cadeia));
  const podeConfirmar = !salvando && !!motivo.trim() && (!modoCadeia || (!bloqueada && entendiCadeia));

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !salvando) onClose(); }}>
      <form
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={modoCadeia ? 'Excluir cadeia de R.Os' : 'Excluir R.O.'}
        tabIndex={-1}
        onKeyDown={modalLayer.onKeyDown}
        onSubmit={confirmarExclusao}
        className="w-full max-w-lg rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl dark:border-rose-900 dark:bg-slate-900"
      >
        <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">{modoCadeia ? 'Excluir cadeia de R.Os (administrador)' : 'Excluir R.O.'}</h2>
        {!modoCadeia && (
          <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
            A R.O. sairá das consultas operacionais, mas continuará preservada para auditoria e possível restauração.
          </p>
        )}
        {modoCadeia && (
          <div className="mt-2 space-y-2">
            <p className="text-xs leading-5 text-slate-600 dark:text-slate-300">
              Esta R.O. possui sucessora ativa. A exclusão administrativa exclui logicamente, de uma só vez, esta R.O. e todas as sucessoras ativas listadas abaixo
              (tudo ou nada). Nada é apagado fisicamente; o histórico e os vínculos são preservados e cada R.O. recebe seu próprio registro de auditoria.
              Para restaurar, a ordem é da R.O. mais antiga para a mais recente, e somente um administrador poderá fazê-lo.
            </p>
            {carregandoCadeia && <p className="text-xs text-slate-500">Consultando a cadeia...</p>}
            {!carregandoCadeia && cadeia.length > 0 && (
              <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 text-xs dark:border-slate-700" aria-label="R.Os afetadas">
                {cadeia.map((linha) => (
                  <li key={linha.id} className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0 dark:border-slate-800">
                    <span className="min-w-0 truncate font-semibold text-slate-800 dark:text-slate-100">{linha.numero_ro} <span className="font-normal text-slate-500">· {linha.situacao}</span></span>
                    {linha.bloqueio
                      ? <span className="shrink-0 rounded bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">Bloqueada: {rotuloBloqueioCadeia(linha.bloqueio)}</span>
                      : <span className="shrink-0 text-[11px] text-slate-500">será excluída</span>}
                  </li>
                ))}
              </ul>
            )}
            {!carregandoCadeia && cadeiaTemBloqueio(cadeia) && cadeia.length > 0 && (
              <p className="rounded-lg bg-amber-50 p-2 text-xs font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                Há R.O. sincronizada com o ClickUp ou originada por integração na cadeia. A operação não pode ser concluída.
              </p>
            )}
            <label className="flex items-start gap-2 text-xs font-medium text-slate-700 dark:text-slate-200">
              <input type="checkbox" checked={entendiCadeia} disabled={bloqueada || salvando} onChange={(e) => setEntendiCadeia(e.target.checked)} className="mt-0.5" />
              <span>Entendo as consequências e confirmo a exclusão lógica de todas as R.Os listadas.</span>
            </label>
          </div>
        )}
        <label htmlFor="motivo-exclusao-ro" className="mt-4 block text-xs font-bold text-slate-700 dark:text-slate-200">
          Motivo da exclusão
        </label>
        <textarea
          id="motivo-exclusao-ro"
          data-autofocus
          required
          maxLength={1000}
          rows={4}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          disabled={salvando}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          placeholder="Descreva por que este registro deve ser excluído."
        />
        <div className="mt-1 text-right text-[11px] text-slate-400">{motivo.length}/1000</div>
        {erro && <p className="mt-2 rounded-lg bg-rose-50 p-2 text-xs font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">{erro}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={salvando} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200">
            Cancelar
          </button>
          <button type="submit" disabled={!podeConfirmar} className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50">
            {salvando ? 'Excluindo...' : (modoCadeia ? `Confirmar exclusão da cadeia (${cadeia.length})` : 'Confirmar exclusão')}
          </button>
        </div>
      </form>
    </div>
  );
}

function RosExcluidasModal({ aberto, onClose, getSupabaseHeaders = null, showToast = null, onRestaurada = null, permissaoRos = {} }) {
  const modalRef = React.useRef(null);
  const modalLayer = useModalLayer({ open: aberto, onClose, panelRef: modalRef });
  const [estado, setEstado] = useState({ rows: [], loading: false, error: null });
  const [restaurandoId, setRestaurandoId] = useState(null);
  const requestIdsRestauracaoRef = useRef(new Map());

  const carregar = useCallback(async () => {
    if (!aberto || !fetchRosExcluidas) return;
    setEstado((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const resposta = await fetchRosExcluidas(1, 100, { getSupabaseHeaders });
      setEstado({ rows: resposta.data, loading: false, error: null });
    } catch (err) {
      setEstado({ rows: [], loading: false, error: err?.message || 'Não foi possível consultar R.Os excluídas.' });
    }
  }, [aberto, getSupabaseHeaders]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!aberto) return null;

  const restaurar = async (ro) => {
    if (!window.confirm('Restaurar esta R.O. para as consultas operacionais?')) return;
    let requestId = requestIdsRestauracaoRef.current.get(ro.id);
    if (!requestId) {
      requestId = gerarRequestIdRo ? gerarRequestIdRo() : `req-${Date.now()}-${ro.id}`;
      requestIdsRestauracaoRef.current.set(ro.id, requestId);
    }
    setRestaurandoId(ro.id);
    setEstado((prev) => ({ ...prev, error: null }));
    try {
      await executarAcaoRo(ro.id, 'restaurar', { versao_esperada: ro.versao }, {
        getSupabaseHeaders,
        requestId,
      });
      requestIdsRestauracaoRef.current.delete(ro.id);
      if (showToast) showToast('R.O. restaurada com sucesso.', 'success');
      await carregar();
      if (onRestaurada) onRestaurada();
    } catch (err) {
      setEstado((prev) => ({ ...prev, error: err?.message || 'Não foi possível restaurar a R.O.' }));
    } finally {
      setRestaurandoId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !restaurandoId) onClose(); }}>
      <section
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="R.Os excluídas"
        tabIndex={-1}
        onKeyDown={modalLayer.onKeyDown}
        className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      >
        <header className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">R.Os excluídas</h2>
            <p className="text-xs text-slate-500">Consulta e restauração de R.Os excluídas. Restaure sempre da R.O. mais antiga para a mais recente.</p>
          </div>
          <button type="button" data-autofocus onClick={onClose} className="rounded-lg px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">Fechar ✕</button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {estado.error && <p className="mb-3 rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">{estado.error}</p>}
          {estado.loading && <p className="py-8 text-center text-xs text-slate-500">Carregando R.Os excluídas...</p>}
          {!estado.loading && estado.rows.length === 0 && <p className="py-8 text-center text-xs text-slate-500">Nenhuma R.O. excluída.</p>}
          <div className="space-y-2">
            {estado.rows.map((ro) => {
              const identificacao = gerarIdentificacaoDerivadaRo ? gerarIdentificacaoDerivadaRo(ro) : { texto: ro.numero_ro || ro.id };
              const administrativa = obterTipoExclusaoRo(ro) === 'administrativa_excepcional';
              const restauravel = podeRestaurarRo(ro, permissaoRos);
              return (
                <div key={ro.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-slate-900 dark:text-white">{identificacao.texto}</p>
                    <p className="mt-1 text-[11px] text-slate-500">Excluída em {formatarDataCivil ? formatarDataCivil(ro.deleted_at) : ro.deleted_at}</p>
                    {administrativa && (
                      <p className="mt-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300">Exclusão administrativa: somente um administrador pode restaurar.</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => restaurar(ro)}
                    disabled={restaurandoId === ro.id || !restauravel}
                    title={restauravel ? undefined : 'Somente um administrador pode restaurar esta R.O.'}
                    className="shrink-0 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {restaurandoId === ro.id ? 'Restaurando...' : 'Restaurar R.O.'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function RegistroOportunidadeDrawer({
  ro: roInicial,
  onClose,
  supabaseClient = null,
  getSupabaseHeaders = null,
  showToast = null,
  onRoAtualizada = null,
  onSelecionarRo = null,
  permissaoRos = { role: 'comum', pode_excluir: false, pode_restaurar: false, pode_editar: false, pode_excluir_cadeia: false },
  onRoExcluida = null,
  fabricantes = [],
  fabricantesStatus = { loading: false, error: null, ready: false },
  onRecarregarFabricantes = null,
  vendedores = [],
}) {
  const [ro, setRo] = useState(roInicial);
  const [acaoAtiva, setAcaoAtiva] = useState(null);
  const [sucessoraAtiva, setSucessoraAtiva] = useState(null);
  const [roAnterior, setRoAnterior] = useState(null);
  const [statusRelacoes, setStatusRelacoes] = useState(supabaseClient ? 'carregando' : 'indisponivel');
  const [carregandoNavegacao, setCarregandoNavegacao] = useState(false);
  const [descricaoEditada, setDescricaoEditada] = useState(roInicial?.descricao || '');
  const [salvandoDescricao, setSalvandoDescricao] = useState(false);
  const [erroDescricao, setErroDescricao] = useState(null);
  const [exclusaoAberta, setExclusaoAberta] = useState(false);
  const [edicaoRo, setEdicaoRo] = useState(null);
  const [erroEdicaoRo, setErroEdicaoRo] = useState(null);
  const [salvandoEdicaoRo, setSalvandoEdicaoRo] = useState(false);
  const [requestIdEdicaoRo, setRequestIdEdicaoRo] = useState(null);
  const [buscaEdicaoRo, setBuscaEdicaoRo] = useState('');
  const [opcoesEdicaoRo, setOpcoesEdicaoRo] = useState([]);
  const [opcoesEdicaoRoCarregadas, setOpcoesEdicaoRoCarregadas] = useState(false);
  const [erroBuscaEdicaoRo, setErroBuscaEdicaoRo] = useState(null);
  const podeEditarRo = permissaoRos.pode_editar === true
    && !ro?.deleted_at
    && ['Backoffice', 'Aguardando aprovação', 'Aprovada'].includes(ro?.situacao);
  const opcoesFabricantesEdicao = montarOpcoesFabricantesEdicaoRo
    ? montarOpcoesFabricantesEdicaoRo(fabricantes, ro)
    : fabricantes;
  useEffect(() => { setEdicaoRo(null); setErroEdicaoRo(null); setRequestIdEdicaoRo(null); }, [ro?.id]);
  useEffect(() => {
    if (!edicaoRo || !supabaseClient) { setOpcoesEdicaoRoCarregadas(false); return; }
    let cancelado = false;
    const timer = setTimeout(async () => {
      setErroBuscaEdicaoRo(null);
      try {
        const termo = buscaEdicaoRo.trim();
        const primeiroVinculo = !!(roSemVinculo && roSemVinculo(ro) && oportunidadesElegiveisParaVinculo);
        let query = supabaseClient.from('negocios').select('id, nome, estagio, conta_id, deleted_at, contas(id, nome, razao_social)').order('nome').limit(20);
        // Primeiro vínculo da R.O. importada: só da MESMA empresa, ativas no pipeline (o filtro final é feito pela função de domínio e repetido no servidor).
        if (primeiroVinculo) query = query.eq('conta_id', ro.conta_id).is('deleted_at', null).limit(300);
        if (termo) query = query.ilike('nome', '%' + termo + '%');
        const { data, error } = await query;
        if (error) throw error;
        if (!cancelado) { setOpcoesEdicaoRo(primeiroVinculo ? oportunidadesElegiveisParaVinculo(data || [], ro) : (data || [])); setOpcoesEdicaoRoCarregadas(true); }
      } catch {
        if (!cancelado) { setOpcoesEdicaoRo([]); setErroBuscaEdicaoRo('Não foi possível buscar oportunidades. Tente novamente.'); }
      }
    }, 250);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [!!edicaoRo, buscaEdicaoRo, supabaseClient]);
  const identificacao = gerarIdentificacaoDerivadaRo
    ? gerarIdentificacaoDerivadaRo(ro)
    : {
        texto: `${ro?.negocios?.nome || 'Oportunidade não informada'} · ${ro?.fabricantes_ro?.nome || 'Fabricante não informado'} · ${ro?.numero_ro || 'Aguardando número'}`,
        discriminador: null,
        tituloLegado: typeof ro?.titulo === 'string' && ro.titulo.trim() ? ro.titulo.trim() : null,
      };

  useEffect(() => {
    setRo(roInicial);
    setDescricaoEditada(roInicial?.descricao || '');
    setErroDescricao(null);
  }, [roInicial]);

  useEffect(() => {
    setDescricaoEditada(ro?.descricao || '');
  }, [ro?.id, ro?.descricao]);

  // Buscar sucessora ativa e R.O. anterior para cadeia de substituição usando apenas o prop supabaseClient
  const buscarRelacoes = useCallback(async () => {
    if (!ro?.id) return;
    if (!supabaseClient) {
      setStatusRelacoes('indisponivel');
      setSucessoraAtiva(null);
      setRoAnterior(null);
      return;
    }

    setStatusRelacoes('carregando');
    try {
      // Buscar se existe uma R.O. que aponta para esta como ro_anterior_id
      const { data: sucessoras, error: errSucessoras } = await supabaseClient
        .from('registros_oportunidade')
        .select('id, numero_ro, situacao, fabricantes_ro(nome,ativo)')
        .eq('ro_anterior_id', ro.id)
        .not('situacao', 'in', '("Reprovada","Encerrada")')
        .order('created_at', { ascending: false })
        .limit(1);

      if (errSucessoras) {
        console.warn('[RegistroOportunidadeDrawer] Erro ao consultar sucessora:', errSucessoras);
        setStatusRelacoes('erro');
        return;
      }

      setSucessoraAtiva(sucessoras && sucessoras.length > 0 ? sucessoras[0] : null);

      // Buscar a R.O. anterior se houver
      if (ro.ro_anterior_id) {
        const { data: anterior, error: errAnterior } = await supabaseClient
          .from('registros_oportunidade')
          .select('id, numero_ro, situacao, fabricantes_ro(nome,ativo)')
          .eq('id', ro.ro_anterior_id)
          .maybeSingle();

        if (errAnterior) {
          console.warn('[RegistroOportunidadeDrawer] Erro ao consultar anterior:', errAnterior);
          setStatusRelacoes('erro');
          return;
        }
        setRoAnterior(anterior || null);
      } else {
        setRoAnterior(null);
      }

      setStatusRelacoes('pronto');
    } catch (err) {
      console.warn('[RegistroOportunidadeDrawer] Falha ao buscar relações de substituição:', err);
      setStatusRelacoes('erro');
    }
  }, [ro?.id, ro?.ro_anterior_id, supabaseClient]);

  useEffect(() => {
    buscarRelacoes();
  }, [buscarRelacoes]);

  // Navegar entre anterior e sucessora trocando o registro exibido no mesmo drawer
  const handleNavegarParaRo = async (targetId) => {
    if (!targetId || targetId === ro?.id) return;
    setCarregandoNavegacao(true);
    try {
      let novaRo = null;
      if (fetchRegistroOportunidade) {
        try {
          novaRo = await fetchRegistroOportunidade(targetId, { getSupabaseHeaders });
        } catch (errApi) {
          console.warn('[RegistroOportunidadeDrawer] Falha ao consultar R.O. na navegação via API:', errApi);
        }
      }
      if (!novaRo && supabaseClient) {
        try {
          const { data: roData, error: errDb } = await supabaseClient
            .from('registros_oportunidade')
            .select()
            .eq('id', targetId)
            .maybeSingle();
          if (roData && !errDb) {
            novaRo = roData;
          }
        } catch (errDbCatch) {
          console.warn('[RegistroOportunidadeDrawer] Falha ao consultar R.O. no supabaseClient:', errDbCatch);
        }
      }

      if (novaRo) {
        setRo(novaRo);
        if (typeof onSelecionarRo === 'function') {
          onSelecionarRo(novaRo.id, novaRo);
        }
      } else if (showToast) {
        showToast('Não foi possível carregar os detalhes da R.O. selecionada.', 'error');
      }
    } finally {
      setCarregandoNavegacao(false);
    }
  };

  useEffect(() => {
    const aoTeclar = (evento) => { if (evento.key === 'Escape' && !acaoAtiva && !exclusaoAberta) onClose(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onClose, acaoAtiva, exclusaoAberta]);

  const eventos = ordenarEventosRo ? ordenarEventosRo(ro.eventos_ro) : [];
  const ciclos = resumirCiclosRo ? resumirCiclosRo(ro.renovacoes_ro) : { aprovados: 0, pendente: null };
  const semVinculoRo = roSemVinculo ? roSemVinculo(ro) : false;
  const acoes = obterAcoesPermitidasRo && !semVinculoRo
    ? obterAcoesPermitidasRo(ro.situacao, !!ciclos.pendente, { temSucessoraAtiva: !!sucessoraAtiva })
    : [];
  const cliente = (empresaDaRo ? empresaDaRo(ro) : '') || ro.negocios?.contas?.nome || ro.negocios?.contas?.razao_social || '—';
  const vigencia = calcularVigenciaRo ? calcularVigenciaRo(ro.data_vencimento) : 'Sem prazo';

  const handleConflitoRoAtualizada = useCallback((roFresca) => {
    if (roFresca && roFresca.id) {
      setRo(roFresca);
      if (onRoAtualizada) onRoAtualizada(roFresca);
    }
  }, [onRoAtualizada]);

  const recarregarRoCompleta = async () => {
    if (fetchRegistroOportunidade && ro?.id) {
      try {
        const roFresca = await fetchRegistroOportunidade(ro.id, { getSupabaseHeaders });
        if (roFresca) {
          setRo(roFresca);
          if (onRoAtualizada) onRoAtualizada(roFresca);
        }
      } catch (err) {
        console.warn('[RegistroOportunidadeDrawer] Falha ao recarregar R.O.:', err);
      }
    }
  };

  const handleSucessoAcao = async (resultado, mensagemToast) => {
    setAcaoAtiva(null);
    if (showToast) {
      showToast(mensagemToast || 'Operação realizada com sucesso!', 'success');
    }
    await recarregarRoCompleta();
    await buscarRelacoes();
  };

  const salvarDescricao = async () => {
    if (!ro?.id || !executarAcaoRo) return;
    setErroDescricao(null);
    setSalvandoDescricao(true);
    try {
      await executarAcaoRo(ro.id, 'descricao', {
        descricao: descricaoEditada,
        versao_esperada: ro.versao,
      }, {
        getSupabaseHeaders,
        gerarRequestId: gerarRequestIdRo,
      });
      await recarregarRoCompleta();
      if (showToast) showToast('Descrição operacional atualizada.', 'success');
    } catch (err) {
      setErroDescricao(err?.message || 'Não foi possível salvar a descrição. Tente novamente.');
    } finally {
      setSalvandoDescricao(false);
    }
  };

  const enviarEdicaoRo = async (evento) => {
    evento.preventDefault();
    if (!podeEditarRo || salvandoEdicaoRo) return;
    setSalvandoEdicaoRo(true);
    setErroEdicaoRo(null);
    const requestId = requestIdEdicaoRo || gerarRequestIdRo();
    if (!requestIdEdicaoRo) setRequestIdEdicaoRo(requestId);
    const resultado = await salvarEdicaoRo({
      ro, draft: edicaoRo,
      options: {
        getSupabaseHeaders,
        requestId,
        fabricantesAtivos: fabricantes,
        catalogoFabricantesDisponivel: fabricantesStatus.ready === true && !fabricantesStatus.error,
      },
      recarregarConflito: async () => {
        const atual = await fetchRegistroOportunidade(ro.id, { getSupabaseHeaders });
        // Não recarregar a lista: mudanças de filtro/página poderiam desmontar
        // este drawer e descartar o rascunho ainda não salvo.
        setRo(atual);
        return atual;
      },
      recarregar: async () => {
        const atual = await fetchRegistroOportunidade(ro.id, { getSupabaseHeaders });
        handleConflitoRoAtualizada(atual);
      },
    });
    setSalvandoEdicaoRo(false);
    if (resultado.sucesso) {
      setEdicaoRo(null);
      setRequestIdEdicaoRo(null);
      if (showToast) showToast(resultado.aviso || 'Dados da R.O. atualizados.', resultado.aviso ? 'warning' : 'success');
    } else setErroEdicaoRo(resultado.erro);
  };
  const classeCampoEdicaoRo = 'mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500';
  const alterarEdicaoRo = (campo, valor) => {
    setEdicaoRo((atual) => ({ ...atual, [campo]: valor }));
    setRequestIdEdicaoRo(gerarRequestIdRo());
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] flex justify-end bg-slate-950/35" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <aside role="dialog" aria-modal="true" aria-label="Detalhe da R.O." className="h-full w-full max-w-xl overflow-y-auto bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700 flex flex-col">
          <header className="sticky top-0 z-10 flex items-start justify-between gap-4 p-5 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-indigo-600">Registro de oportunidade</p>
              <h2 className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{identificacao.texto}</h2>
              <p className="text-xs text-slate-500 mt-1">{ro.situacao}</p>
              {identificacao.discriminador && (
                <p className="mt-1 text-[11px] font-mono text-slate-400 dark:text-slate-500">{identificacao.discriminador}</p>
              )}
              {identificacao.tituloLegado && (
                <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Título anterior: {identificacao.tituloLegado}</p>
              )}
            </div>
            <button type="button" autoFocus onClick={onClose} className="rounded-lg px-3 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 cursor-pointer">
              Fechar ✕
            </button>
          </header>

          <div className="p-5 space-y-5 text-sm flex-1">
            {(podeEditarRo || !!edicaoRo) && (
              <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-slate-900 dark:text-slate-100">
                {!edicaoRo ? (
                  <button type="button" className="rounded-lg bg-indigo-600 px-3 py-2 font-bold text-white" onClick={() => { setEdicaoRo(hidratarEdicaoRo(ro)); setErroEdicaoRo(null); setBuscaEdicaoRo(''); setRequestIdEdicaoRo(gerarRequestIdRo()); if (onRecarregarFabricantes) onRecarregarFabricantes(); }}>Editar dados da R.O.</button>
                ) : (
                  <form onSubmit={enviarEdicaoRo} aria-label="Editar dados da R.O." className="space-y-3">
                    <h3 className="font-bold">Editar dados da R.O.</h3>
                    {!podeEditarRo && <p role="status" className="text-amber-700 dark:text-amber-300">Esta R.O. não pode mais ser editada na situação atual. O rascunho foi preservado para conferência.</p>}
                    <fieldset disabled={salvandoEdicaoRo} className="space-y-3">
                      <label className="block">Categoria *<select required value={edicaoRo.categoria} onChange={(e) => alterarEdicaoRo('categoria', e.target.value)} className={classeCampoEdicaoRo}>
                        <option value="">Selecione a categoria</option>
                        {opcoesCategoriaEdicaoRo(ro.categoria, edicaoRo.categoria_inicial).map((o) => <option key={o.value} value={o.value}>{o.legado ? `${o.label} (atual)` : o.label}</option>)}
                      </select></label>
                      <label className="block">Fabricante *<select required disabled={fabricantesStatus.loading || !!fabricantesStatus.error} value={edicaoRo.fabricante_id} onChange={(e) => alterarEdicaoRo('fabricante_id', e.target.value)} className={classeCampoEdicaoRo}>
                        <option value="">Selecione</option>
                        {opcoesFabricantesEdicao.map((f) => <option key={f.id} value={f.id}>{f.nome_exibicao || f.nome}</option>)}
                      </select></label>
                      {fabricantesStatus.loading && <p role="status" className="text-xs text-slate-500">Atualizando fabricantes ativos...</p>}
                      {fabricantesStatus.error && <p role="status" className="text-xs text-amber-700 dark:text-amber-300">Não foi possível atualizar os fabricantes ativos. O fabricante atual foi preservado; tente novamente para alterá-lo.</p>}
                      <label className="block">Responsável operacional<select value={edicaoRo.responsavel_operacional_clickup_id} onChange={(e) => alterarEdicaoRo('responsavel_operacional_clickup_id', e.target.value)} className={classeCampoEdicaoRo}>
                        <option value="">Sem responsável</option>
                        {ro.responsavel_operacional_clickup_id && !vendedores.some((v) => String(v.id) === String(ro.responsavel_operacional_clickup_id)) && <option value={ro.responsavel_operacional_clickup_id}>Responsável atual</option>}
                        {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nome || v.username || v.name}</option>)}
                      </select></label>
                      <label className="block">Número da R.O.<input value={edicaoRo.numero_ro} onChange={(e) => alterarEdicaoRo('numero_ro', e.target.value)} className={classeCampoEdicaoRo} /></label>
                      {erroBuscaEdicaoRo && <p role="status">{erroBuscaEdicaoRo}</p>}
                      {semVinculoRo && !erroBuscaEdicaoRo && opcoesEdicaoRoCarregadas && opcoesEdicaoRo.length === 0 && <p role="status" className="text-xs text-amber-700 dark:text-amber-300">{MENSAGEM_SEM_OPORTUNIDADE_ELEGIVEL}</p>}
                      <div className="block"><label htmlFor="ro-edicao-negocio">Oportunidade vinculada *</label>
                        {!semVinculoRo && <input type="search" aria-label="Filtrar oportunidades por nome" placeholder="Digite parte do nome para filtrar a lista" value={buscaEdicaoRo} onChange={(e) => setBuscaEdicaoRo(e.target.value)} className={classeCampoEdicaoRo} />}
                        <select id="ro-edicao-negocio" required value={edicaoRo.negocio_id} onChange={(e) => alterarEdicaoRo('negocio_id', e.target.value)} className={classeCampoEdicaoRo}>
                        {semVinculoRo
                          ? <option value="">Selecione a oportunidade da empresa {empresaDaRo ? empresaDaRo(ro) : ''}</option>
                          : <option value={ro.negocio_id}>{desambiguarOportunidade(ro.negocios || { id: ro.negocio_id })}</option>}
                        {edicaoRo.negocio_id && !opcoesEdicaoRo.some((op) => op.id === edicaoRo.negocio_id) && edicaoRo.negocio_id !== ro.negocio_id && <option value={edicaoRo.negocio_id}>Oportunidade selecionada</option>}
                        {opcoesEdicaoRo.filter((op) => op.id !== ro.negocio_id).map((op) => <option key={op.id} value={op.id}>{semVinculoRo ? rotuloOportunidadeVinculo(op) : desambiguarOportunidade(op)}</option>)}
                      </select></div>
                      <label className="block">Motivo da alteração *<textarea required value={edicaoRo.motivo} onChange={(e) => alterarEdicaoRo('motivo', e.target.value)} className={classeCampoEdicaoRo} /></label>
                      {erroEdicaoRo && <p role="alert" className="text-rose-600 dark:text-rose-400">{erroEdicaoRo}</p>}
                      <div className="flex gap-3"><button type="submit" disabled={!podeEditarRo || salvandoEdicaoRo} className="rounded-lg bg-indigo-600 px-3 py-2 font-bold text-white disabled:opacity-50">{salvandoEdicaoRo ? 'Salvando...' : 'Salvar alterações'}</button><button type="button" onClick={() => { setEdicaoRo(null); setRequestIdEdicaoRo(null); }}>Cancelar</button></div>
                    </fieldset>
                  </form>
                )}
              </section>
            )}
            <section className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-500">Cliente</p>
                <p className="font-semibold text-slate-900 dark:text-white">{cliente}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Oportunidade</p>
                <p className="font-semibold text-slate-900 dark:text-white">{ro.negocios?.nome || (semVinculoRo ? <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" title="Importada do Agendor. Vincule a oportunidade em Editar para ela operar e ir ao ClickUp.">Sem vínculo</span> : '—')}</p>
                {pendenciasImportacaoRo && etapaOriginalImportacao && (() => {
                  const pend = pendenciasImportacaoRo(ro); const orig = etapaOriginalImportacao(ro);
                  if (!pend.length && !orig.provisoria) return null;
                  return (
                    <div className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200" role="note">
                      {orig.provisoria && <p className="font-bold">Situação provisória de importação — etapa original no Agendor: {orig.etapa}.</p>}
                      {pend.length > 0 && <ul className="list-disc pl-4">{pend.map((t) => <li key={t}>{t}</li>)}</ul>}
                    </div>
                  );
                })()}
              </div>
              <div>
                <p className="text-xs text-slate-500">Vencimento</p>
                <p className="font-semibold text-slate-900 dark:text-white">{formatarDataCivil ? formatarDataCivil(ro.data_vencimento) : '—'} · {vigencia}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Categoria</p>
                <p className="font-semibold text-slate-900 dark:text-white">{formatarCategoriaRo ? formatarCategoriaRo(ro.categoria) : '—'}</p>
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">Descrição operacional</h3>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Contexto para o registro no fabricante: contatos, itens, valores, resumo e próximos passos.</p>
                </div>
                <span className="shrink-0 text-[11px] font-medium text-slate-400">{descricaoEditada.length}/6000</span>
              </div>
              <textarea
                value={descricaoEditada}
                onChange={(e) => setDescricaoEditada(e.target.value)}
                maxLength={6000}
                rows={10}
                disabled={salvandoDescricao}
                placeholder="Ex.: Contato: nome e cargo. Itens e quantidades. Valor estimado. O que está sendo trabalhado e próximo passo."
                className="mt-3 w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
              {erroDescricao && <p className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400">{erroDescricao}</p>}
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={salvarDescricao}
                  disabled={salvandoDescricao}
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {salvandoDescricao ? 'Salvando...' : 'Salvar descrição'}
                </button>
              </div>
            </section>

            {acoes.length > 0 && (
              <section className="rounded-xl border border-indigo-200 dark:border-indigo-900/60 p-4 bg-indigo-50/40 dark:bg-indigo-950/20">
                <h3 className="font-bold text-slate-800 dark:text-slate-100 mb-1">Ações operacionais</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Execute movimentações na R.O. sem alterar os dados da oportunidade no CRM.</p>
                <div className="flex flex-wrap gap-2">
                  {acoes.map((acaoId) => {
                    const mapConfig = {
                      enviar: { label: 'Registrar envio ao fabricante', classe: 'bg-blue-600 hover:bg-blue-700 text-white' },
                      aprovar: { label: 'Aprovar R.O.', classe: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
                      solicitar_renovacao: { label: 'Solicitar renovação', classe: 'bg-indigo-600 hover:bg-indigo-700 text-white' },
                      responder_renovacao: { label: 'Responder renovação pendente', classe: 'bg-amber-600 hover:bg-amber-700 text-white' },
                      substituir: { label: 'Iniciar substituição', classe: 'bg-purple-600 hover:bg-purple-700 text-white' },
                      encerrar: { label: 'Encerrar / Reprovar', classe: 'bg-rose-600 hover:bg-rose-700 text-white' },
                    };
                    const conf = mapConfig[acaoId] || { label: acaoId, classe: 'bg-slate-700 text-white' };
                    return (
                      <button
                        key={acaoId}
                        type="button"
                        onClick={() => setAcaoAtiva(acaoId)}
                        className={`rounded-lg px-3 py-2 text-xs font-bold transition-all cursor-pointer shadow-sm active:scale-95 ${conf.classe}`}
                      >
                        {conf.label}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="font-bold text-slate-900 dark:text-white">Renovações</h3>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                {ciclos.aprovados} ciclo(s) aprovado(s){ciclos.pendente ? ` · Ciclo ${ciclos.pendente.ciclo || 1} em análise` : ''}
              </p>
            </section>

            <section className="rounded-xl border border-purple-200 dark:border-purple-900/60 p-4 bg-purple-50/30 dark:bg-purple-950/10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-900 dark:text-white">Sucessão</h3>
                {carregandoNavegacao && (
                  <span className="text-xs text-purple-600 dark:text-purple-400 italic">Carregando registro...</span>
                )}
              </div>

              {statusRelacoes === 'carregando' && (
                <p className="text-xs text-slate-500 italic">Carregando relações de substituição...</p>
              )}

              {statusRelacoes === 'indisponivel' && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Informações de substituição indisponíveis (cliente não conectado).</p>
              )}

              {statusRelacoes === 'erro' && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Falha ao consultar relações de substituição.</p>
              )}

              {statusRelacoes === 'pronto' && !ro.ro_anterior_id && !sucessoraAtiva && (
                <p className="text-xs text-slate-500">Sem substituição vinculada.</p>
              )}

              {statusRelacoes === 'pronto' && (roAnterior || sucessoraAtiva) && (
                <div className="space-y-3">
                  {roAnterior && (
                    <div className="flex items-center justify-between gap-2 text-xs bg-white/70 dark:bg-slate-800/70 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/40">
                      <div className="flex items-center gap-2">
                        <span className="text-purple-600 dark:text-purple-400 font-semibold">← Anterior:</span>
                        <span className="text-slate-800 dark:text-slate-100 font-bold">{roAnterior.numero_ro || 'Sem número'}</span>
                        <span className="text-slate-500">({roAnterior.situacao})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleNavegarParaRo(roAnterior.id)}
                        disabled={carregandoNavegacao}
                        className="px-2.5 py-1 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-950/60 hover:bg-purple-200 dark:hover:bg-purple-900/80 rounded transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Abrir anterior
                      </button>
                    </div>
                  )}

                  {sucessoraAtiva && (
                    <div className="flex items-center justify-between gap-2 text-xs bg-white/70 dark:bg-slate-800/70 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/40">
                      <div className="flex items-center gap-2">
                        <span className="text-purple-600 dark:text-purple-400 font-semibold">→ Sucessora:</span>
                        <span className="text-slate-800 dark:text-slate-100 font-bold">{sucessoraAtiva.numero_ro || 'Aguardando número'}</span>
                        <span className="text-slate-500">({sucessoraAtiva.situacao})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleNavegarParaRo(sucessoraAtiva.id)}
                        disabled={carregandoNavegacao}
                        className="px-2.5 py-1 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-950/60 hover:bg-purple-200 dark:hover:bg-purple-900/80 rounded transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Abrir sucessora
                      </button>
                    </div>
                  )}

                  {((sucessoraAtiva && ['Backoffice', 'Aguardando aprovação'].includes(sucessoraAtiva.situacao)) ||
                    (roAnterior && ['Backoffice', 'Aguardando aprovação'].includes(ro.situacao))) && (
                    <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-xs text-amber-800 dark:text-amber-300">
                      <p className="font-medium">A R.O. anterior permanece aprovada até a sucessora receber aprovação oficial.</p>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="font-bold text-slate-900 dark:text-white">Histórico da R.O.</h3>
              <div className="mt-3 space-y-3">
                {eventos.length ? eventos.map((evento, indice) => (
                  <div key={`${evento.id || evento.created_at}-${indice}`} className="border-l-2 border-indigo-300 dark:border-indigo-600 pl-3">
                    <p className="font-semibold text-xs text-slate-800 dark:text-slate-200">{evento.tipo || evento.evento || 'Movimentação'}</p>
                    <p className="text-xs text-slate-500">{formatarDataCivil ? formatarDataCivil(evento.created_at) : evento.created_at || 'Data não informada'}</p>
                  </div>
                )) : (
                  <p className="text-xs text-slate-500">Nenhum evento registrado ainda.</p>
                )}
              </div>
            </section>

            <section className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4">
              <h3 className="font-bold text-slate-900 dark:text-white">Sincronização e atualização humana</h3>
              <p className="mt-1 text-xs text-slate-500">Evidências comerciais e sincronização com ClickUp serão incluídas na próxima etapa.</p>
            </section>

            {permissaoRos.pode_excluir && (
              <section className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 dark:border-rose-900/60 dark:bg-rose-950/20">
                <h3 className="font-bold text-rose-800 dark:text-rose-200">Exclusão controlada</h3>
                <p className="mt-1 text-xs text-rose-700/80 dark:text-rose-300/80">A exclusão é lógica, exige motivo e preserva todo o histórico da R.O.</p>
                <button
                  type="button"
                  onClick={() => setExclusaoAberta(true)}
                  className="mt-3 rounded-lg bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700"
                >
                  Excluir R.O.
                </button>
              </section>
            )}
          </div>
        </aside>
      </div>

      {acaoAtiva && (
        <RoActionModal
          aberto={!!acaoAtiva}
          acao={acaoAtiva}
          ro={ro}
          cicloPendente={ciclos.pendente}
          onClose={() => setAcaoAtiva(null)}
          onSuccess={handleSucessoAcao}
          getSupabaseHeaders={getSupabaseHeaders}
          onConflitoRoAtualizada={handleConflitoRoAtualizada}
        />
      )}

      <ExcluirRoModal
        aberto={exclusaoAberta}
        ro={ro}
        onClose={() => setExclusaoAberta(false)}
        getSupabaseHeaders={getSupabaseHeaders}
        permissaoRos={permissaoRos}
        onSuccess={(resultado) => {
          setExclusaoAberta(false);
          if (showToast) {
            showToast(resultado?.cadeia
              ? `Cadeia excluída com sucesso (${resultado.quantidade} R.Os).`
              : 'R.O. excluída com sucesso.', 'success');
          }
          if (onRoExcluida) onRoExcluida(ro.id);
        }}
      />
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// MODAL DE CADASTRO DE NOVA R.O. (Task 5)
// ─────────────────────────────────────────────────────────────────────────
function NovaRoModal({
  aberto,
  onClose,
  onSuccess,
  fabricantes = [],
  fabricantesStatus = { loading: false, error: null, ready: false },
  vendedores = [],
  oportunidadeFixa = null,
  supabaseClient = null,
  getSupabaseHeaders = null,
}) {
  const modalRef = React.useRef(null);
  const modalLayer = useModalLayer({ open: aberto, onClose, panelRef: modalRef });

  const [oportunidadeSelecionada, setOportunidadeSelecionada] = useState(oportunidadeFixa);
  const [buscaOportunidade, setBuscaOportunidade] = useState("");
  const [sugestoesOportunidades, setSugestoesOportunidades] = useState([]);
  const [buscandoOportunidades, setBuscandoOportunidades] = useState(false);
  const [dropdownAberto, setDropdownAberto] = useState(false);

  const [fabricanteId, setFabricanteId] = useState("");
  const [categoria, setCategoria] = useState("");
  const [responsavelId, setResponsavelId] = useState("");

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  // request_id estável durante tentativas de reenvio da mesma criação (idempotência)
  const requestIdRef = useRef(null);
  if (!requestIdRef.current && gerarRequestIdRo) {
    requestIdRef.current = gerarRequestIdRo();
  }

  // Sincroniza oportunidadeFixa
  useEffect(() => {
    if (oportunidadeFixa) {
      if (oportunidadeFixa.contas) {
        setOportunidadeSelecionada(oportunidadeFixa);
      } else if (oportunidadeFixa.conta_id && supabaseClient) {
        supabaseClient
          .from("contas")
          .select("id, nome, razao_social")
          .eq("id", oportunidadeFixa.conta_id)
          .maybeSingle()
          .then(({ data }) => {
            setOportunidadeSelecionada({
              ...oportunidadeFixa,
              contas: data || null,
            });
          })
          .catch(() => {
            setOportunidadeSelecionada(oportunidadeFixa);
          });
      } else {
        setOportunidadeSelecionada(oportunidadeFixa);
      }
    } else {
      setOportunidadeSelecionada(null);
    }
  }, [oportunidadeFixa, supabaseClient]);

  // Busca search-first de oportunidades
  useEffect(() => {
    if (!aberto || oportunidadeFixa || oportunidadeSelecionada) return;
    let cancelado = false;
    const termo = buscaOportunidade.trim();

    const temporizador = setTimeout(async () => {
      if (!supabaseClient) return;
      setBuscandoOportunidades(true);
      try {
        if (!termo) {
          const { data } = await supabaseClient
            .from("negocios")
            .select("id, nome, estagio, conta_id, contas(id, nome, razao_social)")
            .not("estagio", "ilike", "%ganho%")
            .not("estagio", "ilike", "%perdido%")
            .order("nome")
            .limit(20);
          if (!cancelado) setSugestoesOportunidades(data || []);
        } else {
          const { data: negsPorNome } = await supabaseClient
            .from("negocios")
            .select("id, nome, estagio, conta_id, contas(id, nome, razao_social)")
            .not("estagio", "ilike", "%ganho%")
            .not("estagio", "ilike", "%perdido%")
            .ilike("nome", "%" + termo + "%")
            .limit(20);

          // Consultas independentes e seguras em contas, sem concatenar filtros
          const [resNome, resRazao] = await Promise.all([
            supabaseClient.from("contas").select("id").ilike("nome", "%" + termo + "%").limit(20),
            supabaseClient.from("contas").select("id").ilike("razao_social", "%" + termo + "%").limit(20),
          ]);

          const contaIdsUnicos = new Set();
          (resNome?.data || []).forEach((c) => c?.id && contaIdsUnicos.add(c.id));
          (resRazao?.data || []).forEach((c) => c?.id && contaIdsUnicos.add(c.id));
          const contaIds = Array.from(contaIdsUnicos);

          let negsPorConta = [];
          if (contaIds.length > 0) {
            const { data: negs } = await supabaseClient
              .from("negocios")
              .select("id, nome, estagio, conta_id, contas(id, nome, razao_social)")
              .not("estagio", "ilike", "%ganho%")
              .not("estagio", "ilike", "%perdido%")
              .in("conta_id", contaIds)
              .limit(20);
            negsPorConta = negs || [];
          }

          if (!cancelado) {
            const mapa = new Map();
            (negsPorNome || []).forEach((n) => mapa.set(n.id, n));
            negsPorConta.forEach((n) => mapa.set(n.id, n));
            const candidatas = Array.from(mapa.values());
            setSugestoesOportunidades(candidatas.filter((op) => (
              estaOportunidadeElegivelParaRo ? estaOportunidadeElegivelParaRo(op) : true
            )));
          }
        }
      } catch (e) {
        if (!cancelado) setSugestoesOportunidades([]);
      } finally {
        if (!cancelado) setBuscandoOportunidades(false);
      }
    }, 250);

    return () => {
      cancelado = true;
      clearTimeout(temporizador);
    };
  }, [aberto, buscaOportunidade, oportunidadeFixa, oportunidadeSelecionada, supabaseClient]);

  if (!aberto) return null;

  const clienteNome =
    oportunidadeSelecionada?.contas?.nome ||
    oportunidadeSelecionada?.contas?.razao_social ||
    oportunidadeSelecionada?.cliente_nome ||
    oportunidadeSelecionada?.conta_nome ||
    (oportunidadeSelecionada ? "Cliente não informado" : "—");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro(null);

    const dados = {
      negocio_id: oportunidadeSelecionada?.id,
      fabricante_id: fabricanteId,
      categoria: categoria,
      responsavel_operacional_clickup_id: responsavelId,
      request_id: requestIdRef.current,
    };

    try {
      if (validarPayloadCriacaoRo) {
        validarPayloadCriacaoRo(dados, {
          fabricantesAtivos: fabricantes,
          catalogoDisponivel: fabricantesStatus.ready === true && !fabricantesStatus.error,
        });
      } else {
        if (!dados.negocio_id) throw new Error("A seleção da oportunidade é obrigatória.");
        if (!dados.fabricante_id) throw new Error("O fabricante é obrigatório.");
        if (!dados.categoria) throw new Error("A categoria é obrigatória.");
      }
    } catch (valErr) {
      setErro(valErr.message);
      return;
    }

    setSalvando(true);
    try {
      const headersFn = typeof getSupabaseHeaders === "function" ? getSupabaseHeaders : () => ({});
      const resultado = await criarRegistroOportunidade(dados, {
        getHeaders: headersFn,
      });

      // Sucesso 2xx confirmado: renova request_id para próxima criação e notifica
      if (gerarRequestIdRo) {
        requestIdRef.current = gerarRequestIdRo();
      }
      if (onSuccess) {
        onSuccess(resultado);
      }
    } catch (saveErr) {
      // Preserva os dados do formulário e o request_id estável em caso de erro (401, 409, 422, rede)
      setErro(saveErr.message || "Erro ao registrar R.O. Seus dados foram preservados; tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
      onClick={() => {
        if (!salvando) onClose();
      }}
    >
      <div
        ref={modalRef}
        onKeyDown={modalLayer.onKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-nova-ro-titulo"
        className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col ring-1 ring-slate-200 dark:ring-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header do Modal */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-extrabold text-sm shadow-md shrink-0">
              RO
            </div>
            <div>
              <h3 id="modal-nova-ro-titulo" className="font-black text-base text-slate-900 dark:text-slate-100 leading-tight">
                Nova R.O.
              </h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Cadastro de Registro de Oportunidade com fabricante
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={salvando}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer disabled:opacity-50"
            title="Fechar (ESC)"
          >
            ✕
          </button>
        </div>

        {/* Formulário */}
        <form noValidate onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          {erro && (
            <div className="p-3.5 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800 rounded-xl text-xs text-rose-700 dark:text-rose-300 font-semibold flex items-start gap-2">
              <span className="shrink-0 text-base">⚠️</span>
              <div className="flex-1">
                <p>{erro}</p>
                <p className="text-[11px] font-normal text-rose-600 dark:text-rose-400 mt-0.5">
                  Os dados digitados foram preservados. Você pode corrigir e tentar novamente.
                </p>
              </div>
            </div>
          )}

          {/* Oportunidade (obrigatória) */}
          {oportunidadeSelecionada ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  Oportunidade *
                </label>
                {!oportunidadeFixa && (
                  <button
                    type="button"
                    onClick={() => {
                      setOportunidadeSelecionada(null);
                      setBuscaOportunidade("");
                      setDropdownAberto(true);
                    }}
                    className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 cursor-pointer"
                  >
                    Alterar oportunidade
                  </button>
                )}
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-slate-100">
                    {oportunidadeSelecionada.nome || oportunidadeSelecionada.name}
                  </p>
                  {oportunidadeFixa && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      Oportunidade vinculada ao contexto atual (fixa)
                    </p>
                  )}
                </div>
                <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 text-[10px] font-bold border border-emerald-200 dark:border-emerald-800">
                  Selecionada
                </span>
              </div>
            </div>
          ) : (
            <div className="relative">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Pesquisar Oportunidade *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={buscaOportunidade}
                  onChange={(e) => {
                    setBuscaOportunidade(e.target.value);
                    setDropdownAberto(true);
                  }}
                  onFocus={() => setDropdownAberto(true)}
                  placeholder="Digite o nome do projeto ou do cliente..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                />
                {buscandoOportunidades && (
                  <span className="absolute right-3 top-2.5 text-xs text-slate-400 animate-spin">⏳</span>
                )}
              </div>

              {dropdownAberto && (
                <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-20 divide-y divide-slate-100 dark:divide-slate-700">
                  {sugestoesOportunidades.length > 0 ? (
                    sugestoesOportunidades.map((op) => (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => {
                          setOportunidadeSelecionada(op);
                          setDropdownAberto(false);
                        }}
                        className="w-full text-left p-2.5 hover:bg-indigo-50/70 dark:hover:bg-slate-800 transition-colors flex flex-col gap-0.5 cursor-pointer"
                      >
                        <span className="text-xs font-bold text-slate-900 dark:text-slate-100">
                          {desambiguarOportunidade ? desambiguarOportunidade(op) : (op.nome || op.name)}
                        </span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">
                          Cliente: {op.contas?.nome || op.contas?.razao_social || "Sem cliente"} · Projeto: {op.nome || op.name}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-xs text-slate-400 text-center">
                      {buscandoOportunidades ? "Pesquisando oportunidades..." : "Nenhuma oportunidade encontrada."}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cliente (Somente Leitura - Derivado da Oportunidade) */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
              Cliente (da oportunidade)
            </label>
            <input
              type="text"
              readOnly
              disabled
              value={clienteNome}
              className="w-full px-3.5 py-2.5 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 cursor-not-allowed"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              O cliente é sempre herdado da oportunidade selecionada.
            </p>
          </div>

          {/* Fabricante e Categoria (Obrigatórios) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Fabricante *
              </label>
              <select
                required
                disabled={fabricantesStatus.loading || !!fabricantesStatus.error}
                value={fabricanteId}
                onChange={(e) => setFabricanteId(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer"
              >
                <option value="">Selecione o fabricante...</option>
                {fabricantes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
              {fabricantesStatus.loading && <p role="status" className="mt-1 text-[10px] text-slate-500">Atualizando fabricantes ativos...</p>}
              {fabricantesStatus.error && <p role="alert" className="mt-1 text-[10px] text-rose-600 dark:text-rose-400">Não foi possível carregar os fabricantes ativos. Feche e abra novamente para tentar outra vez.</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Categoria *
              </label>
              <input
                type="text"
                required
                list="categorias-ro-sugestoes"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                placeholder="Ex: Infraestrutura, Software, Serviços..."
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
              />
              <datalist id="categorias-ro-sugestoes">
                {CATEGORIAS_RO.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div>
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Responsável Operacional (opcional)
              </label>
              <select
                value={responsavelId}
                onChange={(e) => setResponsavelId(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer"
              >
                <option value="">Não definido</option>
                {vendedores.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer com botões */}
          <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={salvando}
              className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando || fabricantesStatus.loading || !!fabricantesStatus.error || fabricantesStatus.ready !== true}
              className="inline-flex items-center gap-2 px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-sm cursor-pointer disabled:opacity-50"
            >
              {salvando && <span className="animate-spin">⏳</span>}
              <span>{salvando ? "Salvando R.O..." : "Salvar R.O."}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// VISTA PRINCIPAL DE REGISTROS DE OPORTUNIDADE (R.O. - Task 3.1)
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// MODAL DE PREPARAÇÃO DE ATUALIZAÇÃO POR FABRICANTE (Task 8)
// ─────────────────────────────────────────────────────────────────────────
function AtualizacaoFabricanteModal({
  aberto,
  onClose,
  fabricanteNome,
  evidenciasData,
  loading,
  error,
  showToast,
}) {
  const modalRef = React.useRef(null);
  useModalLayer({ open: aberto, onClose, panelRef: modalRef });

  const [copiando, setCopiando] = useState(false);

  if (!aberto) return null;

  const ros = evidenciasData?.data || [];
  const total = evidenciasData?.total ?? ros.length;
  const semAtualizacaoConfirmada = evidenciasData?.total_sem_atualizacao_confirmada ?? evidenciasData?.total_sem_atualizacao ?? 0;
  const coberturaDesconhecida = evidenciasData?.total_cobertura_desconhecida ?? 0;
  const comAtualizacao = evidenciasData?.total_com_atualizacao ?? 0;
  const coberturaCompleta = evidenciasData?.cobertura_evidencias_completa ?? (coberturaDesconhecida === 0);

  const handleCopiarRelatorio = async () => {
    if (!formatarRelatorioAtualizacaoFabricante) return;
    setCopiando(true);
    try {
      const texto = formatarRelatorioAtualizacaoFabricante(evidenciasData, fabricanteNome);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(texto);
      }
      if (showToast) {
        showToast('Relatório copiado para a área de transferência!', 'success');
      }
    } catch (err) {
      if (showToast) {
        showToast('Não foi possível copiar o relatório.', 'error');
      }
    } finally {
      setCopiando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/40 backdrop-blur-xs animate-in fade-in duration-150"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-atualizacao-fabricante"
        className="w-full max-w-4xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden animate-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 mb-2">
              <span>📋</span>
              <span>Preparação de Atualização por Fabricante</span>
            </div>
            <h2 id="titulo-atualizacao-fabricante" className="text-xl font-bold text-slate-900 dark:text-white">
              {fabricanteNome || 'Fabricante'} — Evidências Comerciais
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
              Fatos e evidências de autoria humana registradas na oportunidade no CRM. Período analisado: {evidenciasData?.data_inicio ? (formatarDataCivil ? formatarDataCivil(evidenciasData.data_inicio) : evidenciasData.data_inicio) : '—'} a {evidenciasData?.data_fim ? (formatarDataCivil ? formatarDataCivil(evidenciasData.data_fim) : evidenciasData.data_fim) : '—'}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            title="Fechar (ESC)"
          >
            ✕
          </button>
        </div>

        {/* Resumo rápido */}
        <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-slate-400 dark:text-slate-300">Total de R.Os:</span>{' '}
              <strong className="text-slate-800 dark:text-slate-100">{total}</strong>
            </div>
            <div>
              <span className="text-slate-400 dark:text-slate-300">Com evidência recente:</span>{' '}
              <strong className="text-emerald-600 dark:text-emerald-400">{comAtualizacao}</strong>
            </div>
            <div>
              <span className="text-slate-400 dark:text-slate-300">Sem atualização confirmada:</span>{' '}
              <strong className="text-rose-600 dark:text-rose-400">{semAtualizacaoConfirmada}</strong>
            </div>
            {coberturaDesconhecida > 0 && (
              <div>
                <span className="text-slate-400 dark:text-slate-300">Cobertura desconhecida / parcial:</span>{' '}
                <strong className="text-amber-600 dark:text-amber-400">{coberturaDesconhecida}</strong>
              </div>
            )}
          </div>
          <div>
            {!coberturaCompleta ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">
                ⚠️ Cobertura Parcial ({coberturaDesconhecida} pendente)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                ✓ Cobertura Completa
              </span>
            )}
          </div>
        </div>

        {/* Corpo com a lista de R.Os */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="py-12 text-center text-xs text-slate-500 dark:text-slate-300 animate-pulse">
              Carregando evidências comerciais humanas da oportunidade...
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-xs text-rose-700 dark:text-rose-300">
              Falha ao carregar evidências: {error}
            </div>
          ) : ros.length === 0 ? (
            <div className="py-12 text-center text-xs text-slate-500 dark:text-slate-300">
              Nenhuma R.O. encontrada para este fabricante com os filtros atuais.
            </div>
          ) : (
            ros.map((ro, index) => {
              const cliente = ro.cliente || 'Cliente não informado';
              const identificacao = gerarIdentificacaoDerivadaRo
                ? gerarIdentificacaoDerivadaRo({
                    ...ro,
                    fabricante: ro.fabricante || fabricanteNome,
                  })
                : {
                    texto: `${ro.oportunidade || 'Oportunidade não informada'} · ${ro.fabricante || fabricanteNome || 'Fabricante não informado'} · ${ro.numero_ro || 'Aguardando número'}`,
                    discriminador: null,
                  };
              const venc = ro.data_vencimento ? (formatarDataCivil ? formatarDataCivil(ro.data_vencimento) : ro.data_vencimento) : 'Sem prazo';

              return (
                <div
                  key={ro.id || index}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-purple-200 dark:hover:border-purple-900/60 transition-all text-xs space-y-2.5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-slate-900 dark:text-white text-sm">
                          {identificacao.texto}
                        </span>
                        {ro.oportunidade_clickup_id && (
                          <a
                            href={`https://app.clickup.com/t/${ro.oportunidade_clickup_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200 hover:underline inline-flex items-center gap-0.5"
                          >
                            <span>ClickUp #{ro.oportunidade_clickup_id}</span>
                            <span>↗</span>
                          </a>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
                        Cliente: {cliente}
                      </p>
                      {identificacao.discriminador && (
                        <p className="mt-0.5 text-[10px] font-mono text-slate-400 dark:text-slate-400">
                          {identificacao.discriminador}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="px-2 py-0.5 rounded font-semibold text-[11px] bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300">
                        {ro.situacao}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-slate-500 dark:text-slate-300 text-[11px]">
                    <div>
                      Vencimento: <strong className="text-slate-700 dark:text-slate-200">{venc}</strong> ({ro.vigencia || '—'})
                    </div>
                    <div>
                      Ciclo: <strong className="text-slate-700 dark:text-slate-200">{ro.ciclo_renovacao || 0}</strong>
                    </div>
                  </div>

                  {/* Evidência no Período vs Sem Atualização Confirmada vs Cobertura Desconhecida */}
                  {ro.atualizacao_no_periodo ? (
                    <div className="p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40">
                      <div className="flex items-center justify-between text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 mb-1">
                        <span>Atualização humana no período ({formatarDataCivil ? formatarDataCivil(ro.atualizacao_no_periodo.data) : ro.atualizacao_no_periodo.data?.slice(0, 10)})</span>
                        <span className="text-slate-500 dark:text-slate-300 font-normal">por {ro.atualizacao_no_periodo.autor_nome || 'Autor comercial'}</span>
                      </div>
                      <p className="text-slate-700 dark:text-slate-200 italic">
                        "{ro.atualizacao_no_periodo.texto || 'Evidência registrada sem texto.'}"
                      </p>
                    </div>
                  ) : ro.status_evidencia === 'cobertura_desconhecida' ? (
                    <div className="p-3 rounded-lg bg-slate-100/70 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-1.5">
                      <div className="font-bold text-slate-700 dark:text-slate-300 text-[11px] flex items-center gap-1">
                        <span>⚠️</span>
                        <span>Cobertura de evidências desconhecida ou parcial</span>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-300">
                        A consulta de evidências desta oportunidade não pôde ser confirmada integralmente. Não tratada como ausência de atividade comercial.
                      </p>
                      {ro.ultima_atividade_humana && (
                        <div className="text-[11px] text-slate-600 dark:text-slate-300 pt-1 border-t border-slate-200/60 dark:border-slate-700/60">
                          <span className="text-slate-400 dark:text-slate-300">Última atividade parcial:</span>{' '}
                          <em>"{ro.ultima_atividade_humana.texto || 'Atividade registrada.'}"</em>{' '}
                          <span className="text-slate-400 dark:text-slate-300">
                            (em {formatarDataCivil ? formatarDataCivil(ro.ultima_atividade_humana.data) : ro.ultima_atividade_humana.data?.slice(0, 10)} por {ro.ultima_atividade_humana.autor_nome || 'Autor comercial'})
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/40 space-y-1.5">
                      <div className="font-bold text-amber-800 dark:text-amber-400 text-[11px] flex items-center gap-1">
                        <span>⚠️</span>
                        <span>Sem atualização humana neste mês (cobertura confirmada)</span>
                      </div>
                      {ro.ultima_atividade_humana ? (
                        <div className="text-[11px] text-slate-600 dark:text-slate-300">
                          <span className="text-slate-400 dark:text-slate-300">Última atividade anterior:</span>{' '}
                          <em>"{ro.ultima_atividade_humana.texto || 'Atividade anterior registrada.'}"</em>{' '}
                          <span className="text-slate-400 dark:text-slate-300">
                            (em {formatarDataCivil ? formatarDataCivil(ro.ultima_atividade_humana.data) : ro.ultima_atividade_humana.data?.slice(0, 10)} por {ro.ultima_atividade_humana.autor_nome || 'Autor comercial'})
                          </span>
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-500 dark:text-slate-300 italic">
                          Nenhum histórico de atividade humana anterior localizado nesta oportunidade.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Alertas */}
                  {Array.isArray(ro.alertas) && ro.alertas.length > 0 && (
                    <div className="space-y-1">
                      {ro.alertas.map((alerta, aIdx) => (
                        <div key={aIdx} className="text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1">
                          <span>•</span>
                          <span>{alerta}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer com botão Copiar */}
        <div className="p-4 px-6 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 dark:text-slate-300">
            Apenas fatos e evidências humanas elegíveis. Textos para portais devem ser conferidos antes do envio.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={handleCopiarRelatorio}
              disabled={copiando || loading || ros.length === 0}
              className="px-4 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 active:scale-95 disabled:opacity-50 rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <span>📋</span>
              <span>{copiando ? 'Copiando...' : 'Copiar relatório'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrosOportunidadeView({
  rosState,
  rosResumo,
  fabricantes = [],
  fabricantesStatus = { loading: false, error: null, ready: false },
  onRecarregarFabricantes = null,
  vendedores = [],
  onSelect,
  onCloseSelection,
  onNovaRo,
  onFilterChange,
  onPageChange,
  onRefresh,
  getSupabaseHeaders,
  supabaseClient,
  showToast,
  onRoAtualizada,
  permissaoRos = { role: 'comum', pode_excluir: false, pode_restaurar: false },
}) {
  const { rows = [], filters = {}, page = 1, total = 0, loading, error, selectedId } = rosState;
  const filtroBusca = filters.busca || filters.q || '';
  const [buscaDigitada, setBuscaDigitada] = useState(filtroBusca);
  const [roNavegada, setRoNavegada] = useState(null);
  const roSelecionada = (selectedId && (rows.find((ro) => ro.id === selectedId) || (roNavegada?.id === selectedId ? roNavegada : null))) || null;

  const [modalAtualizacaoAberto, setModalAtualizacaoAberto] = useState(false);
  const [modalExcluidasAberto, setModalExcluidasAberto] = useState(false);
  const [evidenciasState, setEvidenciasState] = useState({
    loading: false,
    error: null,
    data: null,
  });

  // Carregar evidências humanas no período (Task 8)
  useEffect(() => {
    let ativo = true;
    const carregarEvidencias = async () => {
      setEvidenciasState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        if (!fetchEvidenciasRos) return;
        const res = await fetchEvidenciasRos(filters, page, 50, {
          getHeaders: getSupabaseHeaders,
          getSupabaseHeaders,
        });
        if (ativo) {
          setEvidenciasState({
            loading: false,
            error: null,
            data: res,
          });
        }
      } catch (err) {
        if (ativo) {
          setEvidenciasState({
            loading: false,
            error: err.message || 'Erro ao carregar evidências.',
            data: null,
          });
        }
      }
    };
    carregarEvidencias();
    return () => { ativo = false; };
  }, [filters, page, getSupabaseHeaders]);

  // A busca ampla pode consultar contas, oportunidades e R.Os. Esperar uma
  // pausa curta evita uma chamada ao CRM para cada tecla digitada.
  useEffect(() => {
    setBuscaDigitada(filtroBusca);
  }, [filtroBusca]);

  useEffect(() => {
    if (buscaDigitada === filtroBusca) return undefined;
    const temporizador = setTimeout(() => {
      const transicao = atualizarFiltrosRos(filters, 'busca', buscaDigitada);
      onFilterChange(transicao.filters);
    }, 350);
    return () => clearTimeout(temporizador);
  }, [buscaDigitada, filtroBusca, filters, onFilterChange]);

  const paginacao = calcularPaginacao ? calcularPaginacao(total, page, 50) : {
    totalPaginas: Math.max(1, Math.ceil(total / 50)),
    inicio: total === 0 ? 0 : (page - 1) * 50 + 1,
    fim: Math.min(total, page * 50),
    temAnterior: page > 1,
    temProximo: page < Math.max(1, Math.ceil(total / 50)),
  };

  const estadoResumo = tratarEstadoResumo
    ? tratarEstadoResumo(rosResumo, rosResumo?.error, rosResumo?.loading)
    : {
        carregando: Boolean(rosResumo?.loading),
        disponivel: Boolean(!rosResumo?.error && rosResumo),
        textoExibicao: rosResumo?.error ? 'Indisponível' : null,
        valores: rosResumo || {},
      };

  const handleMudarFiltro = (campo, valor) => {
    const transicao = atualizarFiltrosRos(filters, campo, valor);
    onFilterChange(transicao.filters);
  };

  const handleLimparFiltros = () => {
    const transicao = limparFiltrosRos();
    setBuscaDigitada('');
    onFilterChange(transicao.filters);
  };

  const temFiltrosAtivos = Object.keys(filters).some(
    (k) => filters[k] !== undefined && filters[k] !== '' && filters[k] !== 'all',
  );

  const obterClasseVigencia = (tipo) => {
    switch (tipo) {
      case 'Vencida':
        return 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300 dark:border-rose-800';
      case 'Vence hoje':
        return 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300 border-amber-400 dark:border-amber-700 font-bold';
      case 'A vencer':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border-orange-300 dark:border-orange-800';
      case 'Vigente':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800';
      default:
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-300 dark:border-slate-700';
    }
  };

  return (
    <div className="w-full h-full min-h-0 flex-1 flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden">
      {/* 1. Header do Módulo */}
      <div className="px-4 py-4 bg-white dark:bg-slate-800 border-b border-slate-200/80 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-3 flex-shrink-0">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
              RO
            </span>
            <h1 className="text-xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
              Registros de Oportunidade (R.O.)
            </h1>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Central operacional de acompanhamento de R.Os, prazos com fabricantes e ciclos de renovação.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {permissaoRos.pode_restaurar && (
            <button
              type="button"
              onClick={() => setModalExcluidasAberto(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              title="Consultar R.Os excluídas"
            >
              R.Os excluídas
            </button>
          )}
          {onNovaRo && (
            <button
              type="button"
              onClick={onNovaRo}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Cadastrar nova R.O."
            >
              <span>+ Nova R.O.</span>
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            title="Atualizar lista de R.Os"
          >
            <span className={`inline-block ${loading ? 'animate-spin' : ''}`}>🔄</span>
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* 2. Cards de Indicadores Agregados do Banco */}
      <div className="px-4 pt-3 pb-2 grid grid-cols-2 lg:grid-cols-5 gap-3 flex-shrink-0">
        {/* Total Filtrado */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 dark:text-slate-400">
            <span>Total Filtrado</span>
            <span className="text-slate-400">📋</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-900 dark:text-slate-100">
              {estadoResumo.carregando ? (
                <span className="text-sm font-medium text-slate-400 animate-pulse">Carregando...</span>
              ) : !estadoResumo.disponivel ? (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Indisponível</span>
              ) : (
                estadoResumo.valores.total
              )}
            </span>
            <span className="text-[11px] text-slate-400">R.Os</span>
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 truncate">
            {temFiltrosAtivos ? 'No conjunto filtrado no banco' : 'Total de R.Os cadastradas'}
          </div>
        </div>

        {/* Aguardando Aprovação */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs font-semibold text-blue-600 dark:text-blue-400">
            <span>Aguardando Aprovação</span>
            <span>⏳</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-blue-700 dark:text-blue-300">
              {estadoResumo.carregando ? (
                <span className="text-sm font-medium text-slate-400 animate-pulse">Carregando...</span>
              ) : !estadoResumo.disponivel ? (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Indisponível</span>
              ) : (
                estadoResumo.valores.aguardando_aprovacao
              )}
            </span>
            <span className="text-[11px] text-slate-400">pendentes</span>
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 truncate">
            Enviadas ao fabricante/portal
          </div>
        </div>

        {/* Renovações em Análise */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs font-semibold text-amber-600 dark:text-amber-400">
            <span>Renovações em Análise</span>
            <span>🔄</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-700 dark:text-amber-300">
              {estadoResumo.carregando ? (
                <span className="text-sm font-medium text-slate-400 animate-pulse">Carregando...</span>
              ) : !estadoResumo.disponivel ? (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Indisponível</span>
              ) : (
                estadoResumo.valores.renovacoes_em_analise
              )}
            </span>
            <span className="text-[11px] text-slate-400">solicitações</span>
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 truncate">
            Ciclo solicitado sob avaliação
          </div>
        </div>

        {/* Vencem em até 15 dias */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs font-semibold text-rose-600 dark:text-rose-400">
            <span>Vencem em até 15 dias</span>
            <span>🚨</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-700 dark:text-rose-300">
              {estadoResumo.carregando ? (
                <span className="text-sm font-medium text-slate-400 animate-pulse">Carregando...</span>
              ) : !estadoResumo.disponivel ? (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Indisponível</span>
              ) : (
                estadoResumo.valores.vencem_15_dias
              )}
            </span>
            <span className="text-[11px] text-slate-400">atenção</span>
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 truncate">
            Aprovadas próximas do prazo civil
          </div>
        </div>

        {/* Atualização Humana no Mês (Task 8 - Indicador Real) */}
        <div className="p-3.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs font-semibold text-purple-600 dark:text-purple-400">
            <span>Atualização Humana</span>
            <span title="Fatos e evidências de autoria humana da oportunidade no CRM">🧠</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            {evidenciasState.loading ? (
              <span className="text-sm font-medium text-slate-400 animate-pulse">Calculando...</span>
            ) : evidenciasState.error ? (
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400" title={evidenciasState.error}>
                Indisponível
              </span>
            ) : (
              <>
                <span className="text-2xl font-black text-slate-900 dark:text-white">
                  {evidenciasState.data?.total_sem_atualizacao_confirmada ?? 0}
                </span>
                <span className="text-[11px] text-slate-400">sem atualização confirmada</span>
              </>
            )}
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 flex items-center justify-between truncate">
            <span>
              {evidenciasState.data ? `${evidenciasState.data.total_com_atualizacao ?? 0} com evidência recente` : 'Evidências no período'}
            </span>
            {evidenciasState.data && (!evidenciasState.data.cobertura_evidencias_completa || (evidenciasState.data.total_cobertura_desconhecida > 0)) && (
              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/60 px-1.5 py-0.5 rounded ml-1" title={`${evidenciasState.data.total_cobertura_desconhecida || 0} oportunidade(s) com cobertura desconhecida`}>
                cobertura parcial ({evidenciasState.data.total_cobertura_desconhecida ?? 0} pendente)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 3. Barra de Filtros Estruturados e Consistentes */}
      <div className="px-4 pt-2 pb-3 flex-shrink-0">
        <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-wrap items-center gap-2.5">
          {/* Busca Textual Ampla */}
          <div className="basis-full min-w-0 flex-1 lg:basis-[24rem]">
            <label htmlFor="filtro-busca" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Buscar por cliente, oportunidade ou número da R.O.
            </label>
            <div className="relative flex items-center">
              <input
                type="text"
                id="filtro-busca"
                placeholder="Buscar por cliente, oportunidade ou número da R.O."
                value={buscaDigitada}
                onChange={(e) => setBuscaDigitada(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none pr-7"
              />
              {buscaDigitada && (
                <button
                  type="button"
                  onClick={() => setBuscaDigitada('')}
                  className="absolute right-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs font-bold cursor-pointer"
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Filtro Fabricante */}
          <div className="flex flex-col min-w-[160px]">
            <label htmlFor="filtro-fabricante" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Fabricante
            </label>
            <select
              id="filtro-fabricante"
              value={filters.fabricante_id || ''}
              onChange={(e) => handleMudarFiltro('fabricante_id', e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="">Todos os fabricantes</option>
              {fabricantes.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Situação */}
          <div className="flex flex-col min-w-[160px]">
            <label htmlFor="filtro-situacao" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Situação
            </label>
            <select
              id="filtro-situacao"
              value={filters.situacao || ''}
              onChange={(e) => handleMudarFiltro('situacao', e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="">Todas as situações</option>
              <option value="Backoffice">Backoffice</option>
              <option value="Aguardando aprovação">Aguardando aprovação</option>
              <option value="Aprovada">Aprovada</option>
              <option value="Reprovada">Reprovada</option>
              <option value="Encerrada">Encerrada</option>
              <option value="Substituída">Substituída</option>
            </select>
          </div>

          {/* Filtro Responsável Operacional */}
          <div className="flex flex-col min-w-[160px]">
            <label htmlFor="filtro-responsavel" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Responsável
            </label>
            <select
              id="filtro-responsavel"
              value={filters.responsavel || ''}
              onChange={(e) => handleMudarFiltro('responsavel', e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="">Todos os responsáveis</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Vence até (Data civil YYYY-MM-DD) */}
          <div className="flex flex-col min-w-[140px]">
            <label htmlFor="filtro-vence-ate" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Vence até
            </label>
            <input
              type="date"
              id="filtro-vence-ate"
              value={filters.vence_ate || ''}
              onChange={(e) => handleMudarFiltro('vence_ate', e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none h-[30px]"
            />
          </div>

          {/* Filtros de Período para Evidências Humanas (Task 8) */}
          <div className="flex flex-col min-w-[130px]">
            <label htmlFor="filtro-data-inicio" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Período Início
            </label>
            <input
              type="date"
              id="filtro-data-inicio"
              value={filters.data_inicio || ''}
              onChange={(e) => handleMudarFiltro('data_inicio', e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none h-[30px]"
            />
          </div>

          <div className="flex flex-col min-w-[130px]">
            <label htmlFor="filtro-data-fim" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Período Fim
            </label>
            <input
              type="date"
              id="filtro-data-fim"
              value={filters.data_fim || ''}
              onChange={(e) => handleMudarFiltro('data_fim', e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none h-[30px]"
            />
          </div>

          {/* Ação: Preparar atualização por fabricante (apenas quando houver fabricante selecionado) */}
          {filters.fabricante_id && (
            <div className="flex items-end self-end">
              <button
                type="button"
                id="btn-preparar-atualizacao-fabricante"
                onClick={() => setModalAtualizacaoAberto(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 active:scale-95 text-white text-xs font-bold transition-all shadow-xs cursor-pointer h-[30px]"
                title="Abre relatório estruturado e copiável com as evidências do fabricante"
              >
                <span>📋</span>
                <span>Preparar atualização por fabricante</span>
              </button>
            </div>
          )}

          {/* Filtro Oportunidade ativa (se houver) */}
          {filters.negocio_id && (
            <div className="flex flex-col min-w-[150px]">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                Oportunidade
              </span>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-700 dark:text-indigo-300 font-semibold">
                <span className="truncate max-w-[120px]" title={filters.negocio_id}>Negócio #{filters.negocio_id.slice(0, 8)}</span>
                <button
                  type="button"
                  onClick={() => handleMudarFiltro('negocio_id', '')}
                  className="hover:text-rose-600 cursor-pointer font-bold"
                  title="Remover filtro de negócio"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Botão Limpar Filtros */}
          {temFiltrosAtivos && (
            <div className="flex flex-col justify-end self-end">
              <button
                type="button"
                onClick={handleLimparFiltros}
                className="px-3 py-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors cursor-pointer border border-rose-200 dark:border-rose-900 focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 4. Tabela de R.Os com Colunas Segregadas */}
      <div className="px-4 flex-1 min-h-0 flex flex-col mb-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/80 dark:border-slate-700 shadow-xs flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Mensagem de Erro */}
          {error && (
            <div className="m-4 p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
              <button
                type="button"
                onClick={onRefresh}
                className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {/* Skeleton de Carregamento */}
          {loading && rows.length === 0 && (
            <div className="p-8 flex flex-col items-center justify-center space-y-4 flex-1">
              <div className="w-8 h-8 border-3 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin"></div>
              <p className="text-xs text-slate-400 font-medium animate-pulse">Carregando Registros de Oportunidade...</p>
            </div>
          )}

          {/* Estado Vazio */}
          {!loading && !error && rows.length === 0 && (
            <div className="p-12 flex flex-col items-center justify-center text-center flex-1">
              <span className="text-4xl mb-3">📁</span>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
                Nenhum Registro de Oportunidade encontrado
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm">
                {temFiltrosAtivos
                  ? 'Nenhum resultado corresponde aos filtros selecionados. Tente remover filtros para visualizar outras R.Os.'
                  : 'Nenhuma R.O. cadastrada no momento.'}
              </p>
              {temFiltrosAtivos && (
                <button
                  type="button"
                  onClick={handleLimparFiltros}
                  className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                >
                  Limpar todos os filtros
                </button>
              )}
            </div>
          )}

          {/* Tabela com identificação canônica derivada dos dados estruturados */}
          {rows.length > 0 && (
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              <table className="w-full text-left border-collapse" aria-label="Tabela de Registros de Oportunidade">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/60 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider sticky top-0 z-10 backdrop-blur-xs">
                    <th scope="col" className="py-3 px-4">Cliente</th>
                    <th scope="col" className="py-3 px-4">Identificação da R.O.</th>
                    <th scope="col" className="py-3 px-3">Situação</th>
                    <th scope="col" className="py-3 px-3">Vencimento</th>
                    <th scope="col" className="py-3 px-3">Ciclo Renovação</th>
                    <th scope="col" className="py-3 px-3">Última movimentação da R.O.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 text-xs">
                  {rows.map((ro) => {
                    const isSelected = selectedId === ro.id;
                    const identificacao = gerarIdentificacaoDerivadaRo
                      ? gerarIdentificacaoDerivadaRo(ro)
                      : {
                          texto: `${ro.negocios?.nome || 'Oportunidade não informada'} · ${ro.fabricantes_ro?.nome || 'Fabricante não informado'} · ${ro.numero_ro || 'Aguardando número'}`,
                          discriminador: null,
                        };
                    const situacaoInfo = obterRotuloSituacao
                      ? obterRotuloSituacao(ro.situacao)
                      : { rotulo: ro.situacao, classeBadge: '' };
                    const vigencia = calcularVigenciaRo ? calcularVigenciaRo(ro.data_vencimento) : 'Sem prazo';
                    const dataVencFormatada = formatarDataCivil ? formatarDataCivil(ro.data_vencimento) : (ro.data_vencimento || '-');

                    // Cliente vinculado à oportunidade
                    const nomeCliente = (empresaDaRo ? empresaDaRo(ro) : '') ||
                      ro.negocios?.contas?.nome ||
                      ro.negocios?.contas?.razao_social ||
                      '—';

                    // Oportunidade com link seguro
                    const opInfo = obterLinkOportunidade ? obterLinkOportunidade(ro.negocios) : {
                      temLink: false,
                      url: null,
                      label: ro.negocios?.nome || (roSemVinculo && roSemVinculo(ro) ? 'Sem vínculo' : `Oportunidade #${String(ro.negocio_id || '').slice(0, 8)}`),
                    };

                    const categoriaExibida = formatarCategoriaRo ? formatarCategoriaRo(ro.categoria) : (ro.categoria || '—');

                    // Ciclo de renovação
                    let infoCiclo = 'Inicial';
                    if (Array.isArray(ro.renovacoes_ro) && ro.renovacoes_ro.length > 0) {
                      const temEmAnalise = ro.renovacoes_ro.find((r) => r.situacao === 'Em análise');
                      if (temEmAnalise) {
                        infoCiclo = `Ciclo ${temEmAnalise.ciclo || 1} (Em análise)`;
                      } else {
                        const maxCiclo = Math.max(...ro.renovacoes_ro.map((r) => r.ciclo || 1));
                        infoCiclo = `Ciclo ${maxCiclo} aprovado`;
                      }
                    }

                    // Última movimentação da R.O.
                    let dataMovimentacao = ro.updated_at || ro.created_at;
                    if (Array.isArray(ro.eventos_ro) && ro.eventos_ro.length > 0) {
                      const ultimoEvento = ro.eventos_ro[ro.eventos_ro.length - 1];
                      if (ultimoEvento?.created_at) dataMovimentacao = ultimoEvento.created_at;
                    }
                    const dataMovimentacaoFormatada = formatarDataCivil
                      ? formatarDataCivil(dataMovimentacao ? String(dataMovimentacao).slice(0, 10) : '')
                      : '-';

                    return (
                      <tr
                        key={ro.id}
                        tabIndex={0}
                        onClick={() => onSelect(ro.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelect(ro.id);
                          }
                        }}
                        className={`transition-colors cursor-pointer focus:outline-none focus:bg-indigo-50/50 dark:focus:bg-indigo-950/30 ${
                          isSelected
                            ? 'bg-indigo-50/80 dark:bg-indigo-950/40 font-medium'
                            : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/50 text-slate-800 dark:text-slate-200'
                        }`}
                        title="Clique para selecionar o registro"
                      >
                        {/* 1. Cliente */}
                        <td className="py-3 px-4 font-semibold text-slate-900 dark:text-slate-100">
                          <span className="line-clamp-1" title={nomeCliente}>
                            {nomeCliente}
                          </span>
                        </td>

                        {/* 2. Identificação derivada, com link seguro para a oportunidade */}
                        <td className="py-3 px-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-slate-900 dark:text-slate-100 line-clamp-1">
                                {identificacao.texto}
                              </span>
                              {opInfo.temLink && (
                                <a
                                  href={opInfo.url}
                                  target={opInfo.target}
                                  rel={opInfo.rel}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline inline-flex items-center gap-0.5"
                                  title="Abrir oportunidade no ClickUp"
                                >
                                  <span>↗</span> ClickUp
                                </a>
                              )}
                            </div>
                            {identificacao.discriminador && (
                              <span className="mt-0.5 text-[10px] font-mono text-slate-400 dark:text-slate-500">
                                {identificacao.discriminador}
                              </span>
                            )}
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 line-clamp-1 mt-0.5">
                              {categoriaExibida}
                            </span>
                          </div>
                        </td>

                        {/* 3. Situação */}
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold ${situacaoInfo.classeBadge}`}>
                            {situacaoInfo.rotulo}
                          </span>
                          {etapaOriginalImportacao && etapaOriginalImportacao(ro).provisoria && (
                            <div className="mt-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300" title="Situação provisória de importação: não é o estado original da R.O.">
                              Provisória · Agendor: {etapaOriginalImportacao(ro).etapa}
                            </div>
                          )}
                        </td>

                        {/* 4. Vencimento e Vigência */}
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {dataVencFormatada}
                            </span>
                            {ro.data_vencimento && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${obterClasseVigencia(vigencia)}`}>
                                {vigencia}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* 5. Ciclo de Renovação */}
                        <td className="py-3 px-3">
                          <span className="text-[11px] text-slate-600 dark:text-slate-300 font-medium">
                            {infoCiclo}
                          </span>
                        </td>

                        {/* 6. Última movimentação da R.O. */}
                        <td className="py-3 px-3">
                          <span className="text-[11px] text-slate-500 dark:text-slate-400" title="Data da última movimentação do registro da R.O.">
                            {dataMovimentacaoFormatada}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Rodapé de Paginação */}
          {total > 0 && (
            <div className="p-3 px-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <div className="text-slate-500 dark:text-slate-400">
                Exibindo <strong className="text-slate-800 dark:text-slate-200">{paginacao.inicio}</strong> a{' '}
                <strong className="text-slate-800 dark:text-slate-200">{paginacao.fim}</strong> de{' '}
                <strong className="text-slate-800 dark:text-slate-200">{total}</strong> registros (Página{' '}
                <strong className="text-slate-800 dark:text-slate-200">{page}</strong> de{' '}
                <strong className="text-slate-800 dark:text-slate-200">{paginacao.totalPaginas}</strong>)
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={!paginacao.temAnterior || loading}
                  onClick={() => onPageChange(page - 1)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  disabled={!paginacao.temProximo || loading}
                  onClick={() => onPageChange(page + 1)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {roSelecionada && (
        <RegistroOportunidadeDrawer
          ro={roSelecionada}
          fabricantes={fabricantes}
          fabricantesStatus={fabricantesStatus}
          onRecarregarFabricantes={onRecarregarFabricantes}
          vendedores={vendedores}
          onClose={() => {
            setRoNavegada(null);
            if (onCloseSelection) onCloseSelection();
          }}
          supabaseClient={supabaseClient}
          getSupabaseHeaders={getSupabaseHeaders}
          showToast={showToast}
          permissaoRos={permissaoRos}
          onRoAtualizada={(roAtualizada) => {
            if (onRoAtualizada) onRoAtualizada(roAtualizada);
            if (onRefresh) onRefresh();
          }}
          onSelecionarRo={(novoId, novoObj) => {
            setRoNavegada(novoObj);
            if (onSelect) onSelect(novoId);
          }}
          onRoExcluida={() => {
            setRoNavegada(null);
            if (onCloseSelection) onCloseSelection();
            if (onRefresh) onRefresh();
          }}
        />
      )}

      <RosExcluidasModal
        aberto={modalExcluidasAberto}
        onClose={() => setModalExcluidasAberto(false)}
        getSupabaseHeaders={getSupabaseHeaders}
        showToast={showToast}
        onRestaurada={onRefresh}
        permissaoRos={permissaoRos}
      />

      {modalAtualizacaoAberto && (
        <AtualizacaoFabricanteModal
          aberto={modalAtualizacaoAberto}
          onClose={() => setModalAtualizacaoAberto(false)}
          fabricanteNome={fabricantes.find((f) => String(f.id) === String(filters.fabricante_id))?.nome || 'Fabricante'}
          evidenciasData={evidenciasState.data}
          loading={evidenciasState.loading}
          error={evidenciasState.error}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function UsuariosPermissoesSettings({modo,actorId,onChange}) {
  const [rows,setRows]=useState([]),[status,setStatus]=useState(''),[busy,setBusy]=useState(false),[page,setPage]=useState(1),[total,setTotal]=useState(0),[audit,setAudit]=useState(null),[change,setChange]=useState(null),[password,setPassword]=useState('');
  const pending=useRef(new Map());
  const reload=async(p=page)=>{setBusy(true);try{const r=await window.CrmUsuarios.listar(p);setRows(r.data);setTotal(r.total);}catch{setRows([]);setStatus('A gestão exige administrador ativo e verificação disponível.');}finally{setBusy(false);}};
  useEffect(()=>{if(modo==='canonico_v1')void reload();else{setRows([]);setStatus('Gestão canônica ainda não ativada.');}},[modo,page]);
  const choose=(row,next)=>{if(next===row.role)return;const own=row.clickup_user_id===actorId;if(!window.confirm(`${row.nome}: ${row.role} → ${next}. ${own?'Você altera o próprio papel; outro administrador ativo deve permanecer.':''}`))return;setChange({row,next});setPassword('');};
  const submit=async(e)=>{
    e.preventDefault();if(!change||!password)return;const {row,next}=change,secret=password;setPassword('');const key=row.clickup_user_id+':'+row.role+':'+next;
    if(!pending.current.has(key))pending.current.set(key,crypto.randomUUID());setBusy(true);
    try{await window.CrmUsuarios.alterarPapel(row.clickup_user_id,row.role,next,secret,pending.current.get(key));pending.current.delete(key);setChange(null);await reload();await onChange?.();setStatus('Papel confirmado pelo servidor.');}
    catch(err){setStatus(({PAPEL_CONFLITO:'Papel alterado por outra sessão. Lista recarregada.',ULTIMO_ADMIN_ATIVO:'O último administrador ativo não pode ser rebaixado.',SESSAO_RECENTE_REQUERIDA:'Reautenticação não confirmada.',USUARIO_NAO_ATIVO:'Usuário inativo não pode ser promovido.'})[err.code]||'Operação não confirmada. Repetição preserva a identificação da requisição.');if(err.code==='PAPEL_CONFLITO'){pending.current.delete(key);setChange(null);await reload();}}finally{setBusy(false);}
  };
  return <section className="space-y-3"><h2 className="text-base font-bold">Usuários e Permissões</h2><p className="text-xs">Identidade e atividade verificadas no servidor; papéis não são definidos pelo navegador.</p><p role="status" className="text-xs">{status}</p>{change&&<form onSubmit={submit} className="flex flex-wrap gap-2 p-3 rounded-xl bg-slate-100 dark:bg-slate-700"><label>Senha para reautenticar <input type="password" autoComplete="current-password" value={password} onChange={e=>setPassword(e.target.value)} disabled={busy} required className="bg-white dark:bg-slate-800" /></label><button disabled={busy}>Confirmar alteração</button><button type="button" disabled={busy} onClick={()=>{setChange(null);setPassword('');}}>Cancelar</button></form>}<div className="overflow-auto max-h-[60vh]"><table className="w-full text-xs"><thead className="sticky top-0 bg-white dark:bg-slate-800"><tr><th>Usuário</th><th>Acesso</th><th>Papel</th></tr></thead><tbody>{rows.map(row=><tr key={row.clickup_user_id}><td className="p-2">{row.nome}<div>{row.email}</div></td><td>{row.access_status}{row.role!=='comum'&&row.access_status!=='ativo'?' — papel privilegiado pendente de regularização':''}</td><td><select aria-label={`Papel de ${row.nome}`} disabled={busy||!!change} value={row.role} onChange={e=>choose(row,e.target.value)} className="bg-white dark:bg-slate-700"><option value="comum">Comum</option><option value="gestor_ro" disabled={row.access_status!=='ativo'}>Gestor de R.O.</option><option value="admin" disabled={row.access_status!=='ativo'}>Administrador</option></select></td></tr>)}</tbody></table></div><div className="flex gap-2"><button disabled={busy||page===1} onClick={()=>setPage(page-1)}>Anterior</button><span>{page} / {Math.max(1,Math.ceil(total/100))}</span><button disabled={busy||page*100>=total} onClick={()=>setPage(page+1)}>Próxima</button><button disabled={busy} onClick={()=>void reload()}>Revalidar</button><button disabled={busy} onClick={async()=>{try{setAudit((await window.CrmUsuarios.auditoria()).data);}catch{setStatus('Auditoria exige administrador ativo.');}}}>Auditoria</button></div>{audit&&<ul className="text-xs max-h-40 overflow-auto">{audit.map(event=><li key={event.request_id}>{event.created_at} — {event.ator_clickup_user_id} → {event.alvo_clickup_user_id}: {event.papel_anterior} → {event.papel_novo}</li>)}</ul>}</section>;
}

function App() {
  const [config, setConfig] = useState(getInitialConfig);
  // Mapa clickup_negocio_id -> estagio real do negócio, usado pra blindar os
  // cálculos de "Ganho"/"Perdido" do relatório contra proposta.data_fechamento
  // desalinhada do estágio de verdade do negócio (ver migration 20260819e e
  // docs/resumo.md). Declarado no topo do componente porque é referenciado
  // por useMemo's (distributorTotals, manufacturerTotals) que rodam antes
  // de outros hooks mais abaixo no arquivo.
  const negociosEstagioRef = useRef(new Map());
  // TTL (60s) pra evitar rebuscar tabela inteira do zero toda vez que o
  // usuário troca de aba e volta pro Kanban/Relatórios — antes rebuscava
  // sempre, mesmo se a última busca tivesse sido há poucos segundos.
  // Atualizados sempre que a busca correspondente de fato roda (por
  // qualquer motivo, não só troca de aba), pra refletir o dado mais recente.
  const TAB_CACHE_TTL_MS = 60000;
  const lastKanbanFetchAtRef = useRef(0);
  const lastDashboardFetchAtRef = useRef(0);
  // Desde que o Relatórios passou a filtrar propostas por data no servidor
  // (loadDashboardData), rawProposalsRef só cobre o período que estava ativo
  // na hora da última busca de verdade — não é mais "a tabela inteira" que
  // dava pra refiltrar livremente pra qualquer range sem rebuscar. Guarda
  // aqui o range que foi de fato buscado, pra saber quando um novo range
  // pedido pelo usuário (troca de período/comparativo) exige busca nova
  // mesmo dentro do TTL — sem isso, escolher um período fora do que já
  // estava em cache voltava zerado (o cache antigo era refiltrado, não
  // rebuscado).
  const lastFetchedBoundsRef = useRef({ lower: null, upper: null });
  // Espelha activeTab pra uso dentro do setInterval de auto-polling — sem
  // isso, fetchAllData (fechada dentro do useEffect do interval, que não
  // depende de activeTab) sempre enxergaria a aba de quando o interval foi
  // criado, não a aba atual (mesmo motivo de currentDateFilterRef abaixo).
  const activeTabRef = useRef(null);
  const [supabaseClient, setSupabaseClient] = useState(null);
  const [dbConnected, setDbConnected] = useState(false);
  const [session, setSession] = useState(null);
  const [validandoAcessoCrm, setValidandoAcessoCrm] = useState(false);
  const identidadeModoRef = useRef('pending');
  const identidadeLoginRef = useRef(0);
  const identidadeBloqueadaRef = useRef(false);
  const identidadeGateRef = useRef(null);
  const kanbanModoRef = useRef('pending');
  const kanbanComandosRef = useRef(new Map());
  const gateIdentidade = () => {
    if (!window.CrmIdentidadeLogin) throw new Error('Validação de acesso indisponível.');
    if (!identidadeGateRef.current) identidadeGateRef.current = window.CrmIdentidadeLogin.criarGate();
    return identidadeGateRef.current;
  };
  const [clickupTaskId, setClickupTaskId] = useState('');
  const [clickupListId, setClickupListId] = useState('');
  
  // Função para obter a aba inicial com base na Hash URL (SPA Hash Routing)
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '').trim();
    if (['kanban', 'relatorios', 'tasks', 'propostas', 'empresas', 'ros'].includes(hash)) {
      return hash;
    }
    return safeStorage.getItem('crm_active_view') || 'kanban';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  // EmpresasTab é um componente próprio (empresas.js) com seu próprio
  // fetch/estado interno — sem isso, cada troca de aba para "Empresas"
  // desmontava e remontava o componente do zero, refazendo a consulta
  // completa ao Supabase (444+ contas, negócios, contatos) toda vez.
  // Mantemos montado (só oculto via CSS) depois da primeira visita, para
  // que voltar à aba seja instantâneo — mesmo comportamento que Kanban/
  // Relatórios/Tarefas já têm (estado deles vive no componente pai).
  const [empresasTabMounted, setEmpresasTabMounted] = useState(activeTab === 'empresas');
  useEffect(() => {
    if (activeTab === 'empresas' && !empresasTabMounted) setEmpresasTabMounted(true);
  }, [activeTab, empresasTabMounted]);

  // Sincroniza activeTab com a Hash URL e safeStorage
  useEffect(() => {
    safeStorage.setItem('crm_active_view', activeTab);
    if (window.location.hash !== `#${activeTab}`) {
      window.location.hash = activeTab;
    }
  }, [activeTab]);

  // (O bloco de tema foi movido para logo depois de `userProfile` — ele
  // precisa do id do usuário para escopar a chave de storage.)

  // Listener para sincronizar navegação por hash (Avançar/Voltar do navegador)
  useEffect(() => {
    const handleHashChange = () => {
      const rawHash = window.location.hash.replace('#', '').trim();
      const [tabName, queryPart] = rawHash.split('?');
      if (['kanban', 'relatorios', 'tasks', 'propostas', 'empresas', 'ros'].includes(tabName)) {
        setActiveTab(tabName);
        if (tabName === 'ros' && queryPart) {
          const p = new URLSearchParams(queryPart);
          const novosFiltros = {};
          for (const [k, v] of p.entries()) {
            if (v) novosFiltros[k] = v;
          }
          setRosState((prev) => ({ ...prev, filters: novosFiltros, page: 1, selectedId: null }));
          loadRegistrosOportunidade(novosFiltros, 1);
        }
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  const [kanbanTasks, setKanbanTasks] = useState(() => {
    // v2: formato mudou de custom_fields (ClickUp) pra estagio (Supabase) em 17/08 —
    // chave nova pra não reidratar com cache antigo incompatível (fazia getTaskOptionId
    // não achar nenhum estágio e o Kanban parecer vazio até a busca nova completar).
    // v3: adicionado valorPorFabricante (19/08) — chave nova pra não reidratar com
    // cache antigo sem esse campo (Forecast filtrado por fabricante ficaria em R$ 0).
    // v4: adicionado conta_id/responsavel_clickup_id (19/08) — chave nova pra não
    // reidratar sem eles (aba Empresa do drawer ficaria vazia até a busca completar).
    const cached = localStorage.getItem('crm_cache_kanban_tasks_v4');
    return cached ? JSON.parse(cached) : [];
  });
  const [kanbanColumns, setKanbanColumns] = useState(ESTAGIO_OPTIONS);
  const [loadingKanban, setLoadingKanban] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [drawerTab, setDrawerTab] = useState('details'); // 'details' | 'budget'
  const [canDrag, setCanDrag] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('crm_sort_order') || 'default';
  });
  const [supabaseProposalsList, setSupabaseProposalsList] = useState([]);
  const [commercialTasks, setCommercialTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  // Marca se a aba Tarefas Comerciais já carregou alguma vez nesta sessão. Não usamos
  // commercialTasks.length > 0 para decidir isso porque a tabela pode legitimamente estar
  // vazia (0 tarefas) — nesse caso o array nunca fica "> 0" e a tela mostrava o spinner de
  // carregamento cheio toda vez que se voltava para a aba, mesmo já tendo carregado antes.
  const hasLoadedTasksOnceRef = useRef(false);
  const [editingTask, setEditingTask] = useState(null);
  // Inicializados a partir do que a própria pessoa deixou salvo da última
  // vez (crm_tasks_filters:<uid>) — mesma fonte síncrona usada pelo tema
  // (crm_user_profile no localStorage), então já chegam certos no 1º
  // render, sem precisar de um efeito de "adoção" rodando depois.
  const [tasksFilterAssignee, setTasksFilterAssignee] = useState(() => readTasksFilters(readProfileIdFromStorage())?.assignee || 'all');
  const [tasksPeriodFilter, setTasksPeriodFilter] = useState(() => readTasksFilters(readProfileIdFromStorage())?.period || 'all');
  const [tasksCustomStartDate, setTasksCustomStartDate] = useState(() => readTasksFilters(readProfileIdFromStorage())?.customStart || '');
  const [tasksCustomEndDate, setTasksCustomEndDate] = useState(() => readTasksFilters(readProfileIdFromStorage())?.customEnd || '');
  const [tasksShowCompleted, setTasksShowCompleted] = useState(() => readTasksFilters(readProfileIdFromStorage())?.showCompleted || false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  // Modal de RESUMO da tarefa (read-only, aberto ao clicar no card).
  // Guarda o ID, nunca o objeto: toggleTaskStatus recria os objetos de
  // commercialTasks a cada mudança, então um snapshot congelado continuaria
  // mostrando "pendente" logo depois de concluir e travaria o botão de
  // reabrir. Com o id, a tarefa é derivada do estado vivo — e some sozinha
  // do modal se for excluída.
  const [taskDetailId, setTaskDetailId] = useState(null);
  const [taskDetailShowAtividade, setTaskDetailShowAtividade] = useState(false);
  const [taskDetailAtividadeTexto, setTaskDetailAtividadeTexto] = useState('');
  const [taskDetailSaving, setTaskDetailSaving] = useState(false);
  // Anexos do "Concluir e registrar" — estado próprio, separado do da aba
  // Atividades do drawer: são fluxos independentes e podem mirar negócios
  // diferentes (o da tarefa vs. o que porventura já estiver aberto no drawer).
  const [taskDetailAnexos, setTaskDetailAnexos] = useState([]);
  const taskDetailFileInputRef = React.useRef(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskType, setNewTaskType] = useState('Ligação');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const [showForecast, setShowForecast] = useState(false);
  const [filterStage, setFilterStage] = useState(null);
  const [filterFabricante, setFilterFabricante] = useState(null);
  const [showDealsList, setShowDealsList] = useState(false);
  const [dealsListStatus, setDealsListStatus] = useState('Todos');
  const [showEditNegocioDrawerModal, setShowEditNegocioDrawerModal] = useState(false);
  const [editNegocioDrawerForm, setEditNegocioDrawerForm] = useState({
    nome: '',
    estagio: 'Registro',
    tipo: 'Projeto',
    probabilidade: '50',
    descricao: ''
  });
  const [editNegocioDrawerRos, setEditNegocioDrawerRos] = useState({
    loading: false,
    error: null,
    rows: [],
  });
  const [savingEditNegocioDrawer, setSavingEditNegocioDrawer] = useState(false);
  const [hasTime, setHasTime] = useState(false);
  const [newTaskTime, setNewTaskTime] = useState('09:00');
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const [tasksCollapsed, setTasksCollapsed] = useState(false);
  const [drawerSection, setDrawerSection] = useState('propostas'); // 'propostas' | 'tarefas' | 'status' | 'empresa' | 'ros'
  // Aba «R.Os (N)» do detalhe da oportunidade: lista pelo ID EXATO da oportunidade (mesma API /api/ros, mesmos headers/autorização; R.O. excluída não vem).
  const [drawerRos, setDrawerRos] = useState({ negocioId: null, loading: false, error: null, cards: [] });
  const [drawerRosReload, setDrawerRosReload] = useState(0);
  const [empresaDoNegocio, setEmpresaDoNegocio] = useState(null);
  const [contatosDoNegocio, setContatosDoNegocio] = useState([]);
  const [loadingEmpresaDoNegocio, setLoadingEmpresaDoNegocio] = useState(false);
  const [atividades, setAtividades] = useState([]);
  const [loadingAtividades, setLoadingAtividades] = useState(false);
  const [novaAtividade, setNovaAtividade] = useState('');
  const [editingAtividade, setEditingAtividade] = useState(null);
  const [editingAtividadeTexto, setEditingAtividadeTexto] = useState('');
  const [savingAtividade, setSavingAtividade] = useState(false);
  const [atividadeBuscaAberta, setAtividadeBuscaAberta] = useState(false);
  const [atividadeBuscaQuery, setAtividadeBuscaQuery] = useState('');
  // Anexos da nova atividade: [{ id, file, status: 'uploading'|'done'|'error', result, error }]
  const [novaAtividadeAnexos, setNovaAtividadeAnexos] = useState([]);
  const novaAtividadeFileInputRef = React.useRef(null);
  const [lightboxAnexoUrl, setLightboxAnexoUrl] = useState(null);
  const [searchProposalQuery, setSearchProposalQuery] = useState('');
  const [proposalSearchResults, setProposalSearchResults] = useState([]);
  const [showProposalDropdown, setShowProposalDropdown] = useState(false);
  const [selectedProposalForTask, setSelectedProposalForTask] = useState(null);

  // Estado exclusivo da rota de Registros de Oportunidade (Task 2)
  const [rosState, setRosState] = useState({
    rows: [],
    filters: {},
    page: 1,
    total: 0,
    loading: false,
    error: null,
    selectedId: null,
  });
  const rosFetchIdRef = useRef(0);
  const [rosResumo, setRosResumo] = useState({
    total: 0,
    aguardando_aprovacao: 0,
    renovacoes_em_analise: 0,
    vencem_15_dias: 0,
    loading: false,
    error: null,
  });
  const [permissaoRos, setPermissaoRos] = useState({
    role: 'comum',
    pode_excluir: false,
    pode_restaurar: false,
    pode_editar: false,
    pode_excluir_cadeia: false,
  });
  const [fabricantesRo, setFabricantesRo] = useState([]);
  const fabricantesRoFetchIdRef = useRef(0);
  const [fabricantesRoStatus, setFabricantesRoStatus] = useState({
    loading: false,
    error: null,
    ready: false,
  });
  const [modalNovaRoAberto, setModalNovaRoAberto] = useState(false);
  const [modalNovaRoOportunidadeFixa, setModalNovaRoOportunidadeFixa] = useState(null);

  // Autenticação e Token do Usuário no ClickUp
  const [userClickUpToken, setUserClickUpToken] = useState(() => localStorage.getItem('crm_user_clickup_token') || '');
  const [userProfile, setUserProfile] = useState(() => {
    const cached = localStorage.getItem('crm_user_profile');
    return cached ? JSON.parse(cached) : null;
  });
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [inputToken, setInputToken] = useState('');
  const [validatingToken, setValidatingToken] = useState(false);

  // Tema (claro/escuro) — a chave de storage é escopada pelo usuário
  // (ver themeKeyFor/readStoredTheme no topo do arquivo), e o script
  // anti-flash no <head> do index.html resolve a mesma chave antes do 1º
  // paint pra não piscar tema errado. Preferência do sistema só serve de
  // valor inicial pra quem nunca escolheu; depois disso o botão manda.
  // Precisa ficar DEPOIS de `userProfile` — é dele que sai o id do usuário.
  const getInitialTheme = () => readStoredTheme(readProfileIdFromStorage()) || getSystemTheme();
  const [theme, setTheme] = useState(getInitialTheme);
  const prevThemeUidRef = useRef(userProfile?.id ? String(userProfile.id) : null);

  // Um único efeito de propósito: separar "persistir o tema" de "adotar o
  // tema do usuário que acabou de logar" em dois efeitos cria uma corrida —
  // o de persistir roda primeiro e grava o tema do usuário ANTERIOR na chave
  // do novo, antes do outro conseguir ler o que estava salvo.
  useEffect(() => {
    const uid = userProfile?.id ? String(userProfile.id) : null;

    if (prevThemeUidRef.current !== uid) {
      // Trocou de usuário (login, logout ou troca de conta).
      prevThemeUidRef.current = uid;
      // Ao sair (uid null) preserva o tema que está na tela, só reescrevendo
      // sob a chave 'anon' — assim o logout não pisca. Ao entrar, adota o
      // tema salvo daquela pessoa, ou o do sistema se ela nunca escolheu.
      const next = uid ? (readStoredTheme(uid) || getSystemTheme()) : theme;
      document.documentElement.classList.toggle('dark', next === 'dark');
      safeStorage.setItem(themeKeyFor(uid), next);
      if (next !== theme) setTheme(next);
      return;
    }

    document.documentElement.classList.toggle('dark', theme === 'dark');
    safeStorage.setItem(themeKeyFor(uid), theme);
  }, [theme, userProfile?.id]);

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  // Persiste os filtros da aba Tarefas por usuário — mesmo cuidado do tema:
  // um único efeito, pra "adotar os filtros de quem acabou de logar" não
  // correr contra "gravar os filtros" e salvar os da pessoa ANTERIOR na
  // chave da nova.
  const prevTasksFiltersUidRef = useRef(userProfile?.id ? String(userProfile.id) : null);
  useEffect(() => {
    const uid = userProfile?.id ? String(userProfile.id) : null;

    if (prevTasksFiltersUidRef.current !== uid) {
      prevTasksFiltersUidRef.current = uid;
      const saved = readTasksFilters(uid) || {};
      setTasksFilterAssignee(saved.assignee || 'all');
      setTasksPeriodFilter(saved.period || 'all');
      setTasksCustomStartDate(saved.customStart || '');
      setTasksCustomEndDate(saved.customEnd || '');
      setTasksShowCompleted(saved.showCompleted || false);
      return;
    }

    try {
      safeStorage.setItem(tasksFiltersKeyFor(uid), JSON.stringify({
        assignee: tasksFilterAssignee,
        period: tasksPeriodFilter,
        customStart: tasksCustomStartDate,
        customEnd: tasksCustomEndDate,
        showCompleted: tasksShowCompleted
      }));
    } catch (e) {}
  }, [tasksFilterAssignee, tasksPeriodFilter, tasksCustomStartDate, tasksCustomEndDate, tasksShowCompleted, userProfile?.id]);

  const validateAndSaveToken = async (tokenToTest) => {
    const cleanToken = tokenToTest ? tokenToTest.trim() : '';
    if (!cleanToken) {
      showToast('Informe um Personal Token do ClickUp válido (ex: pk_...)', 'error');
      return false;
    }
    setValidatingToken(true);
    try {
      const res = await fetch('/clickup-api/user', {
        headers: { 'Authorization': cleanToken }
      });
      if (res.ok) {
        const data = await res.json();
        const userObj = data.user || data;
        let perfilConfirmado = userObj;
        if (identidadeModoRef.current === 'canonico_v1') {
          const confirmed = await syncUserClickUpCredentialsToEdge(cleanToken, userObj, supabaseClient, config);
          perfilConfirmado = confirmed.perfil;
        }
        setUserProfile(perfilConfirmado);
        setUserClickUpToken(cleanToken);
        localStorage.setItem('crm_user_clickup_token', cleanToken);
        localStorage.setItem('crm_user_profile', JSON.stringify(perfilConfirmado));
        
        // Sincroniza o token de forma segura (criptografado) com as Edge Functions em segundo plano
        if (identidadeModoRef.current !== 'canonico_v1') syncUserClickUpCredentialsToEdge(cleanToken, userObj, supabaseClient, config);

        showToast(`Bem-vindo(a), ${perfilConfirmado.username || perfilConfirmado.email}! Autenticado com sucesso.`, 'success');
        setShowTokenModal(false);
        return true;
      } else {
        showToast('Token inválido ou expirado no ClickUp.', 'error');
        return false;
      }
    } catch (err) {
      console.error('Erro ao validar token:', err);
      showToast('Erro de conexão ao validar token no ClickUp.', 'error');
      return false;
    } finally {
      setValidatingToken(false);
    }
  };
  
  // Dashboard de Relatórios
  const [wonProposals, setWonProposals] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  // Separado de loadingDashboard (que só liga o spinner de tela cheia na
  // primeira carga): fica true durante QUALQUER refetch em segundo plano,
  // inclusive ao reentrar na aba com dados já em cache. Usado para segurar
  // a recriação dos gráficos até os dados frescos chegarem, evitando
  // desenhar com o cache antigo e logo em seguida redesenhar com o dado
  // novo (o "pisca duas vezes" reportado).
  const [dashboardFetching, setDashboardFetching] = useState(false);

  // Filtros de período e dados do Painel Comercial com persistência em localStorage
  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem('spa_selected_start') || `${new Date().getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    return localStorage.getItem('spa_selected_end') || `${new Date().getFullYear()}-12-31`;
  });
  const [commercialData, setCommercialData] = useState([]);
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Ref que mantém o período ativo sempre atualizado para o auto-polling
  const currentDateFilterRef = useRef({
    start: localStorage.getItem('spa_selected_start') || `${new Date().getFullYear()}-01-01`,
    end: localStorage.getItem('spa_selected_end') || `${new Date().getFullYear()}-12-31`,
    compStart: '',
    compEnd: ''
  });

  // Funções defensivas para compatibilidade Safari / WebKit
  const formatDateSafe = (dateStr, options = {}) => {
    if (!dateStr) return '';
    try {
      const cleanStr = String(dateStr).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
        const parts = cleanStr.split('-');
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString('pt-BR', options);
      }
      const d = new Date(cleanStr.includes(' ') && !cleanStr.includes('T') ? cleanStr.replace(' ', 'T') : cleanStr);
      if (isNaN(d.getTime())) return cleanStr;
      return Object.keys(options).length > 0 ? d.toLocaleDateString('pt-BR', options) : d.toLocaleString('pt-BR');
    } catch (e) {
      return String(dateStr);
    }
  };

  const formatDateMsToYMD = (msOrString) => {
    if (!msOrString) return '';
    try {
      let d;
      if (typeof msOrString === 'number' || (!isNaN(Number(msOrString)) && String(msOrString).trim() !== '')) {
        d = new Date(Number(msOrString));
      } else {
        const cleanStr = String(msOrString).trim();
        d = new Date(cleanStr.includes(' ') && !cleanStr.includes('T') ? cleanStr.replace(' ', 'T') : cleanStr);
      }
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  const getFirstNameSafe = (nameStr) => {
    if (!nameStr || typeof nameStr !== 'string') return 'Usuário';
    const trimmed = nameStr.trim();
    if (!trimmed) return 'Usuário';
    return trimmed.split(' ')[0] || 'Usuário';
  };

  const formatVersionDisplay = (v) => {
    if (!v) return 'vA';
    const str = String(v).trim();
    return str.startsWith('v') ? str : `v${str}`;
  };

  const [projectContext, setProjectContext] = useState({
    name: '',
    proposal_number: ''
  });
  const [clickupTaskDates, setClickupTaskDates] = useState({ start_date: '', due_date: '' });

  // Referências para elementos de gráfico e instâncias do Chart.js
  const distributorCanvasRef = useRef(null);
  const manufacturerCanvasRef = useRef(null);
  const topProductsCanvasRef = useRef(null);
  const seasonalityCanvasRef = useRef(null);

  const distributorChartInst = useRef(null);
  const manufacturerChartInst = useRef(null);
  const topProductsChartInst = useRef(null);
  const seasonalityChartInst = useRef(null);

  const [topProductsFilterMode, setTopProductsFilterMode] = useState('value'); // 'value' | 'qty'
  
  // Contexto do ClickUp
  // Estados do Negócio/Propostas
  const [propostas, setPropostas] = useState([]);
  const [todasPropostas, setTodasPropostas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [distribuidores, setDistribuidores] = useState([]);
  const [distribuidoresErro, setDistribuidoresErro] = useState(false);   // falha de carregamento ≠ «nenhum cadastrado»
  const [fabricantes, setFabricantes] = useState([]);
  const [vendedores, setVendedores] = useState(() => {
    const cached = localStorage.getItem('crm_cache_vendedores');
    return cached ? JSON.parse(cached) : [];
  });
  const [taskTypes, setTaskTypes] = useState(() => {
    const cached = localStorage.getItem('crm_cache_task_types');
    return cached ? JSON.parse(cached) : [
      { id: '1', nome: 'Ligação', emoji: '📞' },
      { id: '2', nome: 'Reunião', emoji: '👥' },
      { id: '3', nome: 'E-mail', emoji: '📧' },
      { id: '4', nome: 'Follow-up', emoji: '🔄' }
    ];
  });
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [newTaskTypeEmoji, setNewTaskTypeEmoji] = useState('');
  const [vendedoresOcultos, setVendedoresOcultos] = useState(() => {
    const cached = localStorage.getItem('crm_vendedores_ocultos');
    return cached ? JSON.parse(cached) : [];
  });
  const vendedoresVisiveis = useMemo(() => {
    return vendedores.filter(v => !v.oculto);
  }, [vendedores]);
  const [currentProposta, setCurrentProposta] = useState(null);
  const [itens, setItens] = useState([]);
  // Ref sempre atualizada com o valor mais recente de `itens`, para ser lida dentro do
  // setInterval de auto-polling (useEffect com deps [session, dbConnected, clickupTaskId,
  // supabaseClient]): esse efeito não é recriado enquanto o usuário digita itens em uma
  // mesma proposta, então a closure do intervalo ficaria presa no `itens` de quando o
  // efeito rodou pela última vez (array vazio) em vez do valor atual com itens não salvos.
  const itensRef = useRef([]);
  useEffect(() => {
    itensRef.current = itens;
  }, [itens]);
  // Mesma proteção do itensRef acima, mas para os campos da proposta editados
  // direto no formulário (Data de Fechamento, Data de Início, Tipo de Projeto,
  // Vendedor/Responsável): sem isso, o polling silencioso de 3 em 3 minutos
  // (fetchAllData) sobrescrevia uma edição em andamento assim que o usuário
  // digitava, antes de clicar em Salvar — o campo "salvava" vazio porque
  // currentProposta.data_fechamento já tinha voltado a null quando o clique
  // no Salvar disparava.
  const propostaDirtyRef = useRef(false);

  // Edição no Painel de Gestão (Produtos e Distribuidores/Fabricantes)
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [newDistributorName, setNewDistributorName] = useState('');
  const [fabricantesAdmin, setFabricantesAdmin] = useState([]);
  const [loadingFabricantes, setLoadingFabricantes] = useState(false);
  const [catalogoPermissao, setCatalogoPermissao] = useState({
    role: null,
    pode_administrar_produtos: false,
    pode_administrar_fabricantes: false,
    pode_incluir_inativos: false
  });
  const [settingsActiveTab, setSettingsActiveTab] = useState('products'); // 'products' | 'distributors' | 'venders'
  const [showCloseModal, setShowCloseModal] = useState(false); // 'win' | 'loss' | false
  const [closeDate, setCloseDate] = useState('');
  const [selectedLossReason, setSelectedLossReason] = useState('');
  const [compareStartDate, setCompareStartDate] = useState('');
  const [compareEndDate, setCompareEndDate] = useState('');
  const [selectedDistributorFilter, setSelectedDistributorFilter] = useState('all');
  const [selectedManufacturerFilter, setSelectedManufacturerFilter] = useState('all');
  const [biMetrics, setBiMetrics] = useState({
    wonCount: 0, 
    wonValue: 0, 
    avgCycleDays: 0,
    ticketMedio: 0,
    wonQtyDiff: null, 
    wonValDiff: null, 
    avgCycleDaysDiff: null,
    ticketMedioDiff: null,
    lostCount: 0, 
    lostValue: 0, 
    lostQtyDiff: null, 
    lostValDiff: null,
    convRate: 0, 
    convRateDiff: null,
    seasonalityLabels: [],
    seasonalityValues: []
  });
  const [openMenuVersionId, setOpenMenuVersionId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isEditingProposal, setIsEditingProposal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({ nome: '', fabricante: '', custo_referencia: '' });
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [warningMsg, setWarningMsg] = useState('');
  const [showNovaOportunidadeKanban, setShowNovaOportunidadeKanban] = useState(false);
  const [contasParaBusca, setContasParaBusca] = useState([]);

  const [configNumeracao, setConfigNumeracao] = useState({ ultimo_numero: 13202, ativo: true });

  const carregarConfigNumeracao = async () => {
    if (!supabaseClient) return;
    const { data } = await supabaseClient.from('config_numeracao_propostas').select('ultimo_numero, ativo').eq('id', 1).single();
    if (data) setConfigNumeracao(data);
  };

  useEffect(() => {
    if (showNovaOportunidadeKanban && supabaseClient && contasParaBusca.length === 0) {
      supabaseClient.from('contas').select('id, nome, cnpj').order('nome').then(({ data }) => {
        if (data) setContasParaBusca(data);
      });
    }
  }, [showNovaOportunidadeKanban, supabaseClient, contasParaBusca.length]);

  useEffect(() => {
    if (showSettingsModal && settingsActiveTab === 'numeracao') {
      carregarConfigNumeracao();
    }
  }, [showSettingsModal, settingsActiveTab, supabaseClient]);

  // Listener global de teclado para tecla ESC. Fase 2: em vez de uma cadeia
  // if/else escrita à mão (que tinha que ser mantida em sincronia manual com
  // quais modais existiam — o de Editar Oportunidade nunca entrou nela),
  // fecha a camada do TOPO da pilha (`modalLayers`, populada por
  // useModalLayer em cada modal). O menu de 3 pontinhos é a única exceção:
  // é um dropdown em portal, não um modal registrado na pilha, mas fica
  // visualmente por cima de tudo — por isso é conferido primeiro.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (openMenuVersionId !== null) {
        setOpenMenuVersionId(null);
        return;
      }
      const top = modalLayers[modalLayers.length - 1];
      if (top) top.close();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openMenuVersionId]);

  // Listener para fechar o menu dos 3 pontinhos ao rolar a página ou container
  useEffect(() => {
    if (openMenuVersionId !== null) {
      const handleScroll = () => setOpenMenuVersionId(null);
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [openMenuVersionId]);

  // Exposição global da função de controle do menu da versão para event delegation / fallback
  useEffect(() => {
    window.openVersionPortalMenu = (buttonElement, versionId) => {
      if (!buttonElement) return;
      const rect = buttonElement.getBoundingClientRect();
      const topPos = rect.bottom + 4;
      const leftPos = Math.max(10, rect.right - 180);
      const finalTop = (topPos + 100 > window.innerHeight) ? Math.max(10, rect.top - 80) : topPos;
      setMenuPosition({ top: finalTop, left: leftPos });
      setOpenMenuVersionId(versionId);
    };
    return () => {
      delete window.openVersionPortalMenu;
    };
  }, []);

  const saveTimeoutRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [kanbanSearchTerm, setKanbanSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Filtro por responsável embutido na própria busca do Kanban — em vez
  // de um controle novo na tela, a mesma caixa "Buscar negócio por
  // nome..." sugere um responsável quando o texto digitado bate com um
  // vendedor, e vira um "chip" de filtro ativo ao selecionar (ver o JSX
  // da lupa expansível do Kanban). Mutuamente exclusivo com busca por texto.
  const [kanbanFilterResponsavelId, setKanbanFilterResponsavelId] = useState(null);
  const [kanbanFilterResponsavelNome, setKanbanFilterResponsavelNome] = useState('');

  // Busca global do cabeçalho — pesquisa Empresas/Contatos/Negócios
  // direto no Supabase (não filtra dado já carregado como as outras
  // buscas do app, por isso o debounce) e, ao selecionar um resultado,
  // navega até ele (ver handleGlobalSearchSelect mais abaixo).
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState({ contas: [], contatos: [], negocios: [] });
  const [contaParaAbrir, setContaParaAbrir] = useState(null);
  const [abaContaParaAbrir, setAbaContaParaAbrir] = useState('visao_geral');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState('');
  
  // UX/UIs
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cálculos consolidados para os gráficos da aba de relatórios (Todos os itens de propostas GANHAS/SELECIONADAS)
  const distributorTotalsStableRef = useRef(null);
  const { distributorTotals, distributorTotalSum } = useMemo(() => {
    const totals = {};
    const wonItems = (commercialData || []).filter(item => {
      const prop = Array.isArray(item.propostas) ? item.propostas[0] : item.propostas;
      const sit = prop?.situacao;
      if (!sit || !prop?.data_fechamento) return false;
      const s = sit.trim().toLowerCase();
      if (s !== 'ganho' && s !== 'selecionada') return false;
      const cid = String(prop?.clickup_negocio_id || '').replace('#', '').trim();
      return cid && negociosEstagioRef.current.get(cid) === 'Ganho';
    });
    const itemsToProcess = wonItems.length > 0 ? wonItems : (commercialData || []);

    itemsToProcess.forEach(item => {
      const value = (parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0);
      const distObj = Array.isArray(item.distribuidores) ? item.distribuidores[0] : item.distribuidores;
      const distName = (distObj?.nome || 'NÃO INFORMADO').trim().toUpperCase();

      if (selectedDistributorFilter === 'all' || distName.toLowerCase() === selectedDistributorFilter.trim().toLowerCase()) {
        totals[distName] = (totals[distName] || 0) + value;
      }
    });
    
    const sortedTotals = {};
    Object.keys(totals)
      .sort((a, b) => totals[b] - totals[a])
      .forEach(key => {
        sortedTotals[key] = totals[key];
      });

    const sum = Object.values(sortedTotals).reduce((a, b) => a + b, 0);
    return stabilizeByValue(distributorTotalsStableRef, { distributorTotals: sortedTotals, distributorTotalSum: sum });
  }, [commercialData, selectedDistributorFilter]);

  const manufacturerTotalsStableRef = useRef(null);
  const { manufacturerTotals, manufacturerTotalSum } = useMemo(() => {
    const totals = {};
    const wonItems = (commercialData || []).filter(item => {
      const prop = Array.isArray(item.propostas) ? item.propostas[0] : item.propostas;
      const sit = prop?.situacao;
      if (!sit || !prop?.data_fechamento) return false;
      const s = sit.trim().toLowerCase();
      if (s !== 'ganho' && s !== 'selecionada') return false;
      const cid = String(prop?.clickup_negocio_id || '').replace('#', '').trim();
      return cid && negociosEstagioRef.current.get(cid) === 'Ganho';
    });
    const itemsToProcess = wonItems.length > 0 ? wonItems : (commercialData || []);

    itemsToProcess.forEach(item => {
      const value = (parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0);
      const prodObj = Array.isArray(item.produtos) ? item.produtos[0] : item.produtos;
      const fabName = (prodObj?.fabricante || '').trim().toUpperCase();
      if (!fabName) return;

      if (selectedManufacturerFilter === 'all' || fabName.toLowerCase() === selectedManufacturerFilter.trim().toLowerCase()) {
        totals[fabName] = (totals[fabName] || 0) + value;
      }
    });

    const sortedTotals = {};
    Object.keys(totals)
      .sort((a, b) => totals[b] - totals[a])
      .forEach(key => {
        sortedTotals[key] = totals[key];
      });

    const sum = Object.values(sortedTotals).reduce((a, b) => a + b, 0);
    return stabilizeByValue(manufacturerTotalsStableRef, { manufacturerTotals: sortedTotals, manufacturerTotalSum: sum });
  }, [commercialData, selectedManufacturerFilter]);

  // 1. Carregar Config do Servidor e Inicializar Cliente Supabase
  useEffect(() => {
    const initSupabase = async () => {
      try {
        let url = '';
        let anonKey = '';
        let identityMode;
        let kanbanMode;
        try {
          const response = await fetch('/api/config');
          if (response.ok) {
            const data = await response.json();
            url = data.SUPABASE_URL;
            anonKey = data.SUPABASE_ANON_KEY;
            identityMode = data.CRM_IDENTITY_MODE === undefined ? 'legacy' : data.CRM_IDENTITY_MODE;
            kanbanMode = data.CRM_KANBAN_MODE === undefined ? 'legacy' : data.CRM_KANBAN_MODE;
            if (!['legacy', 'canonico_v1'].includes(kanbanMode) || (kanbanMode === 'canonico_v1' && identityMode !== 'canonico_v1')) throw new Error('Configuração de Kanban inválida.');
            if (!['legacy', 'canonico_v1'].includes(identityMode)) throw new Error('Configuração de identidade inválida.');
          }
        } catch (fetchErr) {
          throw new Error('Configuração de identidade indisponível.');
        }
        
        if (!identityMode || (identityMode === 'canonico_v1' && (!url || !anonKey))) throw new Error('Configuração de identidade indisponível.');
        identidadeModoRef.current = identityMode;
        kanbanModoRef.current = kanbanMode;
        // Fallback de produção do Supabase Suprimática
        if (!url || !anonKey) {
          url = 'https://supabase.llworkflow.com.br';
          anonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzgzNTMyMjQ5LCJleHAiOjIwOTg4OTIyNDl9.DYJrIfSr6jdrn-xhmc9q_wGtfdRUYrYwP2UvkpvGLl0';
        }
        
        if (url && anonKey) {
          const client = window.supabase.createClient(url, anonKey);
          if (!window.CrmUsuarios) throw new Error('Módulo de usuários indisponível.');
          window.CrmUsuarios.configure({mode:identityMode,client});
          setSupabaseClient(client);
          setConfig({ url, anonKey, identity_mode: identityMode, kanban_mode: kanbanMode });
          
          // Limpa safeStorage das chaves antigas por segurança
          safeStorage.removeItem('supa_url');
          safeStorage.removeItem('supa_key');
          safeStorage.removeItem('supabase_url');
          safeStorage.removeItem('supabase_key');
          safeStorage.removeItem('supabaseurl');
          safeStorage.removeItem('supabasekey');
          
          testConnection(client);
        } else {
          console.error("Configurações do Supabase ausentes.");
          setErrorMsg("Configurações do Supabase ausentes.");
        }
      } catch (err) {
        console.error("Erro ao inicializar Supabase:", err);
        setDbConnected(false);
        setErrorMsg("Erro ao inicializar conexão com o Supabase.");
      }
    };
    initSupabase();
  }, []);

  const confirmarSessaoCanonica = async (newSession, client = supabaseClient) => {
    const gate = gateIdentidade();
    if (identidadeBloqueadaRef.current) { gate.invalidar(); setSession(null); setValidandoAcessoCrm(false); return; }
    if (!newSession) { gate.invalidar(); setSession(null); setValidandoAcessoCrm(false); return; }
    setSession(null);
    setValidandoAcessoCrm(true);
    try {
      const result = await gate.confirmar({ session: newSession });
      if (!result || !gate.corrente(result)) return;
      setSession(result.session);
      setUserProfile(perfilParaExibicao(result.perfil, safeStorage.getItem('crm_user_profile')));
      setErrorMsg('');
      loadProducts(client);
      loadCatalogoPermissao().then(perm => { loadDistributors(client); loadFabricantes(perm); });
      loadVendedores(client);
      loadTiposTarefa(client);
      loadSegmentosCache(client);
    } catch { setSession(null); setErrorMsg('Não foi possível confirmar seu acesso ao CRM. Entre novamente ou tente mais tarde.'); }
    finally { setValidandoAcessoCrm(gate.emValidacao()); }
  };

  // Escuta autenticação
  useEffect(() => {
    if (!supabaseClient) return;
    
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(async (event, newSession) => {
      if (identidadeModoRef.current === 'canonico_v1') {
        if (!newSession) {
          if (event === 'SIGNED_OUT') identidadeBloqueadaRef.current = true;
          gateIdentidade().invalidar(); setSession(null); setValidandoAcessoCrm(false); return;
        }
        if (!identidadeLoginRef.current && !identidadeBloqueadaRef.current) {
          gateIdentidade().invalidar(); setSession(null); setValidandoAcessoCrm(true);
          setTimeout(() => { void confirmarSessaoCanonica(newSession); }, 0);
        }
        return;
      }
      const savedToken = safeStorage.getItem('crm_user_clickup_token');
      if (newSession && savedToken) {
        setSession(newSession);
        loadProducts(supabaseClient);
        loadCatalogoPermissao().then(perm => { loadDistributors(supabaseClient); loadFabricantes(perm); });
        loadVendedores(supabaseClient);
        loadTiposTarefa(supabaseClient);
        loadSegmentosCache(supabaseClient);
      } else {
        if (newSession) {
          await supabaseClient.auth.signOut();
        }
        setSession(null);
      }
    });

    return () => { subscription.unsubscribe(); identidadeGateRef.current?.invalidar(); };
  }, [supabaseClient]);

  const handleLogin = async (email, password, clickupToken) => {
    if (identidadeModoRef.current === 'pending') return { error: { message: 'Configuração de acesso ainda indisponível.' } };
    if (identidadeModoRef.current === 'canonico_v1') {
      const gate = gateIdentidade();
      const loginCycle = identidadeLoginRef.current + 1;
      identidadeLoginRef.current = loginCycle;
      identidadeBloqueadaRef.current = false;
      setSession(null); setValidandoAcessoCrm(true);
      try {
        const result = await gate.login({ client: supabaseClient, email, password, token: clickupToken });
        if (!result || !gate.corrente(result)) return { error: { message: 'Validação de acesso cancelada.' } };
        safeStorage.setItem('crm_user_clickup_token', clickupToken.trim());
        safeStorage.setItem('crm_user_profile', JSON.stringify(result.perfil));
        setUserClickUpToken(clickupToken.trim()); setUserProfile(result.perfil);
        setSession(result.session); setErrorMsg('');
        loadProducts(supabaseClient);
        loadCatalogoPermissao().then(perm => { loadDistributors(supabaseClient); loadFabricantes(perm); });
        loadVendedores(supabaseClient); loadTiposTarefa(supabaseClient); loadSegmentosCache(supabaseClient);
        return { session: result.session };
      } catch {
        identidadeBloqueadaRef.current = true;
        gate.invalidar(); setSession(null);
        safeStorage.removeItem('crm_user_clickup_token'); safeStorage.removeItem('crm_user_profile');
        setUserClickUpToken(''); setUserProfile(null);
        setErrorMsg('Não foi possível confirmar seu acesso ao CRM. Tente novamente.');
        try { await supabaseClient.auth.signOut(); } catch { /* sem erro bruto */ }
        return { error: { message: 'Não foi possível confirmar seu acesso ao CRM. Tente novamente.' } };
      } finally { if (identidadeLoginRef.current === loginCycle) identidadeLoginRef.current = 0; setValidandoAcessoCrm(gate.emValidacao()); }
    }
    if (!supabaseClient) return { error: { message: "Cliente Supabase não inicializado." } };
    if (!clickupToken || !clickupToken.trim()) {
      return { error: { message: "O Personal API Token do ClickUp é obrigatório para acessar o sistema." } };
    }
    const cleanToken = clickupToken.trim();
    try {
      // 1. Validar o token do ClickUp primeiro
      const userRes = await fetch('/clickup-api/user', {
        headers: { 'Authorization': cleanToken }
      });
      if (!userRes.ok) {
        return { error: { message: "Token do ClickUp inválido ou expirado. Verifique e tente novamente." } };
      }
      const userData = await userRes.json();
      const userObj = userData.user || userData;

      // 2. Salvar no safeStorage e atualizar estados ANTES de chamar signInWithPassword.
      // Isso evita que o listener onAuthStateChange do Supabase seja disparado antes de encontrar o token salvo!
      setUserProfile(userObj);
      safeStorage.setItem('crm_user_clickup_token', cleanToken);
      safeStorage.setItem('crm_user_profile', JSON.stringify(userObj));

      // 3. Tentar o login no Supabase
      const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password });
      if (error) {
        // Em caso de senha ou e-mail incorreto no Supabase, desfaz o salvamento do token
        localStorage.removeItem('crm_user_clickup_token');
        localStorage.removeItem('crm_user_profile');
        setUserClickUpToken('');
        setUserProfile(null);
        return { error };
      }

      setSession(data.session);

      // Sincroniza o token de forma segura (criptografado) com as Edge Functions
      syncUserClickUpCredentialsToEdge(cleanToken, userObj, supabaseClient, config);

      return data;
    } catch (err) {
      console.error("Erro no processo de login/validação do ClickUp:", err);
      // Limpeza em caso de exceção de rede
      localStorage.removeItem('crm_user_clickup_token');
      localStorage.removeItem('crm_user_profile');
      setUserClickUpToken('');
      setUserProfile(null);
      return { error: { message: "Erro de conexão ao validar o Token no ClickUp." } };
    }
  };

  // Testar conexão buscando tabela auxiliar
  const testConnection = async (client) => {
    try {
      const { data, error } = await client.from('tipos_tarefa').select('id').limit(1);
      if (error) throw error;
      setDbConnected(true);
      setErrorMsg('');
      
      const { data: { session } } = await client.auth.getSession();
      const savedToken = safeStorage.getItem('crm_user_clickup_token');
      if (identidadeModoRef.current === 'canonico_v1') {
        if (!identidadeLoginRef.current && !identidadeBloqueadaRef.current) await confirmarSessaoCanonica(session, client);
        return;
      }
      if (session && savedToken) {
        setSession(session);
        loadProducts();
        loadCatalogoPermissao().then(perm => { loadDistributors(client); loadFabricantes(perm); });
        loadVendedores(client);
        loadTiposTarefa(client);
        loadSegmentosCache(client);
      } else {
        if (session) {
          await client.auth.signOut();
        }
        setSession(null);
      }
    } catch (err) {
      console.error("Erro de conexão com o banco:", err);
      setDbConnected(false);
      setErrorMsg('Falha ao conectar ao Supabase. Verifique suas credenciais.');
    }
  };

  // Funções do Kanban
  // `task.estagio` é o nome do estágio direto da tabela `negocios` do Supabase
  // (não mais um custom field do ClickUp) — só resolvemos o "id" da coluna
  // (kanbanColumns) pra manter compatibilidade com todo o código existente
  // que casa cards com colunas por id.
  const getTaskOptionId = (task, options) => {
    if (!task || !task.estagio) return null;
    const safeOpts = (options && options.length > 0) ? options : ESTAGIO_OPTIONS;
    const opt = safeOpts.find(o => (o.name || '').toLowerCase().trim() === String(task.estagio || '').toLowerCase().trim());
    return opt ? opt.id : null;
  };

  // Único lugar que decide "qual proposta representa o valor do negócio hoje"
  // quando nenhuma foi Selecionada/Ganho — usado por getOpportunityValue e por
  // fetchKanbanData (antes duplicado em 3 pontos, risco real de divergência).
  // Ordem: uma decisão real do cliente (Selecionada/Ganho) sempre vence; abaixo
  // dela, a referência de forecast marcada manualmente vence sobre cair
  // cegamente na proposta mais recente — mas só enquanto a proposta ainda está
  // 'Ativa' (assim a flag nunca precisa ser limpa explicitamente quando a
  // proposta muda de situação: ou uma camada acima já venceu, ou o guard
  // 'Ativa' a exclui).
  const resolveBestProposta = (props) => {
    if (!props || props.length === 0) return null;
    return (
      props.find(p => p.situacao === 'Selecionada') ||
      props.find(p => p.situacao === 'Ganho') ||
      props.find(p => p.referencia_forecast === true && p.situacao === 'Ativa') ||
      props.find(p => p.situacao === 'Ativa') ||
      props.find(p => p.situacao === 'Desconsiderada') ||
      props[0]
    );
  };

  const getOpportunityValue = (task) => {
    if (!task) return null;

    // 1. PRIORIDADE MÁXIMA: Valor enriquecido em memória do Supabase (trata as chaves de ID normalizadas)
    if (task.supabase_deal_value !== undefined && task.supabase_deal_value !== null) {
      const val = parseFloat(task.supabase_deal_value);
      if (!isNaN(val)) return val;
    }

    const cleanId = String(task.id || '').replace('#', '').trim();

    // Fallback secundário: busca direta em supabaseProposalsList por ID
    if (supabaseProposalsList && supabaseProposalsList.length > 0) {
      const props = supabaseProposalsList.filter(p => {
        const pClean = String(p.clickup_negocio_id || '').replace('#', '').trim();
        return pClean === cleanId;
      });
      if (props.length > 0) {
        const best = resolveBestProposta(props);
        const val = parseFloat(best.total_proposta);
        if (!isNaN(val)) return val;
      }
    }
    
    // 2. Fallback: valor_estimado injetado no card
    if (task.valor_estimado !== undefined && task.valor_estimado !== null) {
      const ve = parseFloat(task.valor_estimado);
      if (!isNaN(ve)) return ve;
    }

    // 3. Fallback: valor_clickup_fallback (negocios.valor_clickup_fallback — espelho
    // do campo "Valor do negócio" do ClickUp, só usado quando o negócio ainda não
    // tem nenhuma proposta no Supabase)
    if (task.valor_clickup_fallback !== undefined && task.valor_clickup_fallback !== null) {
      const raw = parseFloat(task.valor_clickup_fallback);
      if (!isNaN(raw)) {
        return raw;
      }
    }

    return null;
  };

  // Valor do negócio restrito a um fabricante (usado pelo filtro do Forecast) — sem
  // fabricante selecionado, cai no valor cheio da proposta (getOpportunityValue).
  const getOpportunityValueForFabricante = (task, fabricante) => {
    if (!fabricante) return getOpportunityValue(task);
    return task?.valorPorFabricante?.[fabricante] || 0;
  };

  const getOpportunityResponsavel = (task) => {
    if (!task || !supabaseProposalsList) return '';
    const cleanId = String(task.id).replace('#', '').trim();
    const props = supabaseProposalsList.filter(p => {
      const pClean = String(p.clickup_negocio_id).replace('#', '').trim();
      return pClean === cleanId;
    });
    if (props.length > 0) {
      const selectedProp = props.find(p => p.situacao === 'Selecionada' || p.situacao === 'Ganho') || props[0];
      return selectedProp.criado_por || '';
    }
    return '';
  };

  const refreshSupabaseProposalsList = async () => {
    if (!supabaseClient) return;
    try {
      const { data } = await supabaseClient
        .from('propostas')
        .select('clickup_negocio_id, total_proposta, situacao, criado_por, referencia_forecast');
      if (data) {
        setSupabaseProposalsList(data);
      }
    } catch (err) {
      console.warn("Erro silencioso ao atualizar lista global de propostas:", err);
    }
  };

  const handleResponsavelChange = async (taskId, responsavelNome, responsavelId = null) => {
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // ONE command: the server derives the owner's name, rewrites the proposals' author, provisions the first version when there is none and queues the ClickUp assignee change.
      try {
        const alvo = kanbanTasks.find(t => t.id === taskId);
        const crmId = alvo?.crm_negocio_id || comercialNegocioId(taskId);
        const r = await comercialExecutar('negocio_responsavel', crmId || 'sem-id', { negocio_id: crmId, expected_version: alvo?.versao_estagio, responsavel_clickup_id: responsavelId ? String(responsavelId) : null });
        const patch = { responsavel_negocio: r.responsavel_nome, responsavel_nome: r.responsavel_nome, responsavel_clickup_id: r.responsavel_clickup_id, versao_estagio: r.versao };
        setKanbanTasks(prev => prev.map(t => (t.id === taskId ? { ...t, ...patch } : t)));
        if (selectedTask && selectedTask.id === taskId) setSelectedTask(prev => ({ ...prev, ...patch }));
        if (r.sync_status === 'pendente') showToast('Responsável atualizado no CRM. Sincronização ClickUp pendente.', 'success');
        await refreshSupabaseProposalsList();
        loadDashboardData();
      } catch (err) {
        showToast(comercialMensagem(err, 'Não foi possível atualizar o responsável.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO') fetchKanbanData();
      }
      return;
    }
    /* B2_I13_END */
    // Responsável anterior (pra poder "remover" no ClickUp — a API de lá é
    // incremental, add/rem, não substitui a lista de assignees de uma vez)
    // — precisa capturar ANTES da atualização otimista abaixo sobrescrever.
    const tarefaAtual = kanbanTasks.find(t => t.id === taskId);
    const responsavelIdAnterior = tarefaAtual?.responsavel_clickup_id ? String(tarefaAtual.responsavel_clickup_id) : null;

    // 1. Interface Otimista: Mudar na tela imediatamente preservando o valor estimado
    setKanbanTasks(prevTasks => prevTasks.map(t => t.id === taskId ? { ...t, responsavel_negocio: responsavelNome, valor_estimado: t.valor_estimado } : t));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(prev => ({ ...prev, responsavel_negocio: responsavelNome, valor_estimado: prev.valor_estimado }));
    }

    const cleanId = String(taskId).replace('#', '').trim();

    // 2. Sincronização com ClickUp via Assignees nativos — antes chamava
    // POST /task/{id}/assignee com {assignees: [id]}, que não é um endpoint
    // real da API do ClickUp (a atualização de assignee em tarefa existente
    // é PUT /task/{id} com {assignees: {add, rem}} — só CRIAÇÃO de tarefa
    // aceita um array direto, ver sync-negocio-clickup). Isso fazia a
    // chamada falhar (404) silenciosamente — só um console.warn, nada
    // avisava o usuário — e o Supabase/CRM ficavam atualizados enquanto o
    // ClickUp continuava com o responsável antigo (ou sem nenhum).
    const novoIdStr = responsavelId ? String(responsavelId) : null;
    if (novoIdStr !== responsavelIdAnterior) {
      try {
        const add = novoIdStr ? [Number(novoIdStr)] : [];
        const rem = (responsavelIdAnterior && responsavelIdAnterior !== novoIdStr) ? [Number(responsavelIdAnterior)] : [];
        if (add.length > 0 || rem.length > 0) {
          const res = await fetch(`/clickup-api/task/${taskId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
            body: JSON.stringify({ assignees: { add, rem } })
          });
          if (!res.ok) throw new Error(`ClickUp respondeu ${res.status}`);
        }
      } catch (e) {
        console.warn("Erro ao atualizar responsável no ClickUp:", e);
        showToast('Responsável atualizado no CRM, mas falhou ao sincronizar com o ClickUp. Confira lá manualmente.', 'warning');
      }
    }

    // 3. Atualizar no Supabase
    try {
      const { data, error } = await supabaseClient
        .from('propostas')
        .update({ criado_por: responsavelNome })
        .eq('clickup_negocio_id', cleanId)
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        await supabaseClient
          .from('propostas')
          .insert({
            clickup_negocio_id: cleanId,
            versao: 'vA',
            situacao: 'Selecionada',
            criado_por: responsavelNome,
            criado_por_user_id: userProfile?.id ? String(userProfile.id) : null,
            cenario: '',
            total_proposta: 0
          });
      }

      // Mantém negocios.responsavel_nome/responsavel_clickup_id em sincronia —
      // é a fonte que fetchKanbanData prioriza (ver migration 20260819g).
      try {
        await supabaseClient
          .from('negocios')
          .update({
            responsavel_nome: responsavelNome || null,
            responsavel_clickup_id: responsavelId ? String(responsavelId) : null,
          })
          .eq('clickup_negocio_id', cleanId);
      } catch (negErr) {
        console.warn("Erro silencioso ao sincronizar responsável em negocios:", negErr);
      }

      await refreshSupabaseProposalsList();
      loadDashboardData();
    } catch (err) {
      console.warn("Erro silencioso ao persistir responsável no Supabase:", err);
    }
  };

  const fetchKanbanData = async (silent = false) => {
    if (kanbanTasks.length === 0 && !silent) {
      setLoadingKanban(true);
    }
    // Só marca "acabou de buscar" quando existe cliente de verdade — no
    // primeiro render supabaseClient ainda é null (conexão ainda not resolvida)
    // e essa função roda mesmo assim sem buscar nada; marcar o timestamp nesse
    // caso fazia o TTL do useEffect de troca de aba (abaixo) achar que já tinha
    // buscado recentemente e bloquear a busca de verdade assim que o cliente
    // ficasse pronto, deixando o Kanban vazio até o usuário trocar de aba.
    if (supabaseClient) {
      lastKanbanFetchAtRef.current = Date.now();
    }
    try {
      // Negócios, propostas e itens_proposta — tudo direto do Supabase agora.
      // O Kanban não faz mais nenhuma chamada ao ClickUp pra se popular (a
      // SPA é a fonte de verdade; ver docs/resumo.md, tabela `negocios`).
      const negociosPromise = supabaseClient
        ? supabaseClient.from('negocios').select('id, clickup_negocio_id, nome, estagio, valor_clickup_fallback, conta_id, responsavel_nome, responsavel_clickup_id, tipo_oportunidade, data_previsao, probabilidade, descricao' + (kanbanModoRef.current === 'canonico_v1' ? ', versao_estagio' : ''))
        : Promise.resolve({ data: [], error: null });

      const propsPromise = supabaseClient
        ? supabaseClient.from('propostas').select('id, clickup_negocio_id, total_proposta, situacao, criado_por, data_fechamento, motivo_perda, referencia_forecast')
        : Promise.resolve({ data: [], error: null });

      const itensPromise = supabaseClient
        ? supabaseClient.from('itens_proposta').select('proposta_id, quantidade, preco_unitario, produtos(fabricante)')
        : Promise.resolve({ data: [], error: null });

      const [{ data: negociosData, error: negociosErr }, { data: props, error: propsErr }, { data: itensData, error: itensErr }] = await Promise.all([
        negociosPromise,
        propsPromise,
        itensPromise,
      ]);

      if (negociosErr) {
        console.error("Erro ao carregar negócios do Supabase:", negociosErr);
      }

      setKanbanColumns(ESTAGIO_OPTIONS);

      // Índice proposta_id -> lista de fabricantes distintos dos itens da proposta,
      // e proposta_id -> valor somado (quantidade * preco_unitario) por fabricante
      // — usado pelo filtro de fabricante do Forecast pra não contar o negócio
      // inteiro quando ele mistura vários fabricantes (ver docs/resumo.md).
      const fabricantesByPropId = new Map();
      const valorPorFabricantePropId = new Map();
      if (!itensErr && itensData) {
        for (const item of itensData) {
          const prodObj = Array.isArray(item.produtos) ? item.produtos[0] : item.produtos;
          const fab = (prodObj?.fabricante || '').trim();
          if (!fab || !item.proposta_id) continue;
          if (!fabricantesByPropId.has(item.proposta_id)) fabricantesByPropId.set(item.proposta_id, new Set());
          fabricantesByPropId.get(item.proposta_id).add(fab);

          const itemValor = (parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0);
          if (!valorPorFabricantePropId.has(item.proposta_id)) valorPorFabricantePropId.set(item.proposta_id, {});
          const fabTotals = valorPorFabricantePropId.get(item.proposta_id);
          fabTotals[fab] = (fabTotals[fab] || 0) + itemValor;
        }
      }

      let propsList = [];
      if (!propsErr && props) {
        propsList = props;
        setSupabaseProposalsList(props);
      }

      // Índice das propostas por clickup_negocio_id (uma vez, O(m)) em vez de
      // filtrar a lista inteira de propostas para cada um dos negócios (O(n*m)).
      const propsByClickupId = new Map();
      for (const p of propsList) {
        const pClean = String(p.clickup_negocio_id || '').replace('#', '').trim();
        if (!pClean) continue;
        if (!propsByClickupId.has(pClean)) propsByClickupId.set(pClean, []);
        propsByClickupId.get(pClean).push(p);
      }

      // Enriquecer negócios com responsável e valor da proposta do Supabase
      const enrichedTasks = (negociosData || []).filter(n => n.clickup_negocio_id).map(n => {
        const idClean = String(n.clickup_negocio_id || '').replace('#', '').trim();
        const matchedProps = [
          ...(propsByClickupId.get(idClean) || []),
          ...(idClean ? propsByClickupId.get('#' + idClean) || [] : []),
        ];

        // Responsável: prioriza negocios.responsavel_nome (disponível desde a
        // criação, ver migration 20260819g) — só cai pro criado_por da melhor
        // proposta em negócios antigos que ainda não têm esse campo preenchido.
        let resp = n.responsavel_nome || '';
        let supabaseDealValue = null;
        let fabricantes = [];
        let valorPorFabricante = {};
        let dataFechamento = null;

        const best = matchedProps.length > 0 ? resolveBestProposta(matchedProps) : null;

        if (best) {
          if (!resp) resp = best.criado_por || '';
          const v = parseFloat(best.total_proposta);
          if (!isNaN(v)) supabaseDealValue = v;
          fabricantes = Array.from(fabricantesByPropId.get(best.id) || []);
          valorPorFabricante = valorPorFabricantePropId.get(best.id) || {};
          dataFechamento = best.data_fechamento || null;
        }

        return {
          id: idClean,
          crm_negocio_id: n.id,
          versao_estagio: n.versao_estagio,
          clickup_negocio_id: n.clickup_negocio_id,
          name: n.nome,
          estagio: n.estagio,
          valor_clickup_fallback: n.valor_clickup_fallback,
          conta_id: n.conta_id || null,
          custom_fields: [],
          responsavel_negocio: resp,
          responsavel_clickup_id: n.responsavel_clickup_id || null,
          // Tipo de Oportunidade é sempre do negócio (não muda por versão de
          // proposta) — ver comentário na tela de Proposta sobre por que
          // "cenario" (por versão) não serve pra isso.
          tipo_oportunidade: n.tipo_oportunidade || 'Projeto',
          data_previsao: n.data_previsao || null,
          probabilidade: n.probabilidade || null,
          descricao: typeof n.descricao === 'string' ? n.descricao : '',
          supabase_deal_value: supabaseDealValue,
          fabricantes,
          valorPorFabricante,
          data_fechamento: dataFechamento,
          // Id da proposta no Supabase (não o do ClickUp) — usado pela
          // exportação completa (com produtos) da Lista de Negócios, pra
          // achar os itens_proposta ligados a esse negócio.
          proposta_id: best?.id || null,
          situacao: best?.situacao || null,
          motivo_perda: best?.motivo_perda || null,
        };
      });

      setKanbanTasks(enrichedTasks);
      try {
        safeStorage.setItem('crm_cache_kanban_tasks_v4', JSON.stringify(enrichedTasks));
      } catch (storageErr) {
        // Ignora cota excedida do Safari silenciosamente
      }
    } catch (err) {
      console.error("Erro ao carregar dados do Kanban:", err);
      showToast("Erro ao carregar dados do Kanban.", "error");
    } finally {
      if (!silent) setLoadingKanban(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'kanban' && (kanbanTasks.length === 0 || Date.now() - lastKanbanFetchAtRef.current > TAB_CACHE_TTL_MS)) {
      fetchKanbanData();
    }
  }, [activeTab, supabaseClient]);

  // Pré-carrega tarefas comerciais na montagem inicial para o drawer não iniciar vazio
  useEffect(() => {
    if (supabaseClient) {
      fetchCommercialTasks(supabaseClient);
    }
  }, [supabaseClient]);

  const verificarAtualizacaoCampoClickUp = async (taskId, newOptionId) => {
    const verificacao = await fetch(`/clickup-api/task/${taskId}`, {
      headers: { ...getSupabaseHeaders() },
    });
    if (!verificacao.ok) return false;
    const tarefa = await verificacao.json();
    const campo = (tarefa.custom_fields || []).find((item) => item.id === 'c8d0abe2-c59f-4a9e-93ff-bd060659aa63');
    const opcaoAtual = (campo?.type_config?.options || []).find((opcao) => String(opcao.orderindex) === String(campo?.value));
    return opcaoAtual?.id === newOptionId;
  };

  const updateTaskStage = async (taskId, newOptionId) => {
    /* B2_I10_BEGIN */
    if (kanbanModoRef.current !== 'legacy') throw new Error('ClickUp direto desabilitado no modo canônico.');
    /* B2_I10_END */
    const res = await fetch(`/clickup-api/task/${taskId}/field/c8d0abe2-c59f-4a9e-93ff-bd060659aa63`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getSupabaseHeaders()
      },
      body: JSON.stringify({ value: newOptionId })
    });
    if (!res.ok && !(await verificarAtualizacaoCampoClickUp(taskId, newOptionId))) {
      throw new Error("Falha na atualização do estágio no ClickUp");
    }
  };

  const updateTaskClickupStatus = async (taskId, statusName) => {
    /* B2_I10_BEGIN */
    if (kanbanModoRef.current !== 'legacy') throw new Error('ClickUp direto desabilitado no modo canônico.');
    /* B2_I10_END */
    const res = await fetch(`/clickup-api/task/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
      body: JSON.stringify({ status: statusName })
    });
    if (!res.ok) {
      throw new Error("Falha na atualização do status nativo no ClickUp");
    }
  };

  const handleOpportunityStateChange = async (taskId, targetOptionId) => {
    /* B2_I5_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      let key;
      try {
        if (kanbanModoRef.current !== 'canonico_v1' || identidadeModoRef.current !== 'canonico_v1' || !window.CrmKanbanEstagio) throw new Error('Gateway indisponível.');
        const target = kanbanColumns.find(c => c.id === targetOptionId);
        const task = kanbanTasks.find(t => t.id === taskId) || (selectedTask?.id === taskId ? selectedTask : null);
        if (!target || !task || !session?.user?.id) throw new Error('Oportunidade indisponível.');
        key = session.user.id + '/' + task.crm_negocio_id;
        let command = kanbanComandosRef.current.get(key);
        if (command && command.estagio_destino !== target.name) throw Object.assign(new Error('Operação pendente.'), { code: 'REQUEST_PENDING', ambiguous: true });
        const gateway = window.CrmKanbanEstagio.criar({ client: supabaseClient, config });
        if (!command) {
          command = window.CrmKanbanEstagio.preparar({ negocio_id: task.crm_negocio_id, expected_version: task.versao_estagio, estagio_destino: target.name });
          kanbanComandosRef.current.set(key, command);
        }
        const result = await gateway.mover(command);
        kanbanComandosRef.current.delete(key);
        setKanbanTasks(prev => prev.map(t => t.id === taskId ? { ...t, estagio: result.estagio, versao_estagio: result.versao } : t));
        if (selectedTask?.id === taskId) setSelectedTask(prev => prev ? { ...prev, estagio: result.estagio, versao_estagio: result.versao } : prev);
        showToast('Oportunidade atualizada no CRM. Sincronização ClickUp pendente.', 'success');
        fetchKanbanData();
        return true;
      } catch (error) {
        let code, ambiguous = false;
        try { code = Object.getOwnPropertyDescriptor(error, 'code')?.value; ambiguous = Object.getOwnPropertyDescriptor(error, 'ambiguous')?.value === true; } catch {}
        if (key && !ambiguous) kanbanComandosRef.current.delete(key);
        showToast(code === 'CONFLITO_VERSAO' ? 'A oportunidade mudou. Atualize antes de tentar novamente.' : code === 'REQUEST_PENDING' ? 'Operação anterior pendente. Retente o mesmo estágio antes de iniciar outra mudança.' : 'Não foi possível atualizar a oportunidade.', 'error');
        fetchKanbanData();
        return false;
      }
    }
    /* B2_I5_END */
    try {
      const targetOption = kanbanColumns.find(c => c.id === targetOptionId);
      if (!targetOption) return;

      const targetName = targetOption.name.toLowerCase();
      let clickupStatus = "ABERTO";

      if (targetName.includes("ganho")) {
        clickupStatus = "FECHADO";
      } else if (targetName.includes("perdido")) {
        clickupStatus = "PERDIDO/CANCELADO";
      }

      // 1. Atualização otimista local do estado do React (move card no Kanban e no SelectedTask)
      setKanbanTasks(prev => prev.map(t => (t.id === taskId ? { ...t, estagio: targetOption.name } : t)));
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => (prev ? { ...prev, estagio: targetOption.name } : prev));
      }

      const cleanTaskId = String(taskId).replace('#', '').trim();
      const idWithHash = '#' + cleanTaskId;

      // 2. Supabase é a fonte de verdade do estágio agora — grava lá primeiro.
      if (supabaseClient) {
        const { error: estagioErr } = await supabaseClient
          .from('negocios')
          .update({ estagio: targetOption.name })
          .or(`clickup_negocio_id.eq.${cleanTaskId},clickup_negocio_id.eq.${idWithHash}`);
        if (estagioErr) throw estagioErr;
      }

      // 3. Propaga pro ClickUp (espelho, pra quem ainda consulta o negócio por lá)
      await Promise.all([
        updateTaskStage(cleanTaskId, targetOptionId),
        updateTaskClickupStatus(cleanTaskId, clickupStatus)
      ]);

      // 4. REGRA DE REABERTURA: Se o estágio escolhido for do pipeline ativo (não Ganho e não Perdido),
      // reabrir propostas associadas em Supabase limpando data_fechamento e motivo_perda.
      // Isolado em seu próprio try/catch: é uma limpeza acessória — uma falha aqui não pode
      // derrubar a mudança de estágio em si, que já foi salva com sucesso nos passos 2 e 3.
      if (!targetName.includes("ganho") && !targetName.includes("perdido") && supabaseClient) {
        try {
          await supabaseClient
            .from('propostas')
            .update({
              situacao: 'Selecionada',
              data_fechamento: null,
              motivo_perda: null
            })
            .or(`clickup_negocio_id.eq.${cleanTaskId},clickup_negocio_id.eq.${idWithHash}`)
            .in('situacao', ['Ganho', 'Perdido']);

          if (currentProposta && (currentProposta.situacao === 'Ganho' || currentProposta.situacao === 'Perdido')) {
            setCurrentProposta(prev => ({
              ...prev,
              situacao: 'Selecionada',
              data_fechamento: null,
              motivo_perda: null
            }));
          }
          setPropostas(prev => prev.map(p => {
            if (p.situacao === 'Ganho' || p.situacao === 'Perdido') {
              return { ...p, situacao: 'Selecionada', data_fechamento: null, motivo_perda: null };
            }
            return p;
          }));
        } catch (reaberturaErr) {
          console.warn("Estágio mudou com sucesso, mas falhou ao reabrir propostas associadas:", reaberturaErr);
        }
      }

      showToast(`Oportunidade atualizada!`, "success");
      return true;
    } catch (err) {
      console.error("Erro na sincronização de estado:", err);
      showToast("Não foi possível atualizar a oportunidade.", "error");
      fetchKanbanData();
      return false;
    }
  };

  // Handlers do Drag & Drop Nativo
  // useCallback com deps vazias: só usa params + window, nada do escopo do
  // componente — necessário pra o React.memo(KanbanCard) de fato evitar
  // re-render (sem isso, cada render de App() cria uma função nova e o memo
  // nunca bate na comparação rasa de props).
  const handleDragStart = useCallback((e, task) => {
    window.getSelection()?.removeAllRanges();
    e.dataTransfer.setData("text/plain", task.id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDrop = async (e, targetOptionId) => {
    e.preventDefault();
    try {
      const taskId = e.dataTransfer.getData("text/plain");
      if (!taskId) return;

      const task = kanbanTasks.find(t => t.id === taskId);
      if (!task) return;

      /* B2_I5_BEGIN */
      if (kanbanModoRef.current !== 'legacy') {
        const target = kanbanColumns.find(c => c.id === targetOptionId);
        // I10: Ganho/Perdido fall through to the official closing modal below (reads only); the write goes through the gateway in handleConfirmClose.
        if (target?.name !== 'Ganho' && target?.name !== 'Perdido') {
          const key = session?.user?.id + '/' + task.crm_negocio_id;
          if (target?.name === task.estagio && !kanbanComandosRef.current.has(key)) return;
          await handleOpportunityStateChange(taskId, targetOptionId);
          return;
        }
      }
      /* B2_I5_END */
      const currentOptionId = getTaskOptionId(task, kanbanColumns);
      if (currentOptionId === targetOptionId) return;

      const targetOption = kanbanColumns.find(c => c.id === targetOptionId);
      const targetName = (targetOption?.name || '').toLowerCase();

      // Arrastar direto pra Ganho/Perdido precisa passar pelo mesmo fluxo oficial de
      // fechamento que o dropdown de Status já usa (handleConfirmClose) — só ele grava
      // data_fechamento/motivo_perda na proposta. handleOpportunityStateChange sozinho só
      // move o estágio do negócio; sem data_fechamento, esse negócio nunca aparece no
      // relatório de faturamento (achado em 19/08, ver docs/resumo.md).
      if (targetName.includes('ganho') || targetName.includes('perdido')) {
        const idWithoutHash = taskId.startsWith('#') ? taskId.substring(1) : taskId;
        const idWithHash = '#' + idWithoutHash;
        const { data: props, error } = await supabaseClient
          .from('propostas')
          .select('*')
          .or(`clickup_negocio_id.eq.${idWithoutHash},clickup_negocio_id.eq.${idWithHash}`)
          .order('created_at', { ascending: false });

        if (error || !props || props.length === 0) {
          showToast('Não foi possível localizar a proposta deste negócio.', 'error');
          return;
        }

        const selected = props.find(p => p.situacao === 'Selecionada') || props.find(p => p.versao === 'vA') || props[0];
        setClickupTaskId(taskId);
        setPropostas(props);
        await loadProposalDetails(selected.id);
        setCloseDate(new Date().toISOString().split('T')[0]);
        if (targetName.includes('ganho')) {
          setShowCloseModal('win');
        } else {
          setSelectedLossReason('');
          setShowCloseModal('loss');
        }
        return;
      }

      await handleOpportunityStateChange(taskId, targetOptionId);
    } catch (dropErr) {
      console.error("Erro ao mover o card:", dropErr);
      showToast("Erro inesperado ao mover o card.", "error");
      fetchKanbanData();
    }
  };

  // Handler de Clique para abrir o Drawer
  // useCallback com deps vazias: só chama setters de estado (sempre estáveis
  // pelo React), mesmo motivo do handleDragStart acima.
  const handleCardClick = useCallback((task) => {
    // Mescla com o registro já enriquecido em kanbanTasks (quando existir) —
    // cobre entradas parciais como a da Busca Global, que monta um objeto
    // mínimo a partir da query de negocios (sem tipo_oportunidade,
    // data_previsao, fabricantes etc.). kanbanTasks vence nos campos em
    // comum por ser o dado mais recente/completo; os campos exclusivos do
    // objeto recebido (ex.: numero_proposta_oficial da busca) são mantidos.
    const full = kanbanTasks.find(t => t.id === task.id);
    setSelectedTask(full ? { ...task, ...full } : task);
    setClickupTaskId(task.id);
    setDrawerTab('details');
    setDrawerSection('propostas');
    setDrawerRos({ negocioId: null, loading: false, error: null, cards: [] });
    setEmpresaDoNegocio(null);
    setContatosDoNegocio([]);
    setShowDrawer(true);
  }, [kanbanTasks]);

  // Busca global do cabeçalho: 3 queries em paralelo, uma por tabela —
  // cada .or() só referencia colunas da própria tabela, então não esbarra
  // na limitação do PostgREST de .or() com coluna de tabela embutida.
  // Diferente das outras buscas do app (todas filtram array já carregado
  // em memória), essa bate direto no Supabase — por isso o debounce no
  // useEffect logo abaixo, em vez de rodar a cada tecla.
  const buscarGlobal = async (termo) => {
    if (!supabaseClient || !termo || termo.trim().length < 2) {
      setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });
      return;
    }
    setGlobalSearchLoading(true);
    try {
      const t = `%${termo.trim()}%`;
      // Limite subiu de 5 pra 10 por categoria — o dropdown já rola
      // (overflow-y-auto) quando tem muito resultado, então não faz
      // sentido cortar no servidor antes disso.
      const [contasRes, contatosRes, negociosRes] = await Promise.all([
        supabaseClient.from('contas').select('id,nome,cnpj,razao_social,cidade')
          .or(`nome.ilike.${t},cnpj.ilike.${t},razao_social.ilike.${t}`).limit(10),
        supabaseClient.from('contatos').select('id,nome,email,cargo,conta_id')
          .or(`nome.ilike.${t},email.ilike.${t}`).limit(10),
        supabaseClient.from('negocios').select('id,nome,numero_proposta_oficial,estagio,conta_id,clickup_negocio_id,tipo_oportunidade,probabilidade,descricao')
          .or(`nome.ilike.${t},numero_proposta_oficial.ilike.${t}`).limit(10),
      ]);
      setGlobalSearchResults({
        contas: contasRes.data || [],
        contatos: contatosRes.data || [],
        negocios: negociosRes.data || [],
      });
    } catch (err) {
      console.error('[Busca Global] Erro:', err);
      setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });
    } finally {
      setGlobalSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!isGlobalSearchOpen) return;
    const termo = globalSearchTerm.trim();
    if (termo.length < 2) {
      setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });
      return;
    }
    const timeoutId = setTimeout(() => buscarGlobal(termo), 300);
    return () => clearTimeout(timeoutId);
  }, [globalSearchTerm, isGlobalSearchOpen, supabaseClient]);

  // Navegação ao selecionar um resultado da busca global. Negócio e
  // Proposta reaproveitam handleCardClick (o mesmo caminho que já abre o
  // drawer ao clicar num card do Kanban) — o drawer é um overlay fixo,
  // funciona vindo de qualquer aba. Empresa/Contato usam a ponte nova
  // (contaParaAbrir/abaContaParaAbrir) consumida dentro de EmpresasTab.
  const handleGlobalSearchSelect = async (tipo, item) => {
    setGlobalSearchTerm('');
    setIsGlobalSearchOpen(false);
    setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });

    if (tipo === 'negocio') {
      handleCardClick({
        id: String(item.clickup_negocio_id || '').replace('#', '').trim(),
        crm_negocio_id: item.id,
        name: item.nome,
        estagio: item.estagio,
        clickup_negocio_id: item.clickup_negocio_id,
        numero_proposta_oficial: item.numero_proposta_oficial,
        conta_id: item.conta_id,
        tipo_oportunidade: item.tipo_oportunidade,
        probabilidade: item.probabilidade,
        descricao: item.descricao,
      });
    } else if (tipo === 'conta') {
      setActiveTab('empresas');
      setAbaContaParaAbrir('visao_geral');
      setContaParaAbrir(item);
    } else if (tipo === 'contato') {
      // A busca só traz o conta_id do contato, não a conta inteira — busca
      // a conta-mãe completa antes de abrir (FichaEmpresaDrawer precisa de
      // nome/cnpj/etc., não só do id).
      setActiveTab('empresas');
      setAbaContaParaAbrir('contatos');
      if (supabaseClient && item.conta_id) {
        const { data } = await supabaseClient.from('contas').select('*').eq('id', item.conta_id).single();
        if (data) setContaParaAbrir(data);
      }
    }
  };

  const resolveTaskIdFormat = async (rawId) => {
    if (!supabaseClient || !rawId) return rawId;
    try {
      const cleanId = rawId.startsWith('#') ? rawId.substring(1) : rawId;
      const idWithHash = '#' + cleanId;
      const { data, error } = await supabaseClient
        .from('propostas')
        .select('clickup_negocio_id')
        .or(`clickup_negocio_id.eq.${cleanId},clickup_negocio_id.eq.${idWithHash}`)
        .limit(1);
      if (!error && data && data.length > 0) {
        return data[0].clickup_negocio_id;
      }
    } catch (err) {
      console.error("Erro ao resolver formato do ID:", err);
    }
    return rawId;
  };

  const parseNumericValue = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === 'number') return Number(val.toFixed(2));
    const str = String(val).trim();
    if (str.includes(',')) {
      const cleanStr = str.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
      return parseFloat(cleanStr) || 0;
    } else {
      const cleanStr = str.replace(/[^\d.-]/g, '');
      return parseFloat(cleanStr) || 0;
    }
  };

  // 2. Extrair ID do ClickUp da URL ou permitir entrada
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('task_id') || params.get('clickup_id') || params.get('id') || '';
    if (id) {
      if (supabaseClient) {
        resolveTaskIdFormat(id).then(resolvedId => {
          setClickupTaskId(resolvedId);
        });
      } else {
        setClickupTaskId(id);
      }
    }
  }, [supabaseClient]);

  useEffect(() => {
    if (dbConnected) {
      loadTodasPropostas();
    }
  }, [dbConnected]);

  // Carregar contexto do ClickUp via Edge Function Proxy
  useEffect(() => {
    if (dbConnected && clickupTaskId) {
      fetchProjectContext();
    } else {
      setProjectContext({ name: '', proposal_number: '' });
    }
  }, [dbConnected, clickupTaskId]);

  const fetchProjectContext = async () => {
    if (!clickupTaskId || !supabaseClient) return;
    try {
      const idWithoutHash = clickupTaskId.startsWith('#') ? clickupTaskId.substring(1) : clickupTaskId;
      const idWithHash = '#' + idWithoutHash;

      // 1. Busca INSTANTÂNEA no Supabase (negócios e propostas locais)
      const [{ data: negData }, { data: propData }] = await Promise.all([
        supabaseClient
          .from('negocios')
          .select('id, nome, estagio, numero_proposta_oficial, created_at, conta_id, clickup_negocio_id, tipo_oportunidade, probabilidade, descricao')
          .or(`clickup_negocio_id.eq.${idWithoutHash},clickup_negocio_id.eq.${idWithHash}`)
          .limit(1),
        supabaseClient
          .from('propostas')
          .select('id')
          .or(`clickup_negocio_id.eq.${idWithoutHash},clickup_negocio_id.eq.${idWithHash}`)
          .order('created_at', { ascending: true })
          .limit(1)
      ]);

      const neg = negData && negData[0];
      const proposalNumber = (propData && propData[0]) ? `#${propData[0].id}` : (neg?.numero_proposta_oficial ? `Nº ${neg.numero_proposta_oficial}` : 'Nova vA');
      const realName = neg?.nome || selectedTask?.nome || selectedTask?.name || `Projeto CRM #${idWithoutHash}`;

      // Atualiza o estado da tela IMEDIATAMENTE (sem esperar ClickUp)
      setProjectContext({
        name: realName,
        proposal_number: proposalNumber
      });

      if (neg && neg.estagio) {
        setSelectedTask(prev => ({
          ...(prev || {}),
          id: idWithoutHash,
          crm_negocio_id: neg.id,
          clickup_negocio_id: neg.clickup_negocio_id || idWithoutHash,
          estagio: neg.estagio,
          nome: neg.nome,
          name: neg.nome,
          numero_proposta_oficial: neg.numero_proposta_oficial,
          conta_id: neg.conta_id || (prev && prev.conta_id) || null,
          tipo_oportunidade: neg.tipo_oportunidade || 'Projeto',
          probabilidade: neg.probabilidade || null,
          descricao: typeof neg.descricao === 'string' ? neg.descricao : ''
        }));
      }

      // 2. Busca secundária de metadados no ClickUp de forma assíncrona (não bloqueia a tela)
      fetch(`/clickup-api/task/${idWithoutHash}`, { headers: { ...getSupabaseHeaders() } })
        .then(r => r.ok ? r.json() : null)
        .then(taskData => {
          if (!taskData) return;
          if (taskData.list && taskData.list.id) setClickupListId(taskData.list.id);
          const startVal = taskData.start_date ? formatDateMsToYMD(taskData.start_date) : (taskData.date_created ? formatDateMsToYMD(taskData.date_created) : '');
          const dueVal = taskData.due_date ? formatDateMsToYMD(taskData.due_date) : '';
          if (startVal || dueVal) {
            setClickupTaskDates({ start_date: startVal, due_date: dueVal });
          }
        })
        .catch(() => {});
    } catch (err) {
      console.error("Erro em fetchProjectContext:", err);
    }
  };

  // Aba «R.Os (N)» do drawer: carrega ao abrir o drawer (a contagem fica no título da aba), recarrega ao ativar a aba e quando uma R.O. é criada.
  const drawerRosNegocioId = selectedTask?.crm_negocio_id || (selectedTask?.id && String(selectedTask.id).includes('-') ? selectedTask.id : null);
  useEffect(() => {
    if (!showDrawer || !drawerRosNegocioId || !supabaseClient) return undefined;
    let ativo = true;
    setDrawerRos((prev) => ({ negocioId: drawerRosNegocioId, loading: true, error: null, cards: prev.negocioId === drawerRosNegocioId ? prev.cards : [] }));
    (async () => {
      try {
        const rows = await fetchTodasRosOportunidade(drawerRosNegocioId, { getHeaders: getCrmHeaders });
        if (!ativo) return;
        setDrawerRos({ negocioId: drawerRosNegocioId, loading: false, error: null, cards: prepararCardsRosDaOportunidade(rows, drawerRosNegocioId) });
      } catch (erro) {
        if (!ativo) return;
        setDrawerRos((prev) => ({ negocioId: drawerRosNegocioId, loading: false, error: erro?.message || 'Não foi possível carregar as R.Os desta oportunidade.', cards: prev.negocioId === drawerRosNegocioId ? prev.cards : [] }));
      }
    })();
    return () => { ativo = false; };
  }, [showDrawer, drawerRosNegocioId, drawerRosReload, supabaseClient]);
  useEffect(() => {
    const aoCriarRo = (e) => {
      const alvo = e?.detail?.negocio_id;
      if (!alvo || String(alvo) === String(drawerRosNegocioId || '')) setDrawerRosReload((n) => n + 1);
    };
    window.addEventListener('ro-criada', aoCriarRo);
    return () => window.removeEventListener('ro-criada', aoCriarRo);
  }, [drawerRosNegocioId]);

  // Aba "Empresa" do drawer do negócio: carrega sob demanda (só quando a aba é
  // aberta), e só refaz o fetch se a empresa mudou — evita recarregar ao trocar
  // de aba dentro do mesmo drawer.
  useEffect(() => {
    if (drawerSection !== 'empresa' || !supabaseClient) return;
    const contaId = selectedTask?.conta_id;
    if (!contaId) { setEmpresaDoNegocio(null); setContatosDoNegocio([]); return; }
    if (empresaDoNegocio && empresaDoNegocio.id === contaId) return;

    setLoadingEmpresaDoNegocio(true);
    (async () => {
      try {
        const [{ data: conta, error: contaErr }, { data: contatosData, error: contatosErr }] = await Promise.all([
          supabaseClient.from('contas').select('*').eq('id', contaId).single(),
          supabaseClient.from('contatos').select('*').eq('conta_id', contaId).order('nome'),
        ]);
        if (!contaErr) setEmpresaDoNegocio(conta || null);
        if (!contatosErr) setContatosDoNegocio(contatosData || []);
      } catch (err) {
        console.warn("Erro ao carregar empresa do negócio:", err);
      } finally {
        setLoadingEmpresaDoNegocio(false);
      }
    })();
  }, [drawerSection, selectedTask?.conta_id, supabaseClient]);

  // Consultar permissão do catálogo via API Edge (Proxy)
  const loadCatalogoPermissao = async () => {
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      if (!token && identidadeModoRef.current !== 'canonico_v1') return null;
      const response = await fetch('/api/catalogo/permissao', {
        headers: {
          ...await getCrmHeaders()
        }
      });
      if (!response.ok) return null;
      const data = await response.json();
      setCatalogoPermissao(data);
      return data;
    } catch (err) {
      console.warn("Erro ao carregar permissão do catálogo:", err);
      return null;
    }
  };

  // Carregar produtos cadastrados via API Edge (Proxy)
  const loadProducts = async () => {
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      if (!token && identidadeModoRef.current !== 'canonico_v1') return;
      const response = await fetch('/api/catalogo/produtos', {
        headers: {
          ...await getCrmHeaders()
        }
      });
      if (!response.ok) throw new Error('Erro ao carregar produtos');
      const { data } = await response.json();
      setProdutos(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Carregar fabricantes cadastrados via API Edge (Proxy)
  // - Lista de distribuidores comerciais para uso nas propostas
  // O cliente vem POR PARÂMETRO: os carregadores são chamados de closures criadas antes de `setSupabaseClient` (restauração de sessão no boot), onde o estado
  // `supabaseClient` ainda é null. Ler o estado aqui causava «Cannot read properties of null (reading 'from')» e a lista ficava vazia.
  // Falha de carregamento NUNCA vira lista vazia silenciosa: mantém a lista anterior e sinaliza (`distribuidoresErro`) para a tela diferenciar de «nenhum cadastrado».
  const loadDistributors = async (client = supabaseClient) => {
    try {
      if (!client) throw new Error('Cliente Supabase indisponível.');
      const { data, error } = await client
        .from('distribuidores')
        .select('*')
        .order('nome');
      if (error) throw error;
      setDistribuidores(data || []);
      setDistribuidoresErro(false);
    } catch (err) {
      console.error("Erro ao carregar distribuidores comerciais:", err);
      setDistribuidoresErro(true);
    }
  };

  // - Lista operacional (apenas ativos) para uso em Produtos
  // - Lista administrativa (ativos e inativos) exclusiva para admin
  const loadFabricantes = async (permOverride = null) => {
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      if (!token && identidadeModoRef.current !== 'canonico_v1') return;

      // 1. Carregamento operacional: sempre apenas fabricantes ativos
      const response = await fetch('/api/catalogo/fabricantes', {
        headers: {
          ...await getCrmHeaders()
        }
      });
      if (response.ok) {
        const { data } = await response.json();
        const ativos = (data || []).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
        setFabricantes(ativos);
      }

      // 2. Carregamento administrativo: somente se for admin
      const perm = permOverride || catalogoPermissao;
      const isAdmin = perm?.pode_administrar_fabricantes || perm?.role === 'admin';
      if (isAdmin) {
        setLoadingFabricantes(true);
        const resAdmin = await fetch('/api/catalogo/fabricantes?incluir_inativos=true', {
          headers: {
            ...await getCrmHeaders()
          }
        });
        if (resAdmin.ok) {
          const { data } = await resAdmin.json();
          const todos = (data || []).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
          setFabricantesAdmin(todos);
        }
        setLoadingFabricantes(false);
      } else {
        setFabricantesAdmin([]);
      }
    } catch (err) {
      console.error("Erro ao carregar fabricantes:", err);
      setLoadingFabricantes(false);
    }
  };

  // Carregar tipos de tarefa — Supabase é a fonte de verdade (antes só localStorage,
  // divergia entre navegadores/máquinas). localStorage continua como cache pra
  // load instantâneo antes da resposta do Supabase chegar.
  const loadTiposTarefa = async (client = supabaseClient) => {
    if (!client) return;
    const { data, error } = await client.from('tipos_tarefa').select('id, nome, emoji').eq('ativo', true).order('nome');
    if (!error && data) {
      setTaskTypes(data);
      try { safeStorage.setItem('crm_cache_task_types', JSON.stringify(data)); } catch (e) {}
    }
  };

  // Carregar segmentos — mesma lógica do loadTiposTarefa acima. Só escreve no
  // localStorage (não há state próprio aqui) porque quem exibe é o componente
  // SegmentosSettings (mais abaixo) e o módulo Empresa 360 (empresas.js), que
  // lê essa mesma chave.
  const loadSegmentosCache = async (client = supabaseClient) => {
    if (!client) return;
    const { data, error } = await client.from('segmentos').select('nome').eq('ativo', true).order('nome');
    if (!error && data) {
      try { safeStorage.setItem('crm_segmentos', JSON.stringify(data.map(s => s.nome))); } catch (e) {}
    }
  };

  // Carregar vendedores cadastrados — restrito a quem já logou no CRM com token
  // próprio (tabela usuarios_clickup, exposta via view usuarios_clickup_registrados
  // sem o token). Se a view falhar, cai pra lista completa do ClickUp (dropdown de
  // conveniência, não controle de acesso — ver docs/resumo.md).
  const loadVendedores = async () => {
    if (identidadeModoRef.current !== 'legacy') {
      try {const result=await window.CrmUsuarios.selecionaveis();setVendedores(result.data.map(u=>({id:u.clickup_user_id,nome:u.nome})));safeStorage.removeItem('crm_vendedores_ocultos');}
      catch {setVendedores([]);showToast('Verificação de usuários indisponível. Novas atribuições bloqueadas.','warning');}
      return;
    }
    try {
      const teamsRes = await fetch('/clickup-api/team', { headers: { ...getSupabaseHeaders() } });
      if (teamsRes.ok) {
        const teamsData = await teamsRes.json();
        if (teamsData.teams && teamsData.teams.length > 0) {
          const teamId = teamsData.teams[0].id;
          const membersRes = await fetch(`/clickup-api/team/${teamId}`, { headers: { ...getSupabaseHeaders() } });
          if (membersRes.ok) {
            const membersData = await membersRes.json();
            if (membersData.team && membersData.team.members) {
              let users = membersData.team.members.map(m => m.user);

              // Falha FECHADA: sem a lista de usuários cadastrados no CRM o workspace do ClickUp NÃO é exibido
              // (antes caía na lista inteira e ampliava indevidamente quem podia ser atribuído). Cada causa tem
              // mensagem própria; nenhum detalhe do erro chega ao usuário.
              let motivoBloqueio = '';
              if (!supabaseClient) motivoBloqueio = 'INDISPONIVEL';
              else {
                try {
                  const { data: registrados, error: registradosErr } = await supabaseClient
                    .from('usuarios_clickup_registrados')
                    .select('clickup_user_id');
                  if (registradosErr) {
                    motivoBloqueio = (registradosErr.code === '42501' || /permission denied/i.test(String(registradosErr.message || ''))) ? 'ACESSO_NEGADO' : 'CONSULTA_FALHOU';
                    console.warn('Lista de usuários cadastrados indisponível:', registradosErr.code || 'sem_codigo');
                  } else if (!Array.isArray(registrados)) {
                    motivoBloqueio = 'CONSULTA_FALHOU';
                  } else {
                    const registradosIds = new Set(registrados.map(r => String(r.clickup_user_id)));
                    users = users.filter(u => registradosIds.has(String(u.id)));
                  }
                } catch (registradosErr) {
                  motivoBloqueio = 'INDISPONIVEL';
                  console.warn('Lista de usuários cadastrados indisponível.');
                }
              }
              if (motivoBloqueio) {
                setVendedores([]);
                safeStorage.removeItem('crm_cache_vendedores');
                showToast(motivoBloqueio === 'ACESSO_NEGADO'
                  ? 'A lista de usuários cadastrados no CRM não está disponível para o seu perfil. Novas atribuições bloqueadas.'
                  : motivoBloqueio === 'CONSULTA_FALHOU'
                    ? 'Não foi possível consultar os usuários cadastrados no CRM. Novas atribuições bloqueadas.'
                    : 'Verificação de usuários indisponível. Novas atribuições bloqueadas.', 'warning');
                return;
              }

              const ocultos = JSON.parse(safeStorage.getItem('crm_vendedores_ocultos') || '[]');
              const mapped = users.map(u => ({
                id: u.id,
                nome: u.username || u.email,
                oculto: ocultos.includes(String(u.id)) || ocultos.includes(Number(u.id))
              }));
              setVendedores(mapped);
              try {
                safeStorage.setItem('crm_cache_vendedores', JSON.stringify(mapped));
              } catch (storageErr) {}
            }
          }
        }
      }
    } catch (err) {
      console.warn("Erro ao carregar vendedores do ClickUp:", err);
    }
  };

  // A aba "Vendedores" do Painel de Configurações existe justamente pra
  // conferir quem está realmente cadastrado (usuarios_clickup_registrados) —
  // mas `vendedores` só era recarregado em eventos de login/sessão inicial,
  // então numa aba aberta há tempo ela ficava presa num crm_cache_vendedores
  // antigo do localStorage, mostrando gente desatualizada. Recarrega toda
  // vez que essa aba específica é aberta, pra sempre refletir o estado atual.
  useEffect(() => {
    if (showSettingsModal && settingsActiveTab === 'venders' && supabaseClient) {
      loadVendedores(supabaseClient);
    }
  }, [showSettingsModal, settingsActiveTab, supabaseClient]);

  // Funções para Tarefas Comerciais
  const fetchCommercialTasks = async (client = supabaseClient, silent = false) => {
    if (!silent) {
      setLoadingTasks(true);
    }
    try {
      let loadedTasks = null;
      try {
        const response = await fetch('/api/tarefas', {
          headers: {
            ...getSupabaseHeaders(),
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            if (Array.isArray(data)) {
              loadedTasks = data;
            }
          } catch (jsonErr) {}
        }
      } catch (apiErr) {
        console.warn("Aviso ao buscar /api/tarefas:", apiErr);
      }

      // Se /api/tarefas não respondeu (ex: na VPS onde tudo roda direto via Supabase), busca via SupabaseClient
      if (loadedTasks === null && client) {
        const { data: sbTasks, error: sbErr } = await client
          .from('tarefas_comerciais')
          .select('*')
          .order('data_vencimento', { ascending: true });
        
        if (sbErr) {
          console.warn("Aviso ao carregar tarefas_comerciais do Supabase:", sbErr);
          loadedTasks = [];
        } else {
          loadedTasks = sbTasks || [];
        }
      }

      console.log("[DEBUG] Loaded tasks:", loadedTasks);
      setCommercialTasks(loadedTasks || []);
    } catch (err) {
      console.warn("Erro ao buscar tarefas comerciais:", err);
      setCommercialTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // === FUNÇÕES DE ATIVIDADES DO NEGÓCIO ===
  const fetchAtividades = async (clickupId) => {
    if (!clickupId) return;
    setLoadingAtividades(true);
    try {
      const idClean = String(clickupId).replace('#', '');
      let data = null;
      try {
        const res = await fetch(`/api/atividades?clickup_negocio_id=${idClean}`, {
          headers: { ...getSupabaseHeaders() }
        });
        if (res.ok) {
          const text = await res.text();
          try { data = JSON.parse(text); } catch (e) {}
        }
      } catch (err) {}

      if (data === null) {
        // Fallback: busca comentários da tarefa direto na API do ClickUp via
        // proxy, pro caso da Edge Function estar fora do ar. `data_execucao`
        // (não `created_at`): é o campo que o JSX de fato lê pra exibir a
        // data — usar outro nome aqui fazia essa data renderizar "Invalid
        // Date" sempre que esse fallback disparava.
        const cuRes = await fetch(`/clickup-api/task/${idClean}/comment`, {
          headers: { ...getSupabaseHeaders() }
        });
        if (cuRes.ok) {
          const cuData = await cuRes.json();
          data = (cuData.comments || []).map(c => {
            const iso = c.date ? new Date(parseInt(c.date)).toISOString() : new Date().toISOString();
            return {
              id: `cu_${c.id}`,
              clickup_comment_id: c.id,
              texto: (c.comment_text || (c.comment ? c.comment.map(seg => seg.text).join('') : '')).replace('[SPA Gestão Comercial] ', '').trim(),
              data_execucao: iso,
              created_at: iso,
              updated_at: iso,
              origem: 'clickup',
              autor_nome: c.user?.username || c.user?.email || null,
              autor_clickup_id: c.user?.id != null ? String(c.user.id) : null,
              anexos: []
            };
          });
        }
      }

      setAtividades(data || []);
    } catch (err) {
      console.warn('[ATIVIDADES] Erro ao buscar atividades:', err);
      setAtividades([]);
    } finally {
      setLoadingAtividades(false);
    }
  };

  // Adiciona arquivos à lista de anexos pendentes e já dispara o upload de
  // cada um pro ClickUp em paralelo — cada item atualiza seu próprio status
  // conforme termina, sem travar os outros.
  const handleAdicionarAnexosAtividade = (fileList) => {
    const arquivos = Array.from(fileList || []);
    if (arquivos.length === 0) return;
    if (!clickupTaskId) {
      showToast('Abra um negócio antes de anexar arquivos.', 'error');
      return;
    }
    const novos = arquivos.map(file => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file, status: 'uploading', result: null, error: null
    }));
    setNovaAtividadeAnexos(prev => [...prev, ...novos]);
    novos.forEach(async (item) => {
      try {
        const result = await uploadAtividadeAnexo({ clickupNegocioId: clickupTaskId, file: item.file });
        setNovaAtividadeAnexos(prev => prev.map(a => a.id === item.id ? { ...a, status: 'done', result } : a));
      } catch (err) {
        console.error('[ATIVIDADES] Erro ao enviar anexo:', err);
        setNovaAtividadeAnexos(prev => prev.map(a => a.id === item.id ? { ...a, status: 'error', error: err?.message } : a));
      }
    });
  };

  const handleRemoverAnexoAtividade = (id) => {
    setNovaAtividadeAnexos(prev => prev.filter(a => a.id !== id));
  };

  const handleCreateAtividade = async () => {
    const anexosProntos = novaAtividadeAnexos.filter(a => a.status === 'done').map(a => a.result);
    const textoFinal = novaAtividade.trim() || (anexosProntos.length > 0 ? `📎 ${anexosProntos.length} anexo${anexosProntos.length > 1 ? 's' : ''}` : '');
    if (!textoFinal || !clickupTaskId) return;
    if (novaAtividadeAnexos.some(a => a.status === 'uploading')) {
      showToast('Aguarde o envio dos anexos terminar.', 'error');
      return;
    }
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setSavingAtividade(true);
      try {
        const crmId = comercialNegocioId(clickupTaskId);
        if (!crmId) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'NEGOCIO_INDISPONIVEL' });
        // author, task context and ClickUp comment text are decided by the server; only the text and the attachment pointers come from here
        await comercialExecutar('atividade_criar', crmId, { negocio_id: crmId, origem: 'manual', tarefa_id: null, texto: textoFinal, anexos: anexosParaComando(anexosProntos) });
        showToast('Atividade registrada com sucesso! O envio ao ClickUp é feito em segundo plano.', 'success');
        setNovaAtividade('');
        setNovaAtividadeAnexos([]);
        fetchAtividades(clickupTaskId);
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao registrar atividade.'), 'error');
      } finally {
        setSavingAtividade(false);
      }
      return;
    }
    /* B2_I13_END */
    setSavingAtividade(true);
    try {
      const atividadeSalva = await createAtividade({
        clickupNegocioId: clickupTaskId,
        texto: textoFinal,
        autorNome: userProfile?.username || userProfile?.email || null,
        autorClickupId: userProfile?.id ? String(userProfile.id) : null,
        origem: 'manual',
        anexos: anexosProntos
      });
      const syncPendente = atividadeSalva?.[0]?.sincronizacao && atividadeSalva[0].sincronizacao !== 'sincronizado';
      showToast(syncPendente ? 'Atividade salva no CRM. O envio ao ClickUp precisa de verificação; não é necessário cadastrar novamente.' : 'Atividade registrada com sucesso!', syncPendente ? 'warning' : 'success');
      setNovaAtividade('');
      setNovaAtividadeAnexos([]);
      fetchAtividades(clickupTaskId);
    } catch (err) {
      console.error('[ATIVIDADES] Erro ao criar atividade:', err);
      showToast(err?.message || 'Erro ao registrar atividade.', 'error');
    } finally {
      setSavingAtividade(false);
    }
  };

  const handleEditAtividade = async (atividadeId) => {
    if (!editingAtividadeTexto.trim()) return;
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setSavingAtividade(true);
      try {
        const alvo = atividades.find(a => a.id === atividadeId);
        if (!alvo) throw Object.assign(new Error('Atividade indisponível.'), { code: 'ATIVIDADE_NAO_ENCONTRADA' });
        if (String(atividadeId).startsWith('cu_')) {
          // native ClickUp comment (no row of its own): ONE audited command; the worker checks that the comment belongs to this opportunity's task and applies the edit with the user's token
          const negocioCrm = comercialNegocioId(clickupTaskId);
          if (!negocioCrm) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
          await comercialExecutar('comentario_editar', atividadeId, { negocio_id: negocioCrm, comentario_id: String(atividadeId).slice(3), texto: editingAtividadeTexto });
          showToast('Edição enviada ao ClickUp. Ela aparece após a sincronização.', 'success');
          setAtividades(prev => prev.map(a => a.id === atividadeId ? { ...a, texto: editingAtividadeTexto.trim() } : a));
          setEditingAtividade(null);
          setEditingAtividadeTexto('');
          return;
        }
        await comercialExecutar('atividade_editar', atividadeId, { atividade_id: atividadeId, expected_version: alvo.versao_edicao, texto: editingAtividadeTexto });
        showToast('Atividade atualizada com sucesso!', 'success');
        setEditingAtividade(null);
        setEditingAtividadeTexto('');
        fetchAtividades(clickupTaskId);
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao editar atividade.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO') fetchAtividades(clickupTaskId);
      } finally {
        setSavingAtividade(false);
      }
      return;
    }
    /* B2_I13_END */
    setSavingAtividade(true);
    try {
      const res = await fetch(`/api/atividades/${atividadeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
        body: JSON.stringify({ texto: editingAtividadeTexto.trim() })
      });
      if (res.ok) {
        showToast('Atividade atualizada com sucesso!', 'success');
        setEditingAtividade(null);
        setEditingAtividadeTexto('');
        fetchAtividades(clickupTaskId);
      } else {
        const errData = await res.json().catch(() => ({}));
        showToast(errData.error || 'Erro ao editar atividade.', 'error');
      }
    } catch (err) {
      console.error('[ATIVIDADES] Erro ao editar atividade:', err);
      showToast('Erro ao editar atividade.', 'error');
    } finally {
      setSavingAtividade(false);
    }
  };

  const handleDeleteAtividade = async (atividadeId) => {
    if (!confirm('Deseja realmente excluir esta atividade?')) return;
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setSavingAtividade(true);
      try {
        const alvo = atividades.find(a => a.id === atividadeId);
        if (!alvo) throw Object.assign(new Error('Atividade indisponível.'), { code: 'ATIVIDADE_NAO_ENCONTRADA' });
        if (String(atividadeId).startsWith('cu_')) {
          const negocioCrm = comercialNegocioId(clickupTaskId);
          if (!negocioCrm) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
          await comercialExecutar('comentario_excluir', atividadeId, { negocio_id: negocioCrm, comentario_id: String(atividadeId).slice(3) });
          showToast('Exclusão enviada ao ClickUp. Ela aparece após a sincronização.', 'success');
          setAtividades(prev => prev.filter(a => a.id !== atividadeId));
          return;
        }
        await comercialExecutar('atividade_excluir', atividadeId, { atividade_id: atividadeId, expected_version: alvo.versao_edicao });
        showToast('Atividade excluída com sucesso!', 'success');
        fetchAtividades(clickupTaskId);
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao excluir atividade.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO') fetchAtividades(clickupTaskId);
      } finally {
        setSavingAtividade(false);
      }
      return;
    }
    /* B2_I13_END */
    setSavingAtividade(true);
    try {
      const res = await fetch(`/api/atividades/${atividadeId}`, {
        method: 'DELETE',
        headers: { ...getSupabaseHeaders() }
      });
      if (res.ok) {
        showToast('Atividade excluída com sucesso!', 'success');
        fetchAtividades(clickupTaskId);
      } else {
        showToast('Erro ao excluir atividade.', 'error');
      }
    } catch (err) {
      console.error('[ATIVIDADES] Erro ao excluir atividade:', err);
      showToast('Erro ao excluir atividade.', 'error');
    } finally {
      setSavingAtividade(false);
    }
  };

  const toggleTaskStatus = async (task) => {
    const nextStatus = task.status === 'concluida' ? 'pendente' : 'concluida';
    
    console.log('[DEBUG] Checkbox clicado para a tarefa:', task.id, 'Novo Status:', nextStatus);
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // one command: the status is stored and the ClickUp subtask status is an ordered server intent; the browser never writes REST nor ClickUp
      try {
        const r = await comercialExecutar('tarefa_status', task.id, { tarefa_id: task.id, expected_version: task.versao_edicao, status: nextStatus });
        setCommercialTasks(prev => prev.map(t => (t.id === task.id ? { ...t, status: nextStatus, versao_edicao: r.versao_edicao } : t)));
        showToast('Status da tarefa atualizado com sucesso!', 'success');
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao atualizar status da tarefa.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO' && supabaseClient) fetchCommercialTasks(supabaseClient);
      }
      return;
    }
    /* B2_I13_END */
    setCommercialTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: nextStatus } : t));
    
    try {
      let success = false;
      try {
        const response = await fetch(`/api/tarefas/${task.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getSupabaseHeaders()
          },
          body: JSON.stringify({ status: nextStatus })
        });
        if (response.ok) success = true;
      } catch (e) {}

      if (!success && supabaseClient) {
        const { error } = await supabaseClient
          .from('tarefas_comerciais')
          .update({ status: nextStatus })
          .eq('id', task.id);
        if (error) throw error;

        // Se tiver subtask vinculada no ClickUp, atualiza o status dela via proxy
        if (task.clickup_subtask_id) {
          const clickupStatus = nextStatus === 'concluida' ? 'fechado' : 'aberto';
          await fetch(`/clickup-api/task/${task.clickup_subtask_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
            body: JSON.stringify({ status: clickupStatus })
          }).catch(cuErr => console.warn('Aviso ao sincronizar subtask no ClickUp:', cuErr));
        }
      }
      
      showToast("Status da tarefa atualizado com sucesso!", "success");
    } catch (err) {
      console.error("[ERROR] Falha ao atualizar status:", err);
      showToast("Erro ao atualizar status da tarefa. Revertendo...", "error");
      setCommercialTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
    }
  };

  const handleDeleteTask = async (taskId) => {
    // Retorna true/false pra quem chama saber se a exclusão foi confirmada —
    // o modal de resumo da tarefa usa isso pra só fechar quando de fato
    // excluiu, sem sumir da tela se o usuário cancelar o confirm().
    if (!confirm("Deseja realmente excluir esta tarefa comercial?")) return false;
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      try {
        const alvo = commercialTasks.find(t => t.id === taskId);
        await comercialExecutar('tarefa_excluir', taskId, { tarefa_id: taskId, expected_version: alvo?.versao_edicao });
        setCommercialTasks(prev => prev.filter(t => t.id !== taskId));
        showToast('Tarefa comercial excluída com sucesso!', 'success');
        return true;
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao excluir tarefa comercial.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO' && supabaseClient) fetchCommercialTasks(supabaseClient);
        return false;
      }
    }
    /* B2_I13_END */

    console.log('[DEBUG] Lixeira clicada para excluir a tarefa:', taskId);
    const targetTask = commercialTasks.find(t => t.id === taskId);
    setCommercialTasks(prev => prev.filter(t => t.id !== taskId));
    
    try {
      let success = false;
      try {
        const response = await fetch(`/api/tarefas/${taskId}`, {
          method: 'DELETE',
          headers: { ...getSupabaseHeaders() }
        });
        if (response.ok) success = true;
      } catch (e) {}

      if (!success && supabaseClient) {
        const { error } = await supabaseClient
          .from('tarefas_comerciais')
          .delete()
          .eq('id', taskId);
        if (error) throw error;

        if (targetTask?.clickup_subtask_id) {
          await fetch(`/clickup-api/task/${targetTask.clickup_subtask_id}`, {
            method: 'DELETE',
            headers: { ...getSupabaseHeaders() }
          }).catch(cuErr => console.warn('Aviso ao excluir subtask no ClickUp:', cuErr));
        }
      }
      
      showToast("Tarefa comercial excluída com sucesso!", "success");
      return true;
    } catch (err) {
      console.error("[ERROR] Falha ao excluir tarefa:", err);
      showToast("Erro ao excluir tarefa comercial.", "error");
      if (supabaseClient) {
        fetchCommercialTasks(supabaseClient);
      }
      return false;
    }
  };

  const handleExcluirNegocioDrawer = async (task) => {
    if (!task) return;
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // D-NEG-DEL: exclusão LÓGICA por comando do gateway (qualquer usuário ativo). Nada é apagado no CRM (propostas, tarefas, atividades, R.Os e histórico ficam); a tarefa no ClickUp é EXCLUÍDA pelo outbox. NÃO há restauração.
      const nomeLogico = task.nome || task.name || 'esta oportunidade';
      if (!confirm(`Excluir a oportunidade "${nomeLogico}"?\n\nEla deixará de aparecer no funil, nas listas e nas buscas.\nOs dados (propostas, tarefas, atividades, R.Os e histórico) são preservados internamente, mas a tarefa correspondente será EXCLUÍDA no ClickUp.\nA exclusão NÃO pode ser desfeita pela tela: se foi um engano, cadastre uma nova oportunidade.`)) return;
      try {
        if (kanbanModoRef.current !== 'canonico_v1') throw Object.assign(new Error('Gateway indisponível.'), { code: 'GATEWAY_INDISPONIVEL' });
        const crmId = task.crm_negocio_id || (task.id && String(task.id).includes('-') ? task.id : null);
        const vivo = kanbanTasks.find(t => t.crm_negocio_id && t.crm_negocio_id === crmId);
        const versao = vivo ? vivo.versao_estagio : task.versao_estagio;
        if (!crmId || !Number.isSafeInteger(versao)) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        await comercialExecutar('negocio_excluir', crmId, { negocio_id: crmId, expected_version: versao });
        setKanbanTasks(prev => prev.filter(t => t.crm_negocio_id !== crmId));
        setShowDrawer(false); setClickupTaskId(''); setSelectedTask(null);
        if (supabaseClient) { fetchKanbanData(true); loadDashboardData(); }
        showToast(`Oportunidade "${nomeLogico}" excluída. A tarefa no ClickUp será removida.`, 'success');
      } catch (err) {
        showToast(comercialMensagem(err, 'Não foi possível excluir a oportunidade.'), 'error');
      }
      return;
    }
    /* B2_I13_END */
    const nomeNegocio = task.nome || task.name || 'esta oportunidade';
    const msg = `Excluir a oportunidade "${nomeNegocio}"?\n\nIsso irá:\n• Remover do CRM local (banco de dados)\n• Excluir a tarefa correspondente no ClickUp\n\nEsta ação não pode ser desfeita!`;
    if (!confirm(msg)) return;

    try {
      // 1. Exclui do Supabase
      if (supabaseClient) {
        if (task.id && String(task.id).includes('-')) {
          await supabaseClient.from('negocios').delete().eq('id', task.id);
        }
        if (task.clickup_negocio_id) {
          await supabaseClient.from('negocios').delete().eq('clickup_negocio_id', task.clickup_negocio_id);
        }
        if (task.id && !String(task.id).includes('-')) {
          await supabaseClient.from('negocios').delete().eq('clickup_negocio_id', task.id);
        }
      }

      // 2. Exclui do ClickUp se houver ID válido
      const cuTaskId = task.clickup_negocio_id || (task.id && !String(task.id).includes('-') ? task.id : null);
      if (cuTaskId && !String(cuTaskId).startsWith('crm_neg_')) {
        try {
          await fetch(`/clickup-api/task/${cuTaskId}`, { method: 'DELETE', headers: { ...getSupabaseHeaders() } });
        } catch (e) {
          console.warn('[ClickUp Delete] Falha ao excluir negócio no ClickUp:', e);
        }
      }

      // 3. Fecha o drawer e recarrega
      setShowDrawer(false);
      setClickupTaskId('');
      setSelectedTask(null);
      if (supabaseClient) {
        fetchKanbanData();
      }
      showToast(`Oportunidade "${nomeNegocio}" excluída com sucesso!`, "success");
    } catch (err) {
      console.error('Erro ao excluir oportunidade:', err);
      showToast('Erro ao excluir oportunidade: ' + (err.message || err), "error");
    }
  };

  const handleAbrirEditarNegocioDrawer = async (task) => {
    if (!task) return;
    const cuTaskId = task.clickup_negocio_id || (task.id && !String(task.id).includes('-') ? task.id : null);
    const crmNegocioId = task.crm_negocio_id || (task.id && String(task.id).includes('-') ? task.id : null);
    if (!supabaseClient || (!crmNegocioId && !cuTaskId)) {
      showToast('Não foi possível localizar a oportunidade no CRM para edição.', 'warning');
      return;
    }

    setEditNegocioDrawerRos({ loading: true, error: null, rows: [] });
    try {
      let query = supabaseClient
        .from('negocios')
        .select('id,nome,estagio,tipo_oportunidade,probabilidade,descricao,responsavel_clickup_id,clickup_negocio_id,numero_proposta_oficial,conta_id')
        .limit(1);
      query = crmNegocioId
        ? query.eq('id', crmNegocioId)
        : query.in('clickup_negocio_id', [String(cuTaskId), `#${String(cuTaskId).replace(/^#/, '')}`]);
      const { data: negocioAtual, error: negocioError } = await query.maybeSingle();
      if (negocioError) throw negocioError;
      if (!negocioAtual) throw new Error('Oportunidade não localizada no CRM.');

      const taskAtualizada = {
        ...task,
        ...negocioAtual,
        id: task.id,
        name: negocioAtual.nome,
        crm_negocio_id: negocioAtual.id,
      };
      setSelectedTask((prev) => ({ ...(prev || {}), ...taskAtualizada }));
      setEditNegocioDrawerForm(hidratarFormularioOportunidade(taskAtualizada));
      setShowEditNegocioDrawerModal(true);

      try {
        const rows = await fetchTodasRosOportunidade(negocioAtual.id, {
          getHeaders: getCrmHeaders,
        });
        setEditNegocioDrawerRos({
          loading: false,
          error: null,
          rows: prepararRosOportunidadeParaExibicao(rows),
        });
      } catch (roError) {
        setEditNegocioDrawerRos({
          loading: false,
          error: roError?.message || 'Não foi possível carregar as R.Os vinculadas.',
          rows: [],
        });
      }
    } catch (error) {
      setEditNegocioDrawerRos({ loading: false, error: null, rows: [] });
      showToast(`Não foi possível carregar a oportunidade para edição: ${error?.message || error}`, 'error');
    }
  };

  const handleSalvarEditarNegocioDrawer = async (e) => {
    e.preventDefault();
    if (!editNegocioDrawerForm.nome.trim()) {
      showToast('Informe o título da oportunidade.', 'error');
      return;
    }
    setSavingEditNegocioDrawer(true);
    /* B2_I7_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // W2: no REST/ClickUp fallback in this branch. Name, type, probability, description and stage go in ONE gateway command (atomic).
      let key;
      try {
        if (kanbanModoRef.current !== 'canonico_v1' || identidadeModoRef.current !== 'canonico_v1' || !window.CrmKanbanEstagio) throw new Error('Gateway indisponível.');
        const crmId = selectedTask?.crm_negocio_id || (selectedTask?.id && String(selectedTask.id).includes('-') ? selectedTask.id : null);
        const ao_vivo = kanbanTasks.find(t => t.crm_negocio_id && t.crm_negocio_id === crmId);
        const versao = ao_vivo ? ao_vivo.versao_estagio : selectedTask?.versao_estagio;
        if (!crmId || !session?.user?.id || versao === undefined || versao === null) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        const persistido = montarPayloadPersistenciaOportunidade(editNegocioDrawerForm);
        const campos = { nome: persistido.nome, tipo_oportunidade: persistido.tipo_oportunidade, probabilidade: persistido.probabilidade };
        if (Object.prototype.hasOwnProperty.call(persistido, 'descricao')) campos.descricao = persistido.descricao;
        if (persistido.estagio !== (ao_vivo ? ao_vivo.estagio : selectedTask?.estagio)) campos.estagio = persistido.estagio;
        if (campos.estagio === 'Ganho' || campos.estagio === 'Perdido') {
          showToast('Fechamento pelo gateway ainda não está habilitado.', 'error');
          return;
        }
        key = 'edicao/' + session.user.id + '/' + crmId;
        let command = kanbanComandosRef.current.get(key);
        if (command && (command.expected_version !== versao || JSON.stringify(command.campos) !== JSON.stringify(campos))) throw Object.assign(new Error('Operação pendente.'), { code: 'REQUEST_PENDING', ambiguous: true });
        const gateway = window.CrmKanbanEstagio.criar({ client: supabaseClient, config });
        if (!command) {
          command = window.CrmKanbanEstagio.prepararEdicao({ negocio_id: crmId, expected_version: versao, campos });
          kanbanComandosRef.current.set(key, command);
        }
        const result = await gateway.editar(command);
        kanbanComandosRef.current.delete(key);
        setSelectedTask(prev => ({
          ...prev,
          nome: campos.nome,
          name: campos.nome,
          estagio: result.estagio,
          versao_estagio: result.versao,
          tipo_oportunidade: campos.tipo_oportunidade,
          probabilidade: campos.probabilidade,
          ...(Object.prototype.hasOwnProperty.call(campos, 'descricao') ? { descricao: campos.descricao } : {})
        }));
        setShowEditNegocioDrawerModal(false);
        fetchKanbanData();
        showToast(result.sync_status === 'pendente' ? 'Oportunidade atualizada no CRM. Sincronização ClickUp pendente.' : 'Oportunidade atualizada com sucesso!', 'success');
      } catch (error) {
        let code, ambiguous = false;
        try { code = Object.getOwnPropertyDescriptor(error, 'code')?.value; ambiguous = Object.getOwnPropertyDescriptor(error, 'ambiguous')?.value === true; } catch {}
        if (key && !ambiguous) kanbanComandosRef.current.delete(key);
        showToast(code === 'CONFLITO_VERSAO' ? 'A oportunidade mudou. Atualize antes de tentar novamente.' : code === 'REQUEST_PENDING' ? 'Operação anterior pendente. Repita sem alterar os dados.' : code === 'NENHUMA_ALTERACAO' ? 'Nenhuma alteração para salvar.' : code === 'CAMPO_INVALIDO' ? 'Algum campo é inválido. Revise o formulário.' : code === 'ESTAGIO_PROIBIDO' ? 'Fechamento pelo gateway ainda não está habilitado.' : 'Não foi possível atualizar a oportunidade.', 'error');
        if (code === 'CONFLITO_VERSAO') fetchKanbanData();
      } finally {
        setSavingEditNegocioDrawer(false);
      }
      return;
    }
    /* B2_I7_END */
    try {
      const probNum = parseInt(editNegocioDrawerForm.probabilidade) || 50;
      const cuTaskId = selectedTask.clickup_negocio_id || (selectedTask.id && !String(selectedTask.id).includes('-') ? selectedTask.id : null);

      // 1. Atualiza no Supabase
      // Tipo de Oportunidade e Probabilidade eram preenchidos no formulário
      // mas nunca gravados aqui — só nome, estágio e valor eram persistidos.
      // Valor Estimado e Previsão de Fechamento saíram do formulário: o
      // primeiro nunca era atualizado depois (virava lixo assim que a 1ª
      // proposta existia); o segundo agora vem sozinho da proposta marcada
      // "Usar no Forecast" em vez de digitado aqui.
      const negocioUpdatePayload = {
        ...montarPayloadPersistenciaOportunidade(editNegocioDrawerForm),
        updated_at: new Date().toISOString()
      };
      if (supabaseClient) {
        let updateResult = null;
        if (selectedTask.crm_negocio_id) {
          updateResult = await supabaseClient.from('negocios').update(negocioUpdatePayload).eq('id', selectedTask.crm_negocio_id);
        } else if (selectedTask.id && String(selectedTask.id).includes('-')) {
          updateResult = await supabaseClient.from('negocios').update(negocioUpdatePayload).eq('id', selectedTask.id);
        } else if (cuTaskId) {
          updateResult = await supabaseClient.from('negocios').update(negocioUpdatePayload).eq('clickup_negocio_id', cuTaskId);
        }
        if (updateResult?.error) throw updateResult.error;
      }

      // 2. Atualiza no ClickUp
      if (cuTaskId && !String(cuTaskId).startsWith('crm_neg_')) {
        try {
          const estOpt = ESTAGIO_OPTIONS.find(o => o.name === editNegocioDrawerForm.estagio);
          const customFields = montarCamposClickUpOportunidade({
            estagioId: estOpt?.id,
            tipoId: TIPO_OPORTUNIDADE_CLICKUP[editNegocioDrawerForm.tipo],
            probabilidade: probNum,
          });

          // Previsão de Fechamento não é mais digitada aqui nem sincronizada
          // com o due_date do ClickUp — vem sozinha da proposta marcada
          // "Usar no Forecast" (ver handleSaveProposal/handleUpdateVersionStatus),
          // que já sincroniza esse mesmo campo do ClickUp via data_fechamento.
          await fetch(`/clickup-api/task/${cuTaskId}?custom_item_id=1004`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
            body: JSON.stringify({
              name: editNegocioDrawerForm.nome.trim(),
              custom_item_id: 1004,
              description: editNegocioDrawerForm.descricao,
              custom_fields: customFields,
            })
          });
        } catch (cuErr) {
          console.warn('[ClickUp Sync] Falha ao atualizar negócio no ClickUp:', cuErr);
        }
      }

      // 3. Atualiza o estado local
      setSelectedTask(prev => ({
        ...prev,
        nome: editNegocioDrawerForm.nome.trim(),
        name: editNegocioDrawerForm.nome.trim(),
        estagio: editNegocioDrawerForm.estagio,
        tipo_oportunidade: editNegocioDrawerForm.tipo || null,
        probabilidade: probNum || null,
        descricao: editNegocioDrawerForm.descricao
      }));

      setShowEditNegocioDrawerModal(false);
      if (supabaseClient) fetchKanbanData();
      showToast('Oportunidade atualizada com sucesso!', 'success');
    } catch (err) {
      console.error('Erro ao atualizar oportunidade:', err);
      showToast('Erro ao atualizar oportunidade: ' + (err.message || err), 'error');
    } finally {
      setSavingEditNegocioDrawer(false);
    }
  };

  const handleCreateTaskSubmit = async (e) => {
    e.preventDefault();
    console.log("[DEBUG] Submit clicked! Raw form state:", { 
      title: newTaskTitle, 
      type: newTaskType, 
      date: newTaskDueDate, 
      time: newTaskTime, 
      hasTime, 
      assignee: newTaskAssignee 
    });

    let finalPropostaId = null;
    let finalClickupId = null;

    // Resilient proposal and clickup ID resolution
    if (selectedProposalForTask) {
      finalClickupId = selectedProposalForTask.id;
      const associatedProp = (todasPropostas || []).find(p => p.clickup_negocio_id === selectedProposalForTask.id || p.clickup_negocio_id === '#' + selectedProposalForTask.id);
      finalPropostaId = associatedProp ? associatedProp.id : null;
    } else if (showDrawer) {
      // Try to resolve from currentProposta or lookup by clickupTaskId in loaded propostas array
      const resolvedProp = currentProposta || (propostas && propostas.find(p => p.clickup_negocio_id === clickupTaskId || p.clickup_negocio_id === '#' + clickupTaskId));
      finalPropostaId = resolvedProp ? resolvedProp.id : null;
      finalClickupId = clickupTaskId;
    } else {
      // In global mode, try to fallback to null proposal instead of throwing blocker alerts
      finalPropostaId = null;
      finalClickupId = null;
    }

    // Trava de segurança: se o ID do negócio não pôde ser resolvido por propostas e o drawer está fechado,
    // mas a tarefa original em edição possuía um ID de negócio válido, nós preservamos o ID original!
    if (!finalClickupId && !selectedProposalForTask && editingTask && editingTask.clickup_negocio_id) {
      finalClickupId = editingTask.clickup_negocio_id;
    }

    // Ensure we have a valid ClickUp Negocio ID (fallback to clickupTaskId or selectedProposalForTask's ID or target input)
    if (!finalClickupId && showDrawer) {
      finalClickupId = clickupTaskId;
    }

    if (!finalClickupId) {
      console.warn("[DEBUG] Aborted submission: clickup_negocio_id is missing!");
      showToast("ID do negócio do ClickUp não encontrado.", "error");
      return;
    }

    if (!newTaskTitle.trim()) {
      console.warn("[DEBUG] Aborted submission: title is empty!");
      showToast("O título da tarefa é obrigatório.", "error");
      return;
    }
    if (!newTaskDueDate) {
      console.warn("[DEBUG] Aborted submission: date is empty!");
      showToast("A data de vencimento é obrigatória.", "error");
      return;
    }
    
    // Calculate final due date as milliseconds
    let finalDueDateMs;
    if (hasTime) {
      const combinedDateTimeStr = `${newTaskDueDate}T${newTaskTime}:00`;
      finalDueDateMs = new Date(combinedDateTimeStr).getTime();
    } else {
      const combinedDateTimeStr = `${newTaskDueDate}T23:59:59`;
      finalDueDateMs = new Date(combinedDateTimeStr).getTime();
    }

    if (isNaN(finalDueDateMs)) {
      console.warn("[DEBUG] Aborted submission: finalDueDateMs is NaN!");
      showToast("Data de vencimento inválida.", "error");
      return;
    }
    
    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setCreatingTask(true);
      try {
        const crmId = comercialNegocioId(finalClickupId);
        const campos = { titulo: newTaskTitle.trim(), tipo: newTaskType, data_vencimento: finalDueDateMs, due_date_time: !!hasTime, responsavel_clickup_id: newTaskAssignee ? String(newTaskAssignee) : null };
        if (crmId) campos.negocio_id = crmId;
        if (editingTask) {
          await comercialExecutar('tarefa_editar', editingTask.id, { tarefa_id: editingTask.id, expected_version: editingTask.versao_edicao, campos });
        } else {
          if (!crmId) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'NEGOCIO_INDISPONIVEL' });
          await comercialExecutar('tarefa_criar', 'nova/' + crmId, { campos });
        }
        showToast(editingTask ? "Tarefa comercial atualizada com sucesso!" : "Tarefa comercial criada com sucesso!", "success");
        setEditingTask(null);
        setShowNewTaskModal(false);
        setNewTaskTitle('');
        setNewTaskType('Ligação');
        setNewTaskDueDate('');
        setNewTaskAssignee('');
        setHasTime(false);
        setNewTaskTime('09:00');
        setSearchProposalQuery('');
        setSelectedProposalForTask(null);
        setProposalSearchResults([]);
        if (supabaseClient) fetchCommercialTasks(supabaseClient);
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao criar tarefa comercial.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO' && supabaseClient) fetchCommercialTasks(supabaseClient);
      } finally {
        setCreatingTask(false);
      }
      return;
    }
    /* B2_I13_END */
    setCreatingTask(true);
    console.log("[DEBUG] Submitting task with proposal_id:", selectedProposalForTask?.id);
    
    // Resolve project/client name dynamically (limpo sem versao)
    const rawProjectName = selectedProposalForTask?.name || selectedProposalForTask?.nome_projeto || (selectedTask ? selectedTask.name : (currentProposta ? currentProposta.nome_projeto : "Projeto"));
    const activeProjectName = getCleanBusinessName(rawProjectName);

    const payload = {
      id: editingTask?.id || null,
      proposta_id: finalPropostaId,
      clickup_negocio_id: finalClickupId,
      titulo: newTaskTitle.trim(),
      tipo: newTaskType,
      data_vencimento: finalDueDateMs,
      responsavel_clickup_id: newTaskAssignee || null,
      due_date_time: hasTime,
      nome_projeto: activeProjectName,
      clickup_subtask_id: editingTask?.clickup_subtask_id || null
    };

    // Se o usuário não limpou o campo manualmente (X) e o negócio resolvido deu undefined,
    // mas a tarefa original POSSUI um clickup_negocio_id, MANTENHA-O.
    if (!selectedProposalForTask && editingTask && editingTask.clickup_negocio_id) {
       payload.clickup_negocio_id = editingTask.clickup_negocio_id;
    }

    try {
      const method = editingTask ? 'PUT' : 'POST';
      const endpoint = editingTask ? `/api/tarefas/${editingTask.id}` : '/api/tarefas';
      console.log(`[DEBUG] Sending ${method} to ${endpoint} with payload:`, payload);
      
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...getSupabaseHeaders()
        },
        body: JSON.stringify(payload)
      });
      
      console.log("[DEBUG] API Response Status:", response.status);
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Erro ao criar/atualizar tarefa no servidor");
      }
      
      const resData = await response.json();
      console.log('[DEBUG] Resposta do servidor para criacao/edicao:', resData);
      
      showToast(editingTask ? "Tarefa comercial atualizada com sucesso!" : "Tarefa comercial criada com sucesso!", "success");
      setEditingTask(null);
      setShowNewTaskModal(false);
      setNewTaskTitle('');
      setNewTaskType('Ligação');
      setNewTaskDueDate('');
      setNewTaskAssignee('');
      setHasTime(false);
      setNewTaskTime('09:00');
      setSearchProposalQuery('');
      setSelectedProposalForTask(null);
      setProposalSearchResults([]);
      
      // Instantly load tasks to refresh local lists
      if (supabaseClient) {
        fetchCommercialTasks(supabaseClient);
      }
    } catch (err) {
      console.error("[DEBUG] Network/JS Error during submit:", err);
      showToast(err.message || "Erro ao criar tarefa comercial.", "error");
    } finally {
      setCreatingTask(false);
    }
  };

  const handleNewTaskClick = () => {
    setEditingTask(null);
    setNewTaskTitle('');
    setNewTaskType('Ligação');
    setNewTaskDueDate(new Date().toISOString().split('T')[0]);
    setNewTaskTime('09:00');
    setNewTaskAssignee('');
    setHasTime(false);
    
    if (showDrawer && clickupTaskId) {
      // Prioridade absoluta ao negócio aberto no Drawer (selectedTask.name)
      const matchedKanban = (kanbanTasks || []).find(k => String(k.id) === String(clickupTaskId) || String(k.clickup_id) === String(clickupTaskId));
      const rawBusinessName = selectedTask?.name || selectedTask?.nome || matchedKanban?.name || matchedKanban?.nome || currentProposta?.nome_projeto || "Negócio";
      const cleanBusinessName = getCleanBusinessName(rawBusinessName);
      
      const resolvedDeal = {
        id: clickupTaskId,
        clickup_negocio_id: clickupTaskId,
        name: cleanBusinessName,
        nome_projeto: cleanBusinessName
      };
      setSelectedProposalForTask(resolvedDeal);
      setSearchProposalQuery(cleanBusinessName);
    } else {
      setSelectedProposalForTask(null);
      setSearchProposalQuery('');
    }
    
    setShowNewTaskModal(true);
  };

  // Nome do negócio de uma tarefa. Ficava dentro do bloco da aba de Tarefas;
  // subiu pro corpo do componente porque o modal de resumo também precisa.
  // Depende de `propostas`/`kanbanTasks`, então não pode ir pro escopo de
  // módulo junto dos helpers puros.
  const getTaskNegocio = (task) => {
    const localProps = (typeof propostas !== 'undefined' && Array.isArray(propostas) ? propostas : []);
    let matchedProp = localProps.find(p =>
      (task.proposta_id && p.id === task.proposta_id) ||
      (task.clickup_negocio_id && p.clickup_negocio_id === task.clickup_negocio_id)
    );
    if (matchedProp) return matchedProp.nome_projeto || matchedProp.projeto || 'Projeto';
    const activeKanbanCards = (typeof kanbanTasks !== 'undefined' ? kanbanTasks : null) || [];
    const matchedKanbanCard = Array.isArray(activeKanbanCards) && activeKanbanCards.find(c =>
      c.id === task.clickup_negocio_id || c.clickup_id === task.clickup_negocio_id
    );
    if (matchedKanbanCard) return matchedKanbanCard.name || matchedKanbanCard.nome || 'Projeto';
    if (task.nome_projeto && task.nome_projeto !== 'Sem Proposta') return task.nome_projeto;
    const propObj = Array.isArray(task.propostas) ? task.propostas[0] : task.propostas;
    return propObj?.nome_projeto || task.proposta?.nome_projeto || 'Sem Projeto';
  };

  // Id do negócio no ClickUp a partir da tarefa — é o destino da atividade
  // registrada ao concluir. O fallback via proposta_id cobre tarefas antigas
  // que só têm o vínculo com a proposta.
  const getTaskNegocioId = (task) => {
    if (!task) return null;
    if (task.clickup_negocio_id) return task.clickup_negocio_id;
    const localProps = Array.isArray(propostas) ? propostas : [];
    const matchedProp = localProps.find(p => task.proposta_id && p.id === task.proposta_id);
    return matchedProp?.clickup_negocio_id || null;
  };

  const openTaskDetail = (task) => {
    if (!task) return;
    setTaskDetailId(task.id);
    setTaskDetailShowAtividade(false);
    setTaskDetailAtividadeTexto('');
    setTaskDetailSaving(false);
    setTaskDetailAnexos([]);
  };

  const closeTaskDetail = () => {
    setTaskDetailId(null);
    setTaskDetailShowAtividade(false);
    setTaskDetailAtividadeTexto('');
    setTaskDetailSaving(false);
    setTaskDetailAnexos([]);
  };

  // Mesma lógica de handleAdicionarAnexosAtividade, só que mirando o negócio
  // vinculado à TAREFA (não necessariamente o que está aberto no drawer).
  const handleAdicionarAnexosTaskDetail = (fileList, negocioId) => {
    const arquivos = Array.from(fileList || []);
    if (arquivos.length === 0) return;
    if (!negocioId) {
      showToast('Vincule um negócio a esta tarefa antes de anexar arquivos.', 'error');
      return;
    }
    const novos = arquivos.map(file => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file, status: 'uploading', result: null, error: null
    }));
    setTaskDetailAnexos(prev => [...prev, ...novos]);
    novos.forEach(async (item) => {
      try {
        const result = await uploadAtividadeAnexo({ clickupNegocioId: negocioId, file: item.file });
        setTaskDetailAnexos(prev => prev.map(a => a.id === item.id ? { ...a, status: 'done', result } : a));
      } catch (err) {
        console.error('[TAREFAS] Erro ao enviar anexo:', err);
        setTaskDetailAnexos(prev => prev.map(a => a.id === item.id ? { ...a, status: 'error', error: err?.message } : a));
      }
    });
  };

  const handleRemoverAnexoTaskDetail = (id) => {
    setTaskDetailAnexos(prev => prev.filter(a => a.id !== id));
  };

  // Conclui (ou reabre) direto, sem registrar nada — mesmo caminho do
  // checkbox da lista.
  const handleConcluirTarefa = async (task) => {
    if (!task) return;
    await toggleTaskStatus(task);
    closeTaskDetail();
  };

  // Conclui registrando o resultado no histórico do negócio vinculado, sem
  // precisar abrir o negócio.
  //
  // A ordem importa: a atividade vai PRIMEIRO. Se ela falhar, nada mudou —
  // o modal continua aberto com o erro e dá pra tentar de novo. No inverso,
  // a tarefa ficaria concluída sem registro e "refazer" inverteria o status,
  // porque toggleTaskStatus alterna em vez de setar.
  const handleConcluirTarefaComAtividade = async (task) => {
    if (!task) return;
    const anexosProntos = taskDetailAnexos.filter(a => a.status === 'done').map(a => a.result);
    const texto = taskDetailAtividadeTexto.trim() || (anexosProntos.length > 0 ? `📎 ${anexosProntos.length} anexo${anexosProntos.length > 1 ? 's' : ''}` : '');
    if (!texto) return;
    if (taskDetailAnexos.some(a => a.status === 'uploading')) {
      showToast('Aguarde o envio dos anexos terminar.', 'error');
      return;
    }

    const negocioId = getTaskNegocioId(task);
    if (!negocioId) {
      showToast('Esta tarefa não está vinculada a um negócio — edite a tarefa para vincular.', 'error');
      return;
    }

    /* B2_I13_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setTaskDetailSaving(true);
      try {
        const crmId = comercialNegocioId(negocioId);
        if (!crmId) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'NEGOCIO_INDISPONIVEL' });
        // the task context of the activity (type/title) is read by the server from the stored task of THIS opportunity
        await comercialExecutar('atividade_criar', 'tarefa/' + task.id, { negocio_id: crmId, origem: 'tarefa', tarefa_id: task.id, texto, anexos: anexosParaComando(anexosProntos) });
        const drawerId = String(clickupTaskId || '').replace('#', '').trim();
        if (showDrawer && drawerId && drawerId === String(negocioId).replace('#', '').trim()) fetchAtividades(clickupTaskId);
        if (task.status !== 'concluida') await toggleTaskStatus(task);
        showToast('Tarefa concluída e atividade registrada!', 'success');
        closeTaskDetail();
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao registrar atividade.'), 'error');
      } finally {
        setTaskDetailSaving(false);
      }
      return;
    }
    /* B2_I13_END */
    setTaskDetailSaving(true);
    try {
      // `texto` é só o corpo digitado — a referência à tarefa vai nos
      // campos estruturados (origem/tarefaTipo/tarefaTitulo), não mais
      // embutida na string como no formato de ontem.
      const atividadeSalva = await createAtividade({
        clickupNegocioId: negocioId,
        texto,
        autorNome: userProfile?.username || userProfile?.email || null,
        autorClickupId: userProfile?.id ? String(userProfile.id) : null,
        origem: 'tarefa',
        tarefaId: task.id || null,
        tarefaTipo: task.tipo || null,
        tarefaTitulo: task.titulo || null,
        anexos: anexosProntos
      });

      // Se o negócio já estiver aberto no drawer, atualiza a lista de
      // atividades pra não ficar desatualizada atrás do modal.
      const drawerId = String(clickupTaskId || '').replace('#', '').trim();
      if (showDrawer && drawerId && drawerId === String(negocioId).replace('#', '').trim()) {
        fetchAtividades(clickupTaskId);
      }

      // Atividade gravada: se o status falhar agora, o próprio
      // toggleTaskStatus reverte o otimista e avisa — sobra só a atividade
      // registrada, que é reparável.
      if (task.status !== 'concluida') {
        await toggleTaskStatus(task);
      }
      const syncPendente = atividadeSalva?.[0]?.sincronizacao && atividadeSalva[0].sincronizacao !== 'sincronizado';
      showToast(syncPendente ? 'Atividade salva no CRM. O envio ao ClickUp precisa de verificação; não é necessário cadastrar novamente.' : 'Tarefa concluída e atividade registrada no negócio!', syncPendente ? 'warning' : 'success');
      closeTaskDetail();
    } catch (err) {
      console.error('[TAREFAS] Erro ao concluir com atividade:', err);
      showToast(err?.message || 'Erro ao registrar atividade.', 'error');
    } finally {
      setTaskDetailSaving(false);
    }
  };

  const handleEditTaskClick = (task) => {
    console.log('[DEBUG] Inicializando modal de edição. Tarefa:', task);
    setEditingTask(task);
    
    const d = new Date(task.data_vencimento);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    setNewTaskTitle(task.titulo || '');
    setNewTaskType(task.tipo || 'Ligação');
    setNewTaskDueDate(`${year}-${month}-${day}`);
    setNewTaskTime(`${hours}:${minutes}`);
    setNewTaskAssignee(task.responsavel_clickup_id || '');
    setHasTime(task.due_date_time || false);
    
    const listaParaBusca = kanbanTasks || [];

    const negocioCorrespondente = listaParaBusca.find(p => {
      if (!p) return false;
      return task.clickup_negocio_id && String(p.id).trim().toLowerCase() === String(task.clickup_negocio_id).trim().toLowerCase();
    });

    const rawName = negocioCorrespondente?.name || negocioCorrespondente?.nome || task.nome_projeto || "Projeto";
    const cleanBusinessName = getCleanBusinessName(rawName);

    const activeDeal = {
      id: task.clickup_negocio_id,
      clickup_negocio_id: task.clickup_negocio_id,
      name: cleanBusinessName,
      nome_projeto: cleanBusinessName
    };
    
    if (typeof setSelectedProposalForTask === 'function') {
      setSelectedProposalForTask(activeDeal);
    }
    setSearchProposalQuery(cleanBusinessName);
    
    setShowNewTaskModal(true);
  };

  // TaskCard — componente de linha da tarefa (badge de tipo, vencimento,
  // avatar do responsável, ações). Vive no corpo do componente (não mais
  // dentro da IIFE da aba Tarefas) porque é reaproveitado em dois lugares:
  // a lista geral de Tarefas e a lista "Tarefas Associadas" dentro do
  // drawer do negócio — o pedido era esses dois lugares ficarem idênticos,
  // e a garantia mais forte disso é serem literalmente o mesmo componente,
  // não duas cópias que podem divergir de novo no futuro.
  const TaskCard = ({ task }) => {
    const isDone = task.status === 'concluida';
    const tc = getTaskTypeConfig(task.tipo);
    const matchedUser = vendedores.find(v => String(v.id) === String(task.responsavel_clickup_id));
    const assigneeName = matchedUser ? matchedUser.nome : '—';
    const negocio = getTaskNegocio(task);
    const { label: dateLabel, urgent } = formatTaskDate(task.data_vencimento);
    const uc = TASK_URGENCY_CONFIG[urgent] || TASK_URGENCY_CONFIG.normal;

    return (
      <div
        onClick={() => openTaskDetail(task)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTaskDetail(task); } }}
        role="button"
        tabIndex={0}
        title="Ver detalhes da tarefa"
        className={`group relative bg-white dark:bg-slate-800 rounded-xl border transition-all duration-200 hover:shadow-md cursor-pointer ${isDone ? 'opacity-60 border-slate-200 dark:border-slate-700' : 'border-slate-200/80 dark:border-slate-700/80 hover:border-indigo-200/80'}`}
      >
        {/* Barra lateral de urgência */}
        {!isDone && (
          <div className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full ${
            urgent === 'overdue' ? 'bg-rose-500' : urgent === 'today' ? 'bg-amber-400' : urgent === 'soon' ? 'bg-sky-400' : 'bg-slate-200 dark:bg-slate-600'
          }`} />
        )}

        <div className="flex items-center gap-3 p-4 pl-5">
          {/* Checkbox — stopPropagation no wrapper (e não no input) pra
              não interferir na ordem de eventos do onChange: preserva o
              atalho de concluir direto sem abrir o modal de detalhes. */}
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isDone}
              onChange={() => toggleTaskStatus(task)}
              className="w-4.5 h-4.5 rounded-full border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
            />
          </div>

          {/* Conteúdo Principal */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {/* Título */}
                <p className={`text-sm font-bold leading-snug truncate ${isDone ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}>
                  {task.titulo}
                </p>
                {/* Negócio associado */}
                {negocio && negocio !== 'Sem Projeto' && (
                  <p className="text-[11px] text-slate-400 dark:text-slate-400 font-medium mt-0.5 truncate flex items-center gap-1">
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {negocio}
                  </p>
                )}
              </div>

              {/* Lado direito: metadados + ações */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Badge de tipo */}
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${tc.bg} ${tc.text} ${tc.border}`}>
                  {tc.Icon && <tc.Icon size={10} />}
                  <span>{task.tipo}</span>
                </span>

                {/* Data de vencimento */}
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${uc.bg} ${uc.text}`}>
                  {urgent === 'overdue' && (
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  {urgent === 'today' && (
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {dateLabel}
                </span>

                {/* Avatar do responsável — mesmo componente colorido por pessoa usado no Kanban/Empresas */}
                <div className="flex items-center gap-1.5" title={assigneeName}>
                  {typeof AvatarInicial !== 'undefined' ? (
                    <AvatarInicial nome={assigneeName !== '—' ? assigneeName : ''} size="xs" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-[9px] font-extrabold flex-shrink-0 shadow-sm">
                      {assigneeName !== '—' ? assigneeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'}
                    </div>
                  )}
                </div>

                {/* Ações — stopPropagation no container cobre editar e
                    excluir de uma vez, pra não abrir o modal de
                    detalhes junto. Chips sempre visíveis (sem
                    opacity-0 no hover): antes o card parecia inerte
                    até passar o mouse, principalmente no dark mode. */}
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleEditTaskClick(task)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30 dark:hover:bg-indigo-500/25 transition-colors cursor-pointer"
                    title="Editar"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30 dark:hover:bg-rose-500/25 transition-colors cursor-pointer"
                    title="Excluir"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (activeTab === 'tasks' && supabaseClient) {
      const isSilent = hasLoadedTasksOnceRef.current;
      if (!isSilent) {
        setLoadingTasks(true);
      }
      Promise.all([
        fetchCommercialTasks(supabaseClient, isSilent),
        fetchKanbanData(),
        loadVendedores()
      ]).finally(() => {
        hasLoadedTasksOnceRef.current = true;
        setLoadingTasks(false);
      });
    }
  }, [activeTab, supabaseClient]);

  // Fonte única para novas associações de fabricante em R.O. A lista é
  // atualizada a cada abertura dos fluxos de criação/edição e falha fechada.
  const carregarFabricantesRoAtivos = useCallback(async () => {
    const fetchId = ++fabricantesRoFetchIdRef.current;
    setFabricantesRoStatus({ loading: true, error: null, ready: false });
    const token = safeStorage.getItem('crm_user_clickup_token');
    if (!token) {
      setFabricantesRo([]);
      setFabricantesRoStatus({ loading: false, error: 'Sessão indisponível.', ready: false });
      return false;
    }
    try {
      const response = await fetch('/api/catalogo/fabricantes', {
        headers: { ...await getCrmHeaders() },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !Array.isArray(payload?.data)) {
        throw new Error('Catálogo indisponível');
      }
      if (fetchId !== fabricantesRoFetchIdRef.current) return false;
      const ativos = normalizarFabricantesAtivosRo
        ? normalizarFabricantesAtivosRo(payload.data)
        : payload.data.filter((fabricante) => fabricante?.ativo === true);
      setFabricantesRo(ativos);
      setFabricantesRoStatus({ loading: false, error: null, ready: true });
      return true;
    } catch {
      if (fetchId !== fabricantesRoFetchIdRef.current) return false;
      setFabricantesRo([]);
      setFabricantesRoStatus({ loading: false, error: 'Não foi possível carregar os fabricantes ativos.', ready: false });
      return false;
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'ros') carregarFabricantesRoAtivos();
  }, [activeTab, carregarFabricantesRoAtivos]);

  // R.O. (Task 3) - Carrega a lista e o resumo agregado simultaneamente com proteção contra respostas obsoletas
  const loadRegistrosOportunidade = useCallback(async (filtersToUse = rosState.filters, pageToUse = rosState.page) => {
    const fetchId = ++rosFetchIdRef.current;
    setRosState((prev) => ({ ...prev, loading: true, error: null }));
    setRosResumo((prev) => ({ ...prev, loading: true, error: null }));

    // api-ros autentica por sessão Supabase (Bearer); getSupabaseHeaders devolve o token ClickUp do navegador e daria 401 «Sessão Supabase requerida».
    const getHeaders = getCrmHeaders;
    const fetchLista = fetchRegistrosOportunidade
      ? fetchRegistrosOportunidade(filtersToUse, pageToUse, 50, { getHeaders })
      : Promise.resolve({ data: [], total: 0, pagina: pageToUse, limite: 50 });
    const fetchResumo = fetchResumoRos
      ? fetchResumoRos(filtersToUse, { getHeaders })
      : Promise.resolve(null);

    const [resLista, resResumo] = await Promise.allSettled([fetchLista, fetchResumo]);
    if (fetchId !== rosFetchIdRef.current) return;

    if (resLista.status === 'fulfilled') {
      const val = resLista.value;
      setRosState((prev) => ({
        ...prev,
        rows: val.data,
        total: val.total,
        page: val.pagina,
        loading: false,
        error: null,
      }));
    } else {
      setRosState((prev) => ({
        ...prev,
        loading: false,
        error: resLista.reason?.message || 'Erro ao carregar lista de R.Os.',
      }));
    }

    if (resResumo.status === 'fulfilled' && resResumo.value) {
      const valResumo = resResumo.value;
      setRosResumo({
        total: valResumo.total,
        aguardando_aprovacao: valResumo.aguardando_aprovacao,
        renovacoes_em_analise: valResumo.renovacoes_em_analise,
        vencem_15_dias: valResumo.vencem_15_dias,
        loading: false,
        error: null,
      });
    } else {
      setRosResumo((prev) => ({
        ...prev,
        loading: false,
        error: resResumo.status === 'rejected' ? (resResumo.reason?.message || 'Indisponível') : null,
      }));
    }
  }, [rosState.filters, rosState.page]);

  useEffect(() => {
    if (activeTab === 'ros') {
      loadRegistrosOportunidade(rosState.filters, rosState.page);
    }
  }, [activeTab, rosState.filters, rosState.page, loadRegistrosOportunidade]);

  useEffect(() => {
    let ativo = true;
    // Só consulta quando a identidade já foi configurada (CrmUsuarios.configure roda junto de setSupabaseClient, após /api/config). Antes disso
    // headers() falha com CONFIGURACAO_INVALIDA e o catch gravaria «comum» definitivamente (sem Editar/Excluir/Restaurar) ao abrir a página direto na aba R.Os.
    if (activeTab !== 'ros' || !fetchPermissaoRos || !supabaseClient) return undefined;
    fetchPermissaoRos({ getSupabaseHeaders: getCrmHeaders })
      .then((permissao) => {
        if (ativo) setPermissaoRos(permissao);
      })
      .catch(() => {
        if (ativo) {
          setPermissaoRos({ role: 'comum', pode_excluir: false, pode_restaurar: false, pode_editar: false, pode_excluir_cadeia: false });
        }
      });
    return () => { ativo = false; };
  }, [activeTab, userClickUpToken, supabaseClient]);

  const handleNovaRoSucesso = useCallback((novoRegistro) => {
    showToast("Registro de Oportunidade criado com sucesso!", "success");
    setModalNovaRoAberto(false);
    setModalNovaRoOportunidadeFixa(null);
    loadRegistrosOportunidade(rosState.filters, rosState.page);
    window.dispatchEvent(new CustomEvent('ro-criada', { detail: novoRegistro }));
  }, [loadRegistrosOportunidade, rosState.filters, rosState.page, showToast]);

  const handleAbrirNovaRoOportunidade = useCallback(async (task) => {
    if (!task) return;
    if (!supabaseClient) {
      showToast("Esta oportunidade ainda não está sincronizada ao CRM. Cadastre ou sincronize a oportunidade antes de criar uma R.O.", "warning");
      return;
    }

    try {
      const consultarCrm = async (criterio) => {
        let query = supabaseClient
          .from("negocios")
          .select("id, nome, conta_id, estagio, contas(id, nome, razao_social)");
        if (criterio.id) {
          query = query.eq("id", criterio.id);
        } else if (criterio.clickup_negocio_id) {
          query = query.eq("clickup_negocio_id", criterio.clickup_negocio_id);
        } else {
          return null;
        }
        const { data, error } = await query.maybeSingle();
        if (error) throw error;
        return data || null;
      };

      const resultado = await prepararAberturaNovaRo(task, consultarCrm);
      if (!resultado.negocio) {
        showToast(resultado.message, "warning");
        return;
      }

      const negocio = resultado.negocio;
      setModalNovaRoOportunidadeFixa(negocio);
      carregarFabricantesRoAtivos();
      setModalNovaRoAberto(true);
    } catch (err) {
      console.warn("Erro ao consultar oportunidade CRM para R.O.:", err);
      showToast("Esta oportunidade ainda não está sincronizada ao CRM. Cadastre ou sincronize a oportunidade antes de criar uma R.O.", "warning");
    }
  }, [supabaseClient, showToast, carregarFabricantesRoAtivos]);

  const handleNavegarParaRos = useCallback((novosFiltros = {}, roParaDetalhe = null) => {
    setActiveTab('ros');
    setRosState((prev) => ({
      ...prev,
      rows: roParaDetalhe
        ? [...prev.rows.filter((row) => row.id !== roParaDetalhe.id), roParaDetalhe]
        : prev.rows,
      filters: { ...novosFiltros },
      page: 1,
      selectedId: roParaDetalhe?.id || null,
    }));
    loadRegistrosOportunidade(novosFiltros, 1);
  }, [loadRegistrosOportunidade]);

  const handleVerDetalheRoDaOportunidade = useCallback((item) => {
    const ro = item?.ro;
    if (!ro?.id) return;
    const negocioId = ro.negocio_id || selectedTask?.crm_negocio_id;
    setShowEditNegocioDrawerModal(false);
    setShowDrawer(false);
    setClickupTaskId('');
    handleNavegarParaRos(negocioId ? { negocio_id: negocioId } : {}, ro);
  }, [handleNavegarParaRos, selectedTask?.crm_negocio_id]);

  useEffect(() => {
    const handleEventoAbrirNovaRo = (e) => {
      const opp = e.detail?.negocio || e.detail?.oportunidade || (e.detail?.negocio_id ? { id: e.detail.negocio_id } : null);
      if (opp) handleAbrirNovaRoOportunidade(opp);
    };
    window.addEventListener('abrir-nova-ro', handleEventoAbrirNovaRo);
    return () => window.removeEventListener('abrir-nova-ro', handleEventoAbrirNovaRo);
  }, [handleAbrirNovaRoOportunidade]);

  // Armazenamento em memória para filtros instantâneos sem atraso
  const rawProposalsRef = useRef([]);
  const rawCommercialRef = useRef([]);

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.substring(0, 10).split('-');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  };

  // Calcula início/fim do trimestre atual (e do mesmo trimestre no ano anterior, para comparação)
  const getCurrentQuarterRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
    const qEndDate = new Date(year, qStartMonth + 3, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return {
      start: `${year}-${pad(qStartMonth + 1)}-01`,
      end: `${year}-${pad(qEndDate.getMonth() + 1)}-${pad(qEndDate.getDate())}`,
      compStart: `${year - 1}-${pad(qStartMonth + 1)}-01`,
      compEnd: `${year - 1}-${pad(qEndDate.getMonth() + 1)}-${pad(qEndDate.getDate())}`
    };
  };

  const generateMonthlyTimeline = (start, end, wonCurrent, compStart, compEnd, wonComp) => {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    // Identificar se a comparação é de 1 ano completo (ex: 2026 vs 2025)
    const isSingleYearCurrent = start && end && start.getFullYear() === end.getFullYear();
    const isSingleYearComp = compStart && compEnd && compStart.getFullYear() === compEnd.getFullYear();
    
    let labels = [];
    let currentValues = [];
    let compValues = [];
    let currentRawValues = [];
    let compRawValues = [];

    const currentYear = start.getFullYear();
    const compYear = compStart ? compStart.getFullYear() : null;

    if (isSingleYearCurrent && (isSingleYearComp || !compStart)) {
      // 12 meses corridos de Jan a Dez
      labels = monthNames.slice();
      
      for (let m = 0; m < 12; m++) {
        // Soma do mês no ano atual
        const sumCurrent = (wonCurrent || []).reduce((acc, p) => {
          const dateToUse = p.data_fechamento || p.created_at;
          if (!dateToUse) return acc;
          const pd = parseLocalDate(dateToUse);
          if (pd && pd.getFullYear() === currentYear && pd.getMonth() === m) {
            return acc + (parseFloat(p.total_proposta) || 0);
          }
          return acc;
        }, 0);

        currentRawValues.push(sumCurrent);
        currentValues.push(sumCurrent / 1000000);

        // Soma do mês no ano comparativo
        if (compStart && compEnd && wonComp) {
          const sumComp = wonComp.reduce((acc, p) => {
            const dateToUse = p.data_fechamento || p.created_at;
            if (!dateToUse) return acc;
            const pd = parseLocalDate(dateToUse);
            if (pd && pd.getFullYear() === compYear && pd.getMonth() === m) {
              return acc + (parseFloat(p.total_proposta) || 0);
            }
            return acc;
          }, 0);
          compRawValues.push(sumComp);
          compValues.push(sumComp / 1000000);
        }
      }
    } else {
      // Intervalo customizado dinâmico
      let cur = new Date(start.getFullYear(), start.getMonth(), 1);
      const last = new Date(end.getFullYear(), end.getMonth(), 1);
      let count = 0;
      while (cur <= last && count < 60) {
        count++;
        const y = cur.getFullYear();
        const m = cur.getMonth();
        const label = `${monthNames[m]} ${String(y).slice(-2)}`;
        labels.push(label);

        const sumMonth = (wonCurrent || []).reduce((acc, p) => {
          const dateToUse = p.data_fechamento || p.created_at;
          if (!dateToUse) return acc;
          const pd = parseLocalDate(dateToUse);
          if (pd && pd.getFullYear() === y && pd.getMonth() === m) {
            return acc + (parseFloat(p.total_proposta) || 0);
          }
          return acc;
        }, 0);

        currentRawValues.push(sumMonth);
        currentValues.push(sumMonth / 1000000);
        cur.setMonth(cur.getMonth() + 1);
      }
    }

    return { 
      labels, 
      values: currentValues, 
      compValues: compValues.length > 0 ? compValues : null,
      currentRawValues,
      compRawValues: compRawValues.length > 0 ? compRawValues : null,
      currentYearLabel: String(currentYear),
      compYearLabel: compYear ? String(compYear) : null
    };
  };

  // Motor síncrono de filtragem em memória - Execução instantânea (< 1ms)
  const applyFilterRange = (startStr, endStr, compStartStr = compareStartDate, compEndStr = compareEndDate) => {
    currentDateFilterRef.current = {
      start: startStr,
      end: endStr,
      compStart: compStartStr,
      compEnd: compEndStr
    };
    try {
      localStorage.setItem('spa_selected_start', startStr);
      localStorage.setItem('spa_selected_end', endStr);
      if (compStartStr !== undefined) localStorage.setItem('spa_selected_comp_start', compStartStr);
      if (compEndStr !== undefined) localStorage.setItem('spa_selected_comp_end', compEndStr);
    } catch (e) {}

    setStartDate(startStr);
    setEndDate(endStr);
    if (compStartStr !== undefined) setCompareStartDate(compStartStr);
    if (compEndStr !== undefined) setCompareEndDate(compEndStr);

    const start = parseLocalDate(startStr) || new Date(2000, 0, 1);
    const end = parseLocalDate(endStr) || new Date(2100, 0, 1);
    end.setHours(23, 59, 59, 999);

    const allProps = rawProposalsRef.current || [];
    const allItens = rawCommercialRef.current || [];

    // 1. Filtrar propostas do período
    // Blindagem: além do texto de situacao/data_fechamento na própria proposta,
    // exige que o negócio pai esteja de fato no estágio correspondente
    // (negocios.estagio é a fonte real da verdade — ver migration 20260819e).
    // Isso evita que uma proposta com data_fechamento desalinhada (dado legado
    // ou qualquer escrita futura que escape do trigger de consistência) volte
    // a inflar os números do relatório.
    const estagioDoNegocio = (p) => {
      const cid = String(p?.clickup_negocio_id || '').replace('#', '').trim();
      return cid ? negociosEstagioRef.current.get(cid) : undefined;
    };

    const isWonProp = (p) => {
      if (!p || !p.situacao || !p.data_fechamento) return false;
      const s = p.situacao.trim().toLowerCase();
      if (s !== 'ganho' && s !== 'selecionada') return false;
      return estagioDoNegocio(p) === 'Ganho';
    };

    const isLostProp = (p) => {
      if (!p || !p.situacao) return false;
      const s = p.situacao.trim().toLowerCase();
      if (s !== 'perdido' && s !== 'cancelado' && s !== 'desconsiderada') return false;
      return estagioDoNegocio(p) === 'Perdido';
    };

    const currentProps = allProps.filter(p => {
      const dateToUse = p.data_fechamento || p.created_at;
      if (!dateToUse) return false;
      const d = parseLocalDate(dateToUse);
      return d && d >= start && d <= end;
    });

    const wonCurrent = currentProps.filter(isWonProp);
    const lostCurrent = currentProps.filter(isLostProp);

    setWonProposals(wonCurrent);

    // 2. Filtrar itens de propostas comerciais do período
    const filteredItens = allItens.filter(item => {
      const prop = Array.isArray(item.propostas) ? item.propostas[0] : item.propostas;
      if (!prop) return true;
      const dtStr = prop.data_fechamento || prop.created_at;
      if (!dtStr) return true;
      const d = parseLocalDate(dtStr);
      if (!d) return true;
      return d >= start && d <= end;
    });

    setCommercialData(filteredItens);

    // 3. Métricas Executivas e KPIs
    const wonCountCurrent = wonCurrent.length;
    const wonValueCurrent = wonCurrent.reduce((acc, p) => acc + (parseFloat(p.total_proposta) || 0), 0);
    const lostCountCurrent = lostCurrent.length;
    const lostValueCurrent = lostCurrent.reduce((acc, p) => acc + (parseFloat(p.total_proposta) || 0), 0);
    const closedCountCurrent = wonCountCurrent + lostCountCurrent;
    const convRateCurrent = closedCountCurrent > 0 ? (wonCountCurrent / closedCountCurrent) * 100 : (wonCountCurrent > 0 ? 100 : 0);
    const ticketMedioCurrent = wonCountCurrent > 0 ? wonValueCurrent / wonCountCurrent : 0;

    // Ciclo Médio com benchmarks calibrados e cálculo real
    let defaultCycle = 58;
    const y = start.getFullYear();
    if (y === 2023) defaultCycle = 21;
    else if (y === 2024) defaultCycle = 62;
    else if (y === 2025) defaultCycle = 82;
    else if (y === 2026) defaultCycle = 86;

    let totalCycleDays = 0;
    let cycleCount = 0;
    wonCurrent.forEach(p => {
      if (p.data_inicio && p.data_fechamento) {
        const ds = parseLocalDate(p.data_inicio);
        const dc = parseLocalDate(p.data_fechamento);
        if (ds && dc) {
          const diff = Math.round(Math.abs(dc - ds) / (1000 * 60 * 60 * 24));
          if (diff > 0 && diff < 365) {
            totalCycleDays += diff;
            cycleCount++;
          }
        }
      }
    });
    const avgCycleDaysCurrent = cycleCount > 0 ? Math.round(totalCycleDays / cycleCount) : defaultCycle;

    // Comparativos (Delta e YoY)
    let wonComp = [];
    let lostComp = [];
    let wonCountComp = 0;
    let wonValueComp = 0;
    let lostCountComp = 0;
    let lostValueComp = 0;
    let convRateComp = 0;
    let ticketMedioComp = 0;
    let avgCycleDaysComp = 0;

    let wonQtyDiff = null;
    let wonQtyPct = null;
    let wonValDiff = null;
    let wonValPct = null;
    let avgCycleDaysDiff = null;
    let ticketMedioDiff = null;
    let ticketMedioPct = null;
    let lostQtyDiff = null;
    let lostValDiff = null;
    let convRateDiff = null;
    let compLabel = null;
    let currentLabel = String(start.getFullYear());

    const compStart = parseLocalDate(compStartStr);
    const compEnd = parseLocalDate(compEndStr);
    if (compStart && compEnd) {
      compEnd.setHours(23, 59, 59, 999);
      const compProps = allProps.filter(p => {
        const dateToUse = p.data_fechamento || p.created_at;
        if (!dateToUse) return false;
        const d = parseLocalDate(dateToUse);
        return d && d >= compStart && d <= compEnd;
      });

      wonComp = compProps.filter(isWonProp);
      lostComp = compProps.filter(isLostProp);

      wonCountComp = wonComp.length;
      wonValueComp = wonComp.reduce((acc, p) => acc + (parseFloat(p.total_proposta) || 0), 0);
      lostCountComp = lostComp.length;
      lostValueComp = lostComp.reduce((acc, p) => acc + (parseFloat(p.total_proposta) || 0), 0);
      const closedCountComp = wonCountComp + lostCountComp;
      convRateComp = closedCountComp > 0 ? (wonCountComp / closedCountComp) * 100 : (wonCountComp > 0 ? 100 : 0);
      ticketMedioComp = wonCountComp > 0 ? wonValueComp / wonCountComp : 0;

      // Ciclo Médio do período comparativo
      let defaultCompCycle = 58;
      const cy = compStart.getFullYear();
      if (cy === 2023) defaultCompCycle = 21;
      else if (cy === 2024) defaultCompCycle = 62;
      else if (cy === 2025) defaultCompCycle = 82;
      else if (cy === 2026) defaultCompCycle = 86;

      let totalCompCycleDays = 0;
      let compCycleCount = 0;
      wonComp.forEach(p => {
        if (p.data_inicio && p.data_fechamento) {
          const ds = parseLocalDate(p.data_inicio);
          const dc = parseLocalDate(p.data_fechamento);
          if (ds && dc) {
            const diff = Math.round(Math.abs(dc - ds) / (1000 * 60 * 60 * 24));
            if (diff > 0 && diff < 365) {
              totalCompCycleDays += diff;
              compCycleCount++;
            }
          }
        }
      });
      avgCycleDaysComp = compCycleCount > 0 ? Math.round(totalCompCycleDays / compCycleCount) : defaultCompCycle;

      wonQtyDiff = wonCountCurrent - wonCountComp;
      wonQtyPct = wonCountComp > 0 ? ((wonCountCurrent - wonCountComp) / wonCountComp) * 100 : (wonCountCurrent > 0 ? 100 : 0);
      wonValDiff = wonValueCurrent - wonValueComp;
      wonValPct = wonValueComp > 0 ? ((wonValueCurrent - wonValueComp) / wonValueComp) * 100 : (wonValueCurrent > 0 ? 100 : 0);
      ticketMedioDiff = ticketMedioCurrent - ticketMedioComp;
      ticketMedioPct = ticketMedioComp > 0 ? ((ticketMedioCurrent - ticketMedioComp) / ticketMedioComp) * 100 : (ticketMedioCurrent > 0 ? 100 : 0);
      avgCycleDaysDiff = avgCycleDaysCurrent - avgCycleDaysComp;
      lostQtyDiff = lostCountCurrent - lostCountComp;
      lostValDiff = lostValueCurrent - lostValueComp;
      convRateDiff = convRateCurrent - convRateComp;
      compLabel = compStart.getFullYear() === compEnd.getFullYear() ? String(compStart.getFullYear()) : 'Período Comp.';
    }

    // Timeline Sazonal (geração de 1 ou 2 séries)
    const timelineData = generateMonthlyTimeline(start, end, wonCurrent, compStart, compEnd, wonComp);

    const newBiMetrics = {
      wonCount: wonCountCurrent,
      wonValue: wonValueCurrent,
      wonCountComp,
      wonValueComp,
      avgCycleDays: avgCycleDaysCurrent,
      avgCycleDaysComp,
      ticketMedio: ticketMedioCurrent,
      ticketMedioComp,
      wonQtyDiff,
      wonQtyPct,
      wonValDiff,
      wonValPct,
      avgCycleDaysDiff,
      ticketMedioDiff,
      ticketMedioPct,
      lostCount: lostCountCurrent,
      lostCountComp,
      lostValue: lostValueCurrent,
      lostValueComp,
      lostQtyDiff,
      lostValDiff,
      convRate: convRateCurrent,
      convRateComp,
      convRateDiff,
      compLabel,
      currentLabel,
      seasonalityLabels: timelineData.labels,
      seasonalityValues: timelineData.values,
      seasonalityCompValues: timelineData.compValues,
      currentYearLabel: timelineData.currentYearLabel,
      compYearLabel: timelineData.compYearLabel
    };
    // Mesmo motivo do stabilizeByValue acima: sem essa comparação, todo poll
    // de 3 em 3 min (mesmo sem mudança real de dado) trocava a referência de
    // biMetrics, cuja seasonalityLabels/Values/CompValues estão nas
    // dependências do useEffect que recria os gráficos — causando o "piscar".
    setBiMetrics(prev => (JSON.stringify(prev) === JSON.stringify(newBiMetrics)) ? prev : newBiMetrics);
  };

  // Carregar dados brutos para o painel de relatórios (uma única busca paralela)
  const loadDashboardData = async (client = supabaseClient, silent = false, forceRefresh = true) => {
    if (!client) return;
    if (rawProposalsRef.current.length === 0 && !silent) {
      setLoadingDashboard(true);
    }
    setDashboardFetching(true);
    try {
      if (forceRefresh || rawProposalsRef.current.length === 0) {
        lastDashboardFetchAtRef.current = Date.now();

        // Filtro de data no servidor (período atual + comparativo, união dos
        // dois) em vez de buscar a tabela inteira sempre. `data_fechamento.is.null`
        // é mantido no OR pra preservar exatamente o fallback pra created_at que
        // o filtro client-side (applyFilterRange, abaixo) já fazia — validado
        // batendo 100% com a lógica antiga contra os dados reais de produção
        // (Ano Atual/Comparativo/Todo Histórico) antes de subir isso.
        const filt = currentDateFilterRef.current;
        const boundsList = [filt.start, filt.end, filt.compStart, filt.compEnd].filter(Boolean);
        const lowerBound = boundsList.length ? boundsList.reduce((a, b) => (a < b ? a : b)) : '2000-01-01';
        const upperBound = boundsList.length ? boundsList.reduce((a, b) => (a > b ? a : b)) : '2100-01-01';
        const dateOrFilter = `data_fechamento.is.null,and(data_fechamento.gte.${lowerBound},data_fechamento.lte.${upperBound})`;
        lastFetchedBoundsRef.current = { lower: lowerBound, upper: upperBound };

        // itens_proposta continua sem filtro de data no servidor: testado ao
        // vivo contra o Supabase real, tanto o filtro por lista de ids
        // (.in(), estoura limite de tamanho de URL a partir de ~94 propostas —
        // pouco pra um range normal, "Ano Atual" já tem 262) quanto o filtro
        // direto na tabela relacionada via join (`propostas!inner(...)` dentro
        // de um .or()) — essa versão do PostgREST não aceita coluna de tabela
        // relacionada dentro da árvore lógica do or(). Tabela pequena hoje
        // (1.266 linhas) — não compensa o risco de uma solução mais complexa
        // (função no banco via RPC) pra esse ganho.
        const [propsRes, itensRes, negociosRes] = await Promise.all([
          client.from('propostas').select('*').or(dateOrFilter).order('created_at', { ascending: false }),
          client.from('itens_proposta').select(`
            quantidade,
            preco_unitario,
            distribuidor_id,
            produto_id,
            propostas(created_at, data_fechamento, situacao, clickup_negocio_id),
            distribuidores(nome),
            produtos(nome, fabricante)
          `),
          client.from('negocios').select('clickup_negocio_id, estagio')
        ]);
        if (propsRes.error) throw propsRes.error;
        if (itensRes.error) throw itensRes.error;
        if (negociosRes.error) throw negociosRes.error;
        rawProposalsRef.current = propsRes.data || [];
        rawCommercialRef.current = itensRes.data || [];

        const estagioMap = new Map();
        (negociosRes.data || []).forEach(n => {
          const cid = String(n.clickup_negocio_id || '').replace('#', '').trim();
          if (cid) estagioMap.set(cid, n.estagio);
        });
        negociosEstagioRef.current = estagioMap;
      }

      const activeF = currentDateFilterRef.current;
      applyFilterRange(activeF.start, activeF.end, activeF.compStart, activeF.compEnd);
    } catch (err) {
      console.error("Erro ao carregar dados do dashboard:", err);
    } finally {
      if (!silent) setLoadingDashboard(false);
      setDashboardFetching(false);
    }
  };

  // Agregação em tempo real dos produtos mais vendidos para propostas GANHAS
  const topProductsAggregatedStableRef = useRef(null);
  const topProductsAggregated = useMemo(() => {
    if (!commercialData || commercialData.length === 0) return stabilizeByValue(topProductsAggregatedStableRef, []);
    
    // Filtrar apenas itens de propostas com situação GANHO ou SELECIONADA, e cujo
    // negócio pai esteja de fato em estágio Ganho (mesma blindagem de isWonProp
    // em applyFilterRange — ver migration 20260819e).
    const wonItems = commercialData.filter(item => {
      const prop = Array.isArray(item.propostas) ? item.propostas[0] : item.propostas;
      const sit = prop?.situacao;
      if (!sit || !prop?.data_fechamento) return false;
      const s = sit.trim().toLowerCase();
      if (s !== 'ganho' && s !== 'selecionada') return false;
      const cid = String(prop?.clickup_negocio_id || '').replace('#', '').trim();
      return cid && negociosEstagioRef.current.get(cid) === 'Ganho';
    });

    const groups = {};
    let totalVal = 0;
    let totalQty = 0;

    wonItems.forEach(item => {
      const prodObj = Array.isArray(item.produtos) ? item.produtos[0] : item.produtos;
      const name = (prodObj?.nome || prodObj?.fabricante || 'OUTROS PRODUTOS').trim().toUpperCase();
      if (name === 'TASK ID' || name === 'TASK NAME') return;
      const qty = parseInt(item.quantidade) || 1;
      const subtotal = (parseFloat(item.preco_unitario) || 0) * qty;

      if (!groups[name]) {
        groups[name] = { name, val: 0, qty: 0 };
      }
      groups[name].val += subtotal;
      groups[name].qty += qty;
      totalVal += subtotal;
      totalQty += qty;
    });

    const result = Object.values(groups).map((g, idx) => {
      const pctValNum = totalVal > 0 ? (g.val / totalVal) * 100 : 0;
      const pctQtyNum = totalQty > 0 ? (g.qty / totalQty) * 100 : 0;
      return {
        ...g,
        // Mesma paleta compartilhada dos gráficos de Distribuidor/Fabricante
        // (chartColors/chartBorderColors, topo do arquivo) — antes usava uma
        // paleta própria, o que fazia o gráfico de Produtos destoar
        // visualmente dos outros dois (cores diferentes, contorno diferente).
        color: chartColors[idx % chartColors.length],
        borderColor: chartBorderColors[idx % chartBorderColors.length],
        pctValNum,
        pctQtyNum,
        pctValStr: `${pctValNum.toFixed(1)}%`,
        pctQtyStr: `${pctQtyNum.toFixed(1)}%`,
        pctStr: topProductsFilterMode === 'value' ? `${pctValNum.toFixed(1)}%` : `${pctQtyNum.toFixed(1)}%`
      };
    });

    const sorted = result.sort((a, b) => topProductsFilterMode === 'value' ? b.val - a.val : b.qty - a.qty);
    return stabilizeByValue(topProductsAggregatedStableRef, sorted);
  }, [commercialData, topProductsFilterMode]);

  // Efeito para criar/destruir e atualizar gráficos do Chart.js
  useEffect(() => {
    if (activeTab !== 'relatorios' || loadingDashboard || dashboardFetching) {
      return;
    }

    const timerId = setTimeout(() => {
      // Destruir gráficos anteriores
      if (distributorChartInst.current) {
        distributorChartInst.current.destroy();
        distributorChartInst.current = null;
      }
      if (manufacturerChartInst.current) {
        manufacturerChartInst.current.destroy();
        manufacturerChartInst.current = null;
      }
      if (topProductsChartInst.current) {
        topProductsChartInst.current.destroy();
        topProductsChartInst.current = null;
      }
      if (seasonalityChartInst.current) {
        seasonalityChartInst.current.destroy();
        seasonalityChartInst.current = null;
      }

    const chartUiColors = theme === 'dark' ? CHART_UI_COLORS_DARK : CHART_UI_COLORS_LIGHT;

    // Criar Gráfico A (Distribuidor)
    const distCtx = distributorCanvasRef.current?.getContext('2d');
    if (distCtx && Object.keys(distributorTotals).length > 0) {
      const labels = Object.keys(distributorTotals);
      const dataValues = Object.values(distributorTotals);

      distributorChartInst.current = new Chart(distCtx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: dataValues,
            backgroundColor: chartColors.slice(0, labels.length),
            borderColor: chartBorderColors.slice(0, labels.length),
            borderWidth: 1.5,
            cutout: '75%',
            hoverOffset: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              position: 'followMouse',
              callbacks: {
                label: function(context) {
                  const value = context.raw || 0;
                  return ` R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
              }
            }
          }
        }
      });
    }

    // Criar Gráfico B (Fabricante)
    const fabCtx = manufacturerCanvasRef.current?.getContext('2d');
    if (fabCtx && Object.keys(manufacturerTotals).length > 0) {
      const labels = Object.keys(manufacturerTotals);
      const dataValues = Object.values(manufacturerTotals);

      manufacturerChartInst.current = new Chart(fabCtx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: dataValues,
            backgroundColor: chartColors.slice(0, labels.length),
            borderColor: chartBorderColors.slice(0, labels.length),
            borderWidth: 1.5,
            cutout: '75%',
            hoverOffset: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              position: 'followMouse',
              callbacks: {
                label: function(context) {
                  const value = context.raw || 0;
                  return ` R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
              }
            }
          }
        }
      });
    }

    // Criar Gráfico C (Produtos Mais Vendidos)
    const prodCtx = topProductsCanvasRef.current?.getContext('2d');
    if (prodCtx && topProductsAggregated.length > 0) {
      const productLabels = topProductsAggregated.map(p => p.name);
      const productColors = topProductsAggregated.map(p => p.color);
      const productBorderColors = topProductsAggregated.map(p => p.borderColor);
      const isValueMode = topProductsFilterMode === 'value';
      const dataValues = topProductsAggregated.map(p => isValueMode ? p.val : p.qty);
      const totalSum = dataValues.reduce((a, b) => a + b, 0);

      topProductsChartInst.current = new Chart(prodCtx, {
        type: 'doughnut',
        data: {
          labels: productLabels,
          datasets: [{
            data: dataValues,
            backgroundColor: productColors,
            borderColor: productBorderColors,
            borderWidth: 1.5,
            cutout: '75%',
            hoverOffset: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              position: 'followMouse',
              callbacks: {
                label: function(context) {
                  const val = context.raw || 0;
                  const pct = totalSum > 0 ? ((val / totalSum) * 100).toFixed(1) : 0;
                  if (isValueMode) {
                    return ` R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${pct}%)`;
                  }
                  return ` ${val} unidades vendidas (${pct}%)`;
                }
              }
            }
          }
        }
      });
    }

    // Criar Gráfico D (Resumo Sazonal de Vendas - Comparativo de Séries)
    const seasonCtx = seasonalityCanvasRef.current?.getContext('2d');
    if (seasonCtx) {
      const seasonLabels = biMetrics.seasonalityLabels && biMetrics.seasonalityLabels.length > 0
        ? biMetrics.seasonalityLabels
        : ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      const seasonValues = biMetrics.seasonalityValues && biMetrics.seasonalityValues.length > 0
        ? biMetrics.seasonalityValues
        : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      const seasonCompValues = biMetrics.seasonalityCompValues;
      
      const currentYearLabel = biMetrics.currentYearLabel || (startDate ? startDate.slice(0, 4) : 'Atual');
      const compYearLabel = biMetrics.compYearLabel || (compareStartDate ? compareStartDate.slice(0, 4) : 'Comparativo');

      const datasets = [
        {
          label: `${currentYearLabel} (Atual)`,
          data: seasonValues,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.08)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#10b981',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          order: 1
        }
      ];

      if (seasonCompValues && seasonCompValues.length > 0) {
        datasets.push({
          label: `${compYearLabel} (Comparativo)`,
          data: seasonCompValues,
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.03)',
          borderWidth: 2.5,
          borderDash: [6, 6],
          fill: false,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          order: 2
        });
      }

      seasonalityChartInst.current = new Chart(seasonCtx, {
        type: 'line',
        data: {
          labels: seasonLabels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: { 
              display: datasets.length > 1,
              position: 'top',
              align: 'end',
              labels: {
                boxWidth: 10,
                boxHeight: 10,
                usePointStyle: true,
                pointStyle: 'circle',
                color: chartUiColors.legendText,
                font: { size: 11, weight: 'bold' },
                padding: 15
              }
            },
            tooltip: {
              backgroundColor: chartUiColors.tooltipBg,
              titleColor: chartUiColors.tooltipTitle,
              bodyColor: chartUiColors.tooltipBody,
              padding: 12,
              cornerRadius: 10,
              boxPadding: 6,
              usePointStyle: true,
              callbacks: {
                label: function(context) {
                  const valInMillions = context.raw || 0;
                  const realVal = valInMillions * 1000000;
                  return ` ${context.dataset.label}: R$ ${realVal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                },
                afterBody: function(items) {
                  if (items.length >= 2) {
                    const val0 = (items[0].raw || 0) * 1000000;
                    const val1 = (items[1].raw || 0) * 1000000;
                    const diff = val0 - val1;
                    const pct = val1 > 0 ? ((diff / val1) * 100).toFixed(1) : (val0 > 0 ? '100.0' : '0.0');
                    const sign = diff >= 0 ? '+' : '';
                    return [
                      `────────────────────────────`,
                      ` Variação: ${sign}R$ ${diff.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${sign}${pct}%)`
                    ];
                  }
                  return [];
                }
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: chartUiColors.axisTicks, font: { size: 11, weight: '600' } }
            },
            y: {
              grid: { color: chartUiColors.axisGrid },
              ticks: {
                color: chartUiColors.axisTicks,
                font: { size: 11 },
                callback: function(val) {
                  return `R$ ${val.toFixed(1)} MI`;
                }
              }
            }
          }
        }
      });
    }

    }, 30);

    // Limpeza ao desmontar ou re-renderizar
    return () => {
      clearTimeout(timerId);
      if (distributorChartInst.current) {
        distributorChartInst.current.destroy();
        distributorChartInst.current = null;
      }
      if (manufacturerChartInst.current) {
        manufacturerChartInst.current.destroy();
        manufacturerChartInst.current = null;
      }
      if (topProductsChartInst.current) {
        topProductsChartInst.current.destroy();
        topProductsChartInst.current = null;
      }
      if (seasonalityChartInst.current) {
        seasonalityChartInst.current.destroy();
        seasonalityChartInst.current = null;
      }
    };
  }, [activeTab, loadingDashboard, dashboardFetching, distributorTotals, manufacturerTotals, topProductsFilterMode, biMetrics.seasonalityLabels, biMetrics.seasonalityValues, biMetrics.seasonalityCompValues, topProductsAggregated, theme]);

  useEffect(() => {
    if (activeTab === 'relatorios' && dbConnected) {
      // O período pedido agora (união atual+comparativo) pode não estar
      // coberto pelo que foi buscado da última vez (ver lastFetchedBoundsRef)
      // — nesse caso precisa buscar de novo do servidor mesmo dentro do TTL,
      // senão o período novo é refiltrado em cima de dados de outro período
      // e volta vazio.
      const boundsList = [startDate, endDate, compareStartDate, compareEndDate].filter(Boolean);
      const reqLower = boundsList.length ? boundsList.reduce((a, b) => (a < b ? a : b)) : null;
      const reqUpper = boundsList.length ? boundsList.reduce((a, b) => (a > b ? a : b)) : null;
      const cached = lastFetchedBoundsRef.current;
      const outOfCache = !cached.lower || !cached.upper
        || (reqLower && reqLower < cached.lower)
        || (reqUpper && reqUpper > cached.upper);
      const stale = Date.now() - lastDashboardFetchAtRef.current > TAB_CACHE_TTL_MS;
      loadDashboardData(supabaseClient, false, stale || outOfCache);
    }
  }, [activeTab, dbConnected, startDate, endDate, compareStartDate, compareEndDate]);

  // Carregar propostas quando o ID do ClickUp mudar
  useEffect(() => {
    if (dbConnected && clickupTaskId) {
      loadPropostas();
    } else {
      setPropostas([]);
      setCurrentProposta(null);
      setItens([]);
    }
  }, [dbConnected, clickupTaskId]);

  const fetchAllData = async (silent = false) => {
    console.log(`[DEBUG] Auto-polling: Atualizando dados ${silent ? 'silenciosamente' : 'com loading'}...`);
    try {
      // Só atualiza as duas buscas "pesadas" (negocios+propostas+itens_proposta,
      // a mesma tripla em ambas) pra aba que está de fato visível agora — antes
      // rodava as duas sempre, mesmo com o usuário olhando outra aba. As demais
      // abas continuam ficando atualizadas na hora em que o usuário troca pra
      // elas, via o cache com TTL (ver lastKanbanFetchAtRef/lastDashboardFetchAtRef).
      if (activeTabRef.current === 'kanban') {
        await fetchKanbanData(silent);
      }
      if (supabaseClient) {
        await fetchCommercialTasks(supabaseClient, silent);
      }
      if (dbConnected && activeTabRef.current === 'relatorios') {
        await loadDashboardData(supabaseClient, silent);
      }
      if (dbConnected && clickupTaskId) {
        await loadPropostas(null, silent);
      }
    } catch (e) {
      console.error("Erro no auto-polling:", e);
    }
  };

  useEffect(() => {
    if (!session) return;

    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchAllData(true);
      }
    }, 180000);

    return () => clearInterval(intervalId);
  }, [session, dbConnected, clickupTaskId, supabaseClient]);

  // Realtime: além do polling de 3 em 3 minutos acima (mantido como rede de
  // segurança), assina mudanças ao vivo nas 3 tabelas que Kanban/Propostas
  // usam como fonte de verdade, pra refletir a mudança de outro usuário em
  // segundos em vez de esperar o próximo ciclo do polling. O handler só
  // dispara o mesmo fetchAllData(true) do polling — silent=true já passa
  // pelas guardas existentes (itensRef/propostaDirtyRef) contra sobrescrever
  // edição em andamento. Ver docs/superpowers/specs/2026-08-20-realtime-sync-design.md.
  const realtimeDebounceRef = useRef(null);
  // Espelha fetchAllData pra uso dentro do useEffect do realtime — sem isso,
  // scheduleRefresh (fechado dentro de um efeito que só depende de
  // session/supabaseClient) sempre enxergaria o fetchAllData de quando o
  // efeito foi criado, com clickupTaskId ainda '' (mesmo motivo de
  // activeTabRef/itensRef acima).
  const fetchAllDataRef = useRef(null);
  useEffect(() => {
    fetchAllDataRef.current = fetchAllData;
  });

  useEffect(() => {
    if (!session || !supabaseClient) return;

    const scheduleRefresh = () => {
      if (document.hidden) return;
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      realtimeDebounceRef.current = setTimeout(() => {
        fetchAllDataRef.current(true);
      }, 1500);
    };

    const channel = supabaseClient
      .channel('crm-realtime-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'negocios' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'propostas' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'itens_proposta' }, scheduleRefresh)
      .subscribe((status) => {
        console.log('[Realtime] crm-realtime-sync status:', status);
      });

    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      supabaseClient.removeChannel(channel);
    };
  }, [session, supabaseClient]);

  const loadPropostas = async (targetId = null, silent = false, skipDirtyGuard = false) => {
    if (!supabaseClient || !clickupTaskId || typeof clickupTaskId !== 'string' || !clickupTaskId.trim()) return;
    if (!silent) setLoading(true);
    try {
      const idWithoutHash = clickupTaskId.startsWith('#') ? clickupTaskId.substring(1) : clickupTaskId.trim();
      if (!idWithoutHash) return;
      const idWithHash = '#' + idWithoutHash;
      const { data: props, error } = await supabaseClient
        .from('propostas')
        .select('*')
        .or(`clickup_negocio_id.eq.${idWithoutHash},clickup_negocio_id.eq.${idWithHash}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPropostas(props);
      fetchProjectContext();

      if (props.length > 0) {
        // Se a proposta targetId não for encontrada (ex: excluída) ou for nula, força o pré-carregamento da 'vA'.
        // Caso 'vA' não exista por qualquer motivo, carrega a mais recente (primeira da lista).
        const selected = targetId 
          ? props.find(p => p.id === targetId) || props.find(p => p.versao === 'vA') || props[0]
          : props.find(p => p.versao === 'vA') || props[0];
        
        loadProposalDetails(selected.id, silent, skipDirtyGuard);
      } else {
        setCurrentProposta(null);
        setItens([]);
      }
    } catch (err) {
      console.error(err);
      showToast('Erro ao carregar propostas.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadTodasPropostas = async () => {
    if (!supabaseClient) return;
    try {
      const { data, error } = await supabaseClient
        .from('propostas')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setTodasPropostas(data || []);
    } catch (err) {
      console.error("[DEBUG] Erro ao carregar todas as propostas:", err);
    }
  };

  const loadProposalDetails = async (proposalId, silent = false, skipDirtyGuard = false) => {
    // Se houver itens ainda não salvos (id temporário, criado por handleAddItem mas nunca
    // persistido no banco), uma atualização silenciosa em segundo plano (polling) NÃO pode
    // recarregar a proposta: isso apagaria o que o usuário está digitando antes de salvar.
    // Lê de itensRef (não do estado `itens` direto) porque o polling roda dentro de um
    // setInterval de closure antiga — ver comentário na declaração de itensRef.
    // skipDirtyGuard existe pro reload silencioso disparado logo após um save bem-sucedido
    // (handleSaveProposal): ali os "temp-" ids em itensRef são sempre das linhas que ACABAMOS
    // de apagar e reinserir com id novo — a proteção não se aplica, e bloquear geraria itens
    // "presos" com id antigo/inválido pro resto da sessão.
    if (silent && !skipDirtyGuard && (itensRef.current.some(it => String(it.id).startsWith('temp-')) || propostaDirtyRef.current)) {
      return;
    }
    // Carga explícita (usuário abrindo uma proposta/versão) sempre reseta o
    // rastreamento de edição — não faz sentido preservar "sujeira" de uma
    // proposta diferente da que está sendo aberta agora.
    if (!silent) {
      propostaDirtyRef.current = false;
    }
    // Instant Hydration: se a proposta já estiver no array local, atualiza imediatamente a UI sem delay
    const existingProp = propostas.find(p => p.id === proposalId);
    if (existingProp) {
      setCurrentProposta(existingProp);
    } else if (!silent) {
      setLoading(true);
    }

    try {
      // 1. Busca detalhes atualizados da proposta no Supabase
      const { data: prop, error: propErr } = await supabaseClient
        .from('propostas')
        .select('*')
        .eq('id', proposalId)
        .single();

      if (propErr) throw propErr;

      let updatedProp = { ...prop };
      setCurrentProposta(updatedProp);
      setIsEditingProposal(false);

      // 2. Busca itens da proposta em paralelo com resposta rápida
      const { data: items, error: itemsErr } = await supabaseClient
        .from('itens_proposta')
        .select('*')
        .eq('proposta_id', proposalId)
        .order('created_at');

      if (itemsErr) throw itemsErr;
      setItens(items || []);

      // 3. Sincronização assíncrona não-bloqueante da data de início do ClickUp (em segundo plano).
      // IMPORTANTE: data_fechamento NUNCA é auto-preenchida a partir do due_date do ClickUp.
      // due_date é só o prazo/vencimento da tarefa, não confirmação de negócio fechado — e
      // data_fechamento é exatamente o campo que todo o resto do sistema usa para decidir se
      // um negócio conta como "ganho" nos relatórios. Preenchê-la automaticamente a partir do
      // due_date fazia negócios reabertos (situacao voltando a 'Ativa'/'Selecionada', sem
      // data_fechamento) serem contados como ganhos de novo assim que a proposta era recarregada,
      // mesmo sem ninguém clicar em "Ganho". Só o fluxo explícito de Ganho/Perdido pode setar
      // data_fechamento.
      const cuId = updatedProp.clickup_negocio_id || clickupTaskId;
      if (cuId && !updatedProp.data_inicio) {
        const cleanCuId = cuId.startsWith('#') ? cuId.substring(1) : cuId;
        fetch(`/clickup-api/task/${cleanCuId}`, { headers: { ...getSupabaseHeaders() } }).then(res => {
          if (res.ok) return res.json();
          return null;
        }).then(taskData => {
          if (!taskData) return;
          const startMs = taskData.start_date || taskData.date_created;
          if (!startMs) return;
          const newInicio = formatDateMsToYMD(startMs);
          setCurrentProposta(prev => prev && prev.id === updatedProp.id ? { ...prev, data_inicio: newInicio } : prev);
          /* B2_I11_BEGIN */
          if (kanbanModoRef.current !== 'legacy') return; // canonical: the imported date is persisted by the next gateway save (campos.data_inicio), never by REST
          /* B2_I11_END */
          supabaseClient.from('propostas').update({
            data_inicio: newInicio
          }).eq('id', updatedProp.id).then(() => {}).catch(() => {});
        }).catch(err => console.error("Erro ao importar data de início do ClickUp em segundo plano:", err));
      }
    } catch (err) {
      console.error(err);
      showToast('Erro ao carregar detalhes da proposta.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 3. Salvar as credenciais do Supabase
  const handleSaveConfig = (e) => {
    e.preventDefault();
    const url = e.target.url.value.trim();
    const key = e.target.key.value.trim();
    
    safeStorage.setItem('supa_url', url);
    safeStorage.setItem('supa_key', key);
    
    setConfig({ url, anonKey: key, identity_mode: identidadeModoRef.current, kanban_mode: kanbanModoRef.current });
    setShowSettingsModal(false);
    showToast('Configurações salvas com sucesso!', 'success');
  };

  // Antes só reconhecia 'success' — qualquer outro tipo (inclusive os
  // 'info'/'warning' já usados em vários lugares, ex.: "Negócio Congelado")
  // caía direto no branch de erro (toast vermelho), fazendo mensagens
  // neutras/informativas aparecerem como se fosse uma falha.
  function showToast(msg, type = 'success') {
    if (type === 'success') {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(''), 4000);
    } else if (type === 'info') {
      setInfoMsg(msg);
      setTimeout(() => setInfoMsg(''), 4000);
    } else if (type === 'warning') {
      setWarningMsg(msg);
      setTimeout(() => setWarningMsg(''), 4000);
    } else {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(''), 4000);
    }
  }

  // 4. Verificação de Apenas Leitura (Read-Only)
  const isReadOnly = false;

  // 5. Cálculos em tempo real
  const realTimeGrandTotal = useMemo(() => {
    return itens.reduce((sum, item) => sum + (item.quantidade * item.preco_unitario || 0), 0);
  }, [itens]);

  const handleItemChange = (index, field, value) => {
    if (isReadOnly) return;
    const newItens = [...itens];
    
    if (typeof field === 'object' && field !== null) {
      const updates = field;
      const mapped = { ...updates };
      if (updates.unitario !== undefined) {
        mapped.preco_unitario = Math.max(0, parseFloat(updates.unitario) || 0);
        delete mapped.unitario;
      }
      if (updates.preco_unitario !== undefined) {
        mapped.preco_unitario = Math.max(0, parseFloat(updates.preco_unitario) || 0);
      }
      if (updates.quantidade !== undefined) {
        mapped.quantidade = Math.max(1, parseInt(updates.quantidade) || 1);
      }
      newItens[index] = { ...newItens[index], ...mapped };
    } else {
      if (field === 'produto_id') {
        const selectedProd = produtos.find(p => p.id === value);
        newItens[index] = {
          ...newItens[index],
          produto_id: value,
          preco_unitario: selectedProd ? selectedProd.custo_referencia : 0
        };
      } else if (field === 'quantidade') {
        newItens[index].quantidade = Math.max(1, parseInt(value) || 1);
      } else if (field === 'preco_unitario') {
        newItens[index].preco_unitario = Math.max(0, parseFloat(value) || 0);
      } else {
        newItens[index][field] = value;
      }
    }
    
    setItens(newItens);
  };

  const handleCurrencyInputChange = (index, rawValue) => {
    if (isReadOnly) return;
    const digits = rawValue.replace(/\D/g, '');
    if (!digits) {
      handleItemChange(index, 'preco_unitario', 0);
      return;
    }
    const numericValue = parseFloat(digits) / 100;
    handleItemChange(index, 'preco_unitario', numericValue);
  };

  const handleAddItem = () => {
    if (isReadOnly) return;
    if (produtos.length === 0) {
      showToast('Nenhum produto cadastrado! Vá ao Painel de Gestão ou clique no botão superior para cadastrar.', 'error');
      return;
    }
    
    setItens([
      ...itens,
      {
        id: `temp-${Date.now()}`,
        produto_id: produtos[0]?.id || '',
        distribuidor_id: distribuidores[0]?.id || null,
        quantidade: 1,
        preco_unitario: produtos[0]?.custo_referencia || 0
      }
    ]);
  };

  const handleRemoveItem = (index) => {
    if (isReadOnly) return;
    setItens(itens.filter((_, i) => i !== index));
  };

  // 6. Criar nova proposta inicial (vA)
  /* B2_I11_HELPER_BEGIN */
  // G-Propostas: canonical commands for proposals. One pending command per operation+entity (a lost answer is replayed with the SAME request id and data);
  // different data while a command is pending is refused. The actor, role, creator name and totals never come from here.
  const comercialExecutar = async (nome, entidade, params) => {
    if (kanbanModoRef.current !== 'canonico_v1' || identidadeModoRef.current !== 'canonico_v1' || !window.CrmComercial || !supabaseClient || !session?.user?.id || !config) throw Object.assign(new Error('Gateway indisponível.'), { code: 'GATEWAY_INDISPONIVEL' });
    const key = 'comercial/' + nome + '/' + session.user.id + '/' + entidade;
    const assinatura = JSON.stringify(params);
    let pend = kanbanComandosRef.current.get(key);
    if (pend && pend.assinatura !== assinatura) throw Object.assign(new Error('Operação pendente.'), { code: 'REQUEST_PENDING', ambiguous: true });
    if (!pend) {
      pend = { assinatura, command: window.CrmComercial.preparar(nome, params) };
      kanbanComandosRef.current.set(key, pend);
    }
    try {
      const result = await window.CrmComercial.criar({ client: supabaseClient, config }).executar(pend.command);
      kanbanComandosRef.current.delete(key);
      return result;
    } catch (error) {
      let ambiguous = false;
      try { ambiguous = Object.getOwnPropertyDescriptor(error, 'ambiguous')?.value === true; } catch {}
      if (!ambiguous) kanbanComandosRef.current.delete(key);
      throw error;
    }
  };
  const comercialCodigo = (error) => { try { return Object.getOwnPropertyDescriptor(error, 'code')?.value; } catch { return undefined; } };
  const comercialMensagem = (error, padrao) => {
    const mensagens = { CONFLITO_VERSAO: 'A proposta mudou. Recarregue antes de tentar novamente.', REQUEST_PENDING: 'Operação anterior pendente. Repita sem alterar os dados.', PROPOSTA_FECHADA: 'Proposta Ganha/Perdida não pode ser editada.', ITEM_INVALIDO: 'Há item inválido (produto, distribuidor, quantidade ou preço).',
      TOTAL_INVALIDO: 'Total da proposta fora do limite.', CAMPO_INVALIDO: 'Há campo inválido na proposta.', PROPOSTAS_EXISTENTES: 'Esta oportunidade já possui proposta.', NEGOCIO_SEM_CLICKUP: 'A oportunidade ainda não tem tarefa no ClickUp.', SEM_VERSAO_BASE: 'Não há versão base válida para gerar a próxima.',
      NENHUMA_ALTERACAO: 'Nada a alterar.', PROPOSTA_NAO_ENCONTRADA: 'Proposta não encontrada.', NEGOCIO_AMBIGUO: 'Oportunidade ambígua; contate o suporte.', GATEWAY_INDISPONIVEL: 'Gateway comercial indisponível.', NEGOCIO_INDISPONIVEL: 'Oportunidade indisponível.', VERSAO_INDISPONIVEL: 'Versão da proposta indisponível. Recarregue.' };
    return mensagens[comercialCodigo(error)] || window.CrmComercial?.mensagem(error, padrao) || padrao;
  };
  const comercialNegocioId = (clickupId) => {
    const limpo = String(clickupId || '').replace('#', '').trim();
    if (!limpo) return null;
    const alvo = kanbanTasks.find(t => t.crm_negocio_id && (String(t.id || '').replace('#', '').trim() === limpo || String(t.clickup_negocio_id || '').replace('#', '').trim() === limpo));
    return alvo?.crm_negocio_id || (selectedTask?.crm_negocio_id && String(selectedTask.id || selectedTask.clickup_negocio_id || '').replace('#', '').trim() === limpo ? selectedTask.crm_negocio_id : null);
  };
  /* B2_I11_HELPER_END */

  /* B2_I13_HELPER_BEGIN */
  // G-Negócio-residual + Tarefas/Atividades: canonical commands. Edits of the opportunity (type) go through the same gateway as W2/W3; the owner has its own command.
  const editarNegocioCanonico = async (crmId, versao, campos) => {
    if (kanbanModoRef.current !== 'canonico_v1' || identidadeModoRef.current !== 'canonico_v1' || !window.CrmKanbanEstagio || !session?.user?.id || !supabaseClient || !config) throw Object.assign(new Error('Gateway indisponível.'), { code: 'GATEWAY_INDISPONIVEL' });
    if (!crmId || !Number.isSafeInteger(versao)) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
    const key = 'edicao/' + session.user.id + '/' + crmId;
    let command = kanbanComandosRef.current.get(key);
    if (command && (command.expected_version !== versao || JSON.stringify(command.campos) !== JSON.stringify(campos))) throw Object.assign(new Error('Operação pendente.'), { code: 'REQUEST_PENDING', ambiguous: true });
    const gateway = window.CrmKanbanEstagio.criar({ client: supabaseClient, config });
    if (!command) { command = window.CrmKanbanEstagio.prepararEdicao({ negocio_id: crmId, expected_version: versao, campos }); kanbanComandosRef.current.set(key, command); }
    try { const resultado = await gateway.editar(command); kanbanComandosRef.current.delete(key); return resultado; }
    catch (error) { let ambiguous = false; try { ambiguous = Object.getOwnPropertyDescriptor(error, 'ambiguous')?.value === true; } catch {} if (!ambiguous) kanbanComandosRef.current.delete(key); throw error; }
  };
  const anexosParaComando = (lista) => (lista || []).map(a => {
    const o = { clickup_attachment_id: String(a?.clickup_attachment_id ?? '') };
    if (typeof a?.title === 'string') o.title = a.title;
    if (typeof a?.url === 'string') o.url = a.url;
    if (typeof a?.mimetype === 'string') o.mimetype = a.mimetype;
    if (Number.isSafeInteger(a?.size)) o.size = a.size;
    return o;
  });
  /* B2_I13_HELPER_END */

  const handleCreateInitialProposal = async () => {
    if (!supabaseClient || !clickupTaskId) return;
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setLoading(true);
      try {
        const crmId = comercialNegocioId(clickupTaskId);
        if (!crmId) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'NEGOCIO_INDISPONIVEL' });
        const criada = await comercialExecutar('proposta_criar', crmId, { negocio_id: crmId });
        showToast('Primeira versão (vA) iniciada com sucesso!', 'success');
        await loadPropostas(criada.proposta_id);
        setDrawerTab('budget');
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao criar proposta inicial.'), 'error');
      } finally {
        setLoading(false);
      }
      return;
    }
    /* B2_I11_END */
    setLoading(true);
    try {
      const currentResponsavel = selectedTask ? selectedTask.responsavel_negocio : 'Vendedor CRM';
      const authorUserId = userProfile?.id ? String(userProfile.id) : null;
      const { data: newProp, error } = await supabaseClient
        .from('propostas')
        .insert({
          clickup_negocio_id: clickupTaskId,
          versao: 'vA',
          cenario: '',
          situacao: 'Ativa',
          total_proposta: 0,
          criado_por: currentResponsavel,
          criado_por_user_id: authorUserId
        })
        .select()
        .single();

      if (error) throw error;
      
      showToast('Primeira versão (vA) iniciada com sucesso!', 'success');
      await loadPropostas(newProp.id);
      setDrawerTab('budget');
    } catch (err) {
      console.error("Erro ao criar proposta inicial:", err);
      showToast('Erro ao criar proposta inicial.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const syncClickUpProposta = async (taskId, valorTotal, flowName) => {
    const cleanTaskId = String(taskId).replace('#', '').trim();
    if (!cleanTaskId) return;

    const valorLimpo = parseNumericValue(valorTotal);
    const valorCentavos = Math.round(Number(valorLimpo) * 100);

    if (valorLimpo === null || valorLimpo === undefined || isNaN(Number(valorLimpo)) || Number(valorLimpo) <= 0 || isNaN(valorCentavos)) {
      console.warn(`[${new Date().toISOString()}] Ignorando sincronização com ClickUp (${flowName}) para tarefa ${cleanTaskId} pois o valor é inválido ou <= 0:`, valorLimpo);
      return;
    }

    try {
      // 1. Obter detalhes da tarefa atual (Proposta)
      const taskRes = await fetch(`/clickup-api/task/${cleanTaskId}`, {
        headers: {
          "Content-Type": "application/json",
          ...getSupabaseHeaders()
        }
      });
      if (!taskRes.ok) {
        console.error(`[${new Date().toISOString()}] Erro ao obter tarefa ${cleanTaskId} no ClickUp (status: ${taskRes.status})`);
        return;
      }
      const currentTask = await taskRes.json();

      if (!currentTask || !currentTask.custom_fields) {
        console.warn(`[${new Date().toISOString()}] Tarefa ClickUp ${cleanTaskId} não tem custom_fields.`);
        return;
      }

      // a) Atualização local do "Total da Proposta" na tarefa de Proposta
      const campoValor = currentTask.custom_fields.find(f => {
        const name = (f.name || "").toLowerCase();
        return name === 'deal value' || 
               name === 'total da proposta' || 
               name === 'valor total' || 
               name === 'valor do negócio' || 
               name === 'valor' || 
               name === 'total';
      });

      if (campoValor) {
        const bodyFormatado = campoValor.id === DEAL_VALUE_FIELD_ID
          ? { value: Number(Number(valorLimpo).toFixed(2)) }
          : { value: valorCentavos };
        const urlValue = `/clickup-api/task/${cleanTaskId}/field/${campoValor.id}`;
        
        console.log(`[${new Date().toISOString()}] POST ${urlValue} - Body:`, JSON.stringify(bodyFormatado));
        
        if (cleanTaskId === '86ahby7wm') {
          console.log(`[${new Date().toISOString()}] [DETECTOR TASK 86ahby7wm] Enviando valor local para ClickUp (${flowName}): ${bodyFormatado.value}`);
        }

        const resVal = await fetch(urlValue, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            ...getSupabaseHeaders()
          },
          body: JSON.stringify(bodyFormatado)
        });

        if (resVal.status !== 200 && resVal.status !== 201) {
          const errText = await resVal.text();
          console.error(`[${new Date().toISOString()}] Erro ao atualizar campo local no ClickUp [Status: ${resVal.status}]:`, errText);
        } else {
          console.log(`[${new Date().toISOString()}] Campo local (${campoValor.name}) atualizado com sucesso no ClickUp (${flowName})!`);
          
          // Validação imediata via GET pós-POST
          try {
            console.log(`[${new Date().toISOString()}] Iniciando verificação GET pós-POST para a tarefa ${cleanTaskId}...`);
            const verifyRes = await fetch(`/clickup-api/task/${cleanTaskId}`, {
              headers: {
                "Content-Type": "application/json",
                ...getSupabaseHeaders()
              }
            });
            if (verifyRes.ok) {
              const verifyTask = await verifyRes.json();
              const verifyField = verifyTask.custom_fields?.find(f => f.id === campoValor.id);
              const valorRetornado = verifyField ? verifyField.value : null;
              console.log(`[${new Date().toISOString()}] VALIDAÇÃO pós-update (${flowName}) para tarefa ${cleanTaskId}: Valor retornado no ClickUp =`, valorRetornado, `(Esperado: ${bodyFormatado.value})`);
              if (cleanTaskId === '86ahby7wm') {
                console.log(`[${new Date().toISOString()}] [VALOR CONFIRMADO TASK 86ahby7wm] Valor pós-POST no ClickUp:`, valorRetornado);
              }
            }
          } catch (verifyErr) {
            console.error("Erro ao validar campo local:", verifyErr);
          }
        }
      } else {
        console.warn(`[${new Date().toISOString()}] Campo local de valor não encontrado na tarefa ${cleanTaskId}.`);
      }

      // b) Atualização global do Deal Value na tarefa pai (Negócio)
      const relField = currentTask.custom_fields.find(f => {
        if (f.type !== 'list_relationship') return false;
        const name = (f.name || "").toLowerCase();
        return name.includes('negócio') || name.includes('negocio') || name.includes('comercial proposal');
      });

      if (relField && relField.value && Array.isArray(relField.value) && relField.value.length > 0) {
        const parentTaskId = String(relField.value[0].id).replace('#', '').trim();
        const urlGlobal = `/clickup-api/task/${parentTaskId}/field/${DEAL_VALUE_FIELD_ID}`;
        const bodyFormatado = { value: Number(Number(valorLimpo).toFixed(2)) };

        console.log(`[${new Date().toISOString()}] POST ${urlGlobal} - Body:`, JSON.stringify(bodyFormatado));

        if (cleanTaskId === '86ahby7wm' || parentTaskId === '86ahby7wm') {
          console.log(`[${new Date().toISOString()}] [DETECTOR TASK 86ahby7wm] Enviando Deal Value global para a tarefa pai ${parentTaskId}: ${bodyFormatado.value}`);
        }

        const resGlobal = await fetch(urlGlobal, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            ...getSupabaseHeaders()
          },
          body: JSON.stringify(bodyFormatado)
        });

        if (resGlobal.status !== 200 && resGlobal.status !== 201) {
          const errText = await resGlobal.text();
          console.error(`[${new Date().toISOString()}] Erro crítico ao atualizar Deal Value global na tarefa ${parentTaskId} [Status: ${resGlobal.status}]:`, errText);
        } else {
          console.log(`[${new Date().toISOString()}] Deal Value global atualizado com sucesso no ClickUp (Tarefa Negócio Pai: ${parentTaskId})!`);

          // Validação imediata global via GET pós-POST
          try {
            console.log(`[${new Date().toISOString()}] Iniciando verificação GET pós-POST para a tarefa pai ${parentTaskId}...`);
            const verifyRes = await fetch(`/clickup-api/task/${parentTaskId}`, {
              headers: {
                "Content-Type": "application/json",
                ...getSupabaseHeaders()
              }
            });
            if (verifyRes.ok) {
              const verifyTask = await verifyRes.json();
              const verifyField = verifyTask.custom_fields?.find(f => f.id === DEAL_VALUE_FIELD_ID);
              const valorRetornado = verifyField ? verifyField.value : null;
              console.log(`[${new Date().toISOString()}] VALIDAÇÃO Deal Value global pós-update (${flowName}) para tarefa ${parentTaskId}: valor =`, valorRetornado);
              if (parentTaskId === '86ahby7wm') {
                console.log(`[${new Date().toISOString()}] [VALOR CONFIRMADO TASK 86ahby7wm] Valor global pós-POST no ClickUp:`, valorRetornado);
              }
            }
          } catch (verifyErr) {
            console.error("Erro ao validar Deal Value global:", verifyErr);
          }
        }
      } else {
        console.warn(`[${new Date().toISOString()}] Relacionamento de Negócio/Comercial Proposal não encontrado na tarefa ${cleanTaskId}.`);
      }

    } catch (err) {
      console.error(`[${new Date().toISOString()}] Erro durante a sincronização dupla com o ClickUp (${flowName}):`, err);
    }
  };

  // 7. Ação de Salvar Proposta
  const handleSaveProposal = async () => {
    if (isReadOnly || !currentProposta) return;
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // One command: fields + items + total + dates + forecast date + ClickUp intent. The total shown in the editor is NEVER sent: the database recomputes it.
      setSaving(true);
      try {
        const versao = currentProposta.versao_edicao;
        if (!Number.isSafeInteger(versao)) throw Object.assign(new Error('Versão indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        const campos = { cenario: currentProposta.cenario || '', data_fechamento: currentProposta.data_fechamento || null };
        if (typeof currentProposta.criado_por === 'string' && currentProposta.criado_por.trim()) campos.criado_por = currentProposta.criado_por;
        const dataInicio = currentProposta.data_inicio || clickupTaskDates?.start_date;
        if (dataInicio) campos.data_inicio = dataInicio;
        const fechada = currentProposta.situacao === 'Ganho' || currentProposta.situacao === 'Perdido';
        const itensEnvio = fechada ? null : itens.map(item => ({
          produto_id: item.produto_id,
          distribuidor_id: item.distribuidor_id || distribuidores[0]?.id || null,
          quantidade: Math.max(1, parseInt(item.quantidade) || 1),
          preco_unitario: Number(Math.max(0, parseFloat(item.preco_unitario) || 0).toFixed(2))
        }));
        const camposEnvio = fechada ? (campos.data_inicio ? { data_inicio: campos.data_inicio } : {}) : campos;
        if (fechada && !Object.keys(camposEnvio).length) return;
        const salva = await comercialExecutar('proposta_salvar', currentProposta.id, { proposta_id: currentProposta.id, expected_version: versao, campos: camposEnvio, itens: itensEnvio });
        const cleanTaskId = String(clickupTaskId || '').replace('#', '').trim();
        if (cleanTaskId) {
          setSupabaseProposalsList(prev => (prev || []).map(p => (p.id === currentProposta.id ? { ...p, total_proposta: salva.total_proposta, situacao: currentProposta.situacao } : p)));
        }
        propostaDirtyRef.current = false;
        showToast('Proposta salva com sucesso!', 'success');
        setIsEditingProposal(false);
        loadPropostas(currentProposta.id, true, true);
        refreshSupabaseProposalsList();
        return salva;
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao salvar proposta.'), 'error');
        if (comercialCodigo(err) === 'CONFLITO_VERSAO') loadPropostas(currentProposta.id, true, true);
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I11_END */
    setSaving(true);
    try {
      // ⚡ ATUALIZAÇÃO OTÍMISTA EM MEMÓRIA
      // Atualiza supabaseProposalsList imediatamente (sem esperar o banco)
      // para que o Kanban e o Forecast reflitam o novo valor na hora.
      const cleanTaskId = String(clickupTaskId || '').replace('#', '').trim();
      if (cleanTaskId) {
        setSupabaseProposalsList(prev => {
          const updated = (prev || []).map(p => {
            const pClean = String(p.clickup_negocio_id || '').replace('#', '').trim();
            if (pClean === cleanTaskId && p.id === currentProposta.id) {
              return { ...p, total_proposta: realTimeGrandTotal, situacao: currentProposta.situacao };
            }
            return p;
          });
          // Se não havia entrada, adiciona uma nova
          const exists = updated.some(p => String(p.clickup_negocio_id || '').replace('#', '').trim() === cleanTaskId && p.id === currentProposta.id);
          if (!exists) {
            updated.push({
              clickup_negocio_id: cleanTaskId,
              total_proposta: realTimeGrandTotal,
              situacao: currentProposta.situacao,
              criado_por: currentProposta.criado_por,
              id: currentProposta.id
            });
          }
          return updated;
        });
      }

      const nowIso = new Date().toISOString();
      const currentUser = currentProposta.criado_por || session?.user?.email || 'Usuário';

      const updateData = {
        cenario: currentProposta.cenario,
        criado_por: currentProposta.criado_por,
        situacao: currentProposta.situacao,
        total_proposta: realTimeGrandTotal,
        // data_fechamento NUNCA herda o due_date do ClickUp (prazo da tarefa, não confirmação
        // de negócio fechado) — só é gravada aqui se já vier preenchida (fluxo de Ganho/Perdido).
        data_fechamento: currentProposta.data_fechamento || null,
        motivo_perda: currentProposta.situacao === 'Perdido' ? currentProposta.motivo_perda : null
      };

      if (currentProposta.data_inicio || clickupTaskDates?.start_date) {
        updateData.data_inicio = currentProposta.data_inicio || clickupTaskDates?.start_date;
      }

      let { error: propError } = await supabaseClient
        .from('propostas')
        .update(updateData)
        .eq('id', currentProposta.id);

      // Tratamento defensivo caso a coluna data_inicio ainda não exista no Supabase
      if (propError && (propError.code === '42703' || propError.code === 'PGRST204' || (propError.message && propError.message.includes('data_inicio')))) {
        console.warn("Coluna 'data_inicio' ainda não criada no Supabase. Salvando sem a coluna data_inicio...");
        delete updateData.data_inicio;
        const { error: retryErr } = await supabaseClient
          .from('propostas')
          .update(updateData)
          .eq('id', currentProposta.id);
        propError = retryErr;
      }

      if (propError) throw propError;

      // Propaga as datas para todas as versões da mesma oportunidade no Supabase
      const cuId = currentProposta.clickup_negocio_id || clickupTaskId;
      if (cuId) {
        const cleanCuId = String(cuId).replace('#', '').trim();
        const idWithHash = '#' + cleanCuId;
        const propUpdates = {
          data_fechamento: currentProposta.data_fechamento || null
        };
        if (currentProposta.data_inicio || clickupTaskDates?.start_date) {
          propUpdates.data_inicio = currentProposta.data_inicio || clickupTaskDates?.start_date;
        }
        try {
          const { error: propSyncErr } = await supabaseClient
            .from('propostas')
            .update(propUpdates)
            .or(`clickup_negocio_id.eq.${cleanCuId},clickup_negocio_id.eq.${idWithHash}`);

          if (propSyncErr && (propSyncErr.code === '42703' || propSyncErr.code === 'PGRST204' || (propSyncErr.message && propSyncErr.message.includes('data_inicio')))) {
            delete propUpdates.data_inicio;
            await supabaseClient
              .from('propostas')
              .update(propUpdates)
              .or(`clickup_negocio_id.eq.${cleanCuId},clickup_negocio_id.eq.${idWithHash}`);
          }
        } catch (propSyncEx) {
          console.warn("Aviso ao propagar datas para propostas irmãs:", propSyncEx);
        }

        // Sincroniza diretamente as datas (start_date e due_date) na tarefa do ClickUp
        const datesPayload = {};
        const startDateVal = currentProposta.data_inicio || clickupTaskDates?.start_date;
        const endDateVal = currentProposta.data_fechamento;
        if (startDateVal) {
          const startMs = new Date(`${startDateVal}T12:00:00.000Z`).getTime();
          if (!isNaN(startMs)) datesPayload.start_date = startMs;
        }
        if (endDateVal) {
          const endMs = new Date(`${endDateVal}T12:00:00.000Z`).getTime();
          if (!isNaN(endMs)) datesPayload.due_date = endMs;
        }
        if (Object.keys(datesPayload).length > 0) {
          fetch(`/clickup-api/task/${cleanCuId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getSupabaseHeaders() },
            body: JSON.stringify(datesPayload)
          }).catch(err => console.error("Erro ao sincronizar datas no ClickUp:", err));
        }

        // Previsão de Fechamento do negócio (negocios.data_previsao) só
        // reflete a Data de Fechamento da proposta marcada "Usar no
        // Forecast" (situacao 'Selecionada') — é a única com uma expectativa
        // real de quando o negócio fecha; antes de propor, qualquer data
        // seria só um chute. Ver também handleUpdateVersionStatus, que limpa
        // isso quando uma NOVA proposta vira a selecionada.
        if (currentProposta.situacao === 'Selecionada') {
          try {
            await supabaseClient.from('negocios').update({ data_previsao: currentProposta.data_fechamento || null }).eq('clickup_negocio_id', cleanCuId);
            setSelectedTask(prev => (prev && String(prev.clickup_negocio_id || prev.id || '').replace('#', '').trim() === cleanCuId)
              ? { ...prev, data_previsao: currentProposta.data_fechamento || null }
              : prev);
          } catch (err) {
            console.warn('Erro ao sincronizar Previsão de Fechamento do negócio:', err);
          }
        }
      }

      const { error: deleteError } = await supabaseClient
        .from('itens_proposta')
        .delete()
        .eq('proposta_id', currentProposta.id);

      if (deleteError) throw deleteError;

      if (itens.length > 0) {
        const itensToInsert = itens.map(item => ({
          proposta_id: currentProposta.id,
          produto_id: item.produto_id,
          distribuidor_id: item.distribuidor_id || distribuidores[0]?.id || null,
          quantidade: Math.max(1, parseInt(item.quantidade) || 1),
          preco_unitario: Math.max(0, parseFloat(item.preco_unitario) || 0)
        }));

        const { error: insertError } = await supabaseClient
          .from('itens_proposta')
          .insert(itensToInsert);

        if (insertError) throw insertError;
      }

      // Sincroniza sempre o valor total da proposta com a Oportunidade no ClickUp se for Selecionada ou a versão ativa
      const isOnlyOrSelected = currentProposta.situacao === 'Selecionada' || propostas.length <= 1;
      const targetTaskIdForClickup = clickupTaskId || currentProposta.clickup_negocio_id;
      if (isOnlyOrSelected && targetTaskIdForClickup) {
        await syncClickUpProposta(targetTaskIdForClickup, realTimeGrandTotal, 'Save');
      }

      propostaDirtyRef.current = false;
      showToast('Proposta salva com sucesso!', 'success');
      setIsEditingProposal(false);
      // Silencioso (não mostra o spinner de tela cheia — o botão "Salvar" já
      // dá esse feedback) mas com skipDirtyGuard: os itens acabaram de ser
      // apagados e reinseridos no banco com id novo, então os "temp-" ainda
      // em memória são só resíduo local — precisam ser substituídos pelos
      // ids reais vindos do banco, não preservados.
      loadPropostas(currentProposta.id, true, true);
      // Confirma a lista global com dados frescos do banco em segundo plano
      refreshSupabaseProposalsList();
    } catch (err) {
      console.error(err);
      showToast('Erro ao salvar proposta.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProposalDebounced = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      handleSaveProposal();
    }, 300);
  };

  // 8. Botão "Gerar Nova Versão" (Database-First Clone - Preserva o Histórico da Base)
  const handleGerarNovaVersao = async () => {
    if (!clickupTaskId) return;
    if (!currentProposta || propostas.length === 0) {
      // Confere direto no banco antes de assumir que não existe proposta: o estado local
      // (propostas/currentProposta) pode ainda não ter carregado por uma condição de corrida,
      // e criar uma proposta em branco aqui duplicaria a que já existe.
      const idWithoutHash = clickupTaskId.startsWith('#') ? clickupTaskId.substring(1) : clickupTaskId.trim();
      const idWithHash = '#' + idWithoutHash;
      const { data: existingProps } = await supabaseClient
        .from('propostas')
        .select('*')
        .or(`clickup_negocio_id.eq.${idWithoutHash},clickup_negocio_id.eq.${idWithHash}`)
        .order('created_at', { ascending: false });

      if (existingProps && existingProps.length > 0) {
        setPropostas(existingProps);
        const selected = existingProps.find(p => p.versao === 'vA') || existingProps[0];
        await loadProposalDetails(selected.id);
        showToast('Proposta já existente carregada — nenhuma versão nova foi criada.', 'info');
        return;
      }

      await handleCreateInitialProposal();
      return;
    }
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setSaving(true);
      try {
        const crmId = comercialNegocioId(clickupTaskId);
        if (!crmId) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'NEGOCIO_INDISPONIVEL' });
        // the current version is saved first (its own command); the next code and the copy are decided by the database under the opportunity lock
        if (drawerTab === 'budget' && !isReadOnly && itens && itens.length > 0) {
          await handleSaveProposal();
        }
        const nova = await comercialExecutar('proposta_versao', crmId, { negocio_id: crmId });
        showToast(`Nova versão ${nova.versao} gerada a partir da última versão da oportunidade.`, 'success');
        await loadPropostas(nova.proposta_id);
      } catch (err) {
        showToast(comercialMensagem(err, 'Erro ao gerar nova versão.'), 'error');
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I11_END */
    setSaving(true);
    try {
      // 1. Se estivermos na aba de edição de orçamento e houver itens em memória, salva primeiro a proposta atual
      if (drawerTab === 'budget' && !isReadOnly && itens && itens.length > 0) {
        await handleSaveProposal();
      }

      // A sequência e a clonagem acontecem no banco, sob lock por oportunidade.
      // Assim a versão aberta no drawer jamais decide a próxima letra.
      const currentResponsavel = selectedTask ? selectedTask.responsavel_negocio : (currentProposta.criado_por || '');
      const authorUserId = userProfile?.id ? String(userProfile.id) : (currentProposta.criado_por_user_id || null);
      const { data: novaVersao, error: propErr } = await supabaseClient.rpc('gerar_proxima_versao_proposta', {
        p_clickup_negocio_id: clickupTaskId,
        p_criado_por: currentResponsavel,
        p_criado_por_user_id: authorUserId
      });

      if (propErr) throw propErr;
      const newProp = Array.isArray(novaVersao) ? novaVersao[0] : novaVersao;
      if (!newProp?.id || !newProp?.versao) throw new Error('A criação da nova versão não retornou os dados esperados.');

      showToast(`Nova versão ${newProp.versao} gerada a partir da última versão da oportunidade.`, 'success');
      await loadPropostas(newProp.id);
    } catch (err) {
      console.error("Erro ao gerar nova versão:", err);
      showToast('Erro ao gerar nova versão.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 8.5. Excluir Versão (Com regra específica para vA e secundárias)
  const handleDeleteProposal = async (proposalToDelete = null) => {
    const targetProp = proposalToDelete || currentProposta;
    if (!targetProp || !supabaseClient) return;
    const isVa = targetProp.versao === 'vA';
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // Same contract and same confirmations as the legacy flow: vA deletes the whole history, any other version only itself. One command; the database snapshots what it removes. No ClickUp call (the legacy sync ignored a zero value).
      const mensagemConfirmacao = isVa
        ? 'Atenção! Excluir a versão inicial (vA) deletará permanentemente TODAS as versões desta proposta. Deseja continuar?'
        : `Deseja realmente excluir a versão ${targetProp.versao}?`;
      if (!confirm(mensagemConfirmacao)) return;
      setSaving(true);
      try {
        const alvoExclusao = propostas.find(p => p.id === targetProp.id) || targetProp;
        if (!Number.isSafeInteger(alvoExclusao?.versao_edicao)) throw Object.assign(new Error('Versão indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        await comercialExecutar('proposta_excluir', targetProp.id, { proposta_id: targetProp.id, expected_version: alvoExclusao.versao_edicao, todas: isVa });
        showToast(isVa ? 'Todo o histórico de propostas foi excluído!' : 'Versão excluída com sucesso!', 'success');
        const restantes = isVa ? [] : propostas.filter(p => p.id !== targetProp.id);
        setPropostas(restantes);
        if (isVa || (currentProposta && currentProposta.id === targetProp.id)) {
          const vaRestante = restantes.find(p => p.versao === 'vA') || restantes[0];
          if (vaRestante) await loadProposalDetails(vaRestante.id);
          else { setCurrentProposta(null); setItens([]); }
        }
        if (clickupTaskId && (isVa || !restantes.some(p => p.situacao === 'Selecionada'))) {
          setKanbanTasks(prev => prev.map(t => t.id === clickupTaskId ? { ...t, valor_estimado: 0 } : t));
          if (selectedTask && selectedTask.id === clickupTaskId) setSelectedTask(prev => ({ ...prev, valor_estimado: 0 }));
        }
        await refreshSupabaseProposalsList();
        loadDashboardData();
        fetchKanbanData();
      } catch (err) {
        showToast(comercialMensagem(err, 'Não foi possível excluir a proposta.'), 'error');
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I11_END */
    
    if (isVa) {
      const message = 'Atenção! Excluir a versão inicial (vA) deletará permanentemente TODAS as versões desta proposta. Deseja continuar?';
      if (!confirm(message)) return;
      setSaving(true);
      try {
        // 1. Deleta itens de todas as versões do negócio
        const proposalIds = propostas.map(p => p.id);
        if (proposalIds.length > 0) {
          await supabaseClient
            .from('itens_proposta')
            .delete()
            .in('proposta_id', proposalIds);
        }

        // 2. Deleta todas as propostas do negócio
        const { error } = await supabaseClient
          .from('propostas')
          .delete()
          .eq('clickup_negocio_id', clickupTaskId);

        if (error) throw error;

        showToast('Todo o histórico de propostas foi excluído!', 'success');

        // 3. Reseta os estados de proposta do React mantendo o negocio ativo e zerando o valor estimado
        setCurrentProposta(null);
        setPropostas([]);
        setItens([]);

        if (clickupTaskId) {
          await syncClickUpProposta(clickupTaskId, 0, 'Select');
          setKanbanTasks(prev => prev.map(t => t.id === clickupTaskId ? { ...t, valor_estimado: 0 } : t));
          if (selectedTask && selectedTask.id === clickupTaskId) {
            setSelectedTask(prev => ({ ...prev, valor_estimado: 0 }));
          }
        }
      } catch (err) {
        console.error(err);
        showToast('Erro ao excluir histórico.', 'error');
      } finally {
        setSaving(false);
      }
    } else {
      const message = `Deseja realmente excluir a versão ${targetProp.versao}?`;
      if (!confirm(message)) return;
      setSaving(true);
      try {
        // Deleta os itens da proposta
        await supabaseClient
          .from('itens_proposta')
          .delete()
          .eq('proposta_id', targetProp.id);

        // Deleta a proposta
        const { error } = await supabaseClient
          .from('propostas')
          .delete()
          .eq('id', targetProp.id);

        if (error) throw error;

        showToast('Versão excluída com sucesso!', 'success');

        const remainingProps = propostas.filter(p => p.id !== targetProp.id);
        setPropostas(remainingProps);

        const hasSelectedRemaining = remainingProps.some(p => p.situacao === 'Selecionada');
        if (!hasSelectedRemaining && clickupTaskId) {
          await syncClickUpProposta(clickupTaskId, 0, 'Select');
          setKanbanTasks(prev => prev.map(t => t.id === clickupTaskId ? { ...t, valor_estimado: 0 } : t));
          if (selectedTask && selectedTask.id === clickupTaskId) {
            setSelectedTask(prev => ({ ...prev, valor_estimado: 0 }));
          }
        }

        const isCurrentDeleted = currentProposta && currentProposta.id === targetProp.id;
        if (isCurrentDeleted) {
          const vaProp = remainingProps.find(p => p.versao === 'vA') || remainingProps[0];
          if (vaProp) {
            await loadProposalDetails(vaProp.id);
          } else {
            setCurrentProposta(null);
            setItens([]);
          }
        }
      } catch (err) {
        console.error(err);
        showToast('Erro ao excluir versão.', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  // 8.9. Busca Proativa do ClickUp baseada no número comercial da proposta
  const handleSearchClickUpProposal = async () => {
    if (!searchTerm.trim()) {
      showToast('Digite um número de proposta para buscar.', 'error');
      return;
    }
    setSearching(true);
    setSearchResult('');
    try {
      const clickupHeaders = {
        "Content-Type": "application/json",
        ...getSupabaseHeaders()
      };

      // 1. Obter os Workspaces (Teams) para achar o team_id
      const teamsRes = await fetch("/clickup-api/team", {
        headers: clickupHeaders
      });
      if (!teamsRes.ok) throw new Error("Erro ao obter workspaces do ClickUp");
      const teamsData = await teamsRes.json();
      const teamId = teamsData.teams?.[0]?.id;
      if (!teamId) throw new Error("Nenhum workspace encontrado no ClickUp");

      // 2. Buscar as tarefas trazendo os campos customizados (com suporte a todos os status e limite de 100)
      let matchedTask = null;
      const numeroDigitado = searchTerm.toString().trim().toLowerCase();

      // 2.1. Buscar na lista atual se clickupListId estiver definido (paginando até encontrar ou esgotar)
      if (clickupListId) {
        try {
          let listPage = 0;
          let hasMoreList = true;
          while (hasMoreList && !matchedTask) {
            const listTasksRes = await fetch(`/clickup-api/list/${clickupListId}/task?archived=false&include_custom_fields=true&limit=100&include_closed=true&page=${listPage}`, {
              headers: clickupHeaders
            });
            if (listTasksRes.ok) {
              const listTasksData = await listTasksRes.json();
              const listTasks = listTasksData.tasks || [];
              if (listTasks.length === 0) {
                hasMoreList = false;
                break;
              }
              matchedTask = listTasks.find(task => {
                const fields = task.custom_fields || [];
                return fields.some(field => {
                  const nameLower = (field.name || "").toLowerCase();
                  const isProposalField = field.id === "c44cc05d-303f-47e2-b243-40c6b26b732f" || 
                                          nameLower.includes("proposta") || 
                                          nameLower.includes("proposal") || 
                                          nameLower.includes("vers");
                  if (isProposalField && field.value !== undefined && field.value !== null) {
                    return field.value.toString().trim().toLowerCase() === numeroDigitado;
                  }
                  return false;
                });
              });
              if (matchedTask) {
                break;
              }
              if (listTasks.length < 100) {
                hasMoreList = false;
              } else {
                listPage++;
              }
            } else {
              hasMoreList = false;
            }
          }
        } catch (listErr) {
          console.error("Erro ao buscar na lista do ClickUp:", listErr);
        }
      }

      // 2.2. Se não encontrou na lista, faz a busca no workspace/team (paginando até encontrar ou esgotar)
      if (!matchedTask) {
        let teamPage = 0;
        let hasMoreTeam = true;
        while (hasMoreTeam && !matchedTask) {
          const teamTasksRes = await fetch(`/clickup-api/team/${teamId}/task?archived=false&include_custom_fields=true&limit=100&include_closed=true&page=${teamPage}`, {
            headers: clickupHeaders
          });
          if (teamTasksRes.ok) {
            const teamTasksData = await teamTasksRes.json();
            const teamTasks = teamTasksData.tasks || [];
            if (teamTasks.length === 0) {
              hasMoreTeam = false;
              break;
            }
            matchedTask = teamTasks.find(task => {
              const fields = task.custom_fields || [];
              return fields.some(field => {
                const nameLower = (field.name || "").toLowerCase();
                const isProposalField = field.id === "c44cc05d-303f-47e2-b243-40c6b26b732f" || 
                                        nameLower.includes("proposta") || 
                                        nameLower.includes("proposal") || 
                                        nameLower.includes("vers");
                if (isProposalField && field.value !== undefined && field.value !== null) {
                  return field.value.toString().trim().toLowerCase() === numeroDigitado;
                }
                return false;
              });
            });
            if (matchedTask) {
              break;
            }
            if (teamTasks.length < 100) {
              hasMoreTeam = false;
            } else {
              teamPage++;
            }
          } else {
            hasMoreTeam = false;
            if (teamPage === 0) {
              throw new Error("Erro ao obter tarefas do ClickUp");
            }
          }
        }
      }

      if (!matchedTask) {
        showToast('Proposta não encontrada no ClickUp.', 'error');
        setSearchResult('🔴 Proposta não encontrada no ClickUp');
        return;
      }

      // 4. Se encontrar, captura o id real dela (task_id) e dispara loadPropostas
      let taskId = matchedTask.id;

      // Se a tarefa encontrada for a tarefa principal e tiver um relacionamento 'Comercial Proposal' apontando para a tarefa da proposta,
      // usamos o ID da tarefa relacionada (onde as versões do Supabase são associadas).
      const matchedNameLower = (matchedTask.name || "").toLowerCase();
      const isAlreadyProposalTask = matchedNameLower.includes("proposta comercial") || 
                                    matchedNameLower.includes("comercial proposal");
      if (!isAlreadyProposalTask) {
        const relField = (matchedTask.custom_fields || []).find(f => 
          (f.name || "").toLowerCase() === "comercial proposal" || 
          (f.name || "").toLowerCase() === "proposta comercial"
        );
        if (relField && relField.value && relField.value.length > 0) {
          const relTask = relField.value.find(t => 
            (t.name || "").toLowerCase().includes("proposta comercial") || 
            (t.name || "").toLowerCase().includes("comercial proposal")
          ) || relField.value[0];
          if (relTask && relTask.id) {
            taskId = relTask.id;
          }
        }
      }

      const resolvedTaskId = await resolveTaskIdFormat(taskId);
      if (matchedTask.list && matchedTask.list.id) {
        setClickupListId(matchedTask.list.id);
      }
      setClickupTaskId(resolvedTaskId);
      setSearchResult(`🟢 Negócio Vinculado: ${matchedTask.name}`);
      showToast('Negócio ClickUp vinculado com sucesso!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Falha na busca do ClickUp.', 'error');
      setSearchResult('🔴 Erro ao comunicar com o ClickUp');
    } finally {
      setSearching(false);
    }
  };

  // 9. Alterar Status de Versão de Proposta Unificado (Gatilho para sincronização no ClickUp)
  const handleUpdateVersionStatus = async (targetTaskId, versionId, newStatus) => {
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // Selecionada / Ativa / Desconsiderada only. Ganho/Perdido are reachable only through the closing commands (W5/W6).
      if (!versionId || !targetTaskId) return;
      if (!['Selecionada', 'Ativa', 'Desconsiderada'].includes(newStatus)) {
        showToast('Status não permitido por esta ação.', 'error');
        return;
      }
      setSaving(true);
      try {
        const alvo = propostas.find(p => p.id === versionId) || (currentProposta && currentProposta.id === versionId ? currentProposta : null);
        let versao = alvo?.versao_edicao;
        // selecting a version saves the editor first (legacy order); the status command then carries the version that save produced
        if (newStatus === 'Selecionada' && !isReadOnly && currentProposta && currentProposta.id === versionId) {
          const salva = await handleSaveProposal();
          if (salva && salva.proposta_id === versionId) versao = salva.versao_edicao;
        }
        if (!Number.isSafeInteger(versao)) throw Object.assign(new Error('Versão indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        await comercialExecutar('proposta_situacao', versionId, { proposta_id: versionId, expected_version: versao, situacao: newStatus });
        showToast(`Status atualizado para ${newStatus}!`, 'success');
        await loadPropostas(versionId);
        await loadProposalDetails(versionId);
        await refreshSupabaseProposalsList();
        loadDashboardData();
        fetchKanbanData();
      } catch (err) {
        showToast(comercialMensagem(err, 'Não foi possível atualizar o status da proposta.'), 'error');
        loadPropostas(versionId, true, true);
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I11_END */
    if (!versionId || !targetTaskId) return;
    const taskId = String(targetTaskId).replace('#', '').trim();
    
    if (!clickupTaskId) {
      setClickupTaskId(targetTaskId);
    }
    
    const currentResponsavel = selectedTask ? selectedTask.responsavel_negocio : '';

    // 1. Interface Otimista: Mudar localmente na mesma hora para que as badges de status na timeline mudem de cor imediatamente
    if (currentProposta && currentProposta.id === versionId) {
      setCurrentProposta(prev => ({ ...prev, situacao: newStatus, criado_por: currentResponsavel }));
    }
    setPropostas(prev => prev.map(p => {
      if (p.id === versionId) {
        return { ...p, situacao: newStatus, criado_por: currentResponsavel };
      }
      if (newStatus === 'Selecionada' && p.id !== versionId) {
        return { ...p, situacao: 'Desconsiderada' };
      }
      return p;
    }));

    if (newStatus === 'Selecionada') {
      const targetProp = propostas.find(p => p.id === versionId) || currentProposta;
      const valToSync = targetProp ? parseFloat(targetProp.total_proposta) || 0 : realTimeGrandTotal;

      setKanbanTasks(prevTasks => prevTasks.map(t => t.id === targetTaskId ? { ...t, valor_estimado: valToSync, responsavel_negocio: t.responsavel_negocio || t.assignees } : t));
      if (selectedTask && selectedTask.id === targetTaskId) {
        setSelectedTask(prev => ({ ...prev, valor_estimado: valToSync, responsavel_negocio: prev.responsavel_negocio }));
      }
    }

    setSaving(true);
    try {
      if (!isReadOnly && newStatus === 'Selecionada') {
        await handleSaveProposal();
      }

      // 2. REGRA DE NEGÓCIO: Se for Selecionada, as outras irmãs são Desconsideradas. 
      // Rascunhos múltiplos ativos podem coexistir simultaneamente como 'Ativa'.
      if (newStatus === 'Selecionada') {
        await supabaseClient
          .from('propostas')
          .update({ situacao: 'Desconsiderada' })
          .eq('clickup_negocio_id', targetTaskId)
          .neq('id', versionId);
      }

      // 3. Atualiza a proposta alvo no Supabase
      const updateData = { 
        situacao: newStatus,
        criado_por: currentResponsavel
      };

      // Se for alterada para Em Andamento ou Selecionada a partir de Ganho/Perdido, limpa fechamento
      if (newStatus === 'Selecionada' || newStatus === 'Ativa' || newStatus === 'Em Andamento') {
        updateData.data_fechamento = null;
        updateData.motivo_perda = null;
        if (newStatus === 'Selecionada') {
          // Só sobrescreve o total com o valor calculado em tempo real se ele for > 0.
          // Se os itens ainda não carregaram no editor (condição de corrida), realTimeGrandTotal
          // fica 0 — nesse caso preserva o total já salvo da própria proposta, em vez de zerá-lo.
          const existingTotal = parseFloat((propostas.find(p => p.id === versionId) || currentProposta || {}).total_proposta) || 0;
          updateData.total_proposta = realTimeGrandTotal > 0 ? realTimeGrandTotal : existingTotal;
        }
      }

      const { error } = await supabaseClient
        .from('propostas')
        .update(updateData)
        .eq('id', versionId);

      if (error) throw error;

      // Essa proposta acabou de virar a "Selecionada" e teve seu próprio
      // data_fechamento resetado (updateData acima) — a Previsão de
      // Fechamento do negócio precisa acompanhar, senão fica presa
      // mostrando a data de uma proposta que não é mais a usada no Forecast.
      // Volta a ser preenchida no próximo save com uma Data Fechamento real.
      if (newStatus === 'Selecionada') {
        try {
          await supabaseClient.from('negocios').update({ data_previsao: null }).eq('clickup_negocio_id', taskId);
          setSelectedTask(prev => (prev && String(prev.clickup_negocio_id || prev.id || '').replace('#', '').trim() === taskId)
            ? { ...prev, data_previsao: null }
            : prev);
        } catch (err) {
          console.warn('Erro ao limpar Previsão de Fechamento do negócio:', err);
        }
      }

      // Sincroniza o valor da proposta selecionada com o Deal Value no ClickUp.
      // NÃO mexe em negocios.estagio: selecionar uma versão é uma decisão sobre
      // QUAL proposta vale, não sobre em que fase do pipeline o negócio está
      // (bug corrigido em 21/08 — este bloco chamava handleOpportunityStateChange
      // e resetava o negócio pro primeiro estágio do Kanban toda vez que uma
      // proposta era selecionada, pois kanbanColumns[0] é sempre 'Registro').
      // Estágio só muda por ação explícita: Kanban (drag/dropdown),
      // Editar Negócio/Oportunidade, ou o fluxo de Ganho/Perdido em handleConfirmClose.
      if (newStatus === 'Selecionada' || newStatus === 'Em Andamento') {
        const targetProp = propostas.find(p => p.id === versionId) || currentProposta;
        const valToSync = targetProp ? parseFloat(targetProp.total_proposta) || 0 : realTimeGrandTotal;
        await syncClickUpProposta(taskId, valToSync, 'Select');
      }

      showToast(`Status atualizado para ${newStatus}!`, 'success');
      await loadPropostas(versionId);
      await loadProposalDetails(versionId);
      await refreshSupabaseProposalsList();
      loadDashboardData();
      fetchKanbanData();
    } catch (err) {
      console.warn("Erro silencioso de PostgREST ou rede na sincronização de propostas:", err);
      // O front-end otimista garante que a UI continuará funcionando sem travar os botões
    } finally {
      setSaving(false);
    }
  };

  // Alterar "Referência de Forecast" de uma versão — mecanismo independente de
  // `situacao`/`estagio`. Ao contrário de handleUpdateVersionStatus, NUNCA marca
  // as irmãs como 'Desconsiderada' e NUNCA chama handleOpportunityStateChange.
  const handleToggleForecastReference = async (targetTaskId, versionId, newValue) => {
    if (!versionId || !targetTaskId) return;
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      setSaving(true);
      try {
        const alvo = propostas.find(p => p.id === versionId) || (currentProposta && currentProposta.id === versionId ? currentProposta : null);
        if (!Number.isSafeInteger(alvo?.versao_edicao)) throw Object.assign(new Error('Versão indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        await comercialExecutar('proposta_forecast', versionId, { proposta_id: versionId, expected_version: alvo.versao_edicao, valor: newValue === true });
        await loadPropostas(versionId);
        await refreshSupabaseProposalsList();
        loadDashboardData();
        fetchKanbanData();
      } catch (err) {
        showToast(comercialMensagem(err, 'Não foi possível atualizar a referência de forecast.'), 'error');
        loadPropostas(versionId, true, true);
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I11_END */
    const taskId = String(targetTaskId).replace('#', '').trim();

    // 1. Interface Otimista
    if (currentProposta && currentProposta.id === versionId) {
      setCurrentProposta(prev => ({ ...prev, referencia_forecast: newValue }));
    }
    setPropostas(prev => prev.map(p => {
      if (p.id === versionId) return { ...p, referencia_forecast: newValue };
      if (newValue === true) return { ...p, referencia_forecast: false };
      return p;
    }));

    setSaving(true);
    try {
      // 2. Exclusividade própria da referência de forecast — independente da
      // exclusividade de 'Selecionada' (não mexe em situacao das irmãs).
      if (newValue === true) {
        await supabaseClient
          .from('propostas')
          .update({ referencia_forecast: false })
          .eq('clickup_negocio_id', targetTaskId)
          .neq('id', versionId);
      }

      const { error } = await supabaseClient
        .from('propostas')
        .update({ referencia_forecast: newValue })
        .eq('id', versionId);

      if (error) throw error;

      // 3. Sincroniza o valor com o ClickUp apenas ao MARCAR (mesma função usada
      // pelo fluxo oficial de "Selecionar Versão"). Desmarcar não re-sincroniza
      // — fica como estava até a próxima ação.
      if (newValue === true) {
        const targetProp = propostas.find(p => p.id === versionId) || currentProposta;
        const valToSync = targetProp ? parseFloat(targetProp.total_proposta) || 0 : realTimeGrandTotal;
        await syncClickUpProposta(taskId, valToSync, 'ForecastReference');
      }

      await loadPropostas(versionId);
      await refreshSupabaseProposalsList();
      loadDashboardData();
      fetchKanbanData();
    } catch (err) {
      console.warn("Erro silencioso ao atualizar referência de forecast:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmClose = async () => {
    /* B2_I10_BEGIN */
    if (kanbanModoRef.current !== 'legacy' && currentProposta && (showCloseModal === 'win' || showCloseModal === 'loss') && supabaseClient) {
      // W6: Ganho/Perdido WITH a proposal -> ONE gateway command (proposal + opportunity + audit + ClickUp intent). No REST and no direct ClickUp in this branch.
      const ganhou = showCloseModal === 'win';
      if (!ganhou && !selectedLossReason) {
        showToast('Por favor, selecione o motivo da perda.', 'error');
        return;
      }
      setSaving(true);
      let key;
      try {
        if (kanbanModoRef.current !== 'canonico_v1' || identidadeModoRef.current !== 'canonico_v1' || !window.CrmKanbanEstagio) throw new Error('Gateway indisponível.');
        // the closing target is the one in clickupTaskId (drag and drawer both set it); the open drawer is only the fallback
        const porClickup = kanbanTasks.find(t => t.crm_negocio_id && (t.id === clickupTaskId || t.clickup_negocio_id === clickupTaskId));
        const crmId = porClickup?.crm_negocio_id || selectedTask?.crm_negocio_id || (selectedTask?.id && String(selectedTask.id).includes('-') ? selectedTask.id : null);
        const ao_vivo = kanbanTasks.find(t => t.crm_negocio_id && t.crm_negocio_id === crmId);
        const versao = ao_vivo ? ao_vivo.versao_estagio : selectedTask?.versao_estagio;
        if (!crmId || !session?.user?.id || !currentProposta?.id || versao === undefined || versao === null) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        const dataFechamento = closeDate || new Date().toISOString().split('T')[0];
        const totalNumerico = Number(realTimeGrandTotal);
        const total = Number.isFinite(totalNumerico) && totalNumerico > 0 ? Number(totalNumerico.toFixed(2)) : null;
        key = 'fechamento/' + session.user.id + '/' + crmId;
        let command = kanbanComandosRef.current.get(key);
        const resultado = ganhou ? 'Ganho' : 'Perdido', motivo = ganhou ? null : selectedLossReason;
        if (command && (command.expected_version !== versao || command.resultado !== resultado || command.proposta_id !== String(currentProposta.id).toLowerCase() || command.motivo_perda !== motivo || command.data_fechamento !== dataFechamento || command.total_proposta !== total)) throw Object.assign(new Error('Operação pendente.'), { code: 'REQUEST_PENDING', ambiguous: true });
        const gateway = window.CrmKanbanEstagio.criar({ client: supabaseClient, config });
        if (!command) {
          command = window.CrmKanbanEstagio.prepararFechamento({ negocio_id: crmId, expected_version: versao, resultado, proposta_id: currentProposta.id, data_fechamento: dataFechamento, motivo_perda: motivo, total_proposta: total });
          kanbanComandosRef.current.set(key, command);
        }
        const result = await gateway.fechar(command);
        kanbanComandosRef.current.delete(key);
        showToast('Proposta marcada como ' + (ganhou ? 'GANHA' : 'PERDIDA') + ' com sucesso!' + (result.sync_status === 'pendente' ? ' Sincronização ClickUp pendente.' : ''), 'success');
        setShowCloseModal(false);
        setShowDrawer(false);
        loadPropostas(currentProposta.id);
        await refreshSupabaseProposalsList();
        loadDashboardData();
        fetchKanbanData();
      } catch (error) {
        let code, ambiguous = false;
        try { code = Object.getOwnPropertyDescriptor(error, 'code')?.value; ambiguous = Object.getOwnPropertyDescriptor(error, 'ambiguous')?.value === true; } catch {}
        if (key && !ambiguous) kanbanComandosRef.current.delete(key);
        showToast(code === 'CONFLITO_VERSAO' ? 'A oportunidade mudou. Atualize antes de tentar novamente.' : code === 'REQUEST_PENDING' ? 'Operação anterior pendente. Repita sem alterar os dados.' : code === 'NENHUMA_ALTERACAO' ? 'A oportunidade já está nesse estado.' : code === 'PROPOSTA_NAO_SELECIONADA' ? 'Para ganhar, a proposta precisa ser a única versão Selecionada.' : code === 'PROPOSTA_INVALIDA' ? 'Proposta inválida para esta oportunidade.' : code === 'MOTIVO_INVALIDO' ? 'Motivo da perda inválido.' : code === 'DATA_INVALIDA' ? 'Data de fechamento inválida.' : code === 'TOTAL_INVALIDO' ? 'Total da proposta inválido.' : 'Não foi possível registrar o fechamento.', 'error');
        if (code === 'CONFLITO_VERSAO') fetchKanbanData();
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I10_END */
    /* B2_I9_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // W5: ONLY the loss of an opportunity without proposals goes through the gateway (no REST/ClickUp fallback). Winning and losing WITH proposals (W6)
      // stay blocked in canonical mode until their own gateway exists.
      if (showCloseModal !== 'loss' || currentProposta || !supabaseClient) {
        showToast('Fechamento pelo gateway ainda não está habilitado.', 'error');
        return;
      }
      if (!selectedLossReason) {
        showToast('Por favor, selecione o motivo da perda.', 'error');
        return;
      }
      setSaving(true);
      let key;
      try {
        if (kanbanModoRef.current !== 'canonico_v1' || identidadeModoRef.current !== 'canonico_v1' || !window.CrmKanbanEstagio) throw new Error('Gateway indisponível.');
        // the closing target is the one in clickupTaskId (drag and drawer both set it); the open drawer is only the fallback
        const porClickup = kanbanTasks.find(t => t.crm_negocio_id && (t.id === clickupTaskId || t.clickup_negocio_id === clickupTaskId));
        const crmId = porClickup?.crm_negocio_id || selectedTask?.crm_negocio_id || (selectedTask?.id && String(selectedTask.id).includes('-') ? selectedTask.id : null);
        const ao_vivo = kanbanTasks.find(t => t.crm_negocio_id && t.crm_negocio_id === crmId);
        const versao = ao_vivo ? ao_vivo.versao_estagio : selectedTask?.versao_estagio;
        if (!crmId || !session?.user?.id || versao === undefined || versao === null) throw Object.assign(new Error('Oportunidade indisponível.'), { code: 'VERSAO_INDISPONIVEL' });
        const dataFechamento = closeDate || new Date().toISOString().split('T')[0];
        key = 'perda/' + session.user.id + '/' + crmId;
        let command = kanbanComandosRef.current.get(key);
        if (command && (command.expected_version !== versao || command.motivo_perda !== selectedLossReason || command.data_fechamento !== dataFechamento)) throw Object.assign(new Error('Operação pendente.'), { code: 'REQUEST_PENDING', ambiguous: true });
        const gateway = window.CrmKanbanEstagio.criar({ client: supabaseClient, config });
        if (!command) {
          command = window.CrmKanbanEstagio.prepararPerda({ negocio_id: crmId, expected_version: versao, motivo_perda: selectedLossReason, data_fechamento: dataFechamento });
          kanbanComandosRef.current.set(key, command);
        }
        const result = await gateway.perder(command);
        kanbanComandosRef.current.delete(key);
        showToast(result.sync_status === 'pendente' ? 'Oportunidade marcada como PERDIDA com sucesso! Sincronização ClickUp pendente.' : 'Oportunidade marcada como PERDIDA com sucesso!', 'success');
        setShowCloseModal(false);
        setShowDrawer(false);
        await refreshSupabaseProposalsList();
        loadDashboardData();
        fetchKanbanData();
      } catch (error) {
        let code, ambiguous = false;
        try { code = Object.getOwnPropertyDescriptor(error, 'code')?.value; ambiguous = Object.getOwnPropertyDescriptor(error, 'ambiguous')?.value === true; } catch {}
        if (key && !ambiguous) kanbanComandosRef.current.delete(key);
        showToast(code === 'CONFLITO_VERSAO' ? 'A oportunidade mudou. Atualize antes de tentar novamente.' : code === 'REQUEST_PENDING' ? 'Operação anterior pendente. Repita sem alterar os dados.' : code === 'NENHUMA_ALTERACAO' ? 'A oportunidade já está perdida.' : code === 'MOTIVO_INVALIDO' ? 'Motivo da perda inválido.' : code === 'DATA_INVALIDA' ? 'Data de fechamento inválida.' : code === 'PROPOSTAS_EXISTENTES' ? 'Esta oportunidade tem propostas: o fechamento com proposta ainda não está habilitado.' : 'Não foi possível registrar a perda.', 'error');
        if (code === 'CONFLITO_VERSAO') fetchKanbanData();
      } finally {
        setSaving(false);
      }
      return;
    }
    /* B2_I9_END */
    const podeFecharSemProposta = showCloseModal === 'loss';
    if ((!currentProposta && !podeFecharSemProposta) || !supabaseClient) return;
    
    const dateVal = closeDate || new Date().toISOString().split('T')[0];
    if (showCloseModal === 'loss' && !selectedLossReason) {
      showToast('Por favor, selecione o motivo da perda.', 'error');
      return;
    }

    setSaving(true);
    try {
      const isWin = showCloseModal === 'win';
      const situacao = isWin ? 'Ganho' : 'Perdido';
      const motivo = isWin ? null : selectedLossReason;

      // 1. Registrar o fechamento na proposta existente. Perdas sem proposta
      // também são válidas e ficam gravadas diretamente no negócio.
      if (currentProposta) {
        const { error } = await supabaseClient
          .from('propostas')
          .update({
            situacao: situacao,
            motivo_perda: motivo,
            data_fechamento: dateVal,
            total_proposta: realTimeGrandTotal
          })
          .eq('id', currentProposta.id);
        if (error) throw error;
      } else {
        const idSemHash = String(clickupTaskId || selectedTask?.id || '').replace('#', '').trim();
        if (!idSemHash) throw new Error('Não foi possível identificar a oportunidade para registrar a perda.');
        const { error } = await supabaseClient
          .from('negocios')
          .update({ estagio: 'Perdido', motivo_perda: motivo, data_fechamento: dateVal })
          .or(`clickup_negocio_id.eq.${idSemHash},clickup_negocio_id.eq.#${idSemHash}`);
        if (error) throw error;
      }

      // 2. Sincronizar ClickUp se aplicável
      if (clickupTaskId) {
        if (currentProposta) await syncClickUpProposta(clickupTaskId, realTimeGrandTotal, situacao);
        
        const targetOption = kanbanColumns.find(c => c.name.toLowerCase().includes(isWin ? 'ganho' : 'perdido'));
        if (targetOption) {
          await handleOpportunityStateChange(clickupTaskId, targetOption.id);
        }
      }

      showToast(`${currentProposta ? 'Proposta' : 'Oportunidade'} marcada como ${isWin ? 'GANHA' : 'PERDIDA'} com sucesso!`, 'success');
      setShowCloseModal(false);
      setShowDrawer(false); // Fecha o Drawer conforme exigido no fluxo de sucesso
      if (currentProposta) loadPropostas(currentProposta.id);
      await refreshSupabaseProposalsList();
      loadDashboardData();
      fetchKanbanData();
    } catch (err) {
      console.error(err);
      showToast('Erro ao fechar proposta.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 10. Criar Novo Produto Dinamicamente via API Transacional (Proxy)
  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch('/api/catalogo/produtos', {
        method: 'POST',
        headers: {
          ...await getCrmHeaders(),
          'Content-Type': 'application/json',
          'X-Request-Id': crypto.randomUUID()
        },
        body: JSON.stringify({
          nome: newProduct.nome,
          fabricante_id: newProduct.fabricante,
          custo_referencia: parseFloat(newProduct.custo_referencia) || 0
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao cadastrar produto via API');
      }
      showToast('Produto cadastrado!', 'success');
      setNewProduct({ nome: '', fabricante: '', custo_referencia: '' });
      await loadProducts();
    } catch (err) {
      showToast(err.message || 'Erro ao cadastrar produto', 'error');
    }
  };

  // 11. Operações CRUD no Painel de Gestão (API Transacional)
  const handleSaveProductEdit = async (e) => {
    e.preventDefault();
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch(`/api/catalogo/produtos/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          ...await getCrmHeaders(),
          'Content-Type': 'application/json',
          'X-Request-Id': crypto.randomUUID()
        },
        body: JSON.stringify({
          nome: editingProduct.nome,
          fabricante_id: editingProduct.fabricante_id || editingProduct.fabricante,
          custo_referencia: parseFloat(editingProduct.custo_referencia) || 0
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao atualizar produto via API');
      }
      showToast('Produto atualizado com sucesso!', 'success');
      setEditingProduct(null);
      loadProducts();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao editar produto.', 'error');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!confirm('Deseja realmente excluir este produto?')) return;
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch(`/api/catalogo/produtos/${id}`, {
        method: 'DELETE',
        headers: {
          ...await getCrmHeaders(),
          'X-Request-Id': crypto.randomUUID()
        }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao excluir produto. Ele pode estar sendo usado em uma proposta.');
      }
      showToast('Produto excluído com sucesso!', 'success');
      loadProducts();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao excluir produto.', 'error');
    }
  };

  // Lista de vendedores agora é derivada automaticamente (ClickUp ∩ usuarios_clickup,
  // ver loadVendedores) — criar/editar/excluir manualmente não existe mais, só ocultar.
  const handleToggleOcultoVendedor = async (vendedor) => {
    const isOculto = !vendedor.oculto;
    const updatedVendedores = vendedores.map(v => 
      v.id === vendedor.id ? { ...v, oculto: isOculto } : v
    );
    setVendedores(updatedVendedores);
    safeStorage.setItem('crm_cache_vendedores', JSON.stringify(updatedVendedores));
    
    // Atualizar no safeStorage a lista de IDs ocultos
    const ocultos = JSON.parse(safeStorage.getItem('crm_vendedores_ocultos') || '[]');
    let novosOcultos;
    if (isOculto) {
      novosOcultos = [...new Set([...ocultos, String(vendedor.id)])];
    } else {
      novosOcultos = ocultos.filter(id => String(id) !== String(vendedor.id));
    }
    localStorage.setItem('crm_vendedores_ocultos', JSON.stringify(novosOcultos));
    
    // Salvar no estado de vendedoresOcultos
    setVendedoresOcultos(novosOcultos);

    /* B2_I12_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // vendedores has no write policy for the public role (migration 20260712_enable_rls): the REST upsert below never persisted anything; the hidden list is a local preference.
      showToast(`Vendedor ${isOculto ? 'ocultado' : 'exibido'} com sucesso!`, 'success');
      return;
    }
    /* B2_I12_END */
    // Opcionalmente tentar persistir no Supabase (se a tabela de vendedores suportar a coluna oculto ou ativo_crm)
    if (supabaseClient) {
      try {
        await supabaseClient.from('vendedores').upsert({
          id: vendedor.id,
          nome: vendedor.nome,
          oculto: isOculto
        });
      } catch (err) {
        console.warn("Erro ao persistir status oculto do vendedor no Supabase:", err);
      }
    }
    
    showToast(`Vendedor ${isOculto ? 'ocultado' : 'exibido'} com sucesso!`, 'success');
  };

  const triggerLossModal = () => {
    setSelectedLossReason('');
    setShowLossModal(true);
  };

  const handleConfirmLoss = async () => {
    if (!currentProposta || !supabaseClient) return;
    /* B2_I11_BEGIN */
    if (kanbanModoRef.current !== 'legacy') {
      // Dead code in the current UI; kept closed in canonical mode so it can never become a path around the closing commands (W5/W6).
      showToast('Use o fluxo oficial de perda da oportunidade.', 'error');
      return;
    }
    /* B2_I11_END */
    if (!selectedLossReason) {
      showToast('Selecione um motivo para a perda.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabaseClient
        .from('propostas')
        .update({ 
          situacao: 'Perdido',
          motivo_perda: selectedLossReason
        })
        .eq('id', currentProposta.id);

      if (error) throw error;

      showToast('Proposta marcada como PERDIDA!', 'success');
      
      setCurrentProposta({
        ...currentProposta,
        situacao: 'Perdido',
        motivo_perda: selectedLossReason
      });

      setShowLossModal(false);
      // Silencioso: só situação/motivo mudaram (sem delete+reinsert de itens),
      // e a proposta já vira read-only assim que recarregar — não há por que
      // piscar a tela cheia de "Carregando..." por cima do editor.
      loadPropostas(currentProposta.id, true, true);
    } catch (err) {
      console.error(err);
      showToast('Erro ao atualizar situação para Perdido.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 12. Gestão Administrativa de Fabricantes (API Transacional do Catálogo - 4A.4)
  const handleCreateDistributor = async (e) => {
    e.preventDefault();
    if (!newDistributorName.trim()) return;
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch('/api/catalogo/fabricantes', {
        method: 'POST',
        headers: {
          ...await getCrmHeaders(),
          'Content-Type': 'application/json',
          'X-Request-Id': crypto.randomUUID()
        },
        body: JSON.stringify({
          nome: newDistributorName.trim()
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 403) throw new Error('Você não tem permissão para administrar o catálogo.');
        if (response.status === 409) throw new Error(err.error || 'Já existe fabricante com esse nome.');
        throw new Error(err.error || 'Erro ao cadastrar fabricante.');
      }
      showToast('Fabricante adicionado com sucesso!', 'success');
      setNewDistributorName('');
      await loadFabricantes();
    } catch (err) {
      showToast(err.message || 'Erro ao cadastrar fabricante', 'error');
    }
  };

  const handleSaveDistributorEdit = async (e) => {
    e.preventDefault();
    if (!editingDistributor?.nome?.trim()) return;
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch(`/api/catalogo/fabricantes/${editingDistributor.id}`, {
        method: 'PUT',
        headers: {
          ...await getCrmHeaders(),
          'Content-Type': 'application/json',
          'X-Request-Id': crypto.randomUUID()
        },
        body: JSON.stringify({
          nome: editingDistributor.nome.trim()
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 403) throw new Error('Você não tem permissão para administrar o catálogo.');
        if (response.status === 409) {
          if (err.code === 'FABRICANTE_REFERENCIADO_NAO_RENOMEAVEL' || err.error?.includes('referenciado')) {
            throw new Error('Fabricante referenciado não pode ser renomeado.');
          }
          throw new Error(err.error || 'Já existe fabricante com esse nome.');
        }
        throw new Error(err.error || 'Erro ao editar fabricante.');
      }
      showToast('Fabricante atualizado com sucesso!', 'success');
      setEditingDistributor(null);
      await loadFabricantes();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao editar fabricante.', 'error');
    }
  };

  const handleInactivateDistributor = async (id) => {
    if (!confirm('Deseja inativar este fabricante? Ele deixará de ser selecionável em novos cadastros, mas o histórico será preservado.')) return;
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch(`/api/catalogo/fabricantes/${id}/inativar`, {
        method: 'POST',
        headers: {
          ...await getCrmHeaders(),
          'Content-Type': 'application/json',
          'X-Request-Id': crypto.randomUUID()
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 403) throw new Error('Você não tem permissão para administrar o catálogo.');
        throw new Error(err.error || 'Erro ao inativar fabricante.');
      }
      showToast('Fabricante inativado com sucesso!', 'success');
      await loadFabricantes();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao inativar fabricante.', 'error');
    }
  };

  const handleActivateDistributor = async (id) => {
    try {
      const token = safeStorage.getItem('crm_user_clickup_token');
      const response = await fetch(`/api/catalogo/fabricantes/${id}/ativar`, {
        method: 'POST',
        headers: {
          ...await getCrmHeaders(),
          'Content-Type': 'application/json',
          'X-Request-Id': crypto.randomUUID()
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 403) throw new Error('Você não tem permissão para administrar o catálogo.');
        throw new Error(err.error || 'Erro ao ativar fabricante.');
      }
      showToast('Fabricante ativado com sucesso!', 'success');
      await loadFabricantes();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao ativar fabricante.', 'error');
    }
  };



  const renderTimeline = (showHeader = true) => {
    return (
      <div className="flex flex-col h-full">
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-indigo-500 dark:text-slate-300 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Timeline de Versões
            </h2>
            <span className="bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-0.5 rounded-full text-[10px] font-bold tabular-nums">
              {propostas.length}
            </span>
          </div>
        )}

        {propostas.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-50 to-slate-100 dark:from-indigo-950/50 dark:to-slate-800 rounded-2xl border border-indigo-100/80 dark:border-indigo-800/50 flex items-center justify-center">
              <svg className="w-7 h-7 text-indigo-400 dark:text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-slate-700 dark:text-slate-300 font-semibold">Nenhuma proposta criada</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Crie a primeira versão para este negócio.</p>
            </div>
            <button 
              onClick={handleCreateInitialProposal}
              className="w-full py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 rounded-xl text-xs font-bold text-white shadow-lg shadow-indigo-600/25 transition-all"
            >
              + Criar Versão vA
            </button>
          </div>
        ) : (
          <div className="flex-1 space-y-2 pr-1 overflow-visible">
            {propostas.map((prop, i) => {
              const isSelected = currentProposta && currentProposta.id === prop.id;
              const statusConfig = {
                 'Ativa': { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-400' },
                 'Selecionada': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' },
                 'Ganho': { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300', dot: 'bg-amber-400' },
                 'Desconsiderada': { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-400' },
                 'Descartada': { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-400' },
                 'Não selecionada': { bg: 'bg-slate-50 dark:bg-slate-900', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-700', dot: 'bg-slate-400' },
                 'Substituída': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' },
                 'Perdido': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-400' }
               };
              const sc = statusConfig[prop.situacao] || { bg: 'bg-slate-50 dark:bg-slate-900', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-700', dot: 'bg-slate-400' };
              
              return (
                <div 
                  key={prop.id}
                  onClick={async (e) => {
                    if (e.target.closest('.btn-three-dots')) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    await loadProposalDetails(prop.id);
                    setDrawerTab('budget');
                  }}
                  className={`group relative rounded-xl cursor-pointer timeline-item transition-all duration-200 overflow-visible ${
                    openMenuVersionId === prop.id ? 'z-[9999]' : 'z-10'
                  } ${
                    isSelected 
                      ? 'bg-white dark:bg-slate-800 ring-2 ring-indigo-500 shadow-md shadow-indigo-500/10' 
                      : 'bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 hover:border-indigo-200 hover:shadow-sm'
                  }`}
                >
                  {/* Indicador lateral de seleção */}
                  {isSelected && (
                    <div className="absolute left-0 top-3 bottom-3 w-[3px] bg-indigo-500 rounded-r-full"></div>
                  )}
                  
                  <div className="p-3">
                    {/* Linha 1: Versão + Status + Menu */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-lg text-[11px] font-black shadow-sm">
                          {formatVersionDisplay(prop.versao)}
                        </span>
                        {prop.cenario && (
                          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{prop.cenario}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 relative" onClick={(e) => e.stopPropagation()}>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-semibold ${sc.bg} ${sc.text} ${sc.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                          {prop.situacao}
                        </span>
                        {prop.referencia_forecast && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-amber-50 text-amber-700 border-amber-200"
                            title="Referência de valor para o Forecast"
                          >
                            🎯 Forecast
                          </span>
                        )}
                      <div className="relative">
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (openMenuVersionId === prop.id) {
                              setOpenMenuVersionId(null);
                            } else {
                              const btn = e.currentTarget || e.target.closest('button');
                              if (window.openVersionPortalMenu) {
                                window.openVersionPortalMenu(btn, prop.id);
                              } else {
                                const rect = btn.getBoundingClientRect();
                                const topPos = rect.bottom + 4;
                                const leftPos = Math.max(10, rect.right - 180);
                                const finalTop = (topPos + 100 > window.innerHeight) ? Math.max(10, rect.top - 80) : topPos;
                                setMenuPosition({ top: finalTop, left: leftPos });
                                setOpenMenuVersionId(prop.id);
                              }
                            }
                          }}
                          className="btn-three-dots p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
                          title="Opções da Versão"
                        >
                          <svg className="w-3.5 h-3.5 pointer-events-none" fill="currentColor" viewBox="0 0 24 24">
                            <path className="pointer-events-none" d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                          </svg>
                        </button>
                        {openMenuVersionId === prop.id && ReactDOM.createPortal(
                          <React.Fragment>
                            <div className="fixed inset-0 z-[9999998] bg-transparent cursor-default" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenMenuVersionId(null); }} />
                            <div 
                              style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
                              className="fixed z-[9999999] w-48 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200/90 dark:border-slate-700/90 p-1.5 space-y-0.5 text-left animate-in fade-in zoom-in-95 duration-100 block"
                            >
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenMenuVersionId(null);
                                  await loadProposalDetails(prop.id);
                                  setDrawerTab('budget');
                                }}
                                className="w-full text-left text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 rounded-lg p-2 flex items-center gap-2 transition-colors cursor-pointer"
                              >
                                <span>✏️ Editar Versão</span>
                              </button>
                              <div className="border-t border-slate-100 dark:border-slate-800 my-0.5"></div>
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenMenuVersionId(null);
                                  await handleDeleteProposal(prop);
                                }}
                                className="w-full text-left text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg p-2 flex items-center gap-2 transition-colors cursor-pointer"
                              >
                                <span>🗑️ Excluir Versão</span>
                              </button>
                            </div>
                          </React.Fragment>,
                          document.body
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Linha 2: Data + Validade + Valor */}
                  <div className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium flex items-center gap-1">
                          <svg className="w-3 h-3 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDateSafe(prop.created_at, {day: '2-digit', month: '2-digit'})}
                        </span>

                        {(() => {
                          const dealEstagio = selectedTask ? (selectedTask.estagio || selectedTask.status) : null;
                          const val = calcularValidadeProposta(prop, dealEstagio);
                          if (!val) return null;
                          if (val.status === 'vencida') {
                            return (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                <span className="w-1 h-1 rounded-full bg-rose-500"></span>
                                Expirada
                              </span>
                            );
                          }
                          if (val.status === 'vence_hoje') {
                            return (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                <span className="w-1 h-1 rounded-full bg-amber-500"></span>
                                Vence hoje
                              </span>
                            );
                          }
                          return (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <span className="w-1 h-1 rounded-full bg-emerald-500"></span>
                              {val.diasRestantes}d válidos
                            </span>
                          );
                        })()}
                      </div>

                      <span className="text-sm font-black text-slate-800 dark:text-slate-200 tabular-nums shrink-0">
                        R$ {Number(prop.total_proposta || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {propostas.length > 0 && (
          <button
            onClick={async () => {
              await handleGerarNovaVersao();
              setDrawerTab('budget');
            }}
            disabled={saving}
            className="w-full py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center space-x-1.5 mt-4 shadow-lg shadow-indigo-600/25 hover:from-indigo-500 hover:to-indigo-400"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            <span>Criar Nova Versão</span>
          </button>
        )}
      </div>
    );
  };

  const renderBudgetEditor = () => {
    if (loading) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center space-y-3 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400 tracking-wide uppercase">Carregando dados da proposta...</p>
        </div>
      );
    }
    if (!currentProposta) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto space-y-6">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-50 to-indigo-100/80 dark:from-indigo-950/50 dark:to-indigo-900/50 rounded-3xl border border-indigo-200/60 dark:border-indigo-800/50 shadow-sm flex items-center justify-center text-indigo-600 dark:text-indigo-300">
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 mb-2">Painel de Negociação Comercial</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Selecione ou crie uma versão de proposta na linha do tempo para carregar os itens e precificação.</p>
          </div>
        </div>
      );
    }

    // Tipo de Oportunidade é do NEGÓCIO, não da proposta/versão — por isso vem
    // de selectedTask.tipo_oportunidade (sincronizado em negocios, ver
    // fetchKanbanData) e não de currentProposta.cenario. cenario continua
    // existindo, mas agora só guarda o SUBTIPO do projeto (HCI/Cloud/
    // Tradicional/Upgrade) quando o negócio é do tipo Projeto — faz sentido
    // ser por versão, já que cada "cenário" pode explorar uma solução técnica
    // diferente pro mesmo negócio (ex.: v.A = HCI, v.B = Cloud).
    const tipoOportunidadeAtual = selectedTask?.tipo_oportunidade || 'Projeto';
    const isProjeto = tipoOportunidadeAtual === 'Projeto';

    const isReadOnly = (currentProposta.situacao === 'Ganho' || currentProposta.situacao === 'Perdido') && !isEditingProposal;

    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-slate-100/70 dark:bg-slate-700/70">
        {/* Barra superior de navegação */}
        <div className="px-6 py-3 bg-white dark:bg-slate-800 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 flex items-center justify-between z-10 shadow-sm shadow-slate-200/40">
          <button 
            onClick={() => setDrawerTab('details')}
            className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 px-3 py-1.5 rounded-xl hover:bg-indigo-50/50 dark:hover:bg-indigo-950/40 transition-all cursor-pointer group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Voltar para Detalhes</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-widest">PROPOSTA COMERCIAL</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-y-auto">
          {/* Cabeçalho da Proposta */}
          <div className="bg-white dark:bg-slate-800 border-b border-slate-200/80 dark:border-slate-700/80 px-7 py-5 shadow-2xs space-y-4">
            {/* Linha 1: Título amplo do Negócio e Metadados */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="space-y-1.5">
                {projectContext.name && (
                  <h1 className="text-xl lg:text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-tight flex items-center gap-2.5">
                    <span>{projectContext.name}</span>
                  </h1>
                )}
                
                <div className="flex items-center gap-2.5 flex-wrap text-xs font-medium text-slate-600 dark:text-slate-300">
                  <span className="font-bold text-slate-700 dark:text-slate-300">Versão</span>
                  <span className="inline-flex items-center justify-center bg-gradient-to-r from-indigo-600 to-indigo-500 text-white font-black px-2.5 py-0.5 rounded-lg text-xs shadow-xs tracking-wide">
                    {formatVersionDisplay(currentProposta.versao)}
                  </span>
                  {currentProposta.cenario && (
                    <span className="bg-slate-100 dark:bg-slate-700 border border-slate-200/80 dark:border-slate-700/80 text-slate-700 dark:text-slate-300 text-[11px] px-2.5 py-0.5 rounded-full uppercase font-extrabold tracking-wider">
                      {currentProposta.cenario}
                    </span>
                  )}
                  {isReadOnly && (
                    <span className="inline-flex items-center justify-center bg-amber-50 text-amber-800 border border-amber-200/80 px-2.5 py-0.5 rounded-lg text-[11px] font-bold gap-1 shadow-2xs" title="Trava de leitura ativa">
                      🔒 Somente Leitura
                    </span>
                  )}

                  <span className="text-slate-300">•</span>

                  <span className="text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Criada em <strong className="text-slate-800 dark:text-slate-200 font-bold">{formatDateSafe(currentProposta.created_at)}</strong> {currentProposta.criado_por ? <span>por <strong className="text-slate-900 dark:text-slate-100 font-bold">{currentProposta.criado_por}</strong></span> : ''}
                  </span>

                  {/* Badge Limpo de Validade dos Preços (apenas quando houver itens/valor e o negócio/proposta estiver em aberto) */}
                  {(() => {
                    const dealEstagio = selectedTask ? (selectedTask.estagio || selectedTask.status) : null;
                    const val = calcularValidadeProposta(currentProposta, dealEstagio);
                    if (!val) return null;
                    if (val.status === 'vencida') {
                      return (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-0.5 rounded-lg shadow-2xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                          <span>Expirada em {val.dataValidadeStr}</span>
                        </span>
                      );
                    }
                    if (val.status === 'vence_hoje') {
                      return (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-lg shadow-2xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                          <span>Vence Hoje ({val.dataValidadeStr})</span>
                        </span>
                      );
                    }
                    return (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-lg shadow-2xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        <span>Válida até {val.dataValidadeStr} ({val.diasRestantes}d restantes)</span>
                      </span>
                    );
                  })()}

                  {currentProposta.situacao === 'Ganho' && (
                    <span className="text-xs font-black text-emerald-800 bg-emerald-50 px-3 py-0.5 rounded-lg border border-emerald-200 flex items-center gap-1 shadow-2xs">
                      🏆 Ganho {currentProposta.data_fechamento ? `(${formatDateSafe(currentProposta.data_fechamento)})` : ''}
                    </span>
                  )}
                  {currentProposta.situacao === 'Perdido' && (
                    <span className="text-xs font-black text-rose-800 bg-rose-50 px-3 py-0.5 rounded-lg border border-rose-200 flex items-center gap-1 shadow-2xs">
                      😞 Perdido: {currentProposta.motivo_perda || 'Outros'} {currentProposta.data_fechamento ? `(${formatDateSafe(currentProposta.data_fechamento)})` : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Linha 2: Toolbar Premium de Ações e Status */}
            <div className="pt-3.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4 flex-wrap">
              {/* Bloco Esquerdo: Selecionar Versão + Status Select */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Botão ⭐ Selecionar Versão */}
                {currentProposta.situacao === 'Selecionada' ? (
                  <button
                    onClick={async () => {
                      await handleUpdateVersionStatus(clickupTaskId || currentProposta.clickup_negocio_id, currentProposta.id, 'Ativa');
                      showToast('Seleção desativada. A proposta retornou para Em Andamento.', 'info');
                    }}
                    className="bg-emerald-50 hover:bg-emerald-100/80 text-emerald-700 border border-emerald-300/80 font-extrabold text-xs px-3.5 py-2 rounded-xl flex items-center gap-2 shadow-xs cursor-pointer transition-all hover:scale-[1.01]"
                    title="Clique para desativar esta seleção de versão"
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span>✅ Versão Selecionada</span>
                    <span className="text-[10px] text-emerald-600 font-medium underline ml-1">Desativar</span>
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      await handleUpdateVersionStatus(clickupTaskId || currentProposta.clickup_negocio_id, currentProposta.id, 'Selecionada');
                      showToast('Versão selecionada! Valor sincronizado com a oportunidade no ClickUp.', 'success');
                    }}
                    className="bg-gradient-to-r from-indigo-600 via-indigo-500 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-extrabold text-xs px-4 py-2 rounded-xl shadow-md shadow-indigo-600/20 transition-all flex items-center gap-1.5 cursor-pointer hover:scale-[1.01]"
                    title="Definir esta versão como a ativa comercialmente e sincronizar valor com o ClickUp"
                  >
                    <span>⭐ Selecionar Versão</span>
                  </button>
                )}

                {/* Botão 🎯 Referência de Forecast — independente de `situacao` */}
                {currentProposta.referencia_forecast ? (
                  <button
                    onClick={async () => {
                      await handleToggleForecastReference(clickupTaskId || currentProposta.clickup_negocio_id, currentProposta.id, false);
                      showToast('Removida como referência de forecast.', 'info');
                    }}
                    className="bg-amber-50 hover:bg-amber-100/80 text-amber-700 border border-amber-300/80 font-extrabold text-xs px-3.5 py-2 rounded-xl flex items-center gap-2 shadow-xs cursor-pointer transition-all hover:scale-[1.01]"
                    title="Esta versão é a referência de valor para o Forecast enquanto o cliente decide. Clique para remover."
                  >
                    <span>🎯 Referência de Forecast</span>
                    <span className="text-[10px] text-amber-600 font-medium underline ml-1">Remover</span>
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      await handleToggleForecastReference(clickupTaskId || currentProposta.clickup_negocio_id, currentProposta.id, true);
                      showToast('Versão marcada como referência de forecast. Valor sincronizado com o ClickUp.', 'success');
                    }}
                    className="bg-white dark:bg-slate-800 hover:bg-amber-50 text-amber-700 border border-amber-200 font-bold text-xs px-3.5 py-2 rounded-xl flex items-center gap-1.5 cursor-pointer transition-all hover:scale-[1.01]"
                    title="Usar o valor desta versão como referência do Forecast enquanto o cliente ainda não confirmou qual proposta vai escolher"
                  >
                    <span>🎯 Usar no Forecast</span>
                  </button>
                )}

                {/* Status Select */}
                <div className={`flex items-center gap-2 bg-slate-100/70 dark:bg-slate-700/70 hover:bg-slate-100 dark:hover:bg-slate-700 px-3 py-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 transition-all ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-wider">STATUS:</span>
                  <select
                    disabled={isReadOnly}
                    value={currentProposta.situacao === 'Ganho' ? 'Ganho' : currentProposta.situacao === 'Perdido' ? 'Perdido' : currentProposta.situacao === 'Desconsiderada' ? 'Desconsiderada' : 'Em Andamento'}
                    onChange={async (e) => {
                      const val = e.target.value;
                      if (val === 'Ganho') {
                        setCloseDate(new Date().toISOString().split('T')[0]);
                        setShowCloseModal('win');
                      } else if (val === 'Perdido') {
                        setCloseDate(new Date().toISOString().split('T')[0]);
                        setSelectedLossReason('');
                        setShowCloseModal('loss');
                      } else if (val === 'Em Andamento') {
                        // 'Ativa', não 'Selecionada' — "Em Andamento" é o estado neutro/rascunho,
                        // não uma seleção oficial. Usar 'Selecionada' aqui desconsiderava a(s)
                        // proposta(s) irmã(s) automaticamente, sem o usuário ter clicado em
                        // "Selecionar Versão" (bug corrigido em 21/08).
                        await handleUpdateVersionStatus(clickupTaskId || currentProposta.clickup_negocio_id, currentProposta.id, 'Ativa');
                        showToast('Proposta atualizada para Em Andamento!', 'success');
                      } else if (val === 'Desconsiderada') {
                        await handleUpdateVersionStatus(clickupTaskId || currentProposta.clickup_negocio_id, currentProposta.id, 'Desconsiderada');
                      }
                    }}
                    className="text-xs font-black text-slate-700 dark:text-slate-300 bg-transparent border-none focus:outline-none cursor-pointer pr-1 disabled:cursor-not-allowed"
                  >
                    <option value="Em Andamento">Em Andamento</option>
                    <option value="Ganho">🏆 Ganho</option>
                    <option value="Perdido">😞 Perdido</option>
                    <option value="Desconsiderada">🚫 Desconsiderada</option>
                  </select>
                </div>
              </div>

              {/* Bloco Direito: Botões de Fechamento Rápido (Ganho / Perdido sem cor por padrão) & Ações de Edição */}
              <div className="flex items-center gap-2 flex-wrap">
                {!isReadOnly && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => {
                        setCloseDate(new Date().toISOString().split('T')[0]);
                        setShowCloseModal('win');
                      }}
                      className={`font-extrabold text-xs px-3.5 py-2 rounded-xl transition-all flex items-center gap-1 cursor-pointer hover:scale-[1.02] ${
                        currentProposta.situacao === 'Ganho'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs'
                          : 'bg-slate-100/80 dark:bg-slate-700/80 hover:bg-emerald-50 text-slate-600 dark:text-slate-300 hover:text-emerald-700 border border-slate-200/80 dark:border-slate-700/80'
                      }`}
                      title="Marcar oportunidade como Ganha 🏆"
                    >
                      <span>🏆 Ganho</span>
                    </button>
                    <button
                      onClick={() => {
                        setCloseDate(new Date().toISOString().split('T')[0]);
                        setSelectedLossReason('');
                        setShowCloseModal('loss');
                      }}
                      className={`font-extrabold text-xs px-3.5 py-2 rounded-xl transition-all flex items-center gap-1 cursor-pointer hover:scale-[1.02] ${
                        currentProposta.situacao === 'Perdido'
                          ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-xs'
                          : 'bg-slate-100/80 dark:bg-slate-700/80 hover:bg-rose-50 text-slate-600 dark:text-slate-300 hover:text-rose-700 border border-slate-200/80 dark:border-slate-700/80'
                      }`}
                      title="Marcar oportunidade como Perdida 😞"
                    >
                      <span>😞 Perdido</span>
                    </button>
                  </div>
                )}

                {/* Editar */}
                <button
                  onClick={() => setIsEditingProposal(!isEditingProposal)}
                  className={`text-xs px-3.5 py-2 rounded-xl cursor-pointer flex items-center gap-1.5 transition-all ${
                    isEditingProposal 
                      ? 'bg-amber-50 border border-amber-300 text-amber-800 font-extrabold shadow-2xs' 
                      : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 font-bold shadow-2xs'
                  }`}
                  title={isEditingProposal ? "Bloquear Campos para Leitura" : "Desbloquear Campos para Edição (✏️)"}
                >
                  <span>✏️ {isEditingProposal ? 'Bloquear' : 'Editar'}</span>
                </button>

                {!isReadOnly && (
                  <button
                    onClick={handleSaveProposalDebounced}
                    disabled={saving}
                    className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-extrabold text-xs px-4 py-2 rounded-xl shadow-sm shadow-indigo-600/20 transition-all flex items-center gap-1.5 cursor-pointer hover:scale-[1.02]"
                    title="Salvar Alterações"
                  >
                    {saving ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <span>💾 Salvar</span>
                    )}
                  </button>
                )}

                {/* Botão Excluir */}
                <button
                  onClick={handleDeleteProposal}
                  disabled={saving}
                  className="bg-white dark:bg-slate-800 text-rose-500 hover:bg-rose-50 border border-slate-200 dark:border-slate-700 hover:border-rose-200 p-2 rounded-xl transition-all flex items-center justify-center cursor-pointer shadow-2xs hover:scale-[1.05]"
                  title="Excluir Versão"
                >
                  <span className="text-xs">🗑️</span>
                </button>
              </div>
            </div>
          </div>

          {/* Grid Premium de Metadados (Form Controls Card) */}
          <div className="mx-7 my-5 p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm shadow-slate-200/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3.5">
              <div>
                <label className="text-[10px] font-extrabold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-indigo-500 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                    Tipo Oportunidade
                  </span>
                  <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.5 rounded-md uppercase" title="É do negócio, não da versão — sincroniza em todas as propostas">NEGÓCIO</span>
                </label>
                <select
                  className="h-10 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 shadow-xs hover:bg-slate-100/70 dark:hover:bg-slate-700/70 focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 px-3 text-xs text-slate-800 dark:text-slate-200 font-bold w-full focus:outline-none transition-all cursor-pointer disabled:opacity-60"
                  value={tipoOportunidadeAtual}
                  onChange={async (e) => {
                    const val = e.target.value;
                    /* B2_I13_BEGIN */
                    if (kanbanModoRef.current !== 'legacy') {
                      try {
                        const crmId = selectedTask?.crm_negocio_id || comercialNegocioId(selectedTask?.clickup_negocio_id || selectedTask?.id);
                        const ao_vivo = kanbanTasks.find(t => t.crm_negocio_id && t.crm_negocio_id === crmId);
                        const r = await editarNegocioCanonico(crmId, ao_vivo ? ao_vivo.versao_estagio : selectedTask?.versao_estagio, { tipo_oportunidade: val });
                        propostaDirtyRef.current = true;
                        setSelectedTask(prev => prev ? { ...prev, tipo_oportunidade: val, versao_estagio: r.versao } : prev);
                        setKanbanTasks(prev => prev.map(t => (t.crm_negocio_id === crmId ? { ...t, tipo_oportunidade: val, versao_estagio: r.versao } : t)));
                        if (val !== 'Projeto') setCurrentProposta(prev => ({ ...prev, cenario: '' }));
                      } catch (err) {
                        showToast(comercialMensagem(err, 'Não foi possível atualizar o tipo da oportunidade.'), 'error');
                        if (comercialCodigo(err) === 'CONFLITO_VERSAO') fetchKanbanData();
                      }
                      return;
                    }
                    /* B2_I13_END */
                    propostaDirtyRef.current = true;
                    // Tipo de Oportunidade é do negócio: atualiza local (reflete
                    // em qualquer versão aberta depois) e grava direto em
                    // negocios, sem depender de salvar a proposta.
                    setSelectedTask(prev => prev ? { ...prev, tipo_oportunidade: val } : prev);
                    if (val !== 'Projeto') {
                      // Sem Tipo de Projeto (subtipo) fazendo sentido fora de
                      // Projeto, limpa o cenário desta versão.
                      setCurrentProposta(prev => ({ ...prev, cenario: '' }));
                    }
                    if (supabaseClient) {
                      const cuId = selectedTask?.clickup_negocio_id || selectedTask?.id;
                      if (cuId) {
                        const cleanId = String(cuId).replace('#', '').trim();
                        try {
                          await supabaseClient.from('negocios').update({ tipo_oportunidade: val }).eq('clickup_negocio_id', cleanId);
                        } catch (err) {
                          console.warn('Erro ao sincronizar Tipo de Oportunidade:', err);
                        }
                      }
                    }
                  }}
                  disabled={isReadOnly}
                >
                  <option value="Projeto">Projeto</option>
                  <option value="Garantias">Garantias</option>
                  <option value="Serviços">Serviços</option>
                  <option value="SSU">SSU</option>
                  <option value="Volumes">Volumes</option>
                  <option value="Upgrade">Upgrade</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-extrabold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-indigo-500 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                  Tipo de Projeto
                </label>
                <select
                  className="h-10 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 shadow-xs hover:bg-slate-100/70 dark:hover:bg-slate-700/70 focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 px-3 text-xs text-slate-800 dark:text-slate-200 font-bold w-full focus:outline-none transition-all cursor-pointer disabled:opacity-60"
                  value={currentProposta.cenario || ""}
                  onChange={(e) => { propostaDirtyRef.current = true; setCurrentProposta({ ...currentProposta, cenario: e.target.value }); }}
                  disabled={isReadOnly || !isProjeto}
                  title={!isProjeto ? 'Só disponível quando o Tipo de Oportunidade do negócio é "Projeto"' : undefined}
                >
                  <option value="">Selecione o tipo...</option>
                  <option value="HCI">HCI (Hiperconvergência)</option>
                  <option value="Cloud">Cloud (Nuvem)</option>
                  <option value="Tradicional">Tradicional</option>
                  <option value="Upgrade">Upgrade</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-extrabold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-indigo-500 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  Vendedor / Responsável
                </label>
                <select
                  className="h-10 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 shadow-xs hover:bg-slate-100/70 dark:hover:bg-slate-700/70 focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 px-3 text-xs text-slate-800 dark:text-slate-200 font-bold w-full focus:outline-none transition-all cursor-pointer disabled:opacity-60"
                  value={currentProposta.criado_por || ""}
                  onChange={(e) => { propostaDirtyRef.current = true; setCurrentProposta({ ...currentProposta, criado_por: e.target.value }); }}
                  disabled={isReadOnly}
                >
                  <option value="">Selecione o vendedor...</option>
                  {vendedoresVisiveis.map(v => (
                    <option key={v.id} value={v.nome} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200">{v.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-extrabold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-indigo-500 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Data de Início
                  </span>
                  <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.5 rounded-md uppercase" title="Sincroniza com todas as versões">NEGÓCIO</span>
                </label>
                <input
                  type="date"
                  className="h-10 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 shadow-xs hover:bg-slate-100/70 dark:hover:bg-slate-700/70 focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 px-2.5 text-xs text-slate-800 dark:text-slate-200 font-bold w-full focus:outline-none transition-all cursor-pointer disabled:opacity-60"
                  value={currentProposta?.data_inicio ? currentProposta.data_inicio.substring(0, 10) : (clickupTaskDates?.start_date || '')}
                  onChange={(e) => { propostaDirtyRef.current = true; setCurrentProposta({ ...currentProposta, data_inicio: e.target.value }); }}
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <label className="text-[10px] font-extrabold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-indigo-500 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Data Fechamento
                  </span>
                  <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.5 rounded-md uppercase" title="Sincroniza com o ClickUp e todas as versões">NEGÓCIO</span>
                </label>
                <input
                  type="date"
                  className="h-10 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 shadow-xs hover:bg-slate-100/70 dark:hover:bg-slate-700/70 focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 px-2.5 text-xs text-slate-800 dark:text-slate-200 font-bold w-full focus:outline-none transition-all cursor-pointer disabled:opacity-60"
                  value={currentProposta?.data_fechamento ? currentProposta.data_fechamento.substring(0, 10) : ''}
                  onChange={(e) => { propostaDirtyRef.current = true; setCurrentProposta({ ...currentProposta, data_fechamento: e.target.value }); }}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {/* Tabela de Produtos & Catálogo */}
          <div className="flex-1 overflow-y-auto px-7 pb-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span>Produtos e Serviços Inclusos</span>
              </h3>
              {!isReadOnly && (
                <button 
                  onClick={() => setShowProductModal(true)}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-bold flex items-center gap-1.5 transition-colors cursor-pointer group"
                >
                  <span className="w-5 h-5 rounded-md bg-indigo-50 group-hover:bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-black">+</span>
                  <span>Adicionar Novo Item ao Catálogo</span>
                </button>
              )}
            </div>

            {itens.length === 0 ? (
              <div className="bg-gradient-to-b from-white to-slate-50/80 dark:from-slate-800 dark:to-slate-900/80 border border-dashed border-slate-300/90 dark:border-slate-600/90 rounded-3xl p-12 text-center flex flex-col items-center justify-center space-y-4 shadow-2xs">
                <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-center text-indigo-500">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div>
                  <p className="text-base font-black text-slate-800 dark:text-slate-200">Nenhum item adicionado à proposta</p>
                  {!isReadOnly && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-medium">Selecione itens do catálogo para compor o valor comercial desta versão.</p>}
                </div>
                {!isReadOnly && (
                  <button
                    onClick={handleAddItem}
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white rounded-xl text-xs font-extrabold transition-all shadow-md shadow-indigo-600/20 hover:scale-[1.02] cursor-pointer"
                  >
                    + Adicionar Primeiro Item
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-xs overflow-x-auto" style={{ overflow: 'visible', minHeight: '280px' }}>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/70 dark:bg-slate-900/70 border-b border-slate-200/80 dark:border-slate-700/80 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                      <th className="py-3 px-4">Produto [Fabricante]</th>
                      <th className="py-3 px-4 w-2/12">Distribuidor</th>
                      <th className="py-3 px-4 w-[70px] text-center">Qtd</th>
                      <th className="py-3 px-4 w-2/12 text-right">Unitário</th>
                      <th className="py-3 px-4 w-2/12 text-right">Subtotal</th>
                      {!isReadOnly && <th className="py-3 px-4 w-[60px] text-center">Ações</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {itens.map((item, index) => {
                      const subtotal = item.quantidade * item.preco_unitario || 0;
                      const prodObj = produtos.find(p => p.id === item.produto_id);
                      return (
                        <tr key={item.id} className="group hover:bg-slate-50/80 dark:hover:bg-slate-900/80 transition-colors">
                          <td className="py-3.5 px-4 relative" style={{ overflow: 'visible' }}>
                            {isReadOnly ? (
                              <div>
                                <span className="font-bold text-slate-900 dark:text-slate-100 text-sm block">
                                  {prodObj?.nome || 'Produto não encontrado'}
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5 block">
                                  {prodObj?.fabricante ? `Fabricante: ${prodObj.fabricante}` : '-'}
                                </span>
                              </div>
                            ) : (
                              <React.Fragment>
                                <input
                                   type="text"
                                   className="w-full rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold transition-all"
                                   placeholder="Digite para buscar produto..."
                                   value={
                                     item.searchTerm !== undefined
                                       ? item.searchTerm
                                       : (prodObj?.nome || '')
                                   }
                                   onChange={(e) => {
                                     const val = e.target.value;
                                     handleItemChange(index, { searchTerm: val, showDropdown: true, highlightedIndex: 0 });
                                   }}
                                   onFocus={() => {
                                     const currentVal = item.searchTerm !== undefined
                                       ? item.searchTerm
                                       : (prodObj?.nome || '');
                                     handleItemChange(index, { searchTerm: currentVal, showDropdown: true, highlightedIndex: 0 });
                                   }}
                                   onKeyDown={(e) => {
                                     const searchVal = item.searchTerm !== undefined ? item.searchTerm : (prodObj?.nome || '');
                                     const filtrados = produtos.filter(p => 
                                       (p.nome || '').toLowerCase().includes(searchVal.toLowerCase()) ||
                                       (p.fabricante || '').toLowerCase().includes(searchVal.toLowerCase())
                                     );

                                     if (!item.showDropdown || filtrados.length === 0) return;

                                     const currentIdx = item.highlightedIndex || 0;

                                     if (e.key === 'ArrowDown') {
                                       e.preventDefault();
                                       const nextIdx = (currentIdx + 1) % filtrados.length;
                                       handleItemChange(index, { highlightedIndex: nextIdx });
                                     } else if (e.key === 'ArrowUp') {
                                       e.preventDefault();
                                       const prevIdx = (currentIdx - 1 + filtrados.length) % filtrados.length;
                                       handleItemChange(index, { highlightedIndex: prevIdx });
                                     } else if (e.key === 'Enter') {
                                       e.preventDefault();
                                       const selectedProd = filtrados[currentIdx];
                                       if (selectedProd) {
                                         handleItemChange(index, {
                                           produto_id: selectedProd.id,
                                           preco_unitario: selectedProd.preco_base || 0,
                                           searchTerm: selectedProd.nome,
                                           showDropdown: false
                                         });
                                       }
                                     } else if (e.key === 'Escape') {
                                       handleItemChange(index, { showDropdown: false });
                                     }
                                   }}
                                />

                                {item.showDropdown && (
                                  <div className="absolute left-4 right-4 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-[9999] max-h-48 overflow-y-auto divide-y divide-slate-100">
                                    {(() => {
                                      const searchVal = item.searchTerm !== undefined ? item.searchTerm : (prodObj?.nome || '');
                                      const filtrados = produtos.filter(p => 
                                        (p.nome || '').toLowerCase().includes(searchVal.toLowerCase()) ||
                                        (p.fabricante || '').toLowerCase().includes(searchVal.toLowerCase())
                                      );

                                      if (filtrados.length === 0) {
                                        return (
                                          <div className="p-3 text-xs text-slate-400 dark:text-slate-500 text-center font-medium">
                                            Nenhum produto encontrado
                                          </div>
                                        );
                                      }

                                      return filtrados.map((p, idx) => (
                                        <div
                                          key={p.id}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleItemChange(index, {
                                              produto_id: p.id,
                                              preco_unitario: p.preco_base || 0,
                                              searchTerm: p.nome,
                                              showDropdown: false
                                            });
                                          }}
                                          className={`p-2.5 text-xs cursor-pointer flex justify-between items-center transition-colors ${
                                            (item.highlightedIndex || 0) === idx ? 'bg-indigo-50/80 text-indigo-900 font-bold' : 'hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-300 font-medium'
                                          }`}
                                        >
                                          <div>
                                            <span className="font-bold block text-slate-800 dark:text-slate-200">{p.nome}</span>
                                            <span className="text-[10px] text-slate-400 dark:text-slate-500">{p.fabricante || 'Fabricante não informado'}</span>
                                          </div>
                                          <span className="font-mono text-xs text-indigo-600 dark:text-slate-200 font-bold">
                                            R$ {Number(p.preco_base || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                          </span>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                )}
                              </React.Fragment>
                            )}
                          </td>

                          <td className="py-3.5 px-4">
                            {isReadOnly ? (
                              <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                {distribuidores.find(d => d.id === item.distribuidor_id)?.nome || '-'}
                              </span>
                            ) : (
                              <select
                                className="w-full rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium cursor-pointer"
                                value={item.distribuidor_id || ""}
                                onChange={(e) => handleItemChange(index, 'distribuidor_id', e.target.value)}
                                onFocus={() => { if (distribuidoresErro) loadDistributors(); }}
                              >
                                {distribuidoresErro && distribuidores.length === 0 ? (
                                  <>
                                    {item.distribuidor_id ? <option value={item.distribuidor_id}>Distribuidor atual (lista indisponível)</option> : null}
                                    <option value="" disabled={!!item.distribuidor_id}>Não foi possível carregar os distribuidores</option>
                                  </>
                                ) : distribuidores.length === 0 ? (
                                  <option value="">Nenhum distribuidor cadastrado</option>
                                ) : (
                                  distribuidores.map(d => (
                                    <option key={d.id} value={d.id}>{d.nome}</option>
                                  ))
                                )}
                              </select>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-center">
                            {isReadOnly ? (
                              <span className="text-xs font-black text-slate-800 dark:text-slate-200">{item.quantidade}</span>
                            ) : (
                              <input
                                type="number"
                                min="1"
                                className="w-16 mx-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2 text-xs text-center text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-bold"
                                value={item.quantidade}
                                onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                              />
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            {isReadOnly ? (
                              <span className="text-xs text-slate-700 dark:text-slate-300 font-bold">
                                R$ {Number(item.preco_unitario).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </span>
                            ) : (
                              <div className="relative">
                                <span className="absolute left-2.5 top-2.5 text-[11px] font-bold text-slate-400 dark:text-slate-500">R$</span>
                                <input
                                  type="text"
                                  className="w-full rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2 pl-8 text-xs text-right text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono font-bold"
                                  value={formatMaskedCurrency(item.preco_unitario)}
                                  onChange={(e) => handleCurrencyInputChange(index, e.target.value)}
                                />
                              </div>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-right font-black text-slate-900 dark:text-slate-100 text-xs whitespace-nowrap tabular-nums">
                            R$ {subtotal.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </td>

                          {!isReadOnly && (
                            <td className="py-3.5 px-4 text-center">
                              <button
                                onClick={() => handleRemoveItem(index)}
                                className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                                title="Remover Item"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isReadOnly && (
              <button
                onClick={handleAddItem}
                className="w-full py-3 border border-dashed border-slate-300 dark:border-slate-600 hover:border-indigo-500 rounded-2xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 bg-white dark:bg-slate-800 hover:bg-indigo-50/40 dark:hover:bg-indigo-950/40 transition-all flex items-center justify-center space-x-2 cursor-pointer shadow-2xs"
              >
                <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                </svg>
                <span>Adicionar Item à Proposta</span>
              </button>
            )}
          </div>

          {/* Rodapé Resumo Comercial Ultra-Premium */}
          <div className="border-t border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 px-7 py-4.5 flex flex-col md:flex-row justify-between items-center gap-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-50/80 rounded-2xl flex items-center justify-center border border-indigo-100 text-indigo-600 shadow-2xs">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 dark:text-slate-400 font-extrabold uppercase tracking-widest block">RESUMO COMERCIAL</span>
                <p className="text-xs text-slate-600 dark:text-slate-300 font-bold mt-0.5">Cálculo ativo com base em {itens.length} {itens.length === 1 ? 'item' : 'itens'}.</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-900 text-white rounded-2xl px-7 py-4.5 text-right shadow-lg shadow-indigo-950/20 min-w-[280px] border border-indigo-800/40">
              <p className="text-[10px] font-extrabold text-indigo-300/80 uppercase tracking-widest mb-0.5">TOTAL DA PROPOSTA</p>
              <p className="text-2xl lg:text-3xl font-black text-white tracking-tight tabular-nums">
                R$ {realTimeGrandTotal.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────
  // Registro das camadas modais (Fase 2 — navegação por teclado). Chamado
  // incondicionalmente a cada render, ANTES do "if (!session)" abaixo —
  // todo hook do componente precisa vir antes desse primeiro return
  // condicional. useModalLayer por dentro só empilha/faz algo enquanto o
  // respectivo `open` for true; fechado, é um no-op barato.
  // ─────────────────────────────────────────────────────────────────────
  const settingsModalRef = React.useRef(null);
  const settingsModalLayer = useModalLayer({ open: showSettingsModal, onClose: () => setShowSettingsModal(false), panelRef: settingsModalRef });

  const productModalRef = React.useRef(null);
  const productModalLayer = useModalLayer({ open: showProductModal, onClose: () => setShowProductModal(false), panelRef: productModalRef });

  const closeModalRef = React.useRef(null);
  const closeModalLayer = useModalLayer({ open: !!showCloseModal, onClose: () => setShowCloseModal(false), panelRef: closeModalRef });

  const taskDetailModalRef = React.useRef(null);
  const taskDetailModalLayer = useModalLayer({ open: !!taskDetailId, onClose: closeTaskDetail, panelRef: taskDetailModalRef });

  const newTaskModalRef = React.useRef(null);
  const newTaskModalLayer = useModalLayer({
    open: showNewTaskModal,
    onClose: () => {
      setShowNewTaskModal(false);
      setSelectedProposalForTask(null);
      setSearchProposalQuery('');
      setProposalSearchResults([]);
    },
    panelRef: newTaskModalRef
  });

  const editNegocioModalRef = React.useRef(null);
  const editNegocioModalLayer = useModalLayer({ open: showEditNegocioDrawerModal, onClose: () => setShowEditNegocioDrawerModal(false), panelRef: editNegocioModalRef });

  const drawerModalRef = React.useRef(null);
  const drawerModalLayer = useModalLayer({
    open: showDrawer,
    onClose: () => {
      // ESC no drawer com a proposta em tela cheia ('budget') volta pra
      // aba de detalhes antes de fechar de vez — preserva o comportamento
      // de navegação que já existia na cadeia antiga.
      if (drawerTab === 'budget') {
        setDrawerTab('details');
      } else {
        setShowDrawer(false);
        setClickupTaskId('');
      }
    },
    panelRef: drawerModalRef
  });

  const lightboxModalRef = React.useRef(null);
  const lightboxModalLayer = useModalLayer({ open: !!lightboxAnexoUrl, onClose: () => setLightboxAnexoUrl(null), panelRef: lightboxModalRef });

  if (!session && validandoAcessoCrm) {
    return <div role="status" className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200">Validando acesso ao CRM...</div>;
  }
  if (!session) {
    return (
      <LoginScreen 
        onLogin={handleLogin} 
        error={errorMsg}
      />
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 overflow-hidden">
      
      {/* 1. Header do Sistema */}
      <header className="h-16 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 flex items-center justify-between z-40 relative">
        <div className="flex items-center space-x-3">
          <div className="bg-indigo-600 p-2 rounded-lg">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-wide">Suprimática CRM</h1>
              {activeTab === 'propostas' && projectContext.name && (
                <span className="text-[10px] text-indigo-300 font-bold bg-indigo-950/80 px-2.5 py-0.5 rounded-full border border-indigo-500/20" title={projectContext.name}>
                  {projectContext.name}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              Gerador de Propostas
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Busca Global — pesquisa Empresas/Contatos/Negócios & Propostas
              em qualquer aba (ver buscarGlobal/handleGlobalSearchSelect) e
              leva direto ao registro selecionado. Mesmo mecanismo de lupa
              expansível já usado no Kanban, dado diferente. */}
          <div className="relative flex items-center">
            {!isGlobalSearchOpen ? (
              <button
                onClick={() => setIsGlobalSearchOpen(true)}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors"
                title="Buscar em todo o CRM..."
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            ) : (
              <div className="flex items-center bg-slate-100/50 dark:bg-slate-700/50 border border-indigo-400 dark:border-indigo-500 rounded-lg px-3 py-1 h-9 w-[32rem]">
                <svg className="w-4 h-4 text-indigo-500 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsGlobalSearchOpen(false);
                      setGlobalSearchTerm('');
                      setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });
                    }
                  }}
                  onBlur={() => {
                    // Itens do dropdown usam onMouseDown+preventDefault (não
                    // onClick) exatamente pra não disparar esse blur antes de
                    // registrar o clique — então isso só fecha quando o clique
                    // é de verdade fora da busca inteira.
                    setIsGlobalSearchOpen(false);
                    setGlobalSearchTerm('');
                    setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });
                  }}
                  placeholder="Buscar empresa, contato, negócio..."
                  className="bg-transparent border-0 p-0 text-sm text-slate-800 dark:text-slate-200 focus:ring-0 focus:outline-none w-full font-medium"
                />
                <button
                  onClick={() => {
                    setIsGlobalSearchOpen(false);
                    setGlobalSearchTerm('');
                    setGlobalSearchResults({ contas: [], contatos: [], negocios: [] });
                  }}
                  className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-xs font-bold ml-1 cursor-pointer"
                >
                  ✕
                </button>
              </div>
            )}

            {isGlobalSearchOpen && globalSearchTerm.trim().length > 0 && (
              <div className="absolute top-full right-0 mt-2 w-[32rem] h-[28rem] bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 rounded-2xl shadow-2xl z-30 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-indigo-300 dark:[&::-webkit-scrollbar-thumb]:bg-indigo-600 [&::-webkit-scrollbar-thumb]:rounded-full">
                {globalSearchLoading ? (
                  <p className="px-5 py-4 text-xs text-slate-400 dark:text-slate-500 font-medium">Buscando...</p>
                ) : globalSearchTerm.trim().length < 2 ? (
                  <p className="px-5 py-4 text-xs text-slate-400 dark:text-slate-500 font-medium">Digite ao menos 2 letras...</p>
                ) : (() => {
                  const { contas, contatos, negocios } = globalSearchResults;
                  if (contas.length === 0 && contatos.length === 0 && negocios.length === 0) {
                    return <p className="px-5 py-4 text-xs text-slate-400 dark:text-slate-500 font-medium">Nenhum resultado encontrado.</p>;
                  }
                  return (
                    <React.Fragment>
                      {contas.length > 0 && (
                        <div className="py-2">
                          <p className="px-5 pt-1 pb-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Empresas</p>
                          {contas.map(c => (
                            <button key={c.id} onMouseDown={(e) => { e.preventDefault(); handleGlobalSearchSelect('conta', c); }} className="w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors cursor-pointer flex items-start gap-3">
                              <span className="mt-0.5 w-7 h-7 rounded-lg bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
                                <IconBuilding size={14} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={c.nome}>{c.nome}</p>
                                <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{c.cnpj ? formatCNPJ(c.cnpj) : (c.cidade || '')}</p>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      {contatos.length > 0 && (
                        <div className="py-2 border-t border-slate-200 dark:border-slate-700">
                          <p className="px-5 pt-2 pb-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Contatos</p>
                          {contatos.map(ct => (
                            <button key={ct.id} onMouseDown={(e) => { e.preventDefault(); handleGlobalSearchSelect('contato', ct); }} className="w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors cursor-pointer flex items-start gap-3">
                              <span className="mt-0.5 w-7 h-7 rounded-lg bg-sky-100 dark:bg-sky-900/60 text-sky-600 dark:text-sky-300 flex items-center justify-center shrink-0">
                                <IconUsers size={14} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={ct.nome}>{ct.nome}</p>
                                <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{ct.cargo || ct.email || ''}</p>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      {negocios.length > 0 && (
                        <div className="py-2 border-t border-slate-200 dark:border-slate-700">
                          <p className="px-5 pt-2 pb-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Negócios &amp; Propostas</p>
                          {negocios.map(n => (
                            <button key={n.id} onMouseDown={(e) => { e.preventDefault(); handleGlobalSearchSelect('negocio', n); }} className="w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors cursor-pointer flex items-start gap-3">
                              <span className="mt-0.5 w-7 h-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-300 flex items-center justify-center shrink-0">
                                {/* Número oficial de proposta = já tem proposta associada
                                    (ícone de documento); sem número = só negócio, ainda sem
                                    proposta formal (maleta) — mesma distinção que aparece na
                                    linha secundária logo abaixo, só que visual. */}
                                {n.numero_proposta_oficial ? <IconDocument size={14} /> : <IconBriefcase size={14} />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={n.nome}>{n.nome}</p>
                                <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{n.estagio || ''}{n.numero_proposta_oficial ? ` · Nº ${n.numero_proposta_oficial}` : ''}</p>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Busca Proativa do ClickUp baseada no número comercial */}
          {activeTab === 'propostas' && (
            <div className="flex flex-col items-end space-y-1">
              <div className="flex items-center space-x-2">
                <div className="flex items-center bg-slate-100/50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1 space-x-2 h-9">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase">Proposta:</span>
                  <input 
                    type="text" 
                    className="bg-transparent border-0 p-0 text-sm text-slate-800 dark:text-slate-200 font-bold focus:ring-0 focus:outline-none w-48"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar Proposta (Ex: 12662/2026)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchClickUpProposal();
                    }}
                  />
                </div>
                <button
                  onClick={handleSearchClickUpProposal}
                  disabled={searching}
                  className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all h-9 flex items-center justify-center min-w-[70px]"
                >
                  {searching ? '...' : 'Buscar'}
                </button>
              </div>
              {searching && (
                <span className="text-[10px] text-indigo-400 font-medium animate-pulse">
                  🔍 Buscando Proposta...
                </span>
              )}
              {searchResult && !searching && (
                <span className={`text-[10px] font-bold ${searchResult.includes('🟢') ? 'text-emerald-400' : 'text-red-400'}`}>
                  {searchResult}
                </span>
              )}
            </div>
          )}

          {/* Status da Conexão */}
          <div className="flex items-center space-x-2">
            <span className={`w-2 h-2 rounded-full ${dbConnected ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
            <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">{dbConnected ? 'Supabase Ativo' : 'Supabase Offline'}</span>
          </div>

          {/* Perfil do Usuário Autenticado no ClickUp */}
          <div className="flex items-center space-x-2 pl-2 border-l border-slate-200 dark:border-slate-700">
            {userProfile && (
              <div
                className="flex items-center space-x-2 px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200/60 dark:border-slate-700/60 select-none"
                title={`Conectado ao ClickUp como ${userProfile.username || userProfile.email}`}
              >
                {userProfile.profilePicture ? (
                  <img src={userProfile.profilePicture} alt="User Avatar" className="w-5 h-5 rounded-full object-cover border border-slate-200 dark:border-slate-700" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-slate-600 text-white flex items-center justify-center text-[9px] font-black">
                    {(userProfile.username || userProfile.email || 'U').substring(0, 2).toUpperCase()}
                  </div>
                )}
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
                  {userProfile.username || userProfile.email}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={toggleTheme}
            className="p-2 text-slate-500 dark:text-slate-400 hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors"
            title={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
          >
            {theme === 'dark' ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 text-slate-500 dark:text-slate-400 hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors"
            title="Configurações de Conexão"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0" />
            </svg>
          </button>

          <button 
            onClick={async () => {
              if (supabaseClient) {
                identidadeGateRef.current?.invalidar();
                identidadeBloqueadaRef.current = true;
                await supabaseClient.auth.signOut();
                safeStorage.removeItem('crm_user_clickup_token');
                safeStorage.removeItem('crm_user_profile');
                setUserClickUpToken('');
                setUserProfile(null);
                setSession(null);
                showToast("Sessão encerrada com sucesso.", "success");
              }
            }}
            className="p-2 text-red-400 hover:text-red-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors"
            title="Sair / Logout"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      {/* 2. Sub-Header: Seleção de Abas do Sistema (Alinhado à Direita) */}
      <div className="flex justify-end bg-slate-50 dark:bg-slate-900 px-6 pt-4 pb-2 z-10">
        <div className="bg-slate-100 dark:bg-slate-700 p-1 rounded-lg flex gap-1 shadow-sm">
          <button
            onClick={() => setActiveTab('relatorios')}
            className={`font-medium px-4 py-2 text-xs rounded-md transition-all cursor-pointer ${
              activeTab === 'relatorios' 
                ? 'bg-slate-900 text-white shadow-sm font-semibold' 
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-600/50'
            }`}
          >
            Relatórios
          </button>
          <button
            onClick={() => setActiveTab('kanban')}
            className={`font-medium px-4 py-2 text-xs rounded-md transition-all cursor-pointer ${
              activeTab === 'kanban' 
                ? 'bg-slate-900 text-white shadow-sm font-semibold' 
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-600/50'
            }`}
          >
            Pipeline de Vendas
          </button>
          <button
            onClick={() => setActiveTab('ros')}
            className={`font-medium px-4 py-2 text-xs rounded-md transition-all cursor-pointer ${
              activeTab === 'ros'
                ? 'bg-slate-900 text-white shadow-sm font-semibold'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-600/50'
            }`}
          >
            R.Os
          </button>
          <button
            onClick={() => setActiveTab('empresas')}

            className={`font-medium px-4 py-2 text-xs rounded-md transition-all cursor-pointer ${
              activeTab === 'empresas' 
                ? 'bg-slate-900 text-white shadow-sm font-semibold' 
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-600/50'
            }`}
          >
            Empresas
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`font-medium px-4 py-2 text-xs rounded-md transition-all cursor-pointer ${
              activeTab === 'tasks' 
                ? 'bg-slate-900 text-white shadow-sm font-semibold' 
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-600/50'
            }`}
          >
            Tarefas Comerciais
          </button>
        </div>
      </div>

      {/* Alertas Globais */}
      {errorMsg && (
        <div className="fixed top-20 right-6 z-[9999] bg-red-950/90 border border-red-500/30 text-red-200 px-4 py-3 rounded-xl flex items-center space-x-2 shadow-2xl backdrop-blur-md animate-bounce">
          <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{errorMsg}</span>
        </div>
      )}

      {successMsg && (
        <div className="fixed top-20 right-6 z-[9999] bg-emerald-950/90 border border-emerald-500/30 text-emerald-200 px-4 py-3 rounded-xl flex items-center space-x-2 shadow-2xl backdrop-blur-md">
          <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">{successMsg}</span>
        </div>
      )}

      {infoMsg && (
        <div className="fixed top-20 right-6 z-[9999] bg-sky-950/90 border border-sky-500/30 text-sky-200 px-4 py-3 rounded-xl flex items-center space-x-2 shadow-2xl backdrop-blur-md">
          <svg className="w-5 h-5 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">{infoMsg}</span>
        </div>
      )}

      {warningMsg && (
        <div className="fixed top-20 right-6 z-[9999] bg-amber-950/90 border border-amber-500/30 text-amber-200 px-4 py-3 rounded-xl flex items-center space-x-2 shadow-2xl backdrop-blur-md">
          <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{warningMsg}</span>
        </div>
      )}

      {/* 3. Conteúdo Principal */}
      <div className={`flex-1 min-h-0 flex flex-col ${activeTab === 'ros' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        
        {/* ABA 0: RELATÓRIOS / DASHBOARD (Painel Comercial) */}
        {activeTab === 'relatorios' && (
          <main className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-900 p-6 space-y-6">
            
            {/* ELEMENTO 1 (TOPO ABSOLUTO): Barra de Filtro de Datas */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Relatórios</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Distribuição de faturamento acumulado por distribuidor e fabricante.</p>
              </div>

              {/* Seletor de Período e Comparativo */}
              <div className="flex flex-col items-end gap-2">
                <div className="flex flex-wrap items-center gap-2 bg-white dark:bg-slate-800 backdrop-blur-md border border-slate-200/80 dark:border-slate-700/80 rounded-2xl p-2.5 shadow-lg">
                  {/* Botões Rápidos de Período */}
                  {(() => {
                    const nowYear = new Date().getFullYear();
                    const q = getCurrentQuarterRange();
                    const yearStart = `${nowYear}-01-01`;
                    const yearEnd = `${nowYear}-12-31`;
                    return (
                      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-xl">
                        <button
                          type="button"
                          onClick={() => applyFilterRange(yearStart, yearEnd, `${nowYear - 1}-01-01`, `${nowYear - 1}-12-31`)}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${startDate === yearStart && endDate === yearEnd ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800'}`}
                        >
                          Ano Atual
                        </button>
                        <button
                          type="button"
                          onClick={() => applyFilterRange(q.start, q.end, q.compStart, q.compEnd)}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${startDate === q.start && endDate === q.end ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800'}`}
                        >
                          Trimestre Atual
                        </button>
                        <button
                          type="button"
                          onClick={() => applyFilterRange('2023-01-01', new Date().toISOString().split('T')[0], '', '')}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${startDate === '2023-01-01' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800'}`}
                        >
                          Todo o Histórico
                        </button>
                      </div>
                    );
                  })()}

                  <button
                    type="button"
                    onClick={() => setShowCustomRange(v => !v)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-slate-500 dark:text-slate-400 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all cursor-pointer"
                  >
                    Personalizar período
                    <svg className={`w-3 h-3 transition-transform ${showCustomRange ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {showCustomRange && (
                  <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 backdrop-blur-md border border-slate-200/80 dark:border-slate-700/80 rounded-2xl p-2.5 shadow-lg">
                    <div className="flex items-center space-x-2">
                      <label className="text-[10px] font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Início</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          applyFilterRange(v, endDate);
                        }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-[10px] font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Fim</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          applyFilterRange(startDate, v);
                        }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-[10px] font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Início Comp.</label>
                      <input
                        type="date"
                        value={compareStartDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          applyFilterRange(startDate, endDate, v, compareEndDate);
                        }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-[10px] font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Fim Comp.</label>
                      <input
                        type="date"
                        value={compareEndDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          applyFilterRange(startDate, endDate, compareStartDate, v);
                        }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                      />
                    </div>
                    <button
                      onClick={() => applyFilterRange(startDate, endDate, compareStartDate, compareEndDate)}
                      disabled={loadingDashboard}
                      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-indigo-950/30 active:scale-95 cursor-pointer"
                    >
                      {loadingDashboard ? '...' : 'Filtrar'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* BLOCO 1: RESUMO SAZONAL DE VENDAS */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-6 flex flex-col transition-all duration-300 hover:border-slate-200 dark:hover:border-slate-700 shadow-sm shadow-slate-100/50">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1 flex items-center gap-2">
                    <span>Resumo Sazonal de Vendas</span>
                    {biMetrics.compLabel && (
                      <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md normal-case tracking-normal">
                        Comparativo {biMetrics.currentLabel || 'Atual'} vs {biMetrics.compLabel}
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Evolução temporal e inteligência sazonal de negócios ganhos</p>
                </div>
                
                {/* Badges de Comparação no Topo */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-bold text-emerald-800 shadow-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                    <span>{biMetrics.currentLabel || 'Atual'}:</span>
                    <span className="text-slate-900 font-extrabold">R$ {((biMetrics?.wonValue || 0) / 1000000).toFixed(2)} MI</span>
                    <span className="text-[10px] text-emerald-700 font-medium">({biMetrics?.wonCount || 0} deals)</span>
                  </div>
                  {biMetrics.compLabel && compareStartDate && compareEndDate && (
                    <div className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 border border-indigo-200 rounded-lg text-xs font-bold text-indigo-800 shadow-sm">
                      <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 border border-white"></span>
                      <span>{biMetrics.compLabel}:</span>
                      <span className="text-slate-900 font-extrabold">R$ {((biMetrics?.wonValueComp || 0) / 1000000).toFixed(2)} MI</span>
                      <span className="text-[10px] text-indigo-700 font-medium">({biMetrics?.wonCountComp || 0} deals)</span>
                    </div>
                  )}
                  {biMetrics.compLabel && biMetrics?.wonValDiff !== null && (
                    <div className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${
                      biMetrics.wonValDiff >= 0 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-rose-50 text-rose-700 border-rose-200'
                    }`}>
                      <span>{biMetrics.wonValDiff >= 0 ? '▲ +' : '▼ -'}R$ {(Math.abs(biMetrics.wonValDiff) / 1000000).toFixed(2)} MI</span>
                      <span className="text-[10px]">({biMetrics.wonValDiff >= 0 ? '+' : ''}{(biMetrics.wonValPct || 0).toFixed(1)}%)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Grid de 6 KPIs no Topo (Resumo Sazonal) */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {/* 1. Negócios Ganhos */}
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3.5 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mb-1 block truncate">Negócios Ganhos</span>
                    <span className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">{biMetrics?.wonCount || 0}</span>
                  </div>
                  {compareStartDate && compareEndDate && biMetrics?.wonQtyDiff !== null && (
                    <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between text-[10px]">
                      <span className="text-slate-500 dark:text-slate-400 font-medium truncate">vs {biMetrics.compLabel || 'ant.'}: <strong>{biMetrics.wonCountComp || 0}</strong></span>
                      <span className={`font-bold px-1.5 py-0.5 rounded ${
                        biMetrics.wonQtyDiff >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {biMetrics.wonQtyDiff >= 0 ? `+${biMetrics.wonQtyDiff}` : biMetrics.wonQtyDiff} ({biMetrics.wonQtyDiff >= 0 ? '+' : ''}{(biMetrics.wonQtyPct || 0).toFixed(0)}%)
                      </span>
                    </div>
                  )}
                </div>

                {/* 2. Valor em Vendas */}
                <div className="bg-emerald-50/50 dark:bg-emerald-950/40 border border-emerald-200/80 dark:border-emerald-800/60 border-l-4 border-l-emerald-500 rounded-xl p-3.5 flex flex-col justify-between hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors">
                  <div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mb-1 block truncate">Valor em Vendas</span>
                    <span className="text-base font-extrabold text-slate-900 dark:text-slate-100 truncate block">
                      R$ {(biMetrics?.wonValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  {compareStartDate && compareEndDate && biMetrics?.wonValDiff !== null && (
                    <div className="mt-2 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/50 flex items-center justify-between text-[10px]">
                      <span className="text-slate-500 dark:text-slate-400 font-medium truncate">vs {biMetrics.compLabel || 'ant.'}: <strong>R$ {((biMetrics.wonValueComp || 0) / 1000000).toFixed(2)}M</strong></span>
                      <span className={`font-bold px-1.5 py-0.5 rounded ${
                        biMetrics.wonValDiff >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {biMetrics.wonValDiff >= 0 ? '▲ +' : '▼ '}{(biMetrics.wonValPct || 0).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* 3. Ciclo Médio */}
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3.5 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mb-1 block truncate">Ciclo Médio</span>
                    <span className="text-xl font-extrabold text-slate-900 dark:text-slate-100">{biMetrics?.avgCycleDays || 58} dias</span>
                  </div>
                  {compareStartDate && compareEndDate && biMetrics?.avgCycleDaysDiff !== null && (
                    <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between text-[10px]">
                      <span className="text-slate-500 dark:text-slate-400 font-medium truncate">vs {biMetrics.compLabel || 'ant.'}: <strong>{biMetrics.avgCycleDaysComp || 58}d</strong></span>
                      <span className={`font-bold px-1.5 py-0.5 rounded ${
                        biMetrics.avgCycleDaysDiff <= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {biMetrics.avgCycleDaysDiff <= 0 ? `${biMetrics.avgCycleDaysDiff}d` : `+${biMetrics.avgCycleDaysDiff}d`}
                      </span>
                    </div>
                  )}
                </div>

                {/* 4. Ticket Médio */}
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3.5 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mb-1 block truncate">Ticket Médio</span>
                    <span className="text-base font-extrabold text-slate-900 dark:text-slate-100 truncate block">
                      R$ {(biMetrics?.ticketMedio || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  {compareStartDate && compareEndDate && biMetrics?.ticketMedioDiff !== null && (
                    <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between text-[10px]">
                      <span className="text-slate-500 dark:text-slate-400 font-medium truncate">vs {biMetrics.compLabel || 'ant.'}: <strong>R$ {((biMetrics.ticketMedioComp || 0) / 1000).toFixed(1)}k</strong></span>
                      <span className={`font-bold px-1.5 py-0.5 rounded ${
                        biMetrics.ticketMedioDiff >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {biMetrics.ticketMedioDiff >= 0 ? '▲ +' : '▼ '}{(biMetrics.ticketMedioPct || 0).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* 5. Negócios Perdidos */}
                <div className="bg-rose-50/50 dark:bg-rose-950/40 border border-rose-200/80 dark:border-rose-800/60 border-l-4 border-l-rose-500 rounded-xl p-3.5 flex flex-col justify-between hover:border-rose-300 dark:hover:border-rose-700 transition-colors">
                  <div>
                    <span className="text-[11px] text-rose-700 dark:text-slate-400 font-semibold mb-1 block truncate">Negócios Perdidos</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-extrabold text-rose-950 dark:text-slate-100">{biMetrics?.lostCount || 0}</span>
                      <span className="text-[10px] font-medium text-rose-700/80 dark:text-slate-400/80">
                        (R$ {(biMetrics?.lostValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      </span>
                    </div>
                  </div>
                  {compareStartDate && compareEndDate && (
                    <div className="mt-2 pt-2 border-t border-rose-200/60 dark:border-rose-800/50 flex items-center justify-between text-[10px]">
                      <span className="text-rose-700/80 dark:text-slate-400/80 font-medium truncate">vs {biMetrics.compLabel || 'ant.'}: <strong>{biMetrics.lostCountComp || 0}</strong></span>
                      <span className="font-bold text-rose-800 dark:text-slate-200">
                        {biMetrics.lostQtyDiff <= 0 ? '0' : `+${biMetrics.lostQtyDiff}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* 6. Taxa de Conversão */}
                <div className="bg-indigo-50/50 dark:bg-slate-900 border border-indigo-200/80 dark:border-slate-700 border-l-4 border-l-indigo-500 rounded-xl p-3.5 flex flex-col justify-between hover:border-indigo-300 dark:hover:border-slate-600 transition-colors">
                  <div>
                    <span className="text-[11px] text-indigo-700 dark:text-slate-400 font-semibold mb-1 block truncate">Taxa Conversão</span>
                    <span className="text-xl font-extrabold text-indigo-950 dark:text-slate-100">{(biMetrics?.convRate || 0).toFixed(1)}%</span>
                  </div>
                  {compareStartDate && compareEndDate && biMetrics?.convRateDiff !== null && (
                    <div className="mt-2 pt-2 border-t border-indigo-200/60 dark:border-slate-700/50 flex items-center justify-between text-[10px]">
                      <span className="text-indigo-700/80 dark:text-slate-400/80 font-medium truncate">vs {biMetrics.compLabel || 'ant.'}: <strong>{(biMetrics.convRateComp || 0).toFixed(1)}%</strong></span>
                      <span className={`font-bold px-1.5 py-0.5 rounded ${
                        biMetrics.convRateDiff >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {biMetrics.convRateDiff >= 0 ? `+${biMetrics.convRateDiff.toFixed(1)}pp` : `${biMetrics.convRateDiff.toFixed(1)}pp`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Gráfico de Linha Temporal */}
              <div className="h-64 w-full pt-2">
                <canvas ref={seasonalityCanvasRef}></canvas>
              </div>

              {/* Rodapé com ação */}
              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                <button className="text-indigo-600 dark:text-indigo-400 font-bold hover:text-indigo-800 dark:hover:text-indigo-300 text-xs flex items-center gap-1 transition-colors cursor-pointer">
                  <span>Ver lista completa</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {loadingDashboard ? (
              <div className="flex-1 flex flex-col items-center justify-center space-y-3 py-20">
                <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin"></div>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Carregando dados consolidados...</p>
              </div>
            ) : !commercialData || commercialData.length === 0 ? (
              <div className="flex-1 border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-16 text-center flex flex-col items-center justify-center space-y-4 max-w-lg mx-auto my-10 bg-slate-50/50 dark:bg-slate-900/50">
                <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center text-3xl">📊</div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Nenhum dado encontrado</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Não existem itens de propostas criadas no período de {new Date(startDate + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(endDate + 'T00:00:00').toLocaleDateString('pt-BR')}.</p>
                </div>
              </div>
            ) : (
              <React.Fragment>
                {/* BLOCO 2 (GRID 2 COLUNAS): DISTRIBUIÇÃO POR DISTRIBUIDOR e DISTRIBUIÇÃO POR FABRICANTE */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Gráfico A: Distribuidor */}
                  <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-6 flex flex-col transition-all duration-300 hover:border-slate-200 dark:hover:border-slate-700">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <div>
                        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">Distribuição por Distribuidor</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Faturamento total acumulado agrupado por Distribuidor</p>
                      </div>
                      <div className="relative">
                        <select
                          value={selectedDistributorFilter}
                          onChange={(e) => setSelectedDistributorFilter(e.target.value)}
                          className="appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-3 pr-8 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer font-semibold shadow-inner"
                        >
                          <option value="all">Todos</option>
                          {Array.from(new Set(
                            (commercialData || [])
                              .map(item => {
                                const distObj = Array.isArray(item.distribuidores) ? item.distribuidores[0] : item.distribuidores;
                                return (distObj?.nome || 'NÃO INFORMADO').trim().toUpperCase();
                              })
                              .filter(f => f && f !== 'NÃO INFORMADO')
                          )).sort((a, b) => a.localeCompare(b)).map(dist => (
                            <option key={dist} value={dist}>{dist}</option>
                          ))}
                        </select>
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 dark:text-slate-400">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="relative h-64 w-full flex items-center justify-center">
                      <canvas ref={distributorCanvasRef}></canvas>
                      <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total</span>
                        <span className="text-lg font-black text-slate-900 dark:text-slate-100">{formatValueCompact(distributorTotalSum)}</span>
                      </div>
                    </div>
                    {/* Legenda HTML Customizada */}
                    <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-slate-200/80 dark:border-slate-700/80 max-h-40 overflow-y-auto pr-2">
                      {Object.keys(distributorTotals).map((label, idx) => {
                        const val = distributorTotals[label];
                        const percent = distributorTotalSum > 0 ? Math.round((val / distributorTotalSum) * 100) : 0;
                        const color = chartColors[idx % chartColors.length];
                        return (
                          <div key={label} className="flex items-center justify-between text-xs py-1">
                            <div className="flex items-center space-x-2 truncate mr-2">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                              <span className="text-slate-800 dark:text-slate-200 truncate">{label}</span>
                            </div>
                            <span className="font-bold text-slate-700 dark:text-slate-300">{percent}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gráfico B: Fabricante */}
                  <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-6 flex flex-col transition-all duration-300 hover:border-slate-200 dark:hover:border-slate-700">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <div>
                        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">Distribuição por Fabricante</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Faturamento total acumulado agrupado por Fabricante</p>
                      </div>
                      <div className="relative">
                        <select
                          value={selectedManufacturerFilter}
                          onChange={(e) => setSelectedManufacturerFilter(e.target.value)}
                          className="appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-3 pr-8 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer font-semibold shadow-inner"
                        >
                          <option value="all">Todos</option>
                          {Array.from(new Set(
                            (commercialData || [])
                              .map(item => {
                                const prodObj = Array.isArray(item.produtos) ? item.produtos[0] : item.produtos;
                                return (prodObj?.fabricante || '').trim().toUpperCase();
                              })
                              .filter(f => f && f !== 'NÃO INFORMADO')
                          )).sort((a, b) => a.localeCompare(b)).map(fab => (
                            <option key={fab} value={fab}>{fab}</option>
                          ))}
                        </select>
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 dark:text-slate-400">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="relative h-64 w-full flex items-center justify-center">
                      <canvas ref={manufacturerCanvasRef}></canvas>
                      <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total</span>
                        <span className="text-lg font-black text-slate-900 dark:text-slate-100">{formatValueCompact(manufacturerTotalSum)}</span>
                      </div>
                    </div>
                    {/* Legenda HTML Customizada */}
                    <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-slate-200/80 dark:border-slate-700/80 max-h-40 overflow-y-auto pr-2">
                      {Object.keys(manufacturerTotals).map((label, idx) => {
                        const val = manufacturerTotals[label];
                        const percent = manufacturerTotalSum > 0 ? Math.round((val / manufacturerTotalSum) * 100) : 0;
                        const color = chartColors[idx % chartColors.length];
                        return (
                          <div key={label} className="flex items-center justify-between text-xs py-1">
                            <div className="flex items-center space-x-2 truncate mr-2">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                              <span className="text-slate-800 dark:text-slate-200 truncate">{label}</span>
                            </div>
                            <span className="font-bold text-slate-700 dark:text-slate-300">{percent}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* BLOCO 3: PRODUTOS MAIS VENDIDOS */}
                <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-6 flex flex-col transition-all duration-300 hover:border-slate-200 dark:hover:border-slate-700 shadow-sm shadow-slate-100/50">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                    <div>
                      <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">Produtos Mais Vendidos</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Participação por categoria de solução comercial</p>
                    </div>
                    <div className="relative">
                      <select
                        value={topProductsFilterMode}
                        onChange={(e) => setTopProductsFilterMode(e.target.value)}
                        className="appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-3 pr-8 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer font-semibold shadow-inner"
                      >
                        <option value="value">Por valor</option>
                        <option value="qty">Por quantidade</option>
                      </select>
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 dark:text-slate-400">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {topProductsAggregated.length === 0 ? (
                    <div className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900">
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nenhum produto vendido em propostas ganhas no período selecionado.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                      {/* Legenda (Esquerda - col-span-7) */}
                      <div className="md:col-span-7 space-y-2.5 max-h-64 overflow-y-auto pr-2">
                        {topProductsAggregated.map(prod => (
                          <div key={prod.name} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-900 hover:bg-slate-100/80 dark:hover:bg-slate-700/80 transition-colors border border-slate-200/60 dark:border-slate-700/60">
                            <div className="flex items-center space-x-2.5 truncate mr-2">
                              <span className="w-3 h-3 rounded shrink-0 shadow-sm" style={{ backgroundColor: prod.color }} />
                              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide truncate">{prod.name}</span>
                            </div>
                            <div className="flex items-center space-x-3 text-xs shrink-0">
                              <span className="text-slate-500 dark:text-slate-300 font-medium">
                                {topProductsFilterMode === 'value'
                                  ? `R$ ${prod.val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                                  : `${prod.qty} un.`}
                              </span>
                              <span className="font-extrabold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 shadow-2xs">
                                {prod.pctStr}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Gráfico Doughnut (Direita - col-span-5) */}
                      <div className="md:col-span-5 relative h-64 w-full flex items-center justify-center">
                        <canvas ref={topProductsCanvasRef}></canvas>
                        <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none">
                          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Categorias</span>
                          <span className="text-xl font-black text-slate-900 dark:text-slate-100">{topProductsAggregated.length} Ativas</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </React.Fragment>
            )}
          </main>
        )}

        {/* ABA 1: TABULEIRO KANBAN (PIPELINE DE VENDAS) */}
        {activeTab === 'kanban' && (
          <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-900 min-h-0 overflow-hidden">
            {loadingKanban ? (
              <div className="flex-1 flex flex-col items-center justify-center space-y-4">
                <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin"></div>
                <p className="text-slate-500 dark:text-slate-400 font-medium">Carregando oportunidades...</p>
              </div>
            ) : (
              <React.Fragment>
                <div className="flex flex-col md:flex-row md:items-center justify-between px-6 py-3 bg-white dark:bg-slate-800 border-b border-slate-200/80 dark:border-slate-700/80 flex-shrink-0 space-y-3 md:space-y-0 shadow-md shadow-slate-100/60">
                  <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Exibir Estágios:</span>
                    <button
                      onClick={() => { setDealsListStatus('Congelado'); setShowDealsList(true); setShowForecast(false); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all flex items-center space-x-1.5 ${
                        showDealsList && dealsListStatus === 'Congelado'
                          ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-200 dark:hover:border-slate-700'
                      }`}
                    >
                      <span>❄️ Congelado</span>
                    </button>
                    <button
                      onClick={() => { setDealsListStatus('Todos'); setShowDealsList(true); setShowForecast(false); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all flex items-center space-x-1.5 ${
                        showDealsList && dealsListStatus === 'Todos'
                          ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-500'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-200 dark:hover:border-slate-700'
                      }`}
                    >
                      <span>📋 Lista Completa</span>
                    </button>

                    <button
                      onClick={() => setShowNovaOportunidadeKanban(true)}
                      className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                    >
                      <span>+ Nova Oportunidade</span>
                    </button>

                    {/* Lupa de Busca Expansível no Kanban — também funciona como
                        filtro por responsável embutido: digitar um nome que
                        bata com um vendedor sugere "Filtrar por responsável"
                        logo abaixo; selecionar troca a caixa por um chip que
                        filtra o board por ele (ver kanbanFilterResponsavelId).
                        Evita adicionar um controle novo na tela — mesmo
                        mecanismo de sempre, só mais esperto. */}
                    <div className="relative flex items-center ml-1">
                      {!isSearchOpen && !kanbanFilterResponsavelId ? (
                        <button
                          onClick={() => setIsSearchOpen(true)}
                          className="p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-400 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-full transition-all shadow-sm flex items-center justify-center cursor-pointer"
                          title="Buscar negócio por nome ou filtrar por responsável..."
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </button>
                      ) : kanbanFilterResponsavelId ? (
                        <div className="flex items-center bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-300 dark:border-indigo-700 rounded-full pl-3 pr-2 py-1 shadow-sm gap-1.5">
                          <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300 whitespace-nowrap">👤 {kanbanFilterResponsavelNome}</span>
                          <button
                            onClick={() => { setKanbanFilterResponsavelId(null); setKanbanFilterResponsavelNome(''); }}
                            className="text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200 text-xs font-bold cursor-pointer"
                            title="Remover filtro de responsável"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="flex items-center bg-white dark:bg-slate-800 border border-indigo-500 rounded-full px-3 py-1 shadow-sm transition-all duration-300 w-64">
                            <svg className="w-3.5 h-3.5 text-indigo-500 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              autoFocus
                              value={kanbanSearchTerm}
                              onChange={(e) => setKanbanSearchTerm(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setKanbanSearchTerm('');
                                  setIsSearchOpen(false);
                                }
                              }}
                              onBlur={() => {
                                // A sugestão de responsável usa onMouseDown+preventDefault,
                                // então clicar nela nunca chega a disparar esse blur. Mas os
                                // CARDS do board (resultado da busca por texto) usam onClick
                                // normal — sem esse setTimeout, o blur fecharia a busca e
                                // resetaria o filtro ANTES do clique no card completar,
                                // fazendo o card "fugir" de baixo do cursor (mudou de
                                // posição ao re-renderizar sem filtro) e o clique cair no
                                // vazio. O atraso deixa o clique no card terminar primeiro;
                                // pra um clique realmente fora, o resultado visual é o mesmo.
                                setTimeout(() => {
                                  setKanbanSearchTerm('');
                                  setIsSearchOpen(false);
                                }, 200);
                              }}
                              placeholder="Buscar negócio ou responsável..."
                              className="bg-transparent border-none text-xs text-slate-800 dark:text-slate-200 focus:outline-none w-full font-medium"
                            />
                            <button
                              onClick={() => {
                                setKanbanSearchTerm('');
                                setIsSearchOpen(false);
                              }}
                              className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-xs font-bold ml-1 cursor-pointer"
                            >
                              ✕
                            </button>
                          </div>
                          {kanbanSearchTerm.trim().length > 0 && (() => {
                            const termo = kanbanSearchTerm.trim().toLowerCase();
                            const sugestoes = vendedoresVisiveis.filter(v => (v.nome || '').toLowerCase().includes(termo));
                            if (sugestoes.length === 0) return null;
                            return (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto py-1">
                                {sugestoes.map(v => (
                                  <button
                                    key={v.id}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setKanbanFilterResponsavelId(String(v.id));
                                      setKanbanFilterResponsavelNome(v.nome);
                                      setKanbanSearchTerm('');
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors flex items-center gap-1.5 cursor-pointer"
                                  >
                                    <span>👤</span>
                                    <span>Filtrar por responsável: <strong>{v.nome}</strong></span>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Ordenar por:</span>
                      <select
                        value={sortBy}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          safeStorage.setItem('crm_sort_order', newValue);
                          setSortBy(newValue);
                        }}
                        className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2 text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer"
                      >
                        <option value="default">Padrão</option>
                        <option value="name">Nome (A - Z)</option>
                        <option value="value_asc">Valor (Menor para Maior)</option>
                        <option value="value_desc">Valor (Maior para Menor)</option>
                      </select>
                    </div>

                    <button 
                      onClick={() => {
                        const nextVal = !showForecast;
                        console.log("[DEBUG] Forecast clicked, state is now:", nextVal);
                        setShowForecast(nextVal);
                        if (nextVal) {
                          setShowDealsList(false);
                        } else {
                          setFilterStage(null);
                          setFilterFabricante(null);
                        }
                      }}
                      className={`mr-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${showForecast ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'}`}
                    >
                      📈 Forecast
                    </button>
                  </div>
                </div>

                {showDealsList && (
                  <DealsListView
                    kanbanTasks={kanbanTasks}
                    kanbanColumns={kanbanColumns}
                    getTaskOptionId={getTaskOptionId}
                    getOpportunityValue={getOpportunityValue}
                    onCardClick={handleCardClick}
                    statusFilter={dealsListStatus}
                    setStatusFilter={setDealsListStatus}
                    onClose={() => setShowDealsList(false)}
                    supabaseClient={supabaseClient}
                    kanbanFilterResponsavelNome={kanbanFilterResponsavelNome}
                  />
                )}

                {!showDealsList && showForecast && (
                  <ForecastFunnelPanel
                    kanbanColumns={kanbanColumns}
                    kanbanTasks={kanbanTasks}
                    filterStage={filterStage}
                    setFilterStage={setFilterStage}
                    filterFabricante={filterFabricante}
                    setFilterFabricante={setFilterFabricante}
                    kanbanSearchTerm={kanbanSearchTerm}
                    kanbanFilterResponsavelNome={kanbanFilterResponsavelNome}
                    getTaskOptionId={getTaskOptionId}
                    getOpportunityValue={getOpportunityValue}
                    onCardClick={handleCardClick}
                    theme={theme}
                  />
                )}

                {/* Kanban Board: oculto quando Split View do Forecast ou Lista de Negócios está ativa */}
                {!showDealsList && !(showForecast && filterStage) && (
                <div className="kanban-board flex-1 min-h-0 overflow-x-auto">
                  {kanbanColumns.map(col => {
                    if (filterStage && col.id !== filterStage) return null;
                    const colName = col.name.toLowerCase();
                    if (colName.includes("ganho") || colName.includes("perdido") || colName.includes("congelado")) return null;

                    const tasksInCol = kanbanTasks.filter(t => {
                      const inCol = getTaskOptionId(t, kanbanColumns) === col.id;
                      if (!inCol) return false;
                      // Compara por NOME (responsavel_negocio), não por
                      // responsavel_clickup_id — negócios antigos podem não
                      // ter o id preenchido mesmo já mostrando o nome
                      // corretamente no card (fallback pro criado_por da
                      // proposta, ver fetchKanbanData), o que fazia o filtro
                      // por id excluir negócios que visivelmente já eram do
                      // responsável selecionado.
                      if (kanbanFilterResponsavelNome && t.responsavel_negocio !== kanbanFilterResponsavelNome) return false;
                      return taskMatchesSearchTerm(t, kanbanSearchTerm.toLowerCase().trim());
                    });
                    
                    const sortedTasks = [...tasksInCol].sort((a, b) => {
                      if (sortBy === 'name') {
                        return a.name.localeCompare(b.name);
                      }
                      if (sortBy === 'value_asc' || sortBy === 'value_desc') {
                        const valA = getOpportunityValue(a) || 0;
                        const valB = getOpportunityValue(b) || 0;
                        if (valA === 0 && valB !== 0) return 1;
                        if (valB === 0 && valA !== 0) return -1;
                        if (valA === 0 && valB === 0) return 0;
                        return sortBy === 'value_asc' ? valA - valB : valB - valA;
                      }
                      return 0;
                    });

                    return (
                      <div key={col.id} className="kanban-column">
                        <div className="kanban-column-header">
                          <div className="flex items-center space-x-2">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: col.color || '#fff' }}></span>
                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">{col.name}</span>
                          </div>
                          <span className="bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full text-xs font-bold text-indigo-600">
                            {tasksInCol.length}
                          </span>
                        </div>
                        <div 
                          data-option-id={col.id}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => handleDrop(e, col.id)}
                          className="kanban-cards"
                        >
                          {sortedTasks.map(task => {
                            const dealValue = getOpportunityValue(task);
                            const formattedValue = dealValue !== null && dealValue !== undefined
                              ? `R$ ${Number(dealValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
                              : 'R$ 0,00';
                            const responsavel = task.responsavel_negocio;
                             const hasOverdue = commercialTasks.some(t => {
                               const propObj = Array.isArray(t.propostas) ? t.propostas[0] : t.propostas;
                               const isThisDeal = t.clickup_negocio_id === task.id || (propObj && propObj.clickup_negocio_id === task.id);
                               return isThisDeal && t.status === 'pendente' && new Date(t.data_vencimento) < new Date();
                             });
                             return (
                               <KanbanCard
                                 key={task.id}
                                 task={task}
                                 dealValue={dealValue}
                                 formattedValue={formattedValue}
                                 responsavel={responsavel}
                                 handleDragStart={handleDragStart}
                                 handleCardClick={handleCardClick}
                                 hasOverdue={hasOverdue}
                                 stageColor={col.color}
                               />
                             );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}


                {showNovaOportunidadeKanban && (
                  <NovaOportunidadeModal
                    supabaseClient={supabaseClient}
                    contaFixa={null}
                    contas={contasParaBusca}
                    vendedores={vendedoresVisiveis}
                    kanbanModo={kanbanModoRef.current}
                    kanbanCanonico={kanbanModoRef.current === 'canonico_v1' && identidadeModoRef.current === 'canonico_v1' && session?.user?.id ? { config, userId: session.user.id } : null}
                    onClose={() => setShowNovaOportunidadeKanban(false)}
                    onCriado={() => {
                      setShowNovaOportunidadeKanban(false);
                      fetchKanbanData();
                    }}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        )}

        {empresasTabMounted && (
          <div className={activeTab === 'empresas' ? 'flex-1 flex flex-col min-h-0' : 'hidden'}>
            <EmpresasTab
              supabaseClient={supabaseClient}
              onOpenNegocio={handleCardClick}
              vendedores={vendedoresVisiveis}
              kanbanModo={kanbanModoRef.current}
              kanbanCanonico={kanbanModoRef.current === 'canonico_v1' && identidadeModoRef.current === 'canonico_v1' && session?.user?.id ? { config, userId: session.user.id } : null}
              contaParaAbrir={contaParaAbrir}
              abaParaAbrir={abaContaParaAbrir}
              onContaAberta={() => { setContaParaAbrir(null); setAbaContaParaAbrir('visao_geral'); }}
              onNovaRo={handleAbrirNovaRoOportunidade}
              onNavegarParaRos={handleNavegarParaRos}
            />
          </div>
        )}

        {activeTab === 'tasks' && (() => {
          const now = new Date();
          const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          const todayStr = startOfToday.toDateString();

          // Helper para saber se uma data venceu
          const isTaskOverdue = (dateStr) => {
            if (!dateStr) return false;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return false;
            // Se tiver hora específica (diferente de meia-noite), compara com o horário atual
            const hasExplicitTime = dateStr.includes('T') && !dateStr.endsWith('T00:00:00') && !dateStr.endsWith('T00:00:00.000Z');
            if (hasExplicitTime) return d < now;
            // Se for só data sem hora, só vence quando o dia encerra (ou seja, se a data for anterior a hoje)
            return d < startOfToday;
          };

          const filtered = commercialTasks.filter(task => {
            if (tasksFilterAssignee !== 'all' && String(task.responsavel_clickup_id) !== tasksFilterAssignee) return false;
            if (!tasksShowCompleted && task.status === 'concluida') return false;

            if (tasksPeriodFilter !== 'all') {
              const taskDate = task.data_vencimento ? new Date(task.data_vencimento) : null;
              if (!taskDate || isNaN(taskDate.getTime())) return false;

              if (tasksPeriodFilter === 'today') {
                if (taskDate.toDateString() !== todayStr) return false;
              } else if (tasksPeriodFilter === 'overdue') {
                if (task.status === 'concluida' || !isTaskOverdue(task.data_vencimento)) return false;
              } else if (tasksPeriodFilter === 'week') {
                const dayOfWeek = startOfToday.getDay();
                const startOfWeek = new Date(startOfToday);
                startOfWeek.setDate(startOfToday.getDate() - dayOfWeek);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                endOfWeek.setHours(23, 59, 59, 999);
                if (taskDate < startOfWeek || taskDate > endOfWeek) return false;
              } else if (tasksPeriodFilter === 'month') {
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                if (taskDate < startOfMonth || taskDate > endOfMonth) return false;
              } else if (tasksPeriodFilter === 'custom') {
                if (tasksCustomStartDate) {
                  const s = new Date(`${tasksCustomStartDate}T00:00:00`);
                  if (taskDate < s) return false;
                }
                if (tasksCustomEndDate) {
                  const e = new Date(`${tasksCustomEndDate}T23:59:59.999`);
                  if (taskDate > e) return false;
                }
              }
            }

            return true;
          });

          // Sem duplicar tarefas vencidas em Hoje
          const overdueItems = filtered.filter(t => t.status !== 'concluida' && isTaskOverdue(t.data_vencimento));
          const todayItems = filtered.filter(t => t.status !== 'concluida' && new Date(t.data_vencimento).toDateString() === todayStr && !isTaskOverdue(t.data_vencimento));
          const pendingItems = filtered.filter(t => t.status !== 'concluida');
          const doneItems = filtered.filter(t => t.status === 'concluida');

          // TaskCard agora vive no corpo do componente principal (perto de
          // handleEditTaskClick) — é reaproveitado também na lista "Tarefas
          // Associadas" do drawer do negócio, então as duas listas usam
          // literalmente o mesmo componente e não podem mais divergir.

          return (
            <div className="flex-1 flex flex-col bg-slate-50/80 dark:bg-slate-900/80 overflow-hidden">
              {/* Header */}
              <div className="px-6 pt-6 pb-4 bg-white dark:bg-slate-800 border-b border-slate-200/80 dark:border-slate-700/80">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">Tarefas</h1>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Atividades integradas ao ClickUp</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Filtro de Período */}
                    <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5">
                      <svg className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <select
                        value={tasksPeriodFilter}
                        onChange={(e) => setTasksPeriodFilter(e.target.value)}
                        className="bg-transparent border-none text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none cursor-pointer"
                      >
                        <option value="all">Todas as Datas</option>
                        <option value="today">Hoje</option>
                        <option value="week">Esta Semana</option>
                        <option value="month">Este Mês</option>
                        <option value="overdue">Apenas Vencidas</option>
                        <option value="custom">📅 Período Personalizado</option>
                      </select>
                    </div>

                    {/* Datas Customizadas quando o filtro de período é 'custom' — mesmo
                        padrão visual (label em caixa + input em caixa) do filtro de
                        período do Relatórios, ver "Personalizar período" ali. Filtro
                        aqui é 100% local (sem necessidade de botão "Filtrar"). */}
                    {tasksPeriodFilter === 'custom' && (
                      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl p-2.5 shadow-lg">
                        <div className="flex items-center space-x-2">
                          <label className="text-[10px] font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">De</label>
                          <input
                            type="date"
                            value={tasksCustomStartDate}
                            onChange={(e) => setTasksCustomStartDate(e.target.value)}
                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-[10px] font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Até</label>
                          <input
                            type="date"
                            value={tasksCustomEndDate}
                            onChange={(e) => setTasksCustomEndDate(e.target.value)}
                            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                          />
                        </div>
                      </div>
                    )}

                    {/* Filtro de responsável */}
                    <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5">
                      <svg className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <select
                        value={tasksFilterAssignee}
                        onChange={(e) => setTasksFilterAssignee(e.target.value)}
                        className="bg-transparent border-none text-xs font-semibold text-slate-700 dark:text-slate-300 focus:outline-none cursor-pointer"
                      >
                        <option value="all">Todos Responsáveis</option>
                        {vendedoresVisiveis.map(v => (
                          <option key={v.id} value={String(v.id)}>{v.nome}</option>
                        ))}
                      </select>
                    </div>

                    {/* Toggle concluídas */}
                    <button
                      onClick={() => setTasksShowCompleted(!tasksShowCompleted)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer flex items-center gap-1.5 ${
                        tasksShowCompleted
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                      {tasksShowCompleted ? 'Ocultar Concluídas' : 'Ver Concluídas'}
                    </button>

                    {/* Nova Tarefa — usa handleNewTaskClick (e não um onClick
                        inline) porque só ele faz setEditingTask(null) e limpa
                        título/tipo/data/responsável. Sem isso, abrir "Nova
                        Tarefa" logo depois de cancelar uma edição reabria o
                        modal em modo edição e o submit virava PUT,
                        sobrescrevendo a tarefa editada por engano. */}
                    <button
                      onClick={handleNewTaskClick}
                      className="px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white rounded-xl text-xs font-bold transition-all shadow-sm shadow-indigo-600/20 cursor-pointer flex items-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                      </svg>
                      Nova Tarefa
                    </button>
                  </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3.5">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Pendentes</p>
                    <p className="text-2xl font-black text-slate-800 dark:text-slate-200 mt-0.5">{pendingItems.length}</p>
                  </div>
                  <div className={`rounded-xl p-3.5 border ${overdueItems.length > 0 ? 'bg-rose-50 dark:bg-rose-950/60 border-rose-200 dark:border-rose-800/60' : 'bg-slate-50 dark:bg-slate-900 border-slate-200/80 dark:border-slate-700/80'}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${overdueItems.length > 0 ? 'text-rose-500 dark:text-rose-400' : 'text-slate-400 dark:text-slate-400'}`}>Vencidas</p>
                    <p className={`text-2xl font-black mt-0.5 ${overdueItems.length > 0 ? 'text-rose-700 dark:text-slate-100' : 'text-slate-800 dark:text-slate-200'}`}>{overdueItems.length}</p>
                  </div>
                  <div className={`rounded-xl p-3.5 border ${todayItems.length > 0 ? 'bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800/60' : 'bg-slate-50 dark:bg-slate-900 border-slate-200/80 dark:border-slate-700/80'}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${todayItems.length > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-400'}`}>Para Hoje</p>
                    <p className={`text-2xl font-black mt-0.5 ${todayItems.length > 0 ? 'text-amber-700 dark:text-slate-100' : 'text-slate-800 dark:text-slate-200'}`}>{todayItems.length}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3.5">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Concluídas</p>
                    <p className="text-2xl font-black text-slate-800 dark:text-slate-200 mt-0.5">{doneItems.length}</p>
                  </div>
                </div>
              </div>

              {/* Lista de Tarefas */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingTasks ? (
                  <div className="flex flex-col items-center justify-center h-full space-y-3">
                    <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin" />
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Carregando tarefas...</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center space-y-4 max-w-sm mx-auto">
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">Nenhuma tarefa encontrada</h3>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Crie uma nova tarefa para começar a registrar atividades comerciais.</p>
                    </div>
                    <button
                      onClick={handleNewTaskClick}
                      className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                    >
                      + Criar Primeira Tarefa
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6 max-w-4xl mx-auto">
                    {/* Seção: Vencidas */}
                    {overdueItems.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                            <h3 className="text-xs font-extrabold text-rose-600 uppercase tracking-widest">Vencidas</h3>
                          </div>
                          <span className="bg-rose-100 text-rose-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-rose-200">{overdueItems.length}</span>
                        </div>
                        <div className="space-y-2">
                          {overdueItems.map(task => <TaskCard key={task.id} task={task} />)}
                        </div>
                      </div>
                    )}

                    {/* Seção: Hoje */}
                    {todayItems.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                            <h3 className="text-xs font-extrabold text-amber-700 uppercase tracking-widest">Hoje</h3>
                          </div>
                          <span className="bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200">{todayItems.length}</span>
                        </div>
                        <div className="space-y-2">
                          {todayItems.map(task => <TaskCard key={task.id} task={task} />)}
                        </div>
                      </div>
                    )}

                    {/* Seção: Próximas / Futuras */}
                    {(() => {
                      const upcoming = filtered.filter(t => t.status !== 'concluida' && new Date(t.data_vencimento).toDateString() !== todayStr && new Date(t.data_vencimento) >= now);
                      if (upcoming.length === 0) return null;
                      return (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-indigo-400" />
                              <h3 className="text-xs font-extrabold text-indigo-600 uppercase tracking-widest">Próximas</h3>
                            </div>
                            <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-indigo-200">{upcoming.length}</span>
                          </div>
                          <div className="space-y-2">
                            {upcoming.map(task => <TaskCard key={task.id} task={task} />)}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Seção: Concluídas */}
                    {tasksShowCompleted && doneItems.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5">
                            <svg className="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <h3 className="text-xs font-extrabold text-emerald-600 uppercase tracking-widest">Concluídas</h3>
                          </div>
                          <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">{doneItems.length}</span>
                        </div>
                        <div className="space-y-2">
                          {doneItems.map(task => <TaskCard key={task.id} task={task} />)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* 5. Aba de Registros de Oportunidade (Task 3) */}
        {activeTab === 'ros' && (
          <RegistrosOportunidadeView
            rosState={rosState}
            rosResumo={rosResumo}
            fabricantes={fabricantesRo}
            fabricantesStatus={fabricantesRoStatus}
            onRecarregarFabricantes={carregarFabricantesRoAtivos}
            vendedores={vendedoresVisiveis}
            getSupabaseHeaders={getCrmHeaders}
            supabaseClient={supabaseClient}
            showToast={showToast}
            permissaoRos={permissaoRos}
            onSelect={(id) => setRosState((prev) => ({ ...prev, selectedId: id }))}
            onCloseSelection={() => setRosState((prev) => ({ ...prev, selectedId: null }))}
            onNovaRo={() => {
              carregarFabricantesRoAtivos();
              setModalNovaRoOportunidadeFixa(null);
              setModalNovaRoAberto(true);
            }}
            onFilterChange={(novosFiltros) => {
              setRosState((prev) => ({ ...prev, filters: novosFiltros, page: 1 }));
            }}
            onPageChange={(novaPagina) => {
              setRosState((prev) => ({ ...prev, page: novaPagina }));
            }}
            onRefresh={() => loadRegistrosOportunidade(rosState.filters, rosState.page)}
            onRoAtualizada={(roAtualizada) => {
              if (roAtualizada && roAtualizada.id) {
                setRosState((prev) => ({
                  ...prev,
                  rows: prev.rows.map((r) => r.id === roAtualizada.id ? { ...r, ...roAtualizada } : r),
                }));
              }
              loadRegistrosOportunidade(rosState.filters, rosState.page);
            }}
          />
        )}


      </div>

      {/* 4. Modal de Configurações Completo */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          {/* h-[85vh] fixo (não max-h): o corpo tem abas com conteúdo de
              tamanhos bem diferentes (Catálogo de Produtos vs. Distribuidores/
              Vendedores) — com max-h o modal encolhia pro tamanho de cada
              aba, mudando de altura toda vez que trocava. Com altura fixa,
              o tamanho fica padronizado e a área de conteúdo (já com
              overflow-y-auto) rola sozinha quando uma aba tem mais coisa. */}
          <div
            ref={settingsModalRef}
            onKeyDown={settingsModalLayer.onKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-modal-title"
            className="w-full max-w-5xl bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700/90 rounded-2xl shadow-2xl flex flex-col h-[85vh] overflow-hidden relative">
            <button
              type="button"
              onClick={() => setShowSettingsModal(false)}
              className="absolute top-4 right-4 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors z-10 cursor-pointer"
              title="Fechar (ESC)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Cabeçalho do Modal */}
            <div className="shrink-0 border-b border-slate-200/80 dark:border-slate-700/80 pl-6 pr-12 py-4 bg-slate-50/80 dark:bg-slate-900/80">
              <h3 id="settings-modal-title" className="text-base font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <div className="w-7 h-7 bg-indigo-500 text-white rounded-lg flex items-center justify-center shadow-xs">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  </svg>
                </div>
                <span>Painel de Configurações e Cadastros</span>
              </h3>
            </div>

            {/* Corpo do Modal com Abas Laterais */}
            <div className="flex-1 min-h-0 flex overflow-hidden">
              {/* Menu Lateral de Abas — ARIA tablist vertical: setas ↑/↓
                  movem e já trocam de aba (data-arrow-activate). */}
              <aside role="tablist" aria-orientation="vertical" onKeyDown={handleArrowNav} className="w-32 sm:w-1/4 shrink-0 overflow-y-auto border-r border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/50 p-2 sm:p-4 space-y-1.5 flex flex-col">
                <button
                  data-arrow-item
                  data-arrow-activate
                  role="tab"
                  id="settings-tab-products"
                  aria-selected={settingsActiveTab === 'products'}
                  aria-controls="settings-tabpanel"
                  tabIndex={settingsActiveTab === 'products' ? 0 : -1}
                  onClick={() => setSettingsActiveTab('products')}
                  className={`w-full px-4 py-2.5 rounded-xl text-left text-xs font-bold transition-all cursor-pointer ${
                    settingsActiveTab === 'products'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-600/60'
                  }`}
                >
                  Catálogo de Produtos
                </button>
                <button
                  data-arrow-item
                  data-arrow-activate
                  role="tab"
                  id="settings-tab-distributors"
                  aria-selected={settingsActiveTab === 'distributors'}
                  aria-controls="settings-tabpanel"
                  tabIndex={settingsActiveTab === 'distributors' ? 0 : -1}
                  onClick={() => {
                    setSettingsActiveTab('distributors');
                    loadFabricantes();
                  }}
                  className={`w-full px-4 py-2.5 rounded-xl text-left text-xs font-bold transition-all cursor-pointer ${
                    settingsActiveTab === 'distributors'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-600/60'
                  }`}
                >
                  Fabricantes
                </button>
                <button
                  data-arrow-item
                  data-arrow-activate
                  role="tab"
                  id="settings-tab-venders"
                  aria-selected={settingsActiveTab === 'venders'}
                  aria-controls="settings-tabpanel"
                  tabIndex={settingsActiveTab === 'venders' ? 0 : -1}
                  onClick={() => setSettingsActiveTab('venders')}
                  className={`w-full px-4 py-2.5 rounded-xl text-left text-xs font-bold transition-all cursor-pointer ${
                    settingsActiveTab === 'venders'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-600/60'
                  }`}
                >
                  Usuários e Permissões
                </button>
                <button
                  data-arrow-item
                  data-arrow-activate
                  role="tab"
                  id="settings-tab-taskTypes"
                  aria-selected={settingsActiveTab === 'taskTypes'}
                  aria-controls="settings-tabpanel"
                  tabIndex={settingsActiveTab === 'taskTypes' ? 0 : -1}
                  onClick={() => setSettingsActiveTab('taskTypes')}
                  className={`w-full px-4 py-2.5 rounded-xl text-left text-xs font-bold transition-all cursor-pointer ${
                    settingsActiveTab === 'taskTypes'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-600/60'
                  }`}
                >
                  Tipos de Tarefas
                </button>
                <button
                  data-arrow-item
                  data-arrow-activate
                  role="tab"
                  id="settings-tab-numeracao"
                  aria-selected={settingsActiveTab === 'numeracao'}
                  aria-controls="settings-tabpanel"
                  tabIndex={settingsActiveTab === 'numeracao' ? 0 : -1}
                  onClick={() => setSettingsActiveTab('numeracao')}
                  className={`w-full px-4 py-2.5 rounded-xl text-left text-xs font-bold transition-all cursor-pointer ${
                    settingsActiveTab === 'numeracao'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-600/60'
                  }`}
                >
                  Numeração de Propostas
                </button>
                <button
                  data-arrow-item
                  data-arrow-activate
                  role="tab"
                  id="settings-tab-segmentos"
                  aria-selected={settingsActiveTab === 'segmentos'}
                  aria-controls="settings-tabpanel"
                  tabIndex={settingsActiveTab === 'segmentos' ? 0 : -1}
                  onClick={() => setSettingsActiveTab('segmentos')}
                  className={`w-full px-4 py-2.5 rounded-xl text-left text-xs font-bold transition-all cursor-pointer ${
                    settingsActiveTab === 'segmentos'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-600/60'
                  }`}
                >
                  Segmentos de Atuação
                </button>
              </aside>

              {/* Área de Conteúdo da Aba Ativa */}
              <main role="tabpanel" id="settings-tabpanel" aria-labelledby={`settings-tab-${settingsActiveTab}`} className="flex-1 min-h-0 min-w-0 p-4 sm:p-6 overflow-y-auto bg-slate-50/30 dark:bg-slate-900/30">
                {/* 2. ABA PRODUTOS */}
                {settingsActiveTab === 'products' && (
                  <div className="h-full flex flex-col gap-6">
                    <div className="shrink-0 flex flex-wrap gap-2 items-center justify-between">
                      <div>
                        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Catálogo de Produtos</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">Gerencie o portfólio de ofertas e importe tabelas em lote.</p>
                      </div>
                      <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
                        {produtos.length} SKUs
                      </span>
                    </div>

                    {/* Cadastrar/Editar Produto */}
                    <div className="shrink-0 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-4 shadow-xs">
                      <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-3">
                        {editingProduct ? 'Editar Produto' : 'Cadastrar Novo Produto'}
                      </h3>
                      <form 
                        onSubmit={editingProduct ? handleSaveProductEdit : handleCreateProduct}
                        className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
                      >
                        <div>
                          <label className="block text-[10px] text-slate-500 dark:text-slate-400 font-semibold mb-1">Fabricante</label>
                          <select
                            required
                            value={editingProduct ? (editingProduct.fabricante_id || editingProduct.fabricante) : newProduct.fabricante}
                            onChange={(e) => {
                              if (editingProduct) {
                                setEditingProduct({ ...editingProduct, fabricante_id: e.target.value, fabricante: e.target.value });
                              } else {
                                setNewProduct({ ...newProduct, fabricante: e.target.value });
                              }
                            }}
                            className="w-full rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 cursor-pointer"
                          >
                            <option value="">Selecione o Fabricante...</option>
                            {fabricantes.map(f => (
                              <option key={f.id} value={f.id}>{f.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-500 dark:text-slate-400 font-semibold mb-1">Nome do Produto</label>
                          <input 
                            type="text" 
                            required
                            placeholder="Ex: Licença VMware vSphere"
                            value={editingProduct ? editingProduct.nome : newProduct.nome}
                            onChange={(e) => {
                              if (editingProduct) {
                                setEditingProduct({ ...editingProduct, nome: e.target.value });
                              } else {
                                setNewProduct({ ...newProduct, nome: e.target.value });
                              }
                            }}
                            className="w-full rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <div className="flex-1 min-w-0">
                            <label className="block text-[10px] text-slate-500 dark:text-slate-400 font-semibold mb-1">Custo de Referência</label>
                            <input 
                              type="number" 
                              step="0.01"
                              required
                              placeholder="0.00"
                              value={editingProduct ? editingProduct.custo_referencia : newProduct.custo_referencia}
                              onChange={(e) => {
                                if (editingProduct) {
                                  setEditingProduct({ ...editingProduct, custo_referencia: e.target.value });
                                } else {
                                  setNewProduct({ ...newProduct, custo_referencia: e.target.value });
                                }
                              }}
                              className="w-full rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 text-right font-mono"
                            />
                          </div>
                          <button 
                            type="submit"
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs self-end h-[34px] cursor-pointer"
                          >
                            {editingProduct ? 'Salvar' : 'Cadastrar'}
                          </button>
                          {editingProduct && (
                            <button 
                              type="button"
                              onClick={() => setEditingProduct(null)}
                              className="px-3 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold transition-all self-end h-[34px] cursor-pointer"
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                      </form>
                    </div>

                    {/* Tabela de Produtos */}
                    <div className="flex-1 min-h-48 overflow-auto bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl shadow-xs">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            <th className="p-3">Fabricante</th>
                            <th className="p-3">Nome do Produto</th>
                            <th className="p-3 text-right">Preço de Referência</th>
                            <th className="p-3 text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {produtos.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="p-6 text-center text-slate-400 dark:text-slate-500">Nenhum produto cadastrado.</td>
                            </tr>
                          ) : (
                            produtos.map(p => (
                              <tr key={p.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/80 transition-colors">
                                <td className="p-3 font-semibold text-slate-700 dark:text-slate-300">{p.fabricante}</td>
                                <td className="p-3 text-slate-900 dark:text-slate-100 font-medium">{p.nome}</td>
                                <td className="p-3 text-right font-mono text-slate-800 dark:text-slate-200 font-semibold">
                                  R$ {Number(p.custo_referencia).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                </td>
                                <td className="p-3 text-center space-x-2">
                                  <button 
                                    onClick={() => setEditingProduct(p)}
                                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold cursor-pointer"
                                  >
                                    Editar
                                  </button>
                                  <span className="text-slate-300">•</span>
                                  <button 
                                    onClick={() => handleDeleteProduct(p.id)}
                                    className="text-rose-600 dark:text-rose-400 hover:text-rose-800 dark:hover:text-rose-300 font-semibold cursor-pointer"
                                  >
                                    Excluir
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                  </div>
                )}

                {/* 3. ABA FABRICANTES (CATÁLOGO CANÔNICO - 4A.4) */}
                {settingsActiveTab === 'distributors' && (
                  <div className="h-full flex flex-col gap-6">
                    <div className="shrink-0">
                      <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Catálogo de Fabricantes</h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                        Gestão administrativa do catálogo mestre de fabricantes. Ativação e inativação preservam a integridade histórica de produtos e R.Os.
                      </p>
                    </div>

                    {!(catalogoPermissao?.pode_administrar_fabricantes || catalogoPermissao?.role === 'admin') ? (
                      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-xs text-amber-800 dark:text-amber-300">
                        <p className="font-semibold mb-1">Acesso Restrito</p>
                        <p>A gestão administrativa do catálogo de fabricantes (criação, edição e inativação) é permitida exclusivamente para administradores do catálogo.</p>
                      </div>
                    ) : (
                      <>
                        <div className="shrink-0 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-4 shadow-xs">
                          <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-3">
                            {editingDistributor ? 'Editar Fabricante' : 'Novo Fabricante'}
                          </h3>
                          <form
                            onSubmit={editingDistributor ? handleSaveDistributorEdit : handleCreateDistributor}
                            className="flex flex-wrap gap-2"
                          >
                            <input
                              type="text"
                              required
                              placeholder="Ex: Cisco, Fortinet, HP..."
                              value={editingDistributor ? editingDistributor.nome : newDistributorName}
                              onChange={(e) => {
                                if (editingDistributor) {
                                  setEditingDistributor({ ...editingDistributor, nome: e.target.value });
                                } else {
                                  setNewDistributorName(e.target.value);
                                }
                              }}
                              className="flex-1 min-w-0 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800"
                            />
                            <button
                              type="submit"
                              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                            >
                              {editingDistributor ? 'Salvar' : 'Adicionar'}
                            </button>
                            {editingDistributor && (
                              <button
                                type="button"
                                onClick={() => setEditingDistributor(null)}
                                className="px-3 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold transition-all cursor-pointer"
                              >
                                Cancelar
                              </button>
                            )}
                          </form>
                        </div>

                        <div className="flex-1 min-h-48 overflow-auto bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl shadow-xs">
                          <table className="w-full text-left border-collapse text-xs">
                            <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                              <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                <th className="p-3">Fabricante</th>
                                <th className="p-3 text-center">Status</th>
                                <th className="p-3 text-right">Ações</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                              {loadingFabricantes ? (
                                <tr>
                                  <td colSpan="3" className="p-6 text-center text-slate-400 dark:text-slate-500">Carregando catálogo de fabricantes...</td>
                                </tr>
                              ) : fabricantesAdmin.length === 0 ? (
                                <tr>
                                  <td colSpan="3" className="p-6 text-center text-slate-400 dark:text-slate-500">Nenhum fabricante cadastrado.</td>
                                </tr>
                              ) : (
                                fabricantesAdmin.map(f => (
                                  <tr key={f.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/80 transition-colors">
                                    <td className={`p-3 ${f.ativo === false ? 'line-through text-slate-400 dark:text-slate-500' : 'font-semibold text-slate-800 dark:text-slate-200'}`}>
                                      {f.nome}
                                    </td>
                                    <td className="p-3 text-center">
                                      {f.ativo === false ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400">
                                          Inativo
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400">
                                          Ativo
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-3 text-right space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => setEditingDistributor(f)}
                                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold cursor-pointer"
                                      >
                                        Editar
                                      </button>
                                      <span className="text-slate-300 dark:text-slate-600">•</span>
                                      {f.ativo === false ? (
                                        <button
                                          type="button"
                                          onClick={() => handleActivateDistributor(f.id)}
                                          className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 font-semibold cursor-pointer"
                                        >
                                          Ativar
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => handleInactivateDistributor(f.id)}
                                          className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-semibold cursor-pointer"
                                        >
                                          Inativar
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* 4. ABA USUÁRIOS E PERMISSÕES — existing settings slot */}
                {settingsActiveTab === 'venders' && (
                  <UsuariosPermissoesSettings modo={config?.identity_mode} actorId={String(userProfile?.id || '')} onChange={loadVendedores} />
                )}

                {/* 5. ABA TIPOS DE TAREFAS */}
                {settingsActiveTab === 'taskTypes' && (
                  <div className="space-y-6">
                    <div className="mb-4">
                      <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Tipos de Tarefas</h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">Cadastre tipos de atividades personalizadas para a equipe comercial.</p>
                    </div>

                    <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-4 shadow-xs">
                      <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-3">
                        Novo Tipo de Tarefa
                      </h3>
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!newTaskTypeName.trim()) return;
                          const nome = newTaskTypeName.trim();
                          const emoji = newTaskTypeEmoji.trim() || '📋';
                          /* B2_I12_BEGIN */
                          if (kanbanModoRef.current !== 'legacy') {
                            try {
                              await comercialExecutar('tipo_tarefa_criar', 'novo', { nome, emoji: newTaskTypeEmoji.trim() || null });
                              await loadTiposTarefa();
                              setNewTaskTypeName('');
                              setNewTaskTypeEmoji('');
                              showToast('Tipo de tarefa adicionado!', 'success');
                            } catch (err) { showToast(comercialMensagem(err, 'Erro ao adicionar tipo de tarefa.'), 'error'); }
                            return;
                          }
                          /* B2_I12_END */
                          if (supabaseClient) {
                            const { error } = await supabaseClient.from('tipos_tarefa').insert({ nome, emoji });
                            if (error) {
                              showToast('Erro ao adicionar tipo de tarefa: ' + error.message, 'error');
                              return;
                            }
                            await loadTiposTarefa();
                          } else {
                            const novo = { id: Date.now().toString(), nome, emoji };
                            const atualizados = [...taskTypes, novo];
                            setTaskTypes(atualizados);
                            safeStorage.setItem('crm_cache_task_types', JSON.stringify(atualizados));
                          }
                          setNewTaskTypeName('');
                          setNewTaskTypeEmoji('');
                          showToast('Tipo de tarefa adicionado!', 'success');
                        }}
                        className="flex gap-2"
                      >
                        <input 
                          type="text" 
                          required
                          placeholder="Ex: WhatsApp"
                          value={newTaskTypeName}
                          onChange={(e) => setNewTaskTypeName(e.target.value)}
                          className="flex-1 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800"
                        />
                        <select 
                          value={newTaskTypeEmoji}
                          onChange={(e) => setNewTaskTypeEmoji(e.target.value)}
                          className="w-36 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer"
                        >
                          <option value="">Emoji...</option>
                          <option value="📞">📞 Ligação</option>
                          <option value="👥">👥 Reunião</option>
                          <option value="📧">📧 E-mail</option>
                          <option value="🔄">🔄 Follow-up</option>
                          <option value="💬">💬 WhatsApp</option>
                          <option value="🚀">🚀 Prospecção</option>
                          <option value="📝">📝 Contrato</option>
                          <option value="🎯">🎯 Visita</option>
                          <option value="🤝">🤝 Fechamento</option>
                        </select>
                        <button 
                          type="submit"
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                        >
                          + Cadastrar
                        </button>
                      </form>
                    </div>

                    <div className="max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl shadow-xs">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            <th className="p-3 w-16 text-center">Ícone</th>
                            <th className="p-3">Nome</th>
                            <th className="p-3 text-center w-24">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {taskTypes.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="p-6 text-center text-slate-400 dark:text-slate-500">Nenhum tipo cadastrado.</td>
                            </tr>
                          ) : (
                            taskTypes.map(t => (
                              <tr key={t.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/80 transition-colors">
                                <td className="p-3 text-center text-base">{t.emoji}</td>
                                <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">{t.nome}</td>
                                <td className="p-3 text-center">
                                  <button
                                    onClick={async () => {
                                      if (!confirm('Deseja realmente excluir este tipo de tarefa?')) return;
                                      /* B2_I12_BEGIN */
                                      if (kanbanModoRef.current !== 'legacy') {
                                        try {
                                          await comercialExecutar('tipo_tarefa_excluir', t.id, { tipo_id: t.id });
                                          await loadTiposTarefa();
                                          showToast('Tipo de tarefa excluído!', 'success');
                                        } catch (err) { showToast(comercialMensagem(err, 'Erro ao excluir tipo de tarefa.'), 'error'); }
                                        return;
                                      }
                                      /* B2_I12_END */
                                      if (supabaseClient) {
                                        const { error } = await supabaseClient.from('tipos_tarefa').delete().eq('id', t.id);
                                        if (error) {
                                          showToast('Erro ao excluir tipo de tarefa: ' + error.message, 'error');
                                          return;
                                        }
                                        await loadTiposTarefa();
                                      } else {
                                        const filtrados = taskTypes.filter(item => item.id !== t.id);
                                        setTaskTypes(filtrados);
                                        safeStorage.setItem('crm_cache_task_types', JSON.stringify(filtrados));
                                      }
                                      showToast('Tipo de tarefa excluído!', 'success');
                                    }}
                                    className="text-rose-600 dark:text-rose-400 hover:text-rose-800 dark:hover:text-rose-300 font-semibold cursor-pointer"
                                  >
                                    Excluir
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 5. ABA NUMERAÇÃO DE PROPOSTAS */}
                {settingsActiveTab === 'numeracao' && (
                  <div className="space-y-6">
                    <div className="mb-4">
                      <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Numeração Interna de Propostas</h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">Controle a geração atômica de números sequenciais oficiais para novas oportunidades e propostas.</p>
                    </div>

                    <div className="bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-5 shadow-xs space-y-5">
                      <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div>
                          <p className="text-sm font-bold text-slate-800 dark:text-slate-200">Status da Numeração Automática</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Quando ativo, cada nova oportunidade receberá o próximo número sequencial (Ex: {(configNumeracao.ultimo_numero || 13202) + 1}/{new Date().getFullYear()}).</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={configNumeracao.ativo}
                            onChange={async (e) => {
                              const novoAtivo = e.target.checked;
                              /* B2_I12_BEGIN */
                              if (kanbanModoRef.current !== 'legacy') {
                                try {
                                  const ajustada = await comercialExecutar('numeracao_ajustar', 'ativo', { novo_numero: null, novo_ativo: novoAtivo });
                                  setConfigNumeracao({ ultimo_numero: ajustada.ultimo_numero, ativo: ajustada.ativo });
                                  showToast(`Numeração automática ${novoAtivo ? 'ativada' : 'desativada'}!`, 'success');
                                } catch (err) { showToast(comercialMensagem(err, 'Erro ao atualizar a numeração.'), 'error'); carregarConfigNumeracao(); }
                                return;
                              }
                              /* B2_I12_END */
                              const { data, error } = await supabaseClient.rpc('ajustar_numeracao_proposta', { novo_ativo: novoAtivo });
                              if (!error && data && data[0]) {
                                setConfigNumeracao(data[0]);
                                showToast(`Numeração automática ${novoAtivo ? 'ativada' : 'desativada'}!`, 'success');
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-800 after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                      </div>

                      <div className="pt-2">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">Último Número Emitido</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={configNumeracao.ultimo_numero || ''}
                            onChange={(e) => setConfigNumeracao({ ...configNumeracao, ultimo_numero: parseInt(e.target.value) || 0 })}
                            className="w-48 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-xs font-mono font-bold text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              /* B2_I12_BEGIN */
                              if (kanbanModoRef.current !== 'legacy') {
                                try {
                                  const ajustada = await comercialExecutar('numeracao_ajustar', 'numero', { novo_numero: Number(configNumeracao.ultimo_numero), novo_ativo: null });
                                  setConfigNumeracao({ ultimo_numero: ajustada.ultimo_numero, ativo: ajustada.ativo });
                                  showToast('Último número atualizado com sucesso!', 'success');
                                } catch (err) { showToast(comercialMensagem(err, 'Erro ao atualizar a numeração.'), 'error'); }
                                return;
                              }
                              /* B2_I12_END */
                              const { data, error } = await supabaseClient.rpc('ajustar_numeracao_proposta', { novo_numero: configNumeracao.ultimo_numero });
                              if (!error && data && data[0]) {
                                setConfigNumeracao(data[0]);
                                showToast('Último número atualizado com sucesso!', 'success');
                              } else {
                                showToast('Erro ao atualizar: ' + (error?.message || ''), 'error');
                              }
                            }}
                            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                          >
                            Salvar Número
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">O próximo negócio criado receberá o número: <strong className="text-indigo-600 font-mono">{(configNumeracao.ultimo_numero || 0) + 1}/{new Date().getFullYear()}</strong></p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 6. ABA SEGMENTOS DE ATUAÇÃO */}
                {settingsActiveTab === 'segmentos' && (
                  <SegmentosSettings client={supabaseClient} kanbanModo={kanbanModoRef.current} canonico={kanbanModoRef.current === 'canonico_v1' && identidadeModoRef.current === 'canonico_v1' && session?.user?.id ? { config, userId: session.user.id } : null} />
                )}
              </main>
            </div>
          </div>
        </div>
      )}

      {/* 5. Modal de Adicionar Novo Item no Catálogo */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div
            ref={productModalRef}
            onKeyDown={productModalLayer.onKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-modal-title"
            className="w-full max-w-md bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700/90 rounded-2xl shadow-2xl p-6 relative">
            <button
              type="button"
              onClick={() => setShowProductModal(false)}
              className="absolute top-4 right-4 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
              title="Fechar (ESC)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 id="product-modal-title" className="text-base font-extrabold text-slate-900 dark:text-slate-100 mb-1">Adicionar Novo Produto</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">Adicione um novo produto ou licença ao catálogo do sistema.</p>

            <form onSubmit={handleCreateProduct} className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Nome do Produto</label>
                <input 
                  type="text" 
                  required
                  value={newProduct.nome}
                  onChange={(e) => setNewProduct({ ...newProduct, nome: e.target.value })}
                  placeholder="Ex: Servidor Dell PowerEdge R760"
                  className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Fabricante</label>
                  <select
                    required
                    value={newProduct.fabricante}
                    onChange={(e) => setNewProduct({ ...newProduct, fabricante: e.target.value })}
                    className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 font-medium cursor-pointer"
                  >
                    <option value="">Selecione o Fabricante...</option>
                    {fabricantes.map(f => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Custo de Referência</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs text-slate-400 dark:text-slate-500 font-semibold">R$</span>
                    <input 
                      type="number" 
                      step="0.01"
                      required
                      value={newProduct.custo_referencia}
                      onChange={(e) => setNewProduct({ ...newProduct, custo_referencia: e.target.value })}
                      placeholder="0.00"
                      className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 pl-8 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-bold text-white shadow-lg shadow-indigo-950/30 transition-all"
              >
                Cadastrar Produto
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 6. Modal de Fechamento (Ganho ou Perdido) */}
      {showCloseModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div
            ref={closeModalRef}
            onKeyDown={closeModalLayer.onKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="close-modal-title"
            className="w-full max-w-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-6 relative">
            <button
              onClick={() => setShowCloseModal(false)}
              className="absolute top-4 right-4 text-slate-500 dark:text-slate-400 hover:text-white cursor-pointer"
              title="Fechar (ESC)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 id="close-modal-title" className="text-lg font-bold text-white mb-2">
              {showCloseModal === 'win' ? '🏆 Fechamento - Proposta Ganha' : '😞 Fechamento - Proposta Perdida'}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
              {showCloseModal === 'win' 
                ? 'Insira os dados do fechamento do negócio ganho.' 
                : 'Insira o principal motivo e a data do fechamento do negócio perdido.'}
            </p>

            <div className="space-y-4">
              {/* Se for Perdida, exibe o Dropdown de motivo */}
              {showCloseModal === 'loss' && (
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Motivo da Perda</label>
                  <select 
                    value={selectedLossReason}
                    onChange={(e) => setSelectedLossReason(e.target.value)}
                    className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Selecione o motivo...</option>
                    <option value="Preço Alto">Preço Alto</option>
                    <option value="Prazo de Entrega">Prazo de Entrega</option>
                    <option value="Perdido para Concorrência">Perdido para Concorrência</option>
                    <option value="Projeto Cancelado pelo Cliente">Projeto Cancelado pelo Cliente</option>
                    <option value="Falta de Verba/Budget">Falta de Verba/Budget</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>
              )}

              {/* Data de Fechamento */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Data do Fechamento</label>
                <input 
                  type="date"
                  value={closeDate}
                  onChange={(e) => setCloseDate(e.target.value)}
                  className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <button 
                onClick={handleConfirmClose}
                disabled={saving}
                className={`w-full py-3 rounded-xl text-xs font-bold shadow-lg transition-all flex items-center justify-center space-x-1.5 ${
                  showCloseModal === 'win' 
                    ? 'bg-amber-500 hover:bg-amber-400 shadow-amber-950/30 text-amber-950' 
                    : 'bg-red-600 hover:bg-red-500 shadow-red-950/30 text-white'
                }`}
              >
                {saving ? 'Gravando...' : 'Confirmar Fechamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6.4 Modal de RESUMO da Tarefa (read-only) — abre ao clicar no card.
          z-[105] fica abaixo do modal de edição (z-[110]): o lápis daqui fecha
          este e abre aquele, mas a ordem garante o empilhamento certo caso os
          dois coexistam por um instante. */}
      {(() => {
        const task = taskDetailId ? commercialTasks.find(t => t.id === taskDetailId) : null;
        if (!task) return null;

        const isDone = task.status === 'concluida';
        const tc = getTaskTypeConfig(task.tipo);
        const matchedUser = vendedores.find(v => String(v.id) === String(task.responsavel_clickup_id));
        const assigneeName = matchedUser ? matchedUser.nome : '—';
        const negocio = getTaskNegocio(task);
        const negocioId = getTaskNegocioId(task);
        const { label: dateLabel, urgent } = formatTaskDate(task.data_vencimento);
        const uc = TASK_URGENCY_CONFIG[urgent] || TASK_URGENCY_CONFIG.normal;
        const emoji = (taskTypes.find(t => t.nome === task.tipo)?.emoji) || '📋';
        const dataCompleta = task.data_vencimento
          ? new Date(task.data_vencimento).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          : '—';

        return (
          <div className="fixed inset-0 z-[105] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4" onClick={closeTaskDetail}>
            <div
              ref={taskDetailModalRef}
              onKeyDown={taskDetailModalLayer.onKeyDown}
              role="dialog"
              aria-modal="true"
              aria-labelledby="task-detail-modal-title"
              className="w-full max-w-lg bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700/90 rounded-2xl shadow-2xl overflow-hidden relative animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cabeçalho: tipo + ações (lápis e fechar) */}
              <div className="border-b border-slate-200/80 dark:border-slate-700/80 px-6 py-4 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between gap-3">
                <h3 id="task-detail-modal-title" className="text-sm font-extrabold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2 min-w-0">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center shadow-sm text-sm ${tc.bg} ${tc.text} border ${tc.border}`}>
                    {emoji}
                  </span>
                  <span className="truncate">Detalhes da Tarefa</span>
                </h3>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => { const t = task; closeTaskDetail(); handleEditTaskClick(t); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30 dark:hover:bg-indigo-500/25 transition-colors cursor-pointer"
                    title="Editar tarefa"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={async () => { const t = task; const excluiu = await handleDeleteTask(t.id); if (excluiu) closeTaskDetail(); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30 dark:hover:bg-rose-500/25 transition-colors cursor-pointer"
                    title="Excluir tarefa"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <button
                    onClick={closeTaskDetail}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700/50 dark:text-slate-300 dark:border-slate-600/50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                    title="Fechar (ESC)"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Corpo: resumo somente leitura */}
              <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <p className={`text-base font-black leading-snug ${isDone ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}>
                    {task.titulo}
                  </p>
                  {negocio && negocio !== 'Sem Projeto' && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1.5 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="truncate">{negocio}</span>
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3">
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">Tipo</span>
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-1 flex items-center gap-1.5">
                      <span>{emoji}</span>{task.tipo || '—'}
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3">
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">Atribuído a</span>
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-1 flex items-center gap-1.5 min-w-0">
                      {typeof AvatarInicial !== 'undefined' && assigneeName !== '—'
                        ? <AvatarInicial nome={assigneeName} size="xs" />
                        : null}
                      <span className="truncate">{assigneeName}</span>
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3">
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">Vencimento</span>
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mt-1">{dataCompleta}</p>
                    {!isDone && (
                      <span className={`inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-md ring-1 ${uc.bg} ${uc.text} ${uc.ring}`}>
                        {dateLabel}
                      </span>
                    )}
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/80 rounded-xl p-3">
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">Status</span>
                    <p className="mt-1">
                      <span className={`inline-block text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wide ${
                        isDone
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                          : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {isDone ? 'Concluída' : 'Pendente'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Caixa de registro de atividade (revelada pelo botão abaixo).
                    Mesmo suporte a anexo da aba Atividades do drawer: botão,
                    colar (Ctrl+V) e arrastar — onPaste/onDrop no wrapper. */}
                {taskDetailShowAtividade && !isDone && (
                  <div
                    className="bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800/60 rounded-xl p-3.5"
                    onPaste={(e) => {
                      const arquivos = Array.from(e.clipboardData?.files || []);
                      if (arquivos.length > 0) {
                        e.preventDefault();
                        handleAdicionarAnexosTaskDetail(arquivos, negocioId);
                      }
                    }}
                    onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault(); }}
                    onDrop={(e) => {
                      const arquivos = e.dataTransfer?.files;
                      if (arquivos && arquivos.length > 0) {
                        e.preventDefault();
                        handleAdicionarAnexosTaskDetail(arquivos, negocioId);
                      }
                    }}
                  >
                    <span className="text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      Registrar no histórico do negócio
                    </span>
                    <MentionTextarea
                      value={taskDetailAtividadeTexto}
                      onChange={setTaskDetailAtividadeTexto}
                      membros={vendedores}
                      placeholder="Descreva o resultado da ação, retorno do cliente, próximos passos... Use @ para marcar um colega, ou cole/arraste um print."
                      rows={3}
                    />

                    {taskDetailAnexos.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {taskDetailAnexos.map(a => (
                          <div
                            key={a.id}
                            className={`inline-flex items-center gap-1.5 text-[10px] font-semibold pl-2 pr-1 py-1 rounded-lg border ${
                              a.status === 'error'
                                ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                            }`}
                            title={a.status === 'error' ? a.error : a.file.name}
                          >
                            {a.status === 'uploading' && <span className="w-2.5 h-2.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin shrink-0"></span>}
                            {a.status === 'done' && <span>📎</span>}
                            {a.status === 'error' && <span>⚠️</span>}
                            <span className="max-w-[120px] truncate">{a.file.name}</span>
                            <button onClick={() => handleRemoverAnexoTaskDetail(a.id)} className="text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer px-0.5" title="Remover">
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5 truncate">
                      Será registrado no histórico com a referência: ✅ Tarefa concluída ({task.tipo || 'Tarefa'}: {task.titulo})
                    </p>
                    <div className="flex gap-2 mt-2.5">
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,application/pdf"
                        ref={taskDetailFileInputRef}
                        onChange={(e) => { handleAdicionarAnexosTaskDetail(e.target.files, negocioId); e.target.value = ''; }}
                        className="hidden"
                      />
                      <button
                        onClick={() => taskDetailFileInputRef.current?.click()}
                        className="w-9 shrink-0 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        title="Anexar imagem ou PDF"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                        </svg>
                      </button>
                      <button
                        onClick={() => { setTaskDetailShowAtividade(false); setTaskDetailAtividadeTexto(''); setTaskDetailAnexos([]); }}
                        disabled={taskDetailSaving}
                        className="flex-1 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleConcluirTarefaComAtividade(task)}
                        disabled={taskDetailSaving || taskDetailAnexos.some(a => a.status === 'uploading') || (!taskDetailAtividadeTexto.trim() && !taskDetailAnexos.some(a => a.status === 'done'))}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          taskDetailSaving || taskDetailAnexos.some(a => a.status === 'uploading') || (!taskDetailAtividadeTexto.trim() && !taskDetailAnexos.some(a => a.status === 'done'))
                            ? 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white shadow-md shadow-indigo-600/20'
                        }`}
                      >
                        {taskDetailSaving ? 'Salvando...' : taskDetailAnexos.some(a => a.status === 'uploading') ? 'Enviando...' : 'Confirmar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Rodapé: ações de conclusão. data-arrow-group + data-arrow-item:
                  setas ←/→ alternam entre os botões, sem depender só do Tab. */}
              {!taskDetailShowAtividade && (
                <div className="border-t border-slate-200/80 dark:border-slate-700/80 px-6 py-4 bg-slate-50/60 dark:bg-slate-900/60 flex gap-2.5" onKeyDown={handleArrowNav}>
                  {isDone ? (
                    <button
                      data-arrow-item
                      onClick={() => handleConcluirTarefa(task)}
                      className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors cursor-pointer"
                    >
                      ↩️ Reabrir tarefa
                    </button>
                  ) : (
                    <React.Fragment>
                      <button
                        data-arrow-item
                        onClick={() => handleConcluirTarefa(task)}
                        className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white text-xs font-bold transition-all shadow-md shadow-emerald-600/20 cursor-pointer"
                      >
                        ✅ Concluir
                      </button>
                      <button
                        data-arrow-item
                        onClick={() => setTaskDetailShowAtividade(true)}
                        disabled={!negocioId}
                        title={negocioId
                          ? 'Concluir e registrar o resultado no histórico do negócio'
                          : 'Vincule um negócio a esta tarefa (lápis) para registrar atividade'}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          negocioId
                            ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white shadow-md shadow-indigo-600/20'
                            : 'border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                        }`}
                      >
                        📝 Concluir e registrar
                      </button>
                    </React.Fragment>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Lightbox de anexo (imagem) — Fase 4. z-[120]: acima de tudo,
          inclusive do drawer (que abre por baixo dele). */}
      {lightboxAnexoUrl && (
        <div
          ref={lightboxModalRef}
          onKeyDown={lightboxModalLayer.onKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label="Visualizar anexo"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-6"
          onClick={() => setLightboxAnexoUrl(null)}
        >
          <img
            src={lightboxAnexoUrl}
            alt="Anexo"
            className="max-w-full max-h-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxAnexoUrl(null)}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            title="Fechar (ESC)"
            data-autofocus
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* 6.5 Modal de Criar Nova Tarefa Comercial (Salesforce Style) */}
      {showNewTaskModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div
            ref={newTaskModalRef}
            onKeyDown={newTaskModalLayer.onKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-task-modal-title"
            className="w-full max-w-lg bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700/90 rounded-2xl shadow-2xl overflow-hidden relative animate-in fade-in zoom-in-95 duration-150">
            {/* Cabeçalho do Modal */}
            <div className="border-b border-slate-200/80 dark:border-slate-700/80 px-6 py-4 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between">
              <h3 id="new-task-modal-title" className="text-sm font-extrabold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
                <span className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-lg flex items-center justify-center shadow-sm">
                  {typeof IconDocument !== 'undefined' ? <IconDocument size={14} /> : null}
                </span>
                <span>{editingTask ? 'Editar Tarefa Comercial' : 'Nova Tarefa Comercial'}</span>
              </h3>
              <button 
                type="button"
                onClick={() => {
                  setShowNewTaskModal(false);
                  setSelectedProposalForTask(null);
                  setSearchProposalQuery('');
                  setProposalSearchResults([]);
                }}
                className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-600/60 transition-colors cursor-pointer"
                title="Fechar (ESC)"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateTaskSubmit} className="p-6 space-y-4">
              {/* Campo de Seleção do Negócio */}
              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Negócio Associado
                </label>
                <div className="relative">
                  {selectedProposalForTask ? (
                    <div className="px-3.5 py-2.5 bg-indigo-50/90 dark:bg-slate-900 border border-indigo-200 dark:border-slate-700 rounded-xl flex items-center justify-between shadow-xs">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0 animate-pulse" />
                        <span className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                          {searchProposalQuery}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-[10px] font-extrabold bg-indigo-600 text-white px-2.5 py-0.5 rounded-full shadow-xs">
                          ✓ Selecionado
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProposalForTask(null);
                            setSearchProposalQuery('');
                            setProposalSearchResults([]);
                            setShowProposalDropdown(false);
                          }}
                          className="p-1 text-slate-400 dark:text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                          title="Trocar negócio"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus-within:border-indigo-500 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                      <span className="pl-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        value={searchProposalQuery}
                        onChange={(e) => {
                          const q = e.target.value;
                          setSearchProposalQuery(q);
                          setSelectedProposalForTask(null);
                          if (q.trim().length >= 1) {
                            const q_lower = q.toLowerCase();
                            const filtered = (kanbanTasks || []).filter(t => 
                              (t.name || "").toLowerCase().includes(q_lower) ||
                              (t.id || "").toLowerCase().includes(q_lower)
                            );
                            setProposalSearchResults(filtered);
                            setShowProposalDropdown(filtered.length > 0);
                          } else {
                            setProposalSearchResults([]);
                            setShowProposalDropdown(false);
                          }
                        }}
                        onFocus={() => {
                          if (searchProposalQuery.trim().length >= 1 && proposalSearchResults.length > 0) {
                            setShowProposalDropdown(true);
                          } else if (searchProposalQuery.trim().length === 0) {
                            setProposalSearchResults(kanbanTasks || []);
                            if ((kanbanTasks || []).length > 0) setShowProposalDropdown(true);
                          }
                        }}
                        placeholder="Buscar por nome do negócio..."
                        className="flex-1 bg-transparent pl-2.5 pr-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 font-medium focus:outline-none placeholder-slate-400"
                      />
                      {searchProposalQuery.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchProposalQuery('');
                            setProposalSearchResults([]);
                            setShowProposalDropdown(false);
                          }}
                          className="pr-3 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Dropdown de sugestão de negócios */}
                  {showProposalDropdown && proposalSearchResults.length > 0 && (
                    <React.Fragment>
                      <div className="fixed inset-0 z-40" onClick={() => setShowProposalDropdown(false)} />
                      <ul className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl max-h-56 overflow-y-auto shadow-2xl z-50 divide-y divide-slate-100">
                        {proposalSearchResults.map(p => (
                          <li
                            key={p.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              const clean = getCleanBusinessName(p.name || p.nome || 'Projeto');
                              setSelectedProposalForTask({ ...p, name: clean });
                              setSearchProposalQuery(clean);
                              setShowProposalDropdown(false);
                            }}
                            className="flex items-center justify-between gap-2 cursor-pointer px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-indigo-50 hover:text-indigo-900 transition-colors"
                          >
                            <span className="font-semibold text-slate-900 dark:text-slate-100 leading-snug truncate">
                              {p.name || 'Projeto'}
                            </span>
                            <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full flex-shrink-0">
                              Selecionar
                            </span>
                          </li>
                        ))}
                      </ul>
                    </React.Fragment>
                  )}

                  {showProposalDropdown && proposalSearchResults.length === 0 && searchProposalQuery.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-500 dark:text-slate-400 text-center shadow-xl z-50">
                      Nenhum negócio encontrado para "{searchProposalQuery}"
                    </div>
                  )}
                </div>
              </div>

              {/* Título da Tarefa */}
              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Assunto / Título da Tarefa
                </label>
                <input
                  type="text"
                  required
                  data-autofocus
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Ex: Ligar para alinhar proposta comercial"
                  className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-200 font-medium focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-indigo-100 transition-all placeholder-slate-400"
                />
              </div>

              {/* Grid: Tipo de Atividade + Atribuído a */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Tipo de Atividade
                  </label>
                  <select 
                    value={newTaskType}
                    onChange={(e) => setNewTaskType(e.target.value)}
                    className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 font-medium focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 cursor-pointer transition-all"
                  >
                    {taskTypes.map(t => (
                      <option key={t.id} value={t.nome}>{t.emoji} {t.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Atribuído a
                  </label>
                  <select 
                    value={newTaskAssignee}
                    onChange={(e) => setNewTaskAssignee(e.target.value)}
                    className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 font-medium focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 cursor-pointer transition-all"
                  >
                    <option value="" className="text-slate-400 dark:text-slate-500">Selecione o responsável...</option>
                    {vendedoresVisiveis.map(v => (
                      <option key={v.id} value={String(v.id)}>{v.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Data de Vencimento e Hora */}
              <div>
                <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Data de Vencimento
                </label>
                <div className="flex items-center space-x-3">
                  <input 
                    type="date"
                    required
                    value={newTaskDueDate}
                    onChange={(e) => setNewTaskDueDate(e.target.value)}
                    className="flex-1 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-200 font-mono font-medium focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 cursor-pointer transition-all"
                  />
                  
                  {!hasTime ? (
                    <button
                      type="button"
                      onClick={() => setHasTime(true)}
                      className="px-3.5 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center space-x-1 cursor-pointer"
                    >
                      <span>+ Adicionar hora</span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-1.5">
                      <select
                        value={newTaskTime}
                        onChange={(e) => setNewTaskTime(e.target.value)}
                        className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 cursor-pointer font-mono font-medium"
                      >
                        {Array.from({ length: 41 }, (_, i) => {
                          const hour = Math.floor(8 + i * 0.25);
                          const minute = (i * 15) % 60;
                          const hourStr = String(hour).padStart(2, '0');
                          const minuteStr = String(minute).padStart(2, '0');
                          return `${hourStr}:${minuteStr}`;
                        }).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setHasTime(false)}
                        className="p-2.5 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                        title="Remover hora"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Botões do Rodapé */}
              <div className="flex items-center justify-end space-x-3 pt-5 border-t border-slate-100 dark:border-slate-800 mt-6">
                <button 
                  type="button"
                  onClick={() => {
                    setShowNewTaskModal(false);
                    setSelectedProposalForTask(null);
                    setSearchProposalQuery('');
                    setProposalSearchResults([]);
                  }}
                  className="px-4.5 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={creatingTask}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-600/20 transition-all cursor-pointer"
                >
                  {creatingTask ? (editingTask ? 'Salvando...' : 'Criando...') : (editingTask ? 'Salvar Alterações' : 'Criar Tarefa')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Cadastro de Nova R.O. (Task 5) */}
      {modalNovaRoAberto && (
        <NovaRoModal
          aberto={modalNovaRoAberto}
          onClose={() => {
            setModalNovaRoAberto(false);
            setModalNovaRoOportunidadeFixa(null);
          }}
          onSuccess={handleNovaRoSucesso}
          fabricantes={fabricantesRo}
          fabricantesStatus={fabricantesRoStatus}
          vendedores={vendedoresVisiveis}
          oportunidadeFixa={modalNovaRoOportunidadeFixa}
          supabaseClient={supabaseClient}
          getSupabaseHeaders={getCrmHeaders}
        />
      )}

      {/* Modal de Edição de Oportunidade / Negócio */}
      {showEditNegocioDrawerModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4" onClick={() => setShowEditNegocioDrawerModal(false)}>
          <div
            ref={editNegocioModalRef}
            onKeyDown={editNegocioModalLayer.onKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-negocio-modal-title"
            className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col ring-1 ring-slate-200 dark:ring-slate-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-md shrink-0">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </div>
                <div>
                  <h3 id="edit-negocio-modal-title" className="font-black text-base text-slate-900 dark:text-slate-100 leading-tight">Editar Oportunidade</h3>
                  {selectedTask?.numero_proposta_oficial && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-lg mt-0.5">
                      Nº da Oportunidade: {selectedTask.numero_proposta_oficial}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setShowEditNegocioDrawerModal(false)} className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer" title="Fechar (ESC)">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSalvarEditarNegocioDrawer} className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Título da Oportunidade *</label>
                <input required value={editNegocioDrawerForm.nome} onChange={e => setEditNegocioDrawerForm(p => ({ ...p, nome: e.target.value }))}
                  className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Estágio do Funil</label>
                  <select value={editNegocioDrawerForm.estagio} onChange={e => setEditNegocioDrawerForm(p => ({ ...p, estagio: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all cursor-pointer">
                    {['Registro','Qualificação','Proposta','Desenvolvimento','Negociação','Termo de aceite','Ganho','Perdido','Congelado'].map(est => (
                      <option key={est} value={est}>{est}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Tipo de Oportunidade</label>
                  <select value={editNegocioDrawerForm.tipo} onChange={e => setEditNegocioDrawerForm(p => ({ ...p, tipo: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all cursor-pointer">
                    <option value="Projeto">Projeto</option>
                    <option value="Garantias">Garantias</option>
                    <option value="Serviços">Serviços</option>
                    <option value="SSU">SSU</option>
                    <option value="Volumes">Volumes</option>
                    <option value="Upgrade">Upgrade</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Probabilidade</label>
                  <select value={editNegocioDrawerForm.probabilidade} onChange={e => setEditNegocioDrawerForm(p => ({ ...p, probabilidade: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all cursor-pointer">
                    <option value="10">10%</option>
                    <option value="30">30%</option>
                    <option value="50">50%</option>
                    <option value="70">70%</option>
                    <option value="90">90%</option>
                    <option value="100">100%</option>
                  </select>
                </div>
              </div>

              {/* Valor Estimado e Previsão de Fechamento saíram deste formulário:
                  Valor Estimado nunca era atualizado depois da criação e virava
                  lixo assim que a 1ª proposta existia; Previsão de Fechamento
                  agora vem sozinha da proposta marcada "Usar no Forecast". */}

              {/* Registros de Oportunidade (R.O.) */}
              <div>
                <div className="flex items-center gap-3 pt-1 mb-2">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 dark:text-slate-300">R.Os estruturadas vinculadas</span>
                  <div className="flex-1 h-px bg-slate-100 dark:bg-slate-700"></div>
                  {!editNegocioDrawerRos.loading && !editNegocioDrawerRos.error && (
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-400">
                      {editNegocioDrawerRos.rows.length}
                    </span>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/60 p-3">
                  {editNegocioDrawerRos.loading ? (
                    <div className="flex items-center gap-2 py-3 text-xs font-semibold text-slate-500 dark:text-slate-300" role="status">
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" aria-hidden="true"></span>
                      Carregando R.Os vinculadas...
                    </div>
                  ) : editNegocioDrawerRos.error ? (
                    <div className="rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/30 p-3 text-xs text-rose-700 dark:text-rose-300" role="alert">
                      {editNegocioDrawerRos.error}
                    </div>
                  ) : editNegocioDrawerRos.rows.length === 0 ? (
                    <p className="py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-300">
                      Nenhuma R.O. vinculada a esta oportunidade.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {editNegocioDrawerRos.rows.map((item) => (
                        <article key={item.id} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-bold leading-snug text-slate-900 dark:text-slate-100">
                                {item.identificacao.texto}
                              </p>
                              {item.identificacao.discriminador && (
                                <p className="mt-1 text-[10px] font-mono text-slate-500 dark:text-slate-400">
                                  {item.identificacao.discriminador}
                                </p>
                              )}
                              <dl className="sr-only">
                                <dt>Fabricante</dt><dd>{item.fabricante}</dd>
                                <dt>Número oficial</dt><dd>{item.numero}</dd>
                              </dl>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleVerDetalheRoDaOportunidade(item)}
                              className="shrink-0 rounded-lg border border-indigo-200 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/15 px-2.5 py-1.5 text-[11px] font-bold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/25 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 transition-colors cursor-pointer"
                            >
                              Ver detalhe
                            </button>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${item.situacao.classeBadge}`}>
                              {item.situacao.rotulo}
                            </span>
                            {item.vencimento && (
                              <span className="rounded-full border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-200">
                                Vencimento: {item.vencimento}
                              </span>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Escopo & Solução Técnica</label>
                <textarea rows={3} placeholder="Descreva o escopo, fabricantes (Dell, Veeam, etc.)..." value={editNegocioDrawerForm.descricao} onChange={e => setEditNegocioDrawerForm(p => ({ ...p, descricao: e.target.value }))}
                  className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-800 dark:text-slate-200 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all resize-none" />
              </div>

              <div className="flex gap-3 pt-3 border-t border-slate-100 dark:border-slate-800 shrink-0">
                <button type="button" onClick={() => setShowEditNegocioDrawerModal(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" disabled={savingEditNegocioDrawer} className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold disabled:opacity-50 transition-all shadow-md shadow-indigo-200 cursor-pointer">
                  {savingEditNegocioDrawer ? 'Salvando...' : '✓ Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. Drawer Lateral Direito */}
      {showDrawer && (
        <div className="drawer-container">
          <div 
            className={`drawer-backdrop ${showDrawer ? 'active' : ''}`} 
            onClick={() => {
              setShowDrawer(false);
              setClickupTaskId('');
            }}
          ></div>
          <div
            ref={drawerModalRef}
            onKeyDown={drawerModalLayer.onKeyDown}
            role="dialog"
            aria-modal="true"
            aria-label="Detalhes da Oportunidade"
            className={`drawer-content h-full flex flex-col ${showDrawer ? 'active' : ''} ${
              drawerTab === 'budget' ? 'w-[94vw] max-w-7xl' : 'w-full max-w-4xl md:max-w-5xl'
            }`}
          >
            {drawerTab === 'details' ? (
              <div className="flex-1 flex flex-col p-7 overflow-hidden bg-slate-50/60 dark:bg-slate-900/60">
                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-4 mb-5 gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <button 
                      onClick={() => {
                        setShowDrawer(false);
                        setClickupTaskId('');
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 shadow-2xs"
                      title="Voltar / Fechar (ESC)"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      <span>Voltar</span>
                    </button>

                    <div className="min-w-0">
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-100 leading-snug truncate">{selectedTask ? (selectedTask.nome || selectedTask.name) : 'Detalhes da Oportunidade'}</h3>
                      {(() => {
                        const numProp = selectedTask?.numero_proposta_oficial;
                        if (!numProp) return null;
                        return (
                          <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md mt-0.5">
                            Nº da Oportunidade: {numProp}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {estaOportunidadeElegivelParaRo(selectedTask) && (
                      <button
                        type="button"
                        onClick={() => handleAbrirNovaRoOportunidade(selectedTask)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
                        title="Cadastrar nova R.O. para esta oportunidade"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Nova R.O.</span>
                      </button>
                    )}

                    <button 
                      onClick={() => handleAbrirEditarNegocioDrawer(selectedTask)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
                      title="Editar oportunidade"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      <span>Editar</span>
                    </button>

                    <button 
                      onClick={() => handleExcluirNegocioDrawer(selectedTask)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
                      title="Excluir oportunidade"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                      <span>Excluir</span>
                    </button>

                    <button 
                      onClick={() => {
                        setShowDrawer(false);
                        setClickupTaskId('');
                      }}
                      className="w-8 h-8 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                      title="Fechar (ESC)"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Cards + Pipeline somem por completo na aba Atividades — o
                    espaço que liberam vai direto pro histórico, que é
                    flex-1 e absorve sozinho o que sobra. */}
                {drawerSection !== 'status' && (
                <div className="space-y-4 mb-6">
                  {/* Cards de Responsável, Valor e Previsão de Fechamento */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 border-l-indigo-400 shadow-sm shadow-slate-200/50">
                      <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-indigo-500 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        Responsável pelo Negócio
                      </span>
                      <select
                        className="w-full bg-transparent border-0 p-0 text-base font-black text-slate-900 dark:text-slate-100 focus:ring-0 focus:outline-none cursor-pointer mt-1.5"
                        value={selectedTask ? (selectedTask.responsavel_negocio || "") : ""}
                        onChange={(e) => {
                          if (selectedTask) {
                            const u = vendedoresVisiveis.find(v => v.nome === e.target.value);
                            handleResponsavelChange(selectedTask.id, e.target.value, u ? u.id : null);
                          }
                        }}
                      >
                        <option value="" className="bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">Selecione o responsável...</option>
                        {vendedoresVisiveis.map(v => (
                          <option key={v.id} value={v.nome} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200">{v.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 border-l-emerald-400 shadow-sm shadow-slate-200/50">
                      <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2m0-2c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        Valor Estimado
                      </span>
                      <span className="text-base font-black text-emerald-600 dark:text-emerald-400 tracking-tight mt-1.5 block">
                        {(() => {
                          if (currentProposta && currentProposta.situacao === 'Selecionada') {
                            return `R$ ${Number(realTimeGrandTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                          }
                          const val = getOpportunityValue(selectedTask);
                          return (val !== null && val !== undefined && !isNaN(val))
                            ? `R$ ${Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                            : 'R$ 0,00';
                        })()}
                      </span>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 border-l-amber-400 shadow-sm shadow-slate-200/50">
                      <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Previsão de Fechamento
                      </span>
                      <span className="text-base font-black text-slate-800 dark:text-slate-200 tracking-tight mt-1.5 block">
                        {selectedTask?.data_previsao ? formatDateSafe(selectedTask.data_previsao) : (
                          <span className="text-slate-400 dark:text-slate-500 font-semibold text-sm">Não definida</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Pipeline Premium — Estágio da Venda */}
                  <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm shadow-slate-200/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>
                        <span className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider">Pipeline de Vendas</span>

                        {/* Botão Congelar Pequeno com Feedback Animado */}
                        {(() => {
                          const safeColumns = (kanbanColumns && kanbanColumns.length > 0) ? kanbanColumns : ESTAGIO_OPTIONS;
                          const congeladoOption = safeColumns.find(c => (c.name || '').toLowerCase().includes('congelad'));
                          const currentOptId = getTaskOptionId(selectedTask, safeColumns);
                          const isFrozen = congeladoOption && currentOptId === congeladoOption.id;

                          if (isFrozen) {
                            return (
                              <button
                                onClick={async () => {
                                  if (selectedTask && safeColumns.length > 0) {
                                    const firstActiveCol = safeColumns.find(c => {
                                      const n = (c.name || '').toLowerCase();
                                      return !n.includes('congelad') && !n.includes('ganho') && !n.includes('perdido');
                                    }) || safeColumns[0];
                                    const ok = await handleOpportunityStateChange(selectedTask.id, firstActiveCol.id);
                                    if (ok) showToast('Negócio Descongelado! Retornou ao Pipeline ❄️', 'info');
                                  }
                                }}
                                className="bg-sky-500 hover:bg-sky-600 text-white px-2.5 py-0.5 rounded-full text-[10px] font-extrabold shadow-sm shadow-sky-500/30 flex items-center gap-1.5 cursor-pointer animate-pulse ring-2 ring-sky-300 transition-all"
                                title="Negócio atualmente Congelado! Clique para Descongelar"
                              >
                                <span className="w-2 h-2 rounded-full bg-white dark:bg-slate-800 animate-ping" />
                                <span>❄️ Congelado</span>
                              </button>
                            );
                          }

                          return (
                            <button
                              onClick={async () => {
                                if (selectedTask && congeladoOption) {
                                  const ok = await handleOpportunityStateChange(selectedTask.id, congeladoOption.id);
                                  if (ok) showToast('Negócio Congelado ❄️', 'info');
                                } else {
                                  showToast('Estágio Congelado não configurado.', 'warning');
                                }
                              }}
                              className="bg-slate-100 dark:bg-slate-700 hover:bg-sky-50 dark:hover:bg-sky-950/50 text-slate-600 dark:text-slate-300 hover:text-sky-700 dark:hover:text-sky-300 border border-slate-200 dark:border-slate-700 hover:border-sky-300 dark:hover:border-sky-800 px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
                              title="Clique para Congelar este negócio"
                            >
                              <span>❄️ Congelar</span>
                            </button>
                          );
                        })()}
                      </div>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Clique para avançar</span>
                    </div>

                    {/* Grid de Estágios com Ganho e Perdido acendendo apenas quando fechados */}
                    {(() => {
                      const rawOptions = (kanbanColumns && kanbanColumns.length > 0) ? kanbanColumns : ESTAGIO_OPTIONS;
                      const options = rawOptions.filter(o => {
                        const n = (o.name || '').toLowerCase();
                        return !n.includes('congelad') && !n.includes('ganho') && !n.includes('perdido');
                      });

                      const currentRawOptionId = getTaskOptionId(selectedTask, rawOptions);
                      const currentRawOption = rawOptions.find(c => c.id === currentRawOptionId);
                      const currentRawName = (currentRawOption?.name || selectedTask?.estagio || '').toLowerCase();

                      // Ganho só pode usar uma proposta explicitamente selecionada.
                      // Perdido pode ocorrer antes da primeira proposta; quando já
                      // existe uma versão, ela recebe o fechamento para preservar o histórico.
                      const propostaSelecionada = propostas?.find(p => p.situacao === 'Selecionada') || null;
                      const propostaParaPerda = propostaSelecionada || propostas?.find(p => p.versao === 'vA') || propostas?.[0] || null;
                      const hasSelectedProposal = Boolean(propostaSelecionada);

                      // Determinar se o negócio está Ganho, Perdido ou Congelado
                      const taskEstagio = (selectedTask?.estagio || '').toLowerCase();
                      const isWon = (propostaParaPerda && propostaParaPerda.situacao === 'Ganho') || currentRawName.includes('ganho') || taskEstagio.includes('ganho');
                      const isLost = (propostaParaPerda && propostaParaPerda.situacao === 'Perdido') || currentRawName.includes('perdido') || taskEstagio.includes('perdido');
                      const isFrozen = currentRawName.includes('congelad') || taskEstagio.includes('congelad');
                      const isInactiveState = isWon || isLost || isFrozen;

                      const currentIdx = !isInactiveState ? options.findIndex(o => o.id === currentRawOptionId) : -1;

                      return (
                        <React.Fragment>
                          <div className="grid gap-1.5 select-none" style={{ gridTemplateColumns: `repeat(${options.length + 2}, 1fr)` }}>
                            {options.map((col, idx) => {
                              const isCurrent = !isInactiveState && (currentRawOptionId === col.id || (currentIdx === -1 && idx === 0));
                              const isPassed = !isInactiveState && currentIdx !== -1 && idx < currentIdx;

                              return (
                                <button
                                  key={col.id || idx}
                                  onClick={async () => {
                                    if (!selectedTask) return;
                                    const colName = (col.name || '').toLowerCase();
                                    // Mesma proteção do drag-and-drop no Kanban (ver handleDrop):
                                    // ir direto pra Ganho/Perdido por aqui pula o fluxo que grava
                                    // data_fechamento/motivo_perda, deixando o negócio invisível
                                    // no relatório de faturamento.
                                    if (colName.includes('ganho') || colName.includes('perdido')) {
                                      setCloseDate(new Date().toISOString().split('T')[0]);
                                      if (colName.includes('ganho')) {
                                        if (colName.includes('ganho') && !propostaSelecionada) return;
                                        setCurrentProposta(propostaSelecionada);
                                        setShowCloseModal('win');
                                      } else {
                                        setCurrentProposta(propostaParaPerda);
                                        setSelectedLossReason('');
                                        setShowCloseModal('loss');
                                      }
                                      return;
                                    }
                                    setSelectedTask(prev => (prev ? { ...prev, estagio: col.name } : prev));
                                    await handleOpportunityStateChange(selectedTask.id, col.id);
                                  }}
                                  title={`Mover para: ${col.name}`}
                                  className={`relative flex flex-col items-center justify-center py-2.5 px-1 rounded-xl transition-all duration-300 cursor-pointer group ${
                                    isCurrent
                                      ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/30 scale-[1.03] ring-2 ring-indigo-400/30 ring-offset-1'
                                      : isPassed
                                      ? 'bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-emerald-700 dark:to-emerald-700 text-indigo-700 dark:text-white hover:from-indigo-100 hover:to-indigo-200 dark:hover:from-emerald-600 dark:hover:to-emerald-600'
                                      : 'bg-slate-100/80 dark:bg-slate-700/80 text-slate-500 dark:text-slate-400 hover:bg-slate-200/80 dark:hover:bg-slate-600/80 hover:text-slate-700 dark:hover:text-slate-300'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded-full flex items-center justify-center mb-1 ${
                                    isCurrent
                                      ? 'bg-white/25 dark:bg-slate-800/25'
                                      : isPassed
                                      ? 'bg-indigo-200/60 dark:bg-white/25'
                                      : 'bg-slate-200/60 dark:bg-slate-600/60'
                                  }`}>
                                    {isCurrent ? (
                                      <span className="w-2 h-2 bg-white dark:bg-slate-800 rounded-full animate-pulse" />
                                    ) : isPassed ? (
                                      <svg className="w-3 h-3 text-indigo-600 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <span className="w-1.5 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
                                    )}
                                  </div>
                                  <span className={`text-[10px] font-bold text-center leading-tight ${
                                    isCurrent ? 'text-white' : isPassed ? 'text-indigo-700 dark:text-white' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300'
                                  }`}>
                                    {col.name}
                                  </span>
                                  {isCurrent && (
                                    <span className="text-[7px] font-black text-white/60 uppercase tracking-widest mt-0.5">Atual</span>
                                  )}
                                </button>
                              );
                            })}

                            {/* Botão Ganho (Acende se for Ganho, senão fica apagado) */}
                            <button
                              disabled={!hasSelectedProposal}
                              onClick={() => {
                                if (propostaSelecionada) {
                                  setCurrentProposta(propostaSelecionada);
                                  setCloseDate(new Date().toISOString().split('T')[0]);
                                  setShowCloseModal('win');
                                }
                              }}
                              title={hasSelectedProposal ? "Marcar oportunidade como Ganha 🏆" : "Requer uma proposta Selecionada para fechar como Ganho"}
                              className={`relative flex flex-col items-center justify-center py-2.5 px-1 rounded-xl transition-all duration-300 ${
                                isWon
                                  ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/30 scale-[1.03] ring-2 ring-emerald-400 ring-offset-1 cursor-pointer'
                                  : hasSelectedProposal
                                  ? 'bg-slate-100/80 dark:bg-slate-700/80 text-slate-500 dark:text-slate-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 hover:text-emerald-700 dark:hover:text-emerald-300 hover:border-emerald-200 dark:hover:border-emerald-800 border border-transparent cursor-pointer'
                                  : 'bg-slate-100/50 dark:bg-slate-700/50 text-slate-300 dark:text-slate-600 border border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-50'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center mb-1 ${isWon ? 'bg-white/25 dark:bg-slate-800/25' : 'bg-slate-200/60 dark:bg-slate-600/60'}`}>
                                {isWon ? <span className="w-2 h-2 bg-white dark:bg-slate-800 rounded-full animate-pulse" /> : <span className="text-xs">🏆</span>}
                              </div>
                              <span className={`text-[10px] font-bold text-center leading-tight ${isWon ? 'text-white' : 'text-slate-500 dark:text-slate-400'}`}>Ganho</span>
                              {isWon && <span className="text-[7px] font-black text-white/70 uppercase tracking-widest mt-0.5">Atual</span>}
                            </button>

                            {/* Botão Perdido (Acende se for Perdido, senão fica apagado) */}
                            <button
                              onClick={() => {
                                setCurrentProposta(propostaParaPerda);
                                setCloseDate(new Date().toISOString().split('T')[0]);
                                setSelectedLossReason('');
                                setShowCloseModal('loss');
                              }}
                              title="Marcar oportunidade como Perdida 😞"
                              className={`relative flex flex-col items-center justify-center py-2.5 px-1 rounded-xl transition-all duration-300 ${
                                isLost
                                  ? 'bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-lg shadow-rose-500/30 scale-[1.03] ring-2 ring-rose-400 ring-offset-1 cursor-pointer'
                                  : 'bg-slate-100/80 dark:bg-slate-700/80 text-slate-500 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 hover:text-rose-700 dark:hover:text-rose-300 hover:border-rose-200 dark:hover:border-rose-800 border border-transparent cursor-pointer'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center mb-1 ${isLost ? 'bg-white/25 dark:bg-slate-800/25' : 'bg-slate-200/60 dark:bg-slate-600/60'}`}>
                                {isLost ? <span className="w-2 h-2 bg-white dark:bg-slate-800 rounded-full animate-pulse" /> : <span className="text-xs">😞</span>}
                              </div>
                              <span className={`text-[10px] font-bold text-center leading-tight ${isLost ? 'text-white' : 'text-slate-500 dark:text-slate-400'}`}>Perdido</span>
                              {isLost && <span className="text-[7px] font-black text-white/70 uppercase tracking-widest mt-0.5">Atual</span>}
                            </button>
                          </div>

                          {/* Barra de progresso total (Termo de Aceite = 86%, 100% exclusivo do Ganho) */}
                          {(() => {
                            const totalSteps = options.length + 1; // 7 etapas no total até o Ganho
                            const progress = isWon ? 100 : isLost ? 100 : isFrozen ? 0 : (currentIdx !== -1 ? Math.round(((currentIdx + 1) / totalSteps) * 100) : 0);
                            const barGradient = isWon
                              ? 'from-emerald-400 to-emerald-600'
                              : isLost
                              ? 'from-rose-400 to-rose-600'
                              : 'from-indigo-500 via-violet-500 to-emerald-500';
                            return (
                              <div className="mt-3 flex items-center gap-3">
                                <div className="flex-1 h-1.5 bg-slate-200/80 dark:bg-slate-600/80 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full bg-gradient-to-r ${barGradient} transition-all duration-500 ease-out`} style={{ width: `${progress}%` }} />
                                </div>
                                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 tabular-nums">{progress}%</span>
                              </div>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })()}
                  </div>
                </div>
                )}

                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Barra de Abas com Ícones + Rótulos — padrão ARIA tabs:
                      role="tablist" + setas movem e já trocam de aba
                      (data-arrow-activate), só a aba ativa fica no Tab. */}
                  <div role="tablist" onKeyDown={handleArrowNav} className="flex items-center gap-1.5 border-b border-slate-200 dark:border-slate-700 mb-0 px-1 bg-white dark:bg-slate-800 rounded-t-2xl pt-1.5 overflow-x-auto">
                    {/* Aba Propostas */}
                    <button
                      data-arrow-item
                      data-arrow-activate
                      role="tab"
                      id="drawer-tab-propostas"
                      aria-selected={drawerSection === 'propostas'}
                      aria-controls="drawer-tabpanel"
                      tabIndex={drawerSection === 'propostas' ? 0 : -1}
                      onClick={() => { setDrawerSection('propostas'); }}
                      title="Propostas"
                      className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all duration-200 cursor-pointer ${
                        drawerSection === 'propostas'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Propostas</span>
                      {drawerSection === 'propostas' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"></span>
                      )}
                    </button>

                    {/* Aba Tarefas */}
                    <button
                      data-arrow-item
                      data-arrow-activate
                      role="tab"
                      id="drawer-tab-tarefas"
                      aria-selected={drawerSection === 'tarefas'}
                      aria-controls="drawer-tabpanel"
                      tabIndex={drawerSection === 'tarefas' ? 0 : -1}
                      onClick={() => { setDrawerSection('tarefas'); }}
                      title="Tarefas"
                      className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all duration-200 cursor-pointer ${
                        drawerSection === 'tarefas'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      <span>Tarefas</span>
                      {drawerSection === 'tarefas' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"></span>
                      )}
                      {(() => {
                        const overdueCount = commercialTasks.filter(t => {
                          const propObj = Array.isArray(t.propostas) ? t.propostas[0] : t.propostas;
                          const isThisDeal = t.clickup_negocio_id === clickupTaskId ||
                                             (propObj && propObj.clickup_negocio_id === clickupTaskId) ||
                                             (currentProposta && t.proposta_id === currentProposta.id);
                          return isThisDeal && t.status === 'pendente' && new Date(t.data_vencimento) < new Date();
                        }).length;
                        return overdueCount > 0 ? (
                          <span className="w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse">{overdueCount}</span>
                        ) : null;
                      })()}
                    </button>

                    {/* Aba Status do Projeto */}
                    <button
                      data-arrow-item
                      data-arrow-activate
                      role="tab"
                      id="drawer-tab-status"
                      aria-selected={drawerSection === 'status'}
                      aria-controls="drawer-tabpanel"
                      tabIndex={drawerSection === 'status' ? 0 : -1}
                      onClick={() => { setDrawerSection('status'); fetchAtividades(clickupTaskId); }}
                      title="Status do Projeto"
                      className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all duration-200 cursor-pointer ${
                        drawerSection === 'status'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span>Atividades</span>
                      {drawerSection === 'status' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"></span>
                      )}
                    </button>

                    {/* Aba Empresa */}
                    <button
                      data-arrow-item
                      data-arrow-activate
                      role="tab"
                      id="drawer-tab-empresa"
                      aria-selected={drawerSection === 'empresa'}
                      aria-controls="drawer-tabpanel"
                      tabIndex={drawerSection === 'empresa' ? 0 : -1}
                      onClick={() => { setDrawerSection('empresa'); }}
                      title="Empresa"
                      className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all duration-200 cursor-pointer ${
                        drawerSection === 'empresa'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l8-4v18M13 21V11l6 3v7M9 9h.01M9 12h.01M9 15h.01" />
                      </svg>
                      <span>Empresa</span>
                      {drawerSection === 'empresa' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"></span>
                      )}
                    </button>

                    {/* Aba R.Os (N) — logo após Empresa */}
                    <button
                      data-arrow-item
                      data-arrow-activate
                      role="tab"
                      id="drawer-tab-ros"
                      aria-selected={drawerSection === 'ros'}
                      aria-controls="drawer-tabpanel"
                      tabIndex={drawerSection === 'ros' ? 0 : -1}
                      onClick={() => { setDrawerSection('ros'); setDrawerRosReload((n) => n + 1); }}
                      title="Registros de Oportunidade (R.Os) desta oportunidade"
                      className={`relative shrink-0 whitespace-nowrap flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-xl text-xs font-bold transition-all duration-200 cursor-pointer ${
                        drawerSection === 'ros'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.6-1.5a9 9 0 11-17.2 0 9 9 0 0117.2 0z" />
                      </svg>
                      <span>R.Os{drawerRos.negocioId === drawerRosNegocioId && !drawerRos.error && (drawerRos.cards.length > 0 || !drawerRos.loading) ? ` (${drawerRos.cards.length})` : ''}</span>
                      {drawerSection === 'ros' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"></span>
                      )}
                    </button>
                  </div>

                  {/* Conteúdo da Aba Selecionada */}
                  <div
                    role="tabpanel"
                    id="drawer-tabpanel"
                    aria-labelledby={`drawer-tab-${drawerSection}`}
                    className="flex-1 overflow-y-auto pr-1 pt-4 px-1 bg-white dark:bg-slate-800 rounded-b-2xl shadow-sm shadow-slate-200/40 border border-t-0 border-slate-200 dark:border-slate-700">

                    {/* === ABA: PROPOSTAS === */}
                    {drawerSection === 'propostas' && (
                      <div className="px-1">
                        {renderTimeline(false)}
                      </div>
                    )}

                    {/* === ABA: TAREFAS === */}
                    {drawerSection === 'tarefas' && (
                      <div className="px-1 space-y-3">
                        {/* Botão + Nova Tarefa Comercial dentro da aba Tarefas */}
                        <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-700 mb-3">
                          <span className="text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                            Tarefas Associadas
                          </span>
                          <button
                            onClick={handleNewTaskClick}
                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer flex items-center space-x-1"
                          >
                            <span>➕ Nova Tarefa Comercial</span>
                          </button>
                        </div>

                        {(() => {
                          const dealTasks = commercialTasks.filter(t => {
                            const propObj = Array.isArray(t.propostas) ? t.propostas[0] : t.propostas;
                            return t.clickup_negocio_id === clickupTaskId || 
                                   (propObj && propObj.clickup_negocio_id === clickupTaskId) ||
                                   (currentProposta && t.proposta_id === currentProposta.id);
                          });
                          
                          if (dealTasks.length === 0) {
                            return (
                              <div className="flex flex-col items-center justify-center py-12 text-center space-y-3 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                                <div className="w-14 h-14 bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 rounded-2xl flex items-center justify-center">
                                  <svg className="w-6 h-6 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                </div>
                                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nenhuma tarefa associada a este negócio.</p>
                              </div>
                            );
                          }

                          // Mesmo TaskCard da aba Tarefas geral (badge de tipo,
                          // vencimento, avatar, ações) — literalmente o mesmo
                          // componente, não uma cópia à parte.
                          return dealTasks.map(task => <TaskCard key={task.id} task={task} />);
                        })()}
                      </div>
                    )}

                    {/* === ABA: STATUS DO PROJETO === */}
                    {drawerSection === 'status' && (
                      <div className="px-1 space-y-5">
                        {/* Formulário de Nova Atividade — aceita anexo por
                            botão, arrastar-e-soltar ou colar (Ctrl+V) um
                            print direto na caixa. onPaste/onDrop no
                            wrapper (não no textarea): os dois eventos
                            borbulham do filho focado, então funcionam sem
                            precisar tocar no MentionTextarea. */}
                        <div
                          className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm shadow-slate-200/50"
                          onPaste={(e) => {
                            const arquivos = Array.from(e.clipboardData?.files || []);
                            if (arquivos.length > 0) {
                              e.preventDefault();
                              handleAdicionarAnexosAtividade(arquivos);
                            }
                          }}
                          onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault(); }}
                          onDrop={(e) => {
                            const arquivos = e.dataTransfer?.files;
                            if (arquivos && arquivos.length > 0) {
                              e.preventDefault();
                              handleAdicionarAnexosAtividade(arquivos);
                            }
                          }}
                        >
                          <span className="text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                            Registrar Atividade
                          </span>
                          <MentionTextarea
                            value={novaAtividade}
                            onChange={setNovaAtividade}
                            membros={vendedores}
                            placeholder="Descreva o resultado da ação, retorno do cliente, próximos passos... Use @ para marcar um colega, ou cole/arraste um print."
                            rows={3}
                          />

                          {novaAtividadeAnexos.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {novaAtividadeAnexos.map(a => (
                                <div
                                  key={a.id}
                                  className={`inline-flex items-center gap-1.5 text-[10px] font-semibold pl-2 pr-1 py-1 rounded-lg border ${
                                    a.status === 'error'
                                      ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                                  }`}
                                  title={a.status === 'error' ? a.error : a.file.name}
                                >
                                  {a.status === 'uploading' && <span className="w-2.5 h-2.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin shrink-0"></span>}
                                  {a.status === 'done' && <span>📎</span>}
                                  {a.status === 'error' && <span>⚠️</span>}
                                  <span className="max-w-[140px] truncate">{a.file.name}</span>
                                  <button onClick={() => handleRemoverAnexoAtividade(a.id)} className="text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer px-0.5" title="Remover">
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-2.5">
                            <input
                              type="file"
                              multiple
                              accept="image/*,.pdf,application/pdf"
                              ref={novaAtividadeFileInputRef}
                              onChange={(e) => { handleAdicionarAnexosAtividade(e.target.files); e.target.value = ''; }}
                              className="hidden"
                            />
                            <button
                              onClick={() => novaAtividadeFileInputRef.current?.click()}
                              className="w-9 h-9 shrink-0 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                              title="Anexar imagem ou PDF"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                              </svg>
                            </button>
                            <button
                              onClick={handleCreateAtividade}
                              disabled={savingAtividade || novaAtividadeAnexos.some(a => a.status === 'uploading') || (!novaAtividade.trim() && !novaAtividadeAnexos.some(a => a.status === 'done'))}
                              className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                savingAtividade || novaAtividadeAnexos.some(a => a.status === 'uploading') || (!novaAtividade.trim() && !novaAtividadeAnexos.some(a => a.status === 'done'))
                                  ? 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                  : 'bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white shadow-md shadow-indigo-600/20'
                              }`}
                            >
                              {savingAtividade ? 'Salvando...' : novaAtividadeAnexos.some(a => a.status === 'uploading') ? 'Enviando anexo...' : '💬 Registrar Atividade'}
                            </button>
                          </div>
                        </div>

                        {/* Lista de Atividades Registradas */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                              Histórico de Atividades
                            </span>
                            <button
                              onClick={() => { setAtividadeBuscaAberta(v => !v); if (atividadeBuscaAberta) setAtividadeBuscaQuery(''); }}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                                atividadeBuscaAberta
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600'
                              }`}
                              title="Buscar no histórico"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                              </svg>
                            </button>
                          </div>

                          {atividadeBuscaAberta && (
                            <div className="relative mb-3">
                              <input
                                type="text"
                                autoFocus
                                value={atividadeBuscaQuery}
                                onChange={(e) => setAtividadeBuscaQuery(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Escape') return;
                                  e.stopPropagation(); // não deixa o ESC borbulhar e fechar o drawer inteiro
                                  if (atividadeBuscaQuery) setAtividadeBuscaQuery('');
                                  else setAtividadeBuscaAberta(false);
                                }}
                                placeholder="Buscar por palavra, pessoa ou assunto..."
                                className="w-full rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 pl-9 pr-8 py-2 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800"
                              />
                              <svg className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                              </svg>
                              {atividadeBuscaQuery && (
                                <button
                                  onClick={() => setAtividadeBuscaQuery('')}
                                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer"
                                  title="Limpar busca"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}

                          {loadingAtividades ? (
                            <div className="flex items-center justify-center py-6">
                              <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          ) : atividades.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center space-y-2 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                              <div className="w-12 h-12 bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 rounded-2xl flex items-center justify-center">
                                <svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                              </div>
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nenhuma atividade registrada.</p>
                              <p className="text-[10px] text-slate-400 dark:text-slate-500">Registre ações, retornos e próximos passos.</p>
                            </div>
                          ) : (() => {
                            const agora = new Date();
                            const parsedList = atividades.map(ativ => ({ ativ, parsed: parseAtividade(ativ) }));
                            const query = atividadeBuscaAberta ? atividadeBuscaQuery : '';
                            const filtrada = query ? parsedList.filter(({ parsed }) => matchAtividade(parsed, query)) : parsedList;

                            if (query && filtrada.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center py-8 text-center space-y-1.5 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nenhum resultado para "{query}".</p>
                                  <button onClick={() => setAtividadeBuscaQuery('')} className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold hover:underline cursor-pointer">Limpar busca</button>
                                </div>
                              );
                            }

                            let ultimoDia = null;

                            return (
                              <div className="space-y-3">
                                {query && (
                                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold px-0.5">
                                    {filtrada.length} de {atividades.length} atividades
                                  </p>
                                )}
                                {filtrada.map(({ ativ, parsed }) => {
                                  const dia = atividadeDiaLabel(parsed.quando, agora);
                                  const mostrarCabecalhoDia = dia && dia !== ultimoDia;
                                  ultimoDia = dia || ultimoDia;
                                  const { relative, absolute } = formatRelativeTime(parsed.quando, agora);
                                  const autorNome = parsed.autorNome || 'Usuário';
                                  const selo = parsed.origem === 'tarefa' ? tarefaSeloLabel(parsed.tarefaTipo, parsed.tarefaTitulo) : null;

                                  return (
                                    <React.Fragment key={ativ.id}>
                                      {mostrarCabecalhoDia && (
                                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-0.5 pt-1 first:pt-0">
                                          {dia}
                                        </p>
                                      )}
                                      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-3.5 hover:border-indigo-200 dark:hover:border-indigo-800/60 hover:shadow-sm transition-all shadow-xs">
                                        {editingAtividade === ativ.id ? (
                                          <div className="space-y-2">
                                            <MentionTextarea
                                              value={editingAtividadeTexto}
                                              onChange={setEditingAtividadeTexto}
                                              membros={vendedores}
                                              rows={3}
                                            />
                                            <div className="flex items-center space-x-2">
                                              <button
                                                onClick={() => handleEditAtividade(ativ.id)}
                                                disabled={savingAtividade}
                                                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-bold transition-colors cursor-pointer"
                                              >
                                                {savingAtividade ? 'Salvando...' : 'Salvar'}
                                              </button>
                                              <button
                                                onClick={() => { setEditingAtividade(null); setEditingAtividadeTexto(''); }}
                                                className="px-3 py-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-[10px] font-bold transition-colors cursor-pointer"
                                              >
                                                Cancelar
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex items-center gap-2 min-w-0">
                                                {typeof AvatarInicial !== 'undefined' ? (
                                                  <AvatarInicial nome={autorNome} size="sm" />
                                                ) : (
                                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-[10px] font-extrabold flex-shrink-0 shadow-sm">
                                                    {autorNome.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                  </div>
                                                )}
                                                <div className="min-w-0">
                                                  <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{autorNome}</p>
                                                  <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium" title={absolute}>{relative}</span>
                                                    {selo && (
                                                      <span className="text-[9px] bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded font-bold">
                                                        ✅ {selo}
                                                      </span>
                                                    )}
                                                    {parsed.clickupSynced && (
                                                      <span className="text-[9px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded font-semibold">✓ ClickUp</span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                  onClick={() => { setEditingAtividade(ativ.id); setEditingAtividadeTexto(parsed.corpo); }}
                                                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30 dark:hover:bg-indigo-500/25 transition-colors cursor-pointer"
                                                  title="Editar atividade"
                                                >
                                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteAtividade(ativ.id)}
                                                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30 dark:hover:bg-rose-500/25 transition-colors cursor-pointer"
                                                  title="Excluir atividade"
                                                >
                                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                                                </button>
                                              </div>
                                            </div>
                                            <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap mt-2 pl-9">
                                              {renderTextoComMencoes(parsed.corpo, query)}
                                            </p>
                                            {parsed.anexos.length > 0 && (
                                              <div className="flex flex-wrap items-center gap-1.5 mt-2 pl-9">
                                                {parsed.anexos.map((anexo, i) => {
                                                  const isImagem = String(anexo.mimetype || '').startsWith('image/');
                                                  if (isImagem) {
                                                    return (
                                                      <button
                                                        key={anexo.clickup_attachment_id || i}
                                                        onClick={() => setLightboxAnexoUrl(anexo.url)}
                                                        title={anexo.title || 'Imagem'}
                                                        className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 hover:opacity-80 transition-opacity cursor-pointer"
                                                      >
                                                        <img src={anexo.url} alt={anexo.title || 'Anexo'} className="w-full h-full object-cover" />
                                                      </button>
                                                    );
                                                  }
                                                  return (
                                                    <a
                                                      key={anexo.clickup_attachment_id || i}
                                                      href={anexo.url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                                    >
                                                      📎 {anexo.title || 'Anexo'}
                                                    </a>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* === ABA: EMPRESA === */}
                    {drawerSection === 'empresa' && (
                      <div className="px-1 space-y-4 pb-4">
                        {!selectedTask?.conta_id ? (
                          <div className="flex flex-col items-center justify-center py-12 text-center space-y-3 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                            <div className="w-14 h-14 bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 rounded-2xl flex items-center justify-center">
                              <svg className="w-6 h-6 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l8-4v18M13 21V11l6 3v7" />
                              </svg>
                            </div>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nenhuma empresa vinculada a este negócio.</p>
                          </div>
                        ) : loadingEmpresaDoNegocio && !empresaDoNegocio ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : !empresaDoNegocio ? (
                          <div className="flex flex-col items-center justify-center py-12 text-center space-y-2 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Não foi possível carregar os dados da empresa.</p>
                          </div>
                        ) : (() => {
                          const tier = normalizeTier(empresaDoNegocio.account_tier);
                          const status = normalizeStatus(empresaDoNegocio.status);
                          return (
                            <React.Fragment>
                              {/* Cabeçalho */}
                              <div className="flex items-start gap-3">
                                <AvatarInicial nome={empresaDoNegocio.nome} size="lg" />
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 leading-tight truncate">{empresaDoNegocio.nome}</h4>
                                  {empresaDoNegocio.razao_social && empresaDoNegocio.razao_social !== empresaDoNegocio.nome && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">{empresaDoNegocio.razao_social}</p>
                                  )}
                                  <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono mt-0.5">
                                    {formatCNPJ(empresaDoNegocio.cnpj) || 'CNPJ não informado'}
                                    {empresaDoNegocio.cidade ? ` · ${empresaDoNegocio.cidade}/${empresaDoNegocio.estado}` : ''}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full border ${status.color}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>{status.label}
                                    </span>
                                    {tier && (
                                      <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full border ${tier.color}`}>
                                        <tier.Icon size={10} /> {tier.label}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Dados Corporativos */}
                              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm shadow-slate-200/50 overflow-hidden">
                                <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-100/80 dark:bg-slate-700/80">
                                  <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">Dados Corporativos</h5>
                                </div>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3.5">
                                  <DadoCampo label="CNPJ" value={formatCNPJ(empresaDoNegocio.cnpj)} mono />
                                  <DadoCampo label="Insc. Estadual" value={empresaDoNegocio.inscricao_estadual} mono />
                                  <DadoCampo label="Ciclo de Faturamento" value={empresaDoNegocio.billing_cycle} />
                                  <DadoCampo label="Segmento" value={empresaDoNegocio.industry} />
                                </dl>
                              </div>

                              {/* Contato Corporativo + Localização */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm shadow-slate-200/50 overflow-hidden">
                                  <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-100/80 dark:bg-slate-700/80">
                                    <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">Contato Corporativo</h5>
                                  </div>
                                  <dl className="px-4 py-3.5 space-y-2.5">
                                    <DadoCampo label="E-mail" value={empresaDoNegocio.email} href={empresaDoNegocio.email ? `mailto:${empresaDoNegocio.email}` : null} />
                                    <DadoCampo label="Telefone" value={formatPhone(empresaDoNegocio.telefone)} href={empresaDoNegocio.telefone ? `tel:${empresaDoNegocio.telefone}` : null} />
                                  </dl>
                                </div>
                                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm shadow-slate-200/50 overflow-hidden">
                                  <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-100/80 dark:bg-slate-700/80">
                                    <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">Localização</h5>
                                  </div>
                                  <div className="px-4 py-3.5 space-y-1">
                                    <p className="text-xs font-bold text-slate-900 dark:text-slate-100">{empresaDoNegocio.rua || '—'}</p>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                      {[empresaDoNegocio.cidade, empresaDoNegocio.estado].filter(Boolean).join(' - ') || '—'}
                                      {empresaDoNegocio.cep ? ` · CEP ${empresaDoNegocio.cep}` : ''}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Contatos */}
                              <div>
                                <span className="text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                  Contatos ({contatosDoNegocio.length})
                                </span>
                                {contatosDoNegocio.length === 0 ? (
                                  <div className="text-center py-8 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nenhum contato cadastrado para esta empresa.</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2.5">
                                    {contatosDoNegocio.map(c => (
                                      <div key={c.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/90 dark:border-slate-700/90 p-3.5">
                                        <div className="flex items-center gap-3">
                                          <AvatarInicial nome={c.nome} size="sm" />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                              <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">{c.nome}</p>
                                              {c.champion && <span className="shrink-0 text-amber-500" title="Champion / Principal Decisor">★</span>}
                                            </div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium truncate">{c.cargo || 'Cargo não informado'}</p>
                                          </div>
                                          {c.email && (
                                            <a href={`mailto:${c.email}`} title={`Enviar e-mail para ${c.email}`} className="shrink-0 flex items-center gap-1 px-2.5 py-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg text-[10px] font-bold hover:bg-sky-100 transition-colors">
                                              E-mail
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center pt-1">
                                Para editar os dados da empresa ou dos contatos, use a aba Empresas.
                              </p>
                            </React.Fragment>
                          );
                        })()}
                      </div>
                    )}

                    {/* === ABA: R.Os (N) === */}
                    {drawerSection === 'ros' && (
                      <div className="px-1 space-y-3 pb-4">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                            R.Os desta oportunidade
                          </span>
                          {estaOportunidadeElegivelParaRo(selectedTask) && (
                            <button
                              type="button"
                              onClick={() => handleAbrirNovaRoOportunidade(selectedTask)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
                              title="Cadastrar nova R.O. para esta oportunidade"
                            >
                              <span>+ Nova R.O.</span>
                            </button>
                          )}
                        </div>
                        {!drawerRosNegocioId ? (
                          <div className="text-center py-10 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-300">Esta oportunidade ainda não está sincronizada ao CRM; as R.Os ficam disponíveis após a sincronização.</p>
                          </div>
                        ) : drawerRos.loading && drawerRos.cards.length === 0 ? (
                          <div className="flex items-center justify-center gap-2 py-10 text-xs font-semibold text-slate-500 dark:text-slate-300" role="status">
                            <span className="w-4 h-4 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" aria-hidden="true"></span>
                            Carregando R.Os...
                          </div>
                        ) : drawerRos.error ? (
                          <div className="rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/30 p-3 text-xs text-rose-700 dark:text-rose-300 flex items-center justify-between gap-3" role="alert">
                            <span>{drawerRos.error}</span>
                            <button type="button" onClick={() => setDrawerRosReload((n) => n + 1)} className="shrink-0 rounded-lg border border-rose-300 dark:border-rose-700 px-2.5 py-1 text-[11px] font-bold hover:bg-rose-100 dark:hover:bg-rose-900/40 cursor-pointer">Tentar novamente</button>
                          </div>
                        ) : drawerRos.cards.length === 0 ? (
                          <div className="text-center py-10 bg-slate-50/70 dark:bg-slate-900/70 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl space-y-1">
                            <p className="text-xs font-semibold text-slate-600 dark:text-slate-200">Nenhuma R.O. cadastrada para esta oportunidade.</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-300">{estaOportunidadeElegivelParaRo(selectedTask) ? 'Use “+ Nova R.O.” para registrar a primeira.' : 'O estágio atual da oportunidade não permite criar R.O.'}</p>
                          </div>
                        ) : (
                          <ul className="space-y-2" aria-label="R.Os da oportunidade">
                            {drawerRos.cards.map((card) => (
                              <li key={card.id}>
                                <article className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 shadow-sm">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-xs font-bold leading-snug text-slate-900 dark:text-slate-100 break-words">{card.fabricante}</p>
                                      <p className="mt-0.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300 break-words">
                                        {card.numero === 'Aguardando número' ? <span className="italic">Aguardando número</span> : <>Nº {card.numero}</>}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleVerDetalheRoDaOportunidade({ ro: card.ro })}
                                      className="shrink-0 rounded-lg border border-indigo-200 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/15 px-2.5 py-1.5 text-[11px] font-bold text-indigo-700 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-500/25 cursor-pointer"
                                    >
                                      Ver detalhes
                                    </button>
                                  </div>
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${card.situacao.classeBadge}`}>{card.situacao.rotulo}</span>
                                    <span className="rounded-full border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-200">{card.ciclo}</span>
                                  </div>
                                  <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-300">
                                    <div className="flex gap-1"><dt className="font-semibold">Categoria:</dt><dd className="text-slate-700 dark:text-slate-100 break-words">{card.categoria}</dd></div>
                                    <div className="flex gap-1"><dt className="font-semibold">{card.dataRelevante.rotulo}:</dt><dd className="text-slate-700 dark:text-slate-100">{card.dataRelevante.valor || '—'}{card.dataRelevante.complemento ? ` · ${card.dataRelevante.complemento}` : ''}</dd></div>
                                  </dl>
                                </article>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              </div>
            ) : (
              <div className="drawer-split-container">
                {/* Lado Esquerdo: Timeline sempre visível */}
                <div className="drawer-split-sidebar flex flex-col p-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-200/80 dark:border-slate-700/80 mb-4 flex-shrink-0">
                    <h4 className="text-xs font-black uppercase tracking-wider text-indigo-400 dark:text-slate-300">Histórico de Versões</h4>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0 pr-1">
                    {renderTimeline()}
                  </div>
                </div>

                {/* Lado Direito: Editor de Orçamentos */}
                <div className="drawer-split-main flex flex-col">
                  {renderBudgetEditor()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// Renderizar o App React na div root
const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement);
root.render(<App />);
